import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, useReducer, useSyncExternalStore, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import {
  buildResolveMergeConflictClientMessageId,
  buildResolveMergeConflictInstructionText,
  formatForkSessionTitle,
  DEFAULT_REVIEW_PROMPT,
  normalizeAgentErrorDetails,
  normalizeAgentErrorMessage,
} from "@cord/shared";
import { buildOptimisticEmptyThreadSessionRecord } from "@/lib/optimistic-empty-thread-session-record";
import type {
  AgentRuntimeAttachedPullRequest,
  AgentRuntimeActionName,
  AgentRuntimePreferredMcpServerRef,
  AgentRuntimeRequestedSkillRef,
  AgentRuntimeSessionContextRef,
  AgentRuntimeScratchPadRef,
  AgentRuntimeSnapshot,
  AgentRuntimeStatus,
  SessionModelCatalog,
  AgentRuntimeTimelineItem,
  AgentType,
} from "@cord/shared";
import {
  WarningCircle,
  ArrowLeft,
  Trash,
  Archive,
  DotsThree,
  FileText,
  Globe,
  Monitor,
  Users,
  GitBranch,
  GitPullRequest,
  Check,
  X,
  Copy,
  Key,
  SpinnerGap,
  SidebarSimple,
  ArrowsClockwise,
  Terminal,
  Pause,
  Play,
  List,
  LinkSimple,
  DownloadSimple,
  Info,
  Plus,
  Rocket,
  PencilSimple,
} from "@phosphor-icons/react";
import { VSCodeLogoIcon } from "@/components/brand-icons";
import { useScratchPadDocument, SCRATCH_PAD_KEY } from "@/hooks/use-scratch-pad"
import type { NotesMentionItem } from "@/components/chat/NotesMentionPopover"
import { Tooltip } from "@/components/ui/Tooltip";
import { ThreadStatusBadge } from "@/components/ui/ThreadStatusBadge";
import { AnimatedTitle } from "@/components/ui/AnimatedTitle";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ANALYTICS_EVENTS,
  type AnalyticsProperties,
  buildPrAnalyticsProperties,
  buildThreadAnalyticsProperties,
  captureAnalyticsEvent,
} from "@/lib/analytics";
import { cleanupSessionLocalStorage } from "@/lib/session-localstorage-cleanup";
import {
  addDismissedSessionErrorKey,
  readDismissedSessionErrorKeys,
} from "@/lib/session-error-dismissal";
import {
  SHARED_TIMELINE_BOTTOM_OBSTRUCTION_CSS_VAR,
  dispatchSharedTimelineTailScrollRequest,
  dispatchSharedTimelineViewportFlush,
} from "@/lib/shared-timeline-viewport";
import { invalidatePersistedReplicaBySessionId } from "@/lib/thread-sync-runtime-cache";
import { authClient } from "@/lib/auth";
import { useInvalidateReposQueryWhenBuildsSettle } from "@/hooks/use-invalidate-repos-query-when-builds-settle";
import { normalizeArrayResponse } from "@/lib/repository-route-response-normalizers";
import { canShowRepositoryDeployUi } from "@/lib/repository-deploy-ui";
import { api } from "@/lib/api";
import {
  evaluateSpeculativeResumeEligibility,
  SPECULATIVE_RESUME_DWELL_MS,
} from "@/lib/speculative-resume-eligibility";
import {
  deriveRuntimeConversationStatusState,
  deriveRuntimeConversationThreadRuntimeEntry,
} from "@/lib/runtime-conversation-status-state-machine";
import {
  compareRuntimeConversationActivitySnapshotFreshness,
  runtimeConversationActivitySnapshotHasTerminalEvidence,
  useRuntimeConversationActivity,
  type RuntimeConversationActivitySnapshot,
} from "@/stores/runtime-conversation-activity";
import {
  deriveSessionActiveThreadControlState,
  deriveSessionBusyActionDisabledReason,
  hasRuntimeInputPausePendingForActiveConversation,
} from "@/lib/session-active-thread-control-state";
import { useForegroundSessionController } from "@/lib/session-foreground-controller";
import {
  claimForegroundSessionLease,
  getForegroundOwnershipTabId,
  getForegroundSessionLeaseSnapshot,
  isSessionForegroundOwnedByCurrentTab,
  isSessionForegroundOwnedElsewhere,
  subscribeForegroundSessionLeaseSnapshot,
  type SessionForegroundLease,
} from "@/lib/session-foreground-ownership";
import {
  buildThreadRuntimeBroadcastTabId,
  createThreadRuntimeBroadcastChannel,
} from "@/lib/thread-runtime-broadcast";
import { applyPrStatusCacheUpdate, type PullRequestState } from "@/lib/pr-status-cache";
import {
  clearThreadSyncSessionState,
  useThreadSync,
} from "@/hooks/use-thread-sync";
import { getThreadSyncManager } from "@/lib/thread-sync-manager";
import type {
  ChatGptOauthDebug,
  MessageRejectionEvent,
  QueuedMessage,
  SandboxProgress,
  SandboxStatus,
  SessionActionOutcomeNotice,
} from "@/hooks/thread-sync-client-types";
import { type PtyWsStatus } from "@/components/terminal";
import { useAgentRuntime } from "@/stores/agent-runtime";
import { useProviders } from "@/hooks/use-providers";
import { useSessionModelController } from "@/hooks/use-session-model-controller";
import { useThreadRuntimeSessionRowMirror } from "@/hooks/use-thread-runtime-session-row-mirror";
import {
  FORK_BOOTSTRAP_RECONCILIATION_WINDOW_MS,
  ForkBootstrapRequestError,
  buildForkOptimisticSessionRecord,
  buildForkOptimisticSnapshot,
  isForkBootstrapTransportError,
  isForkBootstrapWithinReconciliationWindow,
  isForkBootstrapOptimisticSessionRecord,
} from "@/lib/fork-bootstrap";
import { buildSessionStatusUrl } from "@/lib/session-status-url";
import { isNoRepoSessionRoute } from "@/lib/repoless-session-route";
import {
  SESSION_BRANCH_UPDATED_EVENT,
  type SessionBranchUpdatedDetail,
} from "@/lib/session-branch-sync-events";
import {
  SESSION_START_NEW_WORK_GIT_COMPLETED_EVENT,
  type SessionStartNewWorkGitCompletedDetail,
} from "@/lib/session-start-new-work-events";
import { SESSION_TERMINAL_TOGGLE_REQUEST_EVENT } from "@/lib/session-terminal-toggle-events";
import { SESSION_FOCUS_COMPOSER_EVENT } from "@/lib/session-focus-composer-events";
import { invalidateSessionRepositoryFileQueries } from "@/lib/session-repository-query-invalidation";
import {
  deriveRuntimeLifecycleAvailabilityStatus,
  normalizeConnectionFlowLinkConfig,
  normalizeConnectionFlowRuntimeLifecycleDebugSnapshot as normalizeConnectionFlowRuntimeLifecycleDebugSnapshotPayload,
  normalizeConnectionFlowRuntimeSnapshot as normalizeConnectionFlowRuntimeSnapshotPayload,
  resolveSandboxDashboardUrl,
  type ConnectionFlowLinkConfig,
  type ConnectionFlowRuntimeSnapshot,
} from "@/lib/session-connection-flow";
import { recordSessionTitleTimestamp } from "@/lib/multiplex-thread-activity-support";
import { useInputSettings } from "@/stores/input-settings";
import {
  getRepoOpenPullsQueryKey,
  getRepoSessionPickerQueryKey,
} from "@/lib/repo-data-fetches";
import {
  isContinuationContextOnlyFallbackMessage,
  isContinuationContextOnlyFallbackStatus,
} from "@/lib/continuation-runtime-block-errors";
import {
  CONTINUATION_ADOPTION_PENDING_CHANGED_EVENT,
  readContinuationAdoptionPending,
  writeContinuationAdoptionPending,
  type ContinuationAdoptionPendingChangedDetail,
} from "@/lib/continuation-adoption-pending";
import {
  clearPendingForkBootstrapEntry,
  clearPendingForkBootstrapRequestInFlight,
  isPendingForkBootstrapRequestInFlight,
  markPendingForkBootstrapRequestInFlight,
  patchPendingForkBootstrapEntry,
  setPendingForkBootstrapEntry,
  usePendingForkBootstrapEntry,
} from "@/stores/pending-fork-bootstrap";
import {
  clearPendingForkSnapshot,
  peekPendingForkSnapshot,
  setPendingForkSnapshot,
  type PendingForkSnapshot,
} from "@/stores/pending-fork-snapshot";
import { mergeSessionRecordPreservingHasHistory } from "@/lib/optimistic-empty-thread-session-record";
import {
  type PromptEditorHandle,
  type PromptHistoryEntry,
  type PrPickerItem,
  type SessionPickerItem,
  type SlashCommandItem,
} from "@/components/chat/PromptEditor";
import {
  fetchSessionMentionables,
  getMentionablesRuntimeSkillsCacheRefreshedAt,
  getSessionMentionablesQueryKey,
  mergeMentionablesByRuntimeSkillCacheFreshness,
  type SessionMentionablesResponse,
} from "@/lib/session-mentionables";
import { ChatInputArea } from "@/components/chat/ChatInputArea";
import { ImagePreviewProvider } from "@/components/chat/ImagePreviewProvider";
import {
  SessionCredentialUsagePanel,
  type SessionCredentialUsageResponse,
} from "@/components/chat/SessionCredentialUsagePanel";
import { samePrAdoptionStatus, type PrAdoptionStatus } from "@/components/chat/pr-adoption-status";
import { cn, formatSessionTitle } from "@/lib/utils";
import { isAbortLikeError } from "@/lib/abort-ux";
import { buildNewThreadBootstrapQueuedMessages } from "@/lib/new-thread-bootstrap-queue";
import {
  type ActionAcknowledgementMode,
  type ActionAcknowledgementMatcher,
  hasBlockingPendingSessionActions,
  buildActionQueuedMessage,
  createActionMessageDisposable,
  mergeQueuedMessagesWithPendingSessionActionMessages,
  promoteActionQueuedMessageToServerQueued,
  type ActionStoreSubscribe,
} from "@/lib/action-queued-message-lifecycle";
import { debugWarn } from "@/stores/debug-logs";
import {
  dedupeSessionContextRefs,
  sameSessionContextRefs,
} from "@/lib/session-context-refs";
import {
  dedupePreferredMcpServerRefs,
  dedupeRequestedSkillRefs,
  samePreferredMcpServerRefs,
  sameRequestedSkillRefs,
} from "@/lib/message-link-references";
import type { AgentError } from "@/types/opencode";
import { ForkBootstrapBanner } from "@/components/session/ForkBootstrapBanner";
import { FORK_UNSUPPORTED_PROVIDER_REASON } from "@/lib/sandbox-provider-ui-policy";
import { RuntimeInspector } from "@/components/session/RuntimeInspector";
import { ConnectionFlowRecoveryLifecyclePanel } from "@/components/session/ConnectionFlowRecoveryLifecyclePanel";
import { usePersistentSessionPreviewFrameController } from "@/components/session/PersistentSessionPreviewFrameProvider";
import { SessionLayout } from "@/components/session/SessionLayout";
import { RuntimeChatPane } from "@/components/chat/shared/RuntimeChatPane";
import { TodoDock } from "@/components/chat/shared/TodoDock";
import { CreatePrPopover } from "@/components/session/CreatePrPopover";
import {
  fetchGitStatus,
  getGitLogQueryKey,
  getGitStatusQueryKey,
  getPrStatusQueryKey,
  usePrStatus,
  useGitStatus,
} from "@/hooks/use-git";
import {
  deriveAttachedPullRequestActionStateMachine,
} from "@/lib/attached-pull-request-action-state-machine";
import { sameAttachedPullRequest } from "@/lib/attached-pull-request";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import {
  clearSessionLayoutSnapshotForSession,
  restoreSessionLayoutSnapshotForSession,
  saveSessionLayoutSnapshotForSession,
  useSessionUI,
} from "@/stores/session-ui";
import {
  deriveQuickPrUiState,
  getSessionPrFlowSnapshotTimestamp,
  useSessionPrFlow,
} from "@/stores/session-pr-flow";
import { useSessionPrActionState } from "@/stores/session-pr-action-state";
import {
  isThreadRuntimeYoloSpinnerActive,
  isYoloFlowStepActive,
  useThreadRuntime,
} from "@/stores/thread-runtime";
import { CaretDown, CaretRight, ArrowSquareOut, ClockCounterClockwise, Wrench } from "@phosphor-icons/react";
import { useImageAttachments } from "@/hooks/use-image-attachments";
import type { ImageAttachment } from "@/hooks/use-image-attachments";
import { takePendingAttachments } from "@/stores/pending-attachments";
import {
  loadSessionDraft,
} from "@/lib/session-draft-idb";
import { useShortcutLabels } from "@/hooks/use-shortcut-labels";
import {
  clearPersistentSessionPreviewUiMemory,
  getPersistentSessionPreviewUiMemorySnapshot,
  markPersistentSessionPreviewOpened,
  resolveSelectedPreviewEndpointId,
  setPersistentSessionPreviewReloadKey,
  setPersistentSessionSelectedPreviewEndpointId,
  subscribePersistentSessionPreviewUiMemory,
  touchPersistentSessionPreviewUiMemory,
} from "@/lib/persistent-session-preview-ui-memory";
import {
  getThinkingVariants,
  normalizeThinkingVariant,
  resolveEffectiveThinkingVariant,
  THINKING_OFF_VARIANT,
} from "@/config/model-capabilities";
import { sanitizeClaudeModelSelection } from "@/lib/claude-models";
import {
  hasSessionChangesForArchiveConfirmation,
  isRunningSandboxForArchiveConfirmation,
  RUNNING_SANDBOX_ARCHIVE_CONFIRMATION_MESSAGE,
  RUNNING_SANDBOX_SUSPEND_CONFIRMATION_MESSAGE,
} from "@/lib/running-sandbox-confirmation-messages";
import {
  createSessionActionRequestError,
  createSessionActionRequestErrorFromResponse,
  getSessionActionErrorMessage,
  isIndeterminateSessionActionError,
  shouldRefreshStatusAfterSessionActionError,
} from "@/lib/session-action-request-error";
import { FilePathSetProvider } from "@/contexts/file-path-set";
import { useMobileLayout } from "@/hooks/use-mobile-layout";
import { RepoIcon } from "@/components/layout/RepoIcon";
import { MobileSessionHeader, MobileMenuItem, MobileMenuDivider } from "@/components/mobile/MobileSessionHeader";
import { getAgentCapabilities, type AgentCapabilities } from "@/lib/agent-capabilities";
import { getAgentTypeLabel } from "@/lib/agent-options";
import { useRuntimeComposeModeSync } from "@/hooks/use-runtime-compose-mode-sync";
import {
  deriveClaudePermissionModeFromProjection,
  deriveLatestClaudeBuildPermissionModeFromProjection,
} from "@/lib/compose-mode-sync";
import {
  getTimelineMessageTargetId,
  getAgentRuntimeUserMessage,
  getAgentRuntimeUndoTargetMessageId,
  resolveAgentRuntimeForkTargetId,
} from "@/agent-runtime/selectors";
import {
  applyStoredSessionIntentToLocalStorage,
  readSessionIntentFromSessionRecord,
} from "@/lib/session-model-storage";
import {
  buildExplicitSessionModelIntentFromOutgoingMessage,
  createSessionModelIntentMutationMetadata,
  persistSessionModelIntent,
  shouldIgnoreStaleSessionModelIntentFromServer,
} from "@/lib/session-model-intent-persistence";
import {
  applyRuntimeUserMessagePrefill as applyRuntimeUserMessagePrefillState,
  buildRuntimeUserMessageDraftAttachments,
  buildRuntimeUserMessagePrefill,
  EMPTY_RUNTIME_USER_MESSAGE_PREFILL,
  type RuntimeUserMessagePrefill,
} from "@/lib/runtime-user-message-prefill";
import {
  MobileToolsButton,
  MobileToolsInfoRow,
  MobileToolsLink,
  MobileToolsPanel,
  MobileToolsSection,
} from "@/components/session/MobileToolsPanel";
import {
  type AdoptContinuationResponse,
  applyOptimisticSessionMembershipToSessionListCaches,
  applyRuntimeActionBlockReason,
  asNonEmptyString,
  readAcceptedUserMessageMarker,
  buildPrAdoptionOutcomeKey,
  buildPrAdoptionSuccessAttachmentKey,
  buildConnectionFlowCloudflareDoUrl,
  buildOptionalRuntimeActionAvailability,
  buildPrAdoptionSuccessStatus,
  buildSessionDraftStorageId,
  clearInitialBootstrapServerEnqueueState,
  deriveComposerCanSend,
  deriveTopBarTransportAvailabilityStatus,
  deriveSessionTransportOwnershipState,
  buildSessionRequestError,
  captureSessionCacheMutationSnapshot,
  chatDraftStorageKey,
  EMPTY_PENDING_BOOTSTRAP_DRAFT,
  EMPTY_THREAD_DRAFT,
  findConsumedThreadDraftObservation,
  findPrLinkedSessionTitle,
  formatChatGptOauthDebug,
  formatMenuTimestamp,
  formatSandboxStatus,
  formatServerSyncHistoryDebug,
  formatServiceStatus,
  formatSyncReadyDecisionSummary,
  getSandboxStartupUiLabel,
  deriveSandboxUnavailableReason,
  formatSyncHistoryTokenDebug,
  formatWsStatus,
   getForkThreadErrorMessage,
   getInitialBootstrapServerEnqueueStaleMs,
   hasObservedAttachedPullRequest,
   hasObservedBootstrapClientMessage,
    hasPendingBootstrapDraftIntent,
    isSetupOnlyPendingBootstrapDraft,
    initialBootstrapDraftStorageKey,
   initialContextRefsStorageKey,
   initialMessageStorageKey,
  isInitialBootstrapServerEnqueueStateStale,
  normalizePendingBootstrapDraft,
  parseStoredSessionContextRefs,
  readAcknowledgedPrAdoptionOutcome,
  readAcknowledgedPrAdoptionSuccessAttachment,
  readConsumedPrAdoptionAttachment,
  readInitialBootstrapServerEnqueueState,
  isBranchSwitchInProgressError,
  inferSandboxStartupKind,
  loadChatDraftSync,
   loadThreadDraftSnapshot,
   loadThreadDraftSnapshotIfNewer,
   mergeMissingThreadDraftLinkedMetadata,
  normalizeRuntimeActionAvailability,
  normalizeRuntimeConversationDraftId,
  normalizeSandboxAuthHealthResponse,
  normalizeSessionAgentIdentity,
  normalizeSlashCommandItems,
  optimisticAgentTypeStorageKey,
  optimisticSessionTitleStorageKey,
  parseSlashCommandInput,
  persistChatDraft,
  primeRemoteDraftPersistVersion,
  readErrorMessageFromResponse,
  resolveClaudeOutgoingPermissionMode,
  resolveSessionAgentIdentity,
  restoreSessionCacheMutationSnapshot,
  resolveStopPendingStateTransition,
  saveThreadDraftSnapshot,
  clearThreadViewAndReloadIfStillCurrent,
  selectSessionCacheMutationSource,
  showBranchSwitchInProgressToast,
  shouldPreserveComposerStateAfterBootstrapFinalize,
  buildAbortActiveRunSocketMessage,
  buildQueuedMessagePreviewAttachments,
  buildQueuedMessagePreviewArtifacts,
   buildOutgoingAttachmentFileParts,
   deriveMergedThreadContinuationState,
   buildOpenCodeWebUiLaunchUrl,
   OPENCODE_WEB_UI_VERSION,
  toKnownAgentType,
  type CachedSessionListItem,
  type ContinueSessionResponse,
  type ForkThreadChildSession,
  type OpenCodeAccessSnapshot,
  type ParsedSlashCommand,
  type PendingBootstrapDraft,
  type PendingBootstrapPrAdoption,
  type PrHistoryEntry,
  type RuntimeActionUiState,
  type SandboxAuthHealthResponse,
  type SessionApiErrorBody,
  type SessionAgentIdentity,
  type SessionRequestError,
  type StartNewWorkMutationInput,
  type StartNewWorkPromiseEntry,
  type StartNewWorkResponse,
  type StartNewWorkTrigger,
  type ThreadDraftConsumptionObservation,
  type ThreadDraftSnapshot,
  type TopBarAvailabilityStatus,
  waitForPendingLocalDraftPersist,
  waitForPendingRemoteDraftPersist,
  writeAcceptedUserMessageMarker,
  writeAcknowledgedPrAdoptionOutcome,
  writeAcknowledgedPrAdoptionSuccessAttachment,
  writeConsumedPrAdoptionAttachment,
} from "./-session-page-helpers";
import {
  AgentErrorBanner,
  BareImageBanner,
  BuildHealthBareImageBanner,
  BranchSwitchInProgressNotice,
  ClosedPrNotice,
  EmptyState,
  HistoricalPullRequestBanner,
  MergedPrBanner,
  NewWorkStartedNotice,
  PrHistoryDropdown,
  ResumeRequiredBanner,
  SandboxAuthHealthBanner,
  SandboxErrorBanner,
  SandboxStartupHeaderIndicator,
  StaleBuildBanner,
  TopBarAvailabilityBadge,
} from "./-session-page-presentational";
import {
  applySessionGitExecResult,
  normalizeSessionGitExecResult,
  readSessionGitExecResult,
} from "@/lib/session-git-exec-result";
import {
  clearPersistableSessionSurfaceQueriesForSession,
  unblockPersistableSessionSurfaceQueriesForSession,
} from "@/lib/persisted-session-surface-query-cache";

const RUNTIME_ERROR_DISMISSAL_SCOPE = "runtime";
const SANDBOX_ERROR_DISMISSAL_SCOPE = "sandbox";

type DeploymentStatus = "not_deployed" | "building" | "pushing" | "deploying" | "live" | "failed" | "destroyed";

interface DeploymentDto {
  id: string;
  status: DeploymentStatus;
  url: string | null;
  lastDeployError: string | null;
  lastDeployedAt: string | null;
  deployCount: number;
}

function markSessionRowHasHistory(args: { row: unknown }): unknown {
  if (!args.row || typeof args.row !== "object" || Array.isArray(args.row)) {
    return args.row;
  }
  const record = args.row as Record<string, unknown>;
  if (record.hasHistory === true) return args.row;
  return {
    ...record,
    hasHistory: true,
  };
}

/**
 * Polling interval for deployment status in the top bar.
 * 2.5 seconds balances responsiveness with API load.
 */
const DEPLOY_TOPBAR_POLL_INTERVAL_MS = 2_500;

function DeploymentTopBarControl(args: {
  repoId: string;
  sessionId: string;
  deployEnabled: boolean;
  compact: boolean;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const previousStatusRef = useRef<string | undefined>(undefined);

  const deploymentQuery = useQuery({
    queryKey: ["deployment", args.repoId],
    enabled: args.deployEnabled,
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const response = await fetch(`${baseUrl}/api/repos/${args.repoId}/deployment`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to load deployment status");
      return response.json() as Promise<{ deployEnabled: boolean; deployment: DeploymentDto | null }>;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.deployment?.status;
      return status === "building" || status === "pushing" || status === "deploying"
        ? DEPLOY_TOPBAR_POLL_INTERVAL_MS
        : false;
    },
  });

  // Show toast when deploy transitions to "live" while user is on the session page.
  // This must run in useEffect (not render body) because toast is a side effect
  // and React may re-render without committing, which would duplicate the toast.
  const currentStatus = deploymentQuery.data?.deployment?.status;
  const deployUrl = deploymentQuery.data?.deployment?.url;
  useEffect(() => {
    if (
      currentStatus === "live" &&
      previousStatusRef.current &&
      previousStatusRef.current !== "live" &&
      (previousStatusRef.current === "building" || previousStatusRef.current === "pushing" || previousStatusRef.current === "deploying")
    ) {
      toast.success(
        deployUrl
          ? `Deploy complete — live at ${deployUrl.replace(/^https?:\/\//, "")}`
          : "Deploy complete!",
        { duration: 6000 },
      );
    }
    previousStatusRef.current = currentStatus;
  }, [currentStatus, deployUrl]);

  const deployMutation = useMutation({
    mutationFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const response = await fetch(`${baseUrl}/api/repos/${args.repoId}/deploy`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: args.sessionId }),
      });
      const body = await response.json().catch(() => null) as { error?: string; imageRef?: string; deployment?: DeploymentDto } | null;
      if (!response.ok) throw new Error(body?.error ?? "Failed to start deploy");
      return body;
    },
    onMutate: () => {
      // Navigate immediately so the user sees progress UI right away,
      // rather than waiting for the (potentially slow) deploy POST to finish.
      void navigate({
        to: "/$repoId/deploy",
        params: { repoId: args.repoId },
        search: { sessionId: args.sessionId },
      });
    },
    onSuccess: (data) => {
      // Seed the query cache with the returned deployment so the Deploy page
      // shows the active "building" state immediately instead of briefly
      // displaying stale "live"/"not_deployed" until the refetch completes.
      if (data?.deployment) {
        queryClient.setQueryData(["deployment", args.repoId], {
          deployEnabled: true,
          deployment: data.deployment,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["deployment", args.repoId] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to start deploy");
    },
  });

  if (!args.deployEnabled) return null;

  const deployment = deploymentQuery.data?.deployment ?? null;
  const status = deployment?.status ?? "not_deployed";
  const busy = deployMutation.isPending || status === "building" || status === "pushing" || status === "deploying";
  const isLive = status === "live" && deployment?.url;

  // Consistent height across all deploy badge states (28px matches other top bar controls)
  const DEPLOY_BADGE = "flex h-7 items-center rounded-lg border text-xs font-medium transition-all";

  // When busy/deploying: clicking navigates to the deploy page to watch progress
  if (busy) {
    return (
      <Link
        to="/$repoId/deploy"
        params={{ repoId: args.repoId }}
        search={{ sessionId: args.sessionId }}
        className={cn(
          DEPLOY_BADGE,
          "gap-1.5 border-sky-500/20 bg-sky-500/[0.08] px-2.5 text-sky-300 hover:bg-sky-500/[0.14]",
        )}
      >
        <SpinnerGap className="size-3 animate-spin" />
        {!args.compact && <span>Deploying…</span>}
      </Link>
    );
  }

  // When live: status badge that opens a dropdown with actions
  if (isLive) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              DEPLOY_BADGE,
              "gap-1.5 border-emerald-500/20 bg-emerald-500/[0.07] px-2.5 text-emerald-300 hover:bg-emerald-500/[0.12]",
            )}
          >
            <span className="relative flex size-1.5 shrink-0">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex size-1.5 rounded-full bg-emerald-400" />
            </span>
            {!args.compact && <span>Live</span>}
            <CaretDown className="size-3 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 border-zinc-800 bg-zinc-950 text-zinc-200">
          <DropdownMenuItem asChild>
            <a
              href={deployment.url!}
              target="_blank"
              rel="noreferrer"
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <ArrowSquareOut className="size-3.5 text-zinc-400" />
              Visit site
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2 text-sm"
            onSelect={() => deployMutation.mutate()}
          >
            <ArrowsClockwise className="size-3.5 text-zinc-400" />
            Redeploy
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-zinc-800" />
          <DropdownMenuItem asChild>
            <Link
              to="/$repoId/deploy"
              params={{ repoId: args.repoId }}
              search={{ sessionId: args.sessionId }}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <Wrench className="size-3.5 text-zinc-400" />
              Deploy settings
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // When failed: status badge with dropdown for retry + settings
  if (status === "failed") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              DEPLOY_BADGE,
              "gap-1.5 border-red-500/20 bg-red-500/[0.07] px-2.5 text-red-300 hover:bg-red-500/[0.12]",
            )}
          >
            <span className="size-1.5 shrink-0 rounded-full bg-red-400" />
            {!args.compact && <span>Failed</span>}
            <CaretDown className="size-3 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 border-zinc-800 bg-zinc-950 text-zinc-200">
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2 text-sm"
            onSelect={() => deployMutation.mutate()}
          >
            <ArrowsClockwise className="size-3.5 text-zinc-400" />
            Retry deploy
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-zinc-800" />
          <DropdownMenuItem asChild>
            <Link
              to="/$repoId/deploy"
              params={{ repoId: args.repoId }}
              search={{ sessionId: args.sessionId }}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <Wrench className="size-3.5 text-zinc-400" />
              Deploy settings
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default: not deployed or destroyed — single deploy button
  return (
    <Tooltip content="Deploy this project">
      <button
        type="button"
        onClick={() => deployMutation.mutate()}
        className={cn(
          DEPLOY_BADGE,
          "gap-1.5 border-zinc-700/60 bg-white/[0.03] px-2.5 text-zinc-400 hover:border-zinc-600/60 hover:bg-white/[0.06] hover:text-zinc-300",
        )}
      >
        <Globe className="size-3.5" />
        {!args.compact && <span>Deploy</span>}
      </button>
    </Tooltip>
  );
}

export const Route = createFileRoute("/_authed/$repoId/$sessionId")({
  component: SessionPage,
});

type SpeculativeResumeDebugPhase =
  | "blocked"
  | "scheduled"
  | "timer_cancelled"
  | "timer_fired"
  | "request_started"
  | "request_succeeded"
  | "request_failed";

type SpeculativeResumeRuntimeState = {
  baseUrl: string;
  sessionId: string;
  branchSwitchPending: boolean;
  lifecycleStatus: string | null;
  requiresExplicitResume: boolean;
  resumeActionPending: boolean;
  sandboxRecoverableInactive: boolean;
  sandboxResumeNeeded: boolean;
  sandboxStatus: string | null;
  sessionArchived: boolean;
  sessionSuspended: boolean;
  status: string | null;
  suspendedAt: string | null;
  requestResume: (options?: { force?: boolean; speculative?: boolean }) => Promise<boolean>;
};

function buildSpeculativeResumeDebugPayload(args: {
  state: SpeculativeResumeRuntimeState;
  payload?: Record<string, unknown>;
}): Record<string, unknown> {
  return {
    branchSwitchPending: args.state.branchSwitchPending,
    lifecycleStatus: args.state.lifecycleStatus,
    requiresExplicitResume: args.state.requiresExplicitResume,
    resumeActionPending: args.state.resumeActionPending,
    sandboxRecoverableInactive: args.state.sandboxRecoverableInactive,
    sandboxResumeNeeded: args.state.sandboxResumeNeeded,
    sandboxStatus: args.state.sandboxStatus,
    sessionArchived: args.state.sessionArchived,
    sessionSuspended: args.state.sessionSuspended,
    status: args.state.status,
    suspendedAt: args.state.suspendedAt,
    ...(args.payload ?? {}),
  };
}

function logSpeculativeResumeDebug(args: {
  baseUrl: string;
  sessionId: string;
  phase: SpeculativeResumeDebugPhase;
  reason: string;
  payload?: Record<string, unknown>;
}): void {
  const body = {
    phase: args.phase,
    reason: args.reason,
    ...(args.payload ?? {}),
  };

  console.info("[speculative-resume]", {
    sessionId: args.sessionId,
    ...body,
  });

  void fetch(`${args.baseUrl}/api/sessions/${args.sessionId}/speculative-resume-debug`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => undefined);
}

type RuntimeConversationSnapshot = {
  conversationId: string
  agentType: "opencode" | "claude_code" | "cursor"
  providerSessionId: string | null
  title: string | null
  isDefault: boolean
  statusKind: "idle" | "running" | "waiting_input" | "error" | null
  activityState?: "idle" | "running" | "error" | null
  activityEventSequence?: number | null
  lastActivityEventId?: number | null
  lastFinishedEventId?: number | null
  lastFinishedAt?: number | null
  lastFinishReason?: "completed" | "error" | "aborted" | "retry_exhausted" | "waiting_input" | null
  rawTailId?: number | null
  terminalRawCursor?: number | null
  terminalRawCursorObservedAt?: number | null
  activityObservedAt?: number | null
  createdAt: number
  updatedAt: number
  archivedAt: number | null
}

type AbortRequestPendingState = {
  conversationId: string;
  requestedAt: number;
  requestedRunStartedAt: number | null;
  requestedRunActivityEventId: number | null;
}

function runtimeConversationScopedNoticeTargetsActiveConversation(input: {
  conversationId: unknown;
  runtimeConversationTabsAvailable: boolean;
  activeConversationId: string;
}): boolean {
  if (!input.runtimeConversationTabsAvailable) return true;
  const rawConversationId = typeof input.conversationId === "string"
    ? input.conversationId.trim()
    : "";
  const targetConversationId = rawConversationId.length > 0
    ? rawConversationId
    : "default";
  return targetConversationId === input.activeConversationId;
}

function removeAbortRequestPendingState(input: {
  current: Record<string, AbortRequestPendingState>;
  conversationId: string;
  expected?: AbortRequestPendingState | null;
}): Record<string, AbortRequestPendingState> {
  const currentRequest = input.current[input.conversationId];
  if (!currentRequest) return input.current;
  if (input.expected && currentRequest !== input.expected) return input.current;
  const next = { ...input.current };
  delete next[input.conversationId];
  return next;
}

type OptimisticRuntimeConversationState = {
  sessionId: string
  snapshot: RuntimeConversationSnapshot
  creationKind: "blank" | "fork"
  seed?: PendingForkSnapshot | null
  previousConversationId?: string
}

// Runtime conversation timestamps are epoch milliseconds; zero is the stable
// lower bound used by the default/main placeholder so derived picker rows do
// not change on every render while HTTP conversation data is still loading.
const MIN_RUNTIME_CONVERSATION_CREATED_AT_MS = 0;

type RuntimeConversationControlSnapshot = RuntimeConversationSnapshot | RuntimeConversationActivitySnapshot;

function toRuntimeConversationActivitySnapshot(args: {
  sessionId: string;
  conversation: RuntimeConversationSnapshot;
  timestamp?: number;
}): RuntimeConversationActivitySnapshot {
  return {
    sessionId: args.sessionId,
    conversationId: args.conversation.conversationId,
    agentType: args.conversation.agentType,
    providerSessionId: args.conversation.providerSessionId,
    title: args.conversation.title,
    isDefault: args.conversation.isDefault,
    statusKind: args.conversation.statusKind,
    activityState: args.conversation.activityState ?? null,
    activityEventSequence: args.conversation.activityEventSequence ?? null,
    lastActivityEventId: args.conversation.lastActivityEventId ?? null,
    lastFinishedEventId: args.conversation.lastFinishedEventId ?? null,
    lastFinishedAt: args.conversation.lastFinishedAt ?? null,
    lastFinishReason: args.conversation.lastFinishReason ?? null,
    rawTailId: args.conversation.rawTailId ?? null,
    terminalRawCursor: args.conversation.terminalRawCursor ?? null,
    terminalRawCursorObservedAt: args.conversation.terminalRawCursorObservedAt ?? null,
    activityObservedAt: args.conversation.activityObservedAt ?? null,
    createdAt: args.conversation.createdAt,
    updatedAt: args.conversation.updatedAt,
    archivedAt: args.conversation.archivedAt,
    ...(typeof args.timestamp === "number" ? { timestamp: args.timestamp } : {}),
  };
}

function runtimeConversationHasTerminalEvidence(conversation: RuntimeConversationControlSnapshot | null | undefined): boolean {
  return runtimeConversationActivitySnapshotHasTerminalEvidence(conversation);
}

function selectRuntimeConversationSnapshotForControlState(args: {
  querySnapshot: RuntimeConversationSnapshot | null | undefined;
  activitySnapshot: RuntimeConversationActivitySnapshot | null | undefined;
}): RuntimeConversationControlSnapshot | null {
  if (!args.querySnapshot) return args.activitySnapshot ?? null;
  if (!args.activitySnapshot) return args.querySnapshot;
  return compareRuntimeConversationActivitySnapshotFreshness({
    left: args.activitySnapshot,
    right: args.querySnapshot,
  }) === "left_newer"
    ? args.activitySnapshot
    : args.querySnapshot;
}

function runtimeConversationIsRunning(conversation: RuntimeConversationControlSnapshot | null | undefined): boolean {
  if (!conversation) return false;
  if (runtimeConversationHasTerminalEvidence(conversation)) return false;
  return conversation.statusKind === "running" || conversation.activityState === "running";
}

function runtimeConversationIsLocallyKnownEmpty(args: {
  conversation: RuntimeConversationControlSnapshot | null | undefined;
}): boolean {
  const { conversation } = args;
  if (!conversation || conversation.archivedAt !== null || conversation.isDefault) return false;
  if (runtimeConversationHasTerminalEvidence(conversation)) return false;
  if (conversation.statusKind === "running" || conversation.statusKind === "waiting_input" || conversation.statusKind === "error") {
    return false;
  }
  if (conversation.activityState === "running" || conversation.activityState === "error") {
    return false;
  }
  return true;
}

function deriveRuntimeStatusPhaseForActiveConversation(args: {
  conversation: RuntimeConversationControlSnapshot | null | undefined;
  fallbackPhase: AgentRuntimeStatus["phase"] | null;
  conversationMetadataReady: boolean;
}): AgentRuntimeStatus["phase"] | null {
  const { conversation } = args;
  if (!conversation) {
    return args.conversationMetadataReady ? "idle" : args.fallbackPhase;
  }

  if (conversation.statusKind === "waiting_input") return "waiting_input";
  if (conversation.statusKind === "error") return "error";
  if (conversation.activityState === "error") return "error";

  if (runtimeConversationHasTerminalEvidence(conversation)) {
    return "idle";
  }

  if (conversation.statusKind === "running") return "running";
  if (conversation.statusKind === "idle") return "idle";

  if (conversation.activityState === "running") return "running";

  // Once the runtime-conversation list has loaded, do not let session-global
  // runtime status leak from a different tab into this tab's composer/timeline.
  // Before that metadata is ready, keep the fallback so first-load startup can
  // still reflect the live runtime snapshot for the selected tab.
  return args.conversationMetadataReady ? "idle" : args.fallbackPhase;
}

function filterQueuedMessagesForActiveConversation(args: {
  queuedMessages: QueuedMessage[];
  runtimeConversationTabsAvailable: boolean;
  activeConversationId: string;
}): QueuedMessage[] {
  if (!args.runtimeConversationTabsAvailable) return args.queuedMessages;

  return args.queuedMessages.filter((message) => {
    const conversationId = message.conversationId?.trim();
    return !conversationId || conversationId === args.activeConversationId;
  });
}

function buildThreadDraftConsumptionObservations(args: {
  timelineItems?: readonly AgentRuntimeTimelineItem[] | null;
  queuedMessages?: readonly QueuedMessage[] | null;
}): ThreadDraftConsumptionObservation[] {
  const observations: ThreadDraftConsumptionObservation[] = [];
  for (const item of args.timelineItems ?? []) {
    if (item.kind !== "user_message") continue;
    observations.push({
      timestamp: item.timestamp,
      text: item.text,
      attachmentCount: item.attachmentCount ?? item.attachments?.length ?? 0,
      contextRefs: item.contextRefs ?? [],
      requestedSkillRefs: item.requestedSkillRefs ?? [],
      preferredMcpServerRefs: item.preferredMcpServerRefs ?? [],
      attachedPullRequest: item.attachedPullRequest ?? null,
    });
  }

  for (const message of args.queuedMessages ?? []) {
    if (message.queueKind === "session_action") continue;
    observations.push({
      timestamp: message.timestamp,
      text: message.text,
      attachmentCount: message.attachmentCount ?? message.attachments?.length ?? 0,
      contextRefs: message.contextRefs ?? [],
      requestedSkillRefs: message.requestedSkillRefs ?? [],
      preferredMcpServerRefs: message.preferredMcpServerRefs ?? [],
      attachedPullRequest: message.attachedPullRequest ?? null,
    });
  }
  return observations;
}

function buildDraftConsumptionAttachments(
  attachments: readonly ImageAttachment[],
): ThreadDraftSnapshot["attachments"] {
  return attachments.map((attachment) => ({
    id: attachment.id,
    filename: attachment.filename,
    mime: attachment.mime,
    sizeBytes: attachment.sizeBytes,
    r2Key: attachment.r2Key ?? "",
    previewDataUrl: attachment.previewUrl.startsWith("data:") ? attachment.previewUrl : "",
  }));
}

// Stable empty reference so `useRuntimeConversationActivity` selectors don't
// return a fresh object on every render when a session has no snapshots yet.
// This matches the pattern used by the sidebar (`EMPTY_RUNTIME_CONVERSATIONS`).
const EMPTY_RUNTIME_CONVERSATIONS_BY_ID: Record<string, RuntimeConversationActivitySnapshot> = {};

function shouldEnableSessionPreviewFrame(args: {
  sessionArchived: boolean;
  shouldKeepPreviewWarm: boolean;
  autoLoadPreviewIframes: boolean;
  sandboxStatus: SandboxStatus;
  previewUrl: string | null;
}): boolean {
  if (args.sessionArchived) return false;
  if (args.shouldKeepPreviewWarm) return true;

  return args.autoLoadPreviewIframes && args.sandboxStatus === "running" && args.previewUrl !== null;
}

// Where the top-bar tab list for a session came from this render. The session
// route consumes data from three independent places — the per-session HTTP
// query, the multiplex activity-store zustand cache, and synthetic defaults —
// and the write-back effect needs to know which one to avoid a feedback loop.
type RuntimeConversationsSource =
  | "query"
  | "activity-store"
  | "loading-placeholder"
  | "default";

const FORK_BOOTSTRAP_INTERRUPTED_ERROR_MESSAGE = "Fork preparation was interrupted. Retry to continue.";
const LOCAL_FORK_TITLE_SUFFIX = " (fork)";
// Match the SessionDO runtime-conversation title budget so the fork suffix
// remains visible in the optimistic request title instead of being truncated by
// the backend. This is UI metadata only; unusually long titles are still safe to
// truncate because the original session title remains available elsewhere.
const LOCAL_FORK_TITLE_MAX_LENGTH = 120;
// Five seconds mirrors common archive/undo interactions (Gmail-style): long
// enough to recover accidental tab closes without leaving stale toast UI around.
const RUNTIME_CONVERSATION_CLOSE_UNDO_TOAST_DURATION_MS = 5_000;
// Background/closed session status is only visible through a dropdown, so a
// modest five-second refresh keeps fire-and-forget tasks discoverable without
// polling aggressively while a provider run is still active.
const ARCHIVED_RUNTIME_CONVERSATION_REFETCH_INTERVAL_MS = 5_000;
// Optimistic tab ids intentionally use the same durable `conv_*` namespace as
// server-created runtime conversations. The SessionDO accepts only this UUID
// shape, which lets messages sent during creation queue against the final tab id
// without a later client-side id remap.
const CLIENT_RUNTIME_CONVERSATION_ID_PREFIX = "conv_";
const OPTIMISTIC_RUNTIME_CONVERSATION_TITLE = "New chat";
const OPTIMISTIC_FORK_PREPARING_SEND_REASON = "Wait for fork setup to finish before sending a message.";

function createClientRuntimeConversationId(): string {
  return `${CLIENT_RUNTIME_CONVERSATION_ID_PREFIX}${crypto.randomUUID()}`;
}

function formatLocalForkConversationTitle(sourceTitle: string | null | undefined): string {
  const normalizedSourceTitle = typeof sourceTitle === "string" ? sourceTitle.trim() : "";
  const sourceTitleWithoutSuffix = normalizedSourceTitle.endsWith(LOCAL_FORK_TITLE_SUFFIX)
    ? normalizedSourceTitle.slice(0, -LOCAL_FORK_TITLE_SUFFIX.length).trim()
    : normalizedSourceTitle;
  const maxSourceTitleLength = LOCAL_FORK_TITLE_MAX_LENGTH - LOCAL_FORK_TITLE_SUFFIX.length;
  const visibleSourceTitle = (sourceTitleWithoutSuffix || "New chat")
    .slice(0, maxSourceTitleLength)
    .trimEnd();
  return `${visibleSourceTitle}${LOCAL_FORK_TITLE_SUFFIX}`;
}

function buildOptimisticRuntimeConversationSnapshot(args: {
  conversationId: string;
  agentType: RuntimeConversationSnapshot["agentType"];
  title?: string | null;
  timestamp: number;
}): RuntimeConversationSnapshot {
  return {
    conversationId: args.conversationId,
    agentType: args.agentType,
    providerSessionId: null,
    title: args.title ?? OPTIMISTIC_RUNTIME_CONVERSATION_TITLE,
    isDefault: false,
    statusKind: null,
    activityState: "idle",
    activityEventSequence: null,
    lastActivityEventId: null,
    lastFinishedEventId: null,
    lastFinishedAt: null,
    lastFinishReason: null,
    activityObservedAt: null,
    createdAt: args.timestamp,
    updatedAt: args.timestamp,
    archivedAt: null,
  };
}

async function readRuntimeConversationMutationError(response: Response): Promise<string> {
  const text = await response.text().catch(() => "");
  if (text.trim().length > 0) {
    try {
      const parsed = JSON.parse(text) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const maybeError = (parsed as { error?: unknown }).error;
        if (typeof maybeError === "string" && maybeError.trim().length > 0) {
          return maybeError.trim();
        }
      }
    } catch {
      // Fall back to the raw response text below.
    }
    return text;
  }
  return `Request failed with ${response.status}`;
}

/**
 * Reconciliation polls should be frequent enough to notice a child session as
 * soon as the background fork request lands, but not so frequent that reloads
 * spam the API. One second matches the existing optimistic session retry cadence.
 */
const FORK_BOOTSTRAP_RECONCILE_POLL_INTERVAL_MS = 1_000;
// Wait a short grace period before showing reconnect trouble after a session
// has already been connected once. This avoids flashing transient handoff
// states during normal thread switches while still surfacing genuine transport
// problems quickly enough to be useful.
const RECONNECT_WARNING_DELAY_MS = 5000;
// Give a fresh thread switch a longer grace window before showing any
// connection banner. Normal switches usually settle well under this; if they
// do not, a neutral "Connecting..." hint is more accurate than an immediate
// disconnected error state.
const INITIAL_CONNECTING_NOTICE_DELAY_MS = 10000;
// Connection diagnostics should be fresh enough for a human debugging startup,
// but not refetched on every render. Five seconds matches the existing debug UI
// cadence and is safe because realtime state still flows over the runtime stream.
const CONNECTION_FLOW_STALE_MS = 5_000;
// Runtime access/session IDs change only when the underlying agent session is
// recreated, but recovery UI should reflect a just-materialized ID quickly. Five
// seconds avoids repeated access-token fetches while keeping the dialog fresh.
const RECOVERY_RUNTIME_ACCESS_STALE_MS = 5_000;
// Connection diagnostics are visible only in the actions menu. Ten seconds keeps
// operator data reasonably current without turning the debug panel into a poller
// that competes with the main realtime runtime stream.
const CONNECTION_FLOW_REFETCH_INTERVAL_MS = 10_000;
/**
 * Local IDB writes should feel immediate while still coalescing rapid typing.
 * A third of a second keeps drafts responsive and avoids write amplification.
 */
const LOCAL_DRAFT_PERSIST_DEBOUNCE_MS = 300;

function collectOptimisticRevertHiddenMessageIds(args: {
  snapshot: AgentRuntimeSnapshot | null | undefined;
  messageId: string;
}): string[] {
  const timeline = args.snapshot?.timeline ?? [];
  const collectItemIdentifiers = (item: AgentRuntimeTimelineItem): string[] => {
    const identifiers: string[] = [];
    const addIdentifier = (value: unknown) => {
      if (typeof value !== "string") return;
      const trimmed = value.trim();
      if (trimmed.length === 0 || identifiers.includes(trimmed)) return;
      identifiers.push(trimmed);
    };

    addIdentifier(getTimelineMessageTargetId(item));
    addIdentifier(item.id);
    addIdentifier(item.turnId);
    addIdentifier(item.providerRef?.messageId);
    return identifiers;
  };
  const startIndex = timeline.findIndex((item) => collectItemIdentifiers(item).includes(args.messageId));
  if (startIndex < 0) return [args.messageId];

  const hiddenMessageIds: string[] = [];
  const seenMessageIds = new Set<string>();
  for (let index = startIndex; index < timeline.length; index += 1) {
    for (const targetId of collectItemIdentifiers(timeline[index]!)) {
      if (seenMessageIds.has(targetId)) continue;
      seenMessageIds.add(targetId);
      hiddenMessageIds.push(targetId);
    }
  }

  return hiddenMessageIds.length > 0 ? hiddenMessageIds : [args.messageId];
}

/**
 * Attachment metadata updates are heavier than text writes, so we wait longer.
 * Half a second reduces duplicate writes while still persisting quickly.
 */
const ATTACHMENT_DRAFT_PERSIST_DEBOUNCE_MS = 500;
/**
 * Remote draft sync should be less chatty than local persistence because each
 * write is a network request to the session DO. This value balances freshness
 * across devices with backend write volume.
 */
const REMOTE_DRAFT_PERSIST_DEBOUNCE_MS = 900;
/**
 * When replacing a consumed draft with an empty snapshot, bump the persisted
 * draft version by the smallest possible millisecond so stale non-empty rows
 * are rejected even if their save timestamp ties the consuming message.
 */
const CONSUMED_THREAD_DRAFT_CLEAR_VERSION_INCREMENT_MS = 1;

type RecoverySshAccessSnapshot = {
  sandboxId: string;
  target: string | null;
  token: string;
  host: string;
  sshCommand: string;
  expiresAt: string;
  readyAction: "already_ready" | "waited_for_start" | "started" | "recovered";
  previousTokenRevoked: boolean;
  revocationScope: "none" | "previous_token" | "all_tokens";
};

type RecoveryStatusSnapshot = {
  providerRuntime: {
    status: string | null;
    sandboxId: string | null;
    target: string | null;
    referenceSource: "stored_reference" | "session_lookup" | null;
    ready: boolean | null;
    recoverable: boolean | null;
    error: string | null;
  } | null;
  sshRecovery: {
    available: boolean;
    error: string | null;
  } | null;
  gitHubRecovery: {
    webPushAvailable: boolean;
    manualTokenAvailable: boolean;
  } | null;
};

type RecoveryGithubAccessSnapshot = {
  token: string;
  expiresAt: string;
  shellSnippet: string;
};

type RecoveryRuntimeCliCommand = {
  label: string;
  command: string;
  sessionId: string;
};

type RecoveryOpenCodeAttachCommand = {
  command: string;
};

type RecoveryDialogMode =
  | "healthy"
  | "cord_disconnected"
  | "sandbox_inactive"
  | "recovery_blocked"
  | "sandbox_error";

function normalizeRecoveryStatusSnapshot(value: unknown): RecoveryStatusSnapshot | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const providerRuntimeRaw = row.providerRuntime && typeof row.providerRuntime === "object" && !Array.isArray(row.providerRuntime)
    ? row.providerRuntime as Record<string, unknown>
    : null;
  const sshRecoveryRaw = row.sshRecovery && typeof row.sshRecovery === "object" && !Array.isArray(row.sshRecovery)
    ? row.sshRecovery as Record<string, unknown>
    : null;
  const gitHubRecoveryRaw = row.gitHubRecovery && typeof row.gitHubRecovery === "object" && !Array.isArray(row.gitHubRecovery)
    ? row.gitHubRecovery as Record<string, unknown>
    : null;

  return {
    providerRuntime: providerRuntimeRaw
      ? {
          status: typeof providerRuntimeRaw.status === "string" ? providerRuntimeRaw.status : null,
          sandboxId: typeof providerRuntimeRaw.sandboxId === "string" ? providerRuntimeRaw.sandboxId : null,
          target: typeof providerRuntimeRaw.target === "string" ? providerRuntimeRaw.target : null,
          referenceSource:
            providerRuntimeRaw.referenceSource === "stored_reference" || providerRuntimeRaw.referenceSource === "session_lookup"
              ? providerRuntimeRaw.referenceSource
              : null,
          ready: typeof providerRuntimeRaw.ready === "boolean" ? providerRuntimeRaw.ready : null,
          recoverable: typeof providerRuntimeRaw.recoverable === "boolean" ? providerRuntimeRaw.recoverable : null,
          error: typeof providerRuntimeRaw.error === "string" ? providerRuntimeRaw.error : null,
        }
      : null,
    sshRecovery: sshRecoveryRaw
      ? {
          available: sshRecoveryRaw.available === true,
          error: typeof sshRecoveryRaw.error === "string" ? sshRecoveryRaw.error : null,
        }
      : null,
    gitHubRecovery: gitHubRecoveryRaw
      ? {
          webPushAvailable: gitHubRecoveryRaw.webPushAvailable === true,
          manualTokenAvailable: gitHubRecoveryRaw.manualTokenAvailable === true,
        }
      : null,
  };
}

function normalizeRecoveryGithubAccessSnapshot(value: unknown): RecoveryGithubAccessSnapshot | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const token = typeof row.token === "string" && row.token.trim().length > 0 ? row.token.trim() : null;
  const expiresAt = typeof row.expiresAt === "string" && row.expiresAt.trim().length > 0 ? row.expiresAt.trim() : null;
  const shellSnippet = typeof row.shellSnippet === "string" && row.shellSnippet.trim().length > 0 ? row.shellSnippet.trim() : null;
  if (!token || !expiresAt || !shellSnippet) return null;
  return { token, expiresAt, shellSnippet };
}

function quoteRecoveryShellArg(args: { value: string }): string {
  return `'${args.value.replace(/'/g, `'\\''`)}'`;
}

function buildRecoveryOpenCodeAttachCommand(args: {
  openCodeAccessSnapshot: OpenCodeAccessSnapshot | null;
}): RecoveryOpenCodeAttachCommand | null {
  const snapshot = args.openCodeAccessSnapshot;
  if (!snapshot) return null;
  const url = snapshot.url?.trim() ?? "";
  if (!url || !snapshot.password) return null;

  const sessionId = normalizeRecoveryRuntimeCliSessionId({ value: snapshot.sessionId });
  if (!sessionId) return null;

  const workspaceRoot = typeof snapshot.workspaceRoot === "string" && snapshot.workspaceRoot.trim().length > 0
    ? snapshot.workspaceRoot.trim()
    : null;
  const username = typeof snapshot.username === "string" && snapshot.username.trim().length > 0
    ? snapshot.username.trim()
    : "opencode";
  const usernameEnv = username === "opencode"
    ? ""
    : `OPENCODE_SERVER_USERNAME=${quoteRecoveryShellArg({ value: username })} `;
  const dirArg = workspaceRoot
    ? ` --dir ${quoteRecoveryShellArg({ value: workspaceRoot })}`
    : "";

  const command = [
    `${usernameEnv}OPENCODE_SERVER_PASSWORD=${quoteRecoveryShellArg({ value: snapshot.password })}`,
    "opencode attach",
    quoteRecoveryShellArg({ value: url }),
    dirArg.trim(),
    `--session ${sessionId}`,
  ].filter(Boolean).join(" ");
  return {
    command,
  };
}

// Runtime session IDs are generated by OpenCode/Claude and should be simple CLI
// tokens. Reject anything else rather than shell-quoting so the recovery dialog
// stays copy/paste-friendly without ever rendering a command containing shell
// metacharacters from runtime state.
const RECOVERY_RUNTIME_CLI_SESSION_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

function normalizeRecoveryRuntimeCliSessionId(args: { value: string | null }): string | null {
  const value = typeof args.value === "string" ? args.value.trim() : "";
  if (!value) return null;
  return RECOVERY_RUNTIME_CLI_SESSION_ID_PATTERN.test(value) ? value : null;
}

function buildRecoveryRuntimeCliCommands(args: {
  agentType: SessionAgentIdentity;
  claudeRunnerSessionId: string | null;
  openCodeSessionId: string | null;
}): RecoveryRuntimeCliCommand[] {
  const openCodeSessionId = normalizeRecoveryRuntimeCliSessionId({ value: args.openCodeSessionId });
  const claudeRunnerSessionId = normalizeRecoveryRuntimeCliSessionId({ value: args.claudeRunnerSessionId });
  const commands: RecoveryRuntimeCliCommand[] = [];

  if (args.agentType === "opencode" && openCodeSessionId) {
    commands.push({
      label: "OpenCode TUI",
      command: `opencode --session ${openCodeSessionId}`,
      sessionId: openCodeSessionId,
    });
  }

  if (args.agentType === "claude_code" && claudeRunnerSessionId) {
    commands.push({
      label: "Claude Code",
      command: `claude --resume ${claudeRunnerSessionId}`,
      sessionId: claudeRunnerSessionId,
    });
  }

  return commands;
}

function normalizeRecoverySshAccessSnapshot(raw: unknown): RecoverySshAccessSnapshot | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }

  const row = raw as Record<string, unknown>;
  const sandboxId = typeof row.sandboxId === "string" && row.sandboxId.trim().length > 0
    ? row.sandboxId.trim()
    : null;
  const token = typeof row.token === "string" && row.token.trim().length > 0
    ? row.token.trim()
    : null;
  const host = typeof row.host === "string" && row.host.trim().length > 0
    ? row.host.trim()
    : null;
  const sshCommand = typeof row.sshCommand === "string" && row.sshCommand.trim().length > 0
    ? row.sshCommand.trim()
    : null;
  const expiresAt = typeof row.expiresAt === "string" && row.expiresAt.trim().length > 0
    ? row.expiresAt.trim()
    : null;
  const readyAction =
    row.readyAction === "already_ready"
    || row.readyAction === "waited_for_start"
    || row.readyAction === "started"
    || row.readyAction === "recovered"
      ? row.readyAction
      : null;

  if (!sandboxId || !token || !host || !sshCommand || !expiresAt || !readyAction) {
    return null;
  }

  return {
    sandboxId,
    target: typeof row.target === "string" && row.target.trim().length > 0 ? row.target.trim() : null,
    token,
    host,
    sshCommand,
    expiresAt,
    readyAction,
    previousTokenRevoked: row.previousTokenRevoked === true,
    revocationScope:
      row.revocationScope === "all_tokens" || row.revocationScope === "previous_token"
        ? row.revocationScope
        : "none",
  };
}

function getRecoverySshReadyActionLabel(args: {
  readyAction: RecoverySshAccessSnapshot["readyAction"];
}): string {
  switch (args.readyAction) {
    case "already_ready":
      return "Sandbox was already running.";
    case "waited_for_start":
      return "Waited for the sandbox to finish starting.";
    case "started":
      return "Started the sandbox before minting access.";
    case "recovered":
      return "Recovered the sandbox before minting access.";
  }
}
/**
 * YOLO dispatch should show immediate feedback, but we should not block retries
 * forever if websocket progress never arrives due transport hiccups. Twelve
 * seconds is long enough for normal startup dispatch while still recovering UX.
 */
const YOLO_START_PENDING_TIMEOUT_MS = 12000;
/**
 * YOLO errors are useful only when they are fresh or came from this mounted
 * action. Thirty seconds covers normal websocket/status propagation and a quick
 * thread switch, but prevents stale persisted conflict markers from re-toasting
 * after the user has already started resolution.
 */
const YOLO_ERROR_TOAST_FRESH_WINDOW_MS = 30_000;
/**
 * Keep dedupe keys for ten minutes so remounts and ActivityHub/status replays do
 * not repeat the same YOLO error toast during a normal debugging session. The
 * state machine hint itself also expires at ten minutes.
 */
const YOLO_ERROR_TOAST_DEDUPE_RETENTION_MS = 10 * 60_000;
/**
 * While merge/resolve state is settling, poll GitHub every five seconds. GitHub
 * mergeability commonly lags for a few seconds after conflict resolution, and
 * this interval keeps the header current without making each thread switch issue
 * an immediate remote read forever.
 */
const PR_STATUS_CONFLICT_POLL_INTERVAL_MS = 5_000;
const yoloErrorToastDedupe = new Map<string, number>();

function pruneYoloErrorToastDedupe(nowMs: number): void {
  for (const [key, timestamp] of yoloErrorToastDedupe.entries()) {
    if (nowMs - timestamp > YOLO_ERROR_TOAST_DEDUPE_RETENTION_MS) {
      yoloErrorToastDedupe.delete(key);
    }
  }
}
/**
 * Accepted chat sends should light up the sidebar immediately, but the durable
 * queue/status echo normally arrives within a few websocket turns. Fifteen
 * seconds gives the DO time to accept and reflect the request even on a cold
 * resume path without leaving a sticky generic spinner around forever if that
 * echo is lost. If we routinely exceed this before pending/running state shows,
 * we should raise the bound or derive the state from a more explicit contract.
 */
const SIDEBAR_WORK_REQUEST_PENDING_TIMEOUT_MS = 15_000;

type PendingAttachedPullRequestObservation = {
  clientMessageId: string;
  attachedPullRequest: AgentRuntimeAttachedPullRequest;
};

type SandboxRetryPendingState = {
  baselineErrorMessage: string | null;
  baselineErrorTimestamp: number | null;
  requestedAt: number;
  sessionId: string;
};
type ResumeUiPendingState = {
  requestedAt: number;
  sessionId: string;
};

type LocalStartNewWorkGitPendingState = {
  newBranch: string;
  baseBranch: string;
  trigger: StartNewWorkTrigger;
  requestedAt: number;
  previousPr: {
    number: number;
    url: string | null;
    state: string;
  };
};

type ContinueSessionSourceConversation = {
  conversationId: string | null;
  title: string | null;
};

type ContinueSessionMutationInput = {
  sourceSessionId: string;
  sourceRepositoryId: string;
  sourceSessionTitle: string;
  sourceAgentType: AgentType;
  sourceConversation: ContinueSessionSourceConversation;
  optimisticSessionId: string;
  optimisticTitle: string;
  cacheSnapshot: SessionCacheMutationSnapshot;
};

type StartNewWorkNoticeState = {
  newBranch: string;
  baseBranch: string;
  trigger: StartNewWorkTrigger;
  timestamp: number;
  previousPr: { number: number; url: string | null; state: string };
};

function normalizeRestorablePullRequestState(value: string): PullRequestState | null {
  if (value === "draft" || value === "open" || value === "merged" || value === "closed") {
    return value;
  }
  return null;
}

function restorePreviousPullRequestCachesAfterStartNewWorkFailure(args: {
  queryClient: QueryClient;
  sessionId: string;
  previousPr: StartNewWorkNoticeState["previousPr"];
}): void {
  const prState = normalizeRestorablePullRequestState(args.previousPr.state);
  if (!prState) {
    void args.queryClient.invalidateQueries({ queryKey: ["session", args.sessionId], exact: true });
    void args.queryClient.invalidateQueries({ queryKey: ["sessions"] });
    void args.queryClient.invalidateQueries({ queryKey: ["pr-status", args.sessionId], exact: true });
    return;
  }

  const patchSessionRow = (old: unknown): unknown => {
    if (!old || typeof old !== "object") return old;
    const row = old as Record<string, unknown>;
    const existingPullRequest =
      row.pullRequest && typeof row.pullRequest === "object"
        ? (row.pullRequest as Record<string, unknown>)
        : null;
    return {
      ...row,
      prNumber: args.previousPr.number,
      prUrl: args.previousPr.url,
      prState,
      pullRequest: {
        ...(existingPullRequest ?? {}),
        number: args.previousPr.number,
        url: args.previousPr.url,
        state: prState,
        relationship: "active_owner",
        isActiveOwner: true,
      },
    };
  };

  args.queryClient.setQueryData(["session", args.sessionId], patchSessionRow);
  args.queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
    if (!Array.isArray(old)) return old;
    return old.map((item) => {
      if (!item || typeof item !== "object") return item;
      const row = item as Record<string, unknown>;
      return row.id === args.sessionId ? patchSessionRow(row) : item;
    });
  });
  applyPrStatusCacheUpdate(args.queryClient, {
    sessionId: args.sessionId,
    prNumber: args.previousPr.number,
    prUrl: args.previousPr.url,
    prState,
    timestamp: Date.now(),
  });
}

const START_NEW_WORK_NOTICE_STORAGE_PREFIX = "cord:start-new-work-notice:";
/**
 * Keep the local continuation marker for one month. This preserves the divider
 * across normal refreshes without letting old local-only metadata linger
 * forever after a user changes machines, clears history via backend actions, or
 * manually switches branches long after Keep Working ran.
 */
const START_NEW_WORK_NOTICE_LOCAL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/**
 * DB history does not currently persist the newly-created branch name, so the
 * cross-device fallback can only infer it from branch_assigned_at. Start-new-work
 * branch prep should usually finish within seconds; ten minutes is deliberately
 * generous for cold sandbox/GitHub slowness while avoiding false positives from
 * unrelated branch switches much later in the thread.
 */
const START_NEW_WORK_HISTORY_BRANCH_ASSIGNMENT_MAX_LAG_MS = 10 * 60 * 1000;

function startNewWorkNoticeStorageKey(sessionId: string): string {
  return `${START_NEW_WORK_NOTICE_STORAGE_PREFIX}${sessionId}`;
}

function buildContinuationSourceContextRefTitle(args: {
  sourceSessionTitle: string;
  sourceConversation: ContinueSessionSourceConversation;
}): string {
  const sourceSessionTitle = args.sourceSessionTitle.trim() || "Untitled session";
  const sourceConversationTitle = args.sourceConversation.title?.trim() ?? "";
  if (!args.sourceConversation.conversationId || sourceConversationTitle.length === 0) {
    return sourceSessionTitle;
  }
  if (sourceConversationTitle === sourceSessionTitle) {
    return sourceSessionTitle;
  }
  return `${sourceSessionTitle} / ${sourceConversationTitle}`;
}

function buildContinuationBootstrapDraft(args: {
  sourceSessionId: string;
  sourceRepositoryId: string;
  sourceContextTitle: string;
  sourceConversation: ContinueSessionSourceConversation;
  contextRefLocalId: string;
  origin: "context" | "continue_new_session";
}): PendingBootstrapDraft {
  return {
    text: null,
    clientMessageId: null,
    contextRefs: [
      {
        localId: args.contextRefLocalId,
        sessionId: args.sourceSessionId,
        ...(args.sourceConversation.conversationId ? { conversationId: args.sourceConversation.conversationId } : {}),
        repositoryId: args.sourceRepositoryId,
        title: args.sourceContextTitle,
        origin: args.origin,
      } satisfies AgentRuntimeSessionContextRef,
    ],
    requestedSkillRefs: [],
    preferredMcpServerRefs: [],
    scratchPadRefs: [],
    attachedPullRequest: null,
    prToAdopt: null,
  };
}

function normalizeStartNewWorkTrigger(value: unknown): StartNewWorkTrigger | null {
  return value === "manual" || value === "auto" || value === "background" ? value : null;
}

function parseStartNewWorkNoticeState(value: unknown): StartNewWorkNoticeState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const previousPr = row.previousPr && typeof row.previousPr === "object" && !Array.isArray(row.previousPr)
    ? row.previousPr as Record<string, unknown>
    : null;
  const trigger = normalizeStartNewWorkTrigger(row.trigger);
  const timestamp = typeof row.timestamp === "number" && Number.isFinite(row.timestamp)
    ? Math.floor(row.timestamp)
    : null;
  const previousPrNumber = typeof previousPr?.number === "number" && Number.isFinite(previousPr.number)
    ? Math.floor(previousPr.number)
    : null;
  if (
    typeof row.newBranch !== "string" ||
    row.newBranch.trim().length === 0 ||
    typeof row.baseBranch !== "string" ||
    row.baseBranch.trim().length === 0 ||
    !trigger ||
    timestamp === null ||
    previousPrNumber === null ||
    typeof previousPr?.state !== "string" ||
    previousPr.state.trim().length === 0
  ) {
    return null;
  }

  if (Date.now() - timestamp > START_NEW_WORK_NOTICE_LOCAL_MAX_AGE_MS) {
    return null;
  }

  return {
    newBranch: row.newBranch.trim(),
    baseBranch: row.baseBranch.trim(),
    trigger,
    timestamp,
    previousPr: {
      number: previousPrNumber,
      url: typeof previousPr.url === "string" && previousPr.url.trim().length > 0 ? previousPr.url : null,
      state: previousPr.state.trim(),
    },
  };
}

function readStartNewWorkNoticeState(sessionId: string): StartNewWorkNoticeState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(startNewWorkNoticeStorageKey(sessionId));
    if (!raw) return null;
    const parsed = parseStartNewWorkNoticeState(JSON.parse(raw));
    if (!parsed) {
      window.localStorage.removeItem(startNewWorkNoticeStorageKey(sessionId));
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStartNewWorkNoticeState(args: {
  sessionId: string;
  notice: StartNewWorkNoticeState;
}): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      startNewWorkNoticeStorageKey(args.sessionId),
      JSON.stringify(args.notice),
    );
  } catch {
    // Best-effort continuity only. If localStorage is unavailable, DB history
    // still provides the cross-device fallback once branch prep completes.
  }
}

function clearStartNewWorkNoticeState(sessionId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(startNewWorkNoticeStorageKey(sessionId));
  } catch {
    // Best-effort cleanup; stale values are age-checked on read.
  }
}
/**
 * Resume acceptance and runtime startup are not perfectly synchronized. This
 * grace period suppresses the transient "Sandbox stopped" badge during short
 * handoff gaps between the accepted resume state and the runtime entering a
 * loading/running state. 5 s is generous — the websocket event typically
 * arrives in 1-2 s, but network jitter can push it to ~3 s.
 */
const RESUME_GRACE_PERIOD_MS = 5_000;
/**
 * Resume requests return once the backend accepts the work, but Daytona cold
 * resumes can still take tens of seconds before the sandbox reaches running.
 * Keep resume CTAs in a pending state for up to one minute so accepted resumes
 * do not flicker back to idle immediately. If startup exceeds this bound, the
 * page-wide startup UI still carries the state and the user can retry.
 */
const RESUME_UI_PENDING_TIMEOUT_MS = 60_000;
/**
 * After the backend accepts a resume request the sandbox still needs to cold
 * start (pull snapshot, boot containers, start the agent process). Two minutes
 * matches the backend PR_ACTION_RESUME_TIMEOUT_MS so the frontend and DO use
 * the same deadline. In practice cold resumes finish in 20-40 s; this generous
 * ceiling avoids false-negative timeouts on congested infra.
 */
const RESUME_WAIT_RUNNING_TIMEOUT_MS = 120_000;

/**
 * Tooltip shown on Preview / Process Logs buttons when the repo has no
 * configured runtime processes (the common bare-session case). The buttons
 * stay visible so users can discover them, but are disabled with this hover
 * message. The BareImageBanner already provides the "set up your dev
 * environment" call-to-action for the remediation path.
 */
const NO_RUNTIME_PROCESSES_DISABLED_REASON =
  "No dev server is running. Set up your dev environment to enable this.";
/**
 * Interval between sandbox-status polls while waiting for a resumed sandbox to
 * reach "running". 500 ms keeps the perceived latency under a second once the
 * status flips, which feels near-instant to the user. Going lower would burn
 * CPU for no UX benefit; going higher would feel sluggish.
 */
const RESUME_WAIT_RUNNING_POLL_MS = 500;
/**
 * Sandbox auth health checks shell into the running sandbox, so we keep them
 * relatively infrequent. One minute surfaces persistent broker failures quickly
 * enough for humans while avoiding a noisy background heartbeat.
 */
const SANDBOX_AUTH_HEALTH_REFETCH_INTERVAL_MS = 60_000;
/**
 * Subscription usage is provider-control-plane data, not live token streaming
 * state. Polling every five minutes matches our server-side cache TTL and
 * CodexBar's default background refresh cadence, while avoiding a private
 * upstream endpoint heartbeat on every active session.
 */
const CREDENTIAL_USAGE_REFETCH_INTERVAL_MS = 5 * 60 * 1000;
/**
 * Treat usage query data as fresh for one minute. This lets quick route/focus
 * changes reuse the cache, but still allows manual refresh or the five-minute
 * background poll to pick up provider-side quota changes.
 */
const CREDENTIAL_USAGE_STALE_TIME_MS = 60_000;
/**
 * Polling interval for the bare-image builds query. 5 s matches the
 * BuildStatusTab in settings.tsx — frequent enough to surface phase changes
 * in the banner without hammering the API.
 */
const BARE_BANNER_BUILD_POLL_MS = 5_000;
/**
 * Build-health warnings on prebuilt sessions are informational and do not need
 * live polling; one minute keeps quick tab switches from refetching while still
 * allowing manual invalidation after a rebuild.
 */
const PREBUILT_BUILD_HEALTH_STALE_MS = 60_000;
/**
 * PR-adoption prepare only needs to validate the selected PR and durably
 * register the background operation in the SessionDO. Under normal conditions
 * this should complete in low single-digit seconds. Fifteen seconds leaves room
 * for transient API slowness while preventing send from hanging indefinitely.
 */
const PR_ADOPTION_PREPARE_TIMEOUT_MS = 15_000;
/**
 * Sidebar "Create From" navigation lands on the session route before the create-session
 * POST and SessionDO init are fully settled. Retrying every half-second for up to six
 * seconds covers the normal optimistic-navigation race without making real failures feel
 * sticky. If session bootstrap regularly exceeds this window, we should move readiness
 * coordination server-side instead of stretching the client retry budget.
 */
const BOOTSTRAP_PR_ADOPTION_NOT_READY_RETRY_DELAY_MS = 500;
const BOOTSTRAP_PR_ADOPTION_NOT_READY_MAX_RETRIES = 12;
/**
 * code-server usually becomes reachable within a few seconds once the backend
 * accepts the start request. Waiting fifteen seconds gives the normal control-
 * plane redirect path plenty of time, then falls back to the already-resolved
 * preview URL for browsers that fail to follow the opener-driven redirect.
 */
const CODE_SERVER_POPUP_NAVIGATION_FALLBACK_DELAY_MS = 15_000;
const ARCHIVED_THREAD_READ_ONLY_REASON = "Archived threads are read-only. Unarchive this thread to continue.";
const ARCHIVED_THREAD_RESUME_REASON = "Archived threads must be unarchived before starting the sandbox.";
const CONTINUE_NEW_THREAD_SOURCE_LOADING_REASON = "Wait for the selected chat tab to finish loading before starting a new thread from it.";
const ARCHIVED_THREAD_SLASH_CONTINUE_REASON = "Archived threads are read-only. Unarchive this thread before using /continue.";
const ARCHIVED_THREAD_START_NEW_WORK_REASON = "Archived threads are read-only. Unarchive this thread before starting new work.";
const SUSPENDED_THREAD_ACTION_REASON = "Start the sandbox before using thread actions.";
const MANUAL_RESUME_REQUIRED_REASON = "This thread has a merged or closed PR. Resume manually to run commands again.";
/**
 * Repo-readiness snapshots are only a bridge for immediate navigation gaps while
 * the live git-status query hydrates. Two minutes covers quick thread switches
 * and reconnects, but avoids letting an old PR operation pin the header to stale
 * dirty/clean state after the branch has changed or been updated elsewhere.
 */
const PR_FLOW_REPO_READINESS_FALLBACK_MAX_AGE_MS = 2 * 60_000;

function revokeTemporaryPreviewUrls(previewUrls?: string[]) {
  previewUrls?.forEach((previewUrl) => {
    if (previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
  });
}

function readSessionApiErrorMessage(value: unknown): string {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return "";
  }

  const row = value as Record<string, unknown>;
  return typeof row.message === "string"
    ? row.message.trim()
    : typeof row.error === "string"
      ? row.error.trim()
      : "";
}

function hasAuthoritativeSessionRecordMetadata(value: unknown): boolean {
  if (!value || typeof value !== "object") {
    return false;
  }

  if (isForkBootstrapOptimisticSessionRecord(value)) {
    return false;
  }

  return (
    Object.prototype.hasOwnProperty.call(value, "canManage")
    || Object.prototype.hasOwnProperty.call(value, "initReady")
    || Object.prototype.hasOwnProperty.call(value, "sandboxDashboardUrl")
  );
}

function findCachedSessionListRecordForSession(args: {
  queryClient: QueryClient;
  sessionId: string;
  viewerUserId: string | null;
}): Record<string, unknown> | undefined {
  if (!args.viewerUserId) {
    return undefined;
  }

  const cachedSessionLists = args.queryClient.getQueriesData<unknown>({ queryKey: ["sessions"] });
  for (const [, listData] of cachedSessionLists) {
    if (!Array.isArray(listData)) continue;
    for (const item of listData) {
      if (!item || typeof item !== "object") continue;
      const row = item as Record<string, unknown>;
      if (row.id === args.sessionId && row.userId === args.viewerUserId) {
        return row;
      }
    }
  }

  return undefined;
}

const CONNECTION_FLOW_NEON_TABLES_URL =
  import.meta.env.VITE_CONNECTION_FLOW_NEON_TABLES_URL
  || "";
const CONNECTION_FLOW_NEON_FALLBACK_URL = "https://console.neon.tech/app/projects";
const ADMIN_EMAIL_DOMAIN = "@withpretzel.com";
/** Error type set when the sandbox's base image is outdated but still functional. */
const RUNTIME_ERROR_BASE_IMAGE_STALE = "RUNTIME_BASE_IMAGE_STALE";
const REVIEW_MODE_SLASH_COMMAND_NAME = "review";
const REVIEW_BRANCH_SLASH_COMMAND_NAME = "review-branch";
const REVIEW_TAB_TITLE_PREFIX = "Review:";
// Keep review tab names short enough to fit the tab strip while preserving most
// source-tab labels. Very long user-generated titles are already metadata only,
// so truncation is safe; if tab-strip density changes this can be tuned.
const REVIEW_TAB_TITLE_MAX_SOURCE_CHARS = 80;
const REVIEW_CURRENT_TAB_CONTEXT_PREFIX = [
  "Run Review Mode for the work from the source chat tab attached as Cord linked-session context.",
  "",
  "Before applying the review instructions below, call cord_get_session_context for the attached Cord session-context ref to load the review briefing for the source tab.",
  "Focus on the intent, files/components, risks, and validation signals from the loaded context, but inspect the current branch/filesystem as the source of truth.",
  "",
].join("\n");

function buildCurrentTabReviewPrompt(args: { reviewPrompt: string }): string {
  return `${REVIEW_CURRENT_TAB_CONTEXT_PREFIX}${args.reviewPrompt}`;
}
const YOLO_TOOLTIP_TEXT = "Runs Quick PR, merges the PR, then archives this thread.";
const QUICK_PR_TOOLTIP_TEXT = "Create a pull request from the current branch with one click.";

function buildReviewRuntimeConversationTitle(sourceTitle: string | null | undefined): string {
  const normalizedSourceTitle = typeof sourceTitle === "string" && sourceTitle.trim().length > 0
    ? sourceTitle.trim()
    : "tab";
  const truncatedSourceTitle = normalizedSourceTitle
    .slice(0, REVIEW_TAB_TITLE_MAX_SOURCE_CHARS)
    .trimEnd();
  return `${REVIEW_TAB_TITLE_PREFIX} ${truncatedSourceTitle}`;
}

function buildSessionPullRequestVisitHref(args: {
  sessionId: string;
  repoProvider: "github" | "gitea" | "unsupported";
  pullRequestNumber: number | null;
  rawPullRequestUrl: string | null;
}): string | null {
  if (args.repoProvider !== "gitea") {
    return args.rawPullRequestUrl;
  }
  if (args.pullRequestNumber === null) {
    return args.rawPullRequestUrl;
  }

  const apiBaseUrl = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
  return `${apiBaseUrl}/api/sessions/${encodeURIComponent(args.sessionId)}/pull-request/visit`;
}
/**
 * Local composer-block timeout for command-style session actions. Eight seconds
 * is long enough to preserve the normal "wait for runtime acknowledgement"
 * behavior on healthy sessions, but short enough to stop hung command-style
 * requests such as `/command`, `/review`, and `/summarize` from freezing the
 * composer until the Cloudflare edge eventually returns a 524. If legitimate
 * acknowledgements regularly take longer than this, revisit the upstream
 * dispatch contract rather than stretching the UI timeout indefinitely.
 */
const COMMAND_DRIVEN_SESSION_ACTION_BLOCK_TIMEOUT_MS = 8_000;

/**
 * Keeps the optimistic local "Stopping agent..." state from getting stuck
 * forever if the abort outcome or follow-up runtime snapshot is lost during a
 * reconnect. Fifteen seconds is long enough for normal abort acknowledgements
 * and short transport hiccups, but still lets the user retry under worse
 * network conditions.
 */
const ABORT_REQUEST_PENDING_TIMEOUT_MS = 15_000;

/**
 * Thread-sync keeps the latest queued-command outcome notice in memory. This
 * freshness window prevents stale notices from re-toasting when the page
 * remounts later, while still surfacing outcomes that happened moments ago.
 */
const QUEUED_SESSION_COMMAND_NOTICE_FRESHNESS_WINDOW_MS = 15_000;
/**
 * Keep a bounded in-memory set of recently handled queued-command notice ids so
 * replayed websocket notices do not re-toast after unrelated notices arrive.
 * Fifty entries comfortably covers a noisy page lifetime without growing
 * without bound.
 */
const QUEUED_SESSION_COMMAND_NOTICE_DEDUPE_LIMIT = 50;
/**
 * Runtime slash commands now include skills, and command discovery can shell out
 * to agent runtimes. Keep the current session's list warm for ten minutes to
 * match the SessionDO command-list cache and make repeated `/` opens instant,
 * while still allowing newly-added skills to appear after a normal refresh.
 */
const SESSION_COMMAND_LIST_STALE_TIME_MS = 10 * 60_000;
/**
 * Runtime skill discovery may race sandbox startup mounts by a few seconds. Four
 * warmup attempts cover the initial ready broadcast plus a small post-mount
 * window without turning sessions that genuinely have no skills into polling
 * loops.
 */
const SESSION_SKILL_STARTUP_WARMUP_MAX_ATTEMPTS = 4;
/**
 * Retry startup skill warmup after 1.5 seconds. This is long enough for the
 * sidecar skill directories / persistent-storage projections to appear after
 * the sandbox-ready event, while keeping the cache warm before most users open
 * the composer picker.
 */
const SESSION_SKILL_STARTUP_WARMUP_RETRY_DELAY_MS = 1_500;
const STORAGE_MOUNT_WARNING_STEP =
  "warning_storage_mounts_disabled_missing_r2_management_token";
/**
 * Startup warnings can replay after reconnect because thread-sync rehydrates the
 * recent progress log. Fifteen seconds keeps warnings visible when they just
 * happened while filtering stale history on remount.
 */
const STORAGE_MOUNT_WARNING_FRESHNESS_WINDOW_MS = 15_000;
/**
 * Keep a bounded set of handled warning events so replayed progress frames do
 * not emit duplicate toasts. Twenty entries covers repeated reconnects during a
 * single startup without unbounded growth.
 */
const STORAGE_MOUNT_WARNING_DEDUPE_LIMIT = 20;

function getSessionCommandListQueryKey(sessionId: string) {
  return ["session-command-list", sessionId] as const;
}

async function fetchSessionCommandList(args: {
  sessionId: string;
  signal?: AbortSignal;
}): Promise<SlashCommandItem[]> {
  const baseUrl = import.meta.env.VITE_API_URL || "";
  const response = await fetch(
    `${baseUrl}/api/sessions/${encodeURIComponent(args.sessionId)}/command/list`,
    { credentials: "include", signal: args.signal },
  );
  if (!response.ok) {
    const details = await response.text().catch(() => "");
    throw new Error(details || "Failed to load slash commands");
  }
  const payload = await response.json().catch(() => null);
  return normalizeSlashCommandItems(payload);
}

function hasSkillSlashCommand(items: SlashCommandItem[] | undefined): boolean {
  return (items ?? []).some((item) => item.kind === "skill");
}
const UNKNOWN_AGENT_CAPABILITIES: AgentCapabilities = {
  supportsPromptParts: false,
  supportsImageAttachments: false,
  supportsSlashCommands: false,
  supportsSessionActions: false,
  supportsQuestionDock: false,
};

type PendingSessionAction = {
  id: string;
  conversationId: string;
  clientActionId?: string | null;
  queuedMessage: QueuedMessage | null;
  blocksComposer: boolean;
  cancelScheduledRemoval?: (() => void) | null;
};

function normalizePendingSessionActionClientActionId(
  clientActionId: string | null | undefined,
): string | null {
  if (typeof clientActionId !== "string") {
    return null;
  }
  const trimmed = clientActionId.trim();
  return trimmed.length > 0 ? trimmed : null;
}

type SessionListSortField = "createdAt" | "archivedAt";
type SessionListQueryKind = "active" | "archived" | "all" | "unknown";
type SessionListQueryScope = "global" | "repo" | "organization" | "picker" | "unknown";

type SessionListQueryDescriptor = {
  kind: SessionListQueryKind;
  scope: SessionListQueryScope;
  repositoryId: string | null;
};

type SessionDetailQuerySnapshot = {
  hadQuery: boolean;
  data: unknown;
};

type SessionListQuerySnapshot = {
  queryKey: readonly unknown[];
  descriptor: SessionListQueryDescriptor;
  hadQuery: boolean;
  previousSession: CachedSessionListItem | null;
};

type SessionCacheMutationSnapshot = {
  capturedAt: number;
  detail: SessionDetailQuerySnapshot;
  sessionLists: SessionListQuerySnapshot[];
  knownRows: CachedSessionListItem[];
};

// Keep the recovery-success badge visible for a short window so users notice
// the fallback happened, without pinning a warning badge for the rest of the session.
const RECOVERY_SUCCESS_BADGE_TTL_MS = 5 * 60 * 1000;

const BRANCH_SWITCH_BLOCKED_REASON = "Wait for the current branch switch to finish";

function findCatalogModelVariants(
  catalog: SessionModelCatalog,
  selection: { providerID: string; modelID: string } | null,
): string[] | undefined {
  if (!selection) return undefined;

  const provider = catalog.providers.find(
    (candidate) => candidate.active && candidate.providerID === selection.providerID,
  );
  if (!provider) return undefined;

  return provider.models.find(
    (candidate) => candidate.active && candidate.modelID === selection.modelID,
  )?.variants;
}

// --- localStorage key for one-time Quick PR pulse animation ---
// We show a subtle pulse/glow the first time the inline Quick PR chip appears
// so mobile users notice it. After first interaction (tap) or viewing, never again.
const MOBILE_QUICK_PR_SEEN_KEY = "cord:mobile-quick-pr-seen";

function getMobileQuickPrSeen(): boolean {
  try {
    return localStorage.getItem(MOBILE_QUICK_PR_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function setMobileQuickPrSeen(): void {
  try {
    localStorage.setItem(MOBILE_QUICK_PR_SEEN_KEY, "1");
  } catch {
    // localStorage may be unavailable (private browsing, quota, etc.)
  }
}

/**
 * Compact inline "create PR" chip for mobile header.
 * Shows a GitPullRequest icon with a one-time pulse animation on first appearance
 * to draw the user's attention to the feature.
 *
 * After the first time the user sees it (3s) or taps it, the pulse never shows again.
 */
function MobileQuickPrCreateChip({ onTrigger }: { onTrigger: () => void }) {
  const [showPulse, setShowPulse] = useState(() => !getMobileQuickPrSeen());

  // Auto-dismiss the pulse after a few seconds and mark as seen
  // 3000ms — enough to catch the eye without being annoying.
  const PULSE_DURATION_MS = 3000;
  useEffect(() => {
    if (!showPulse) return;
    const timer = setTimeout(() => {
      setShowPulse(false);
      setMobileQuickPrSeen();
    }, PULSE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [showPulse]);

  return (
    <button
      type="button"
      onClick={() => {
        if (showPulse) {
          setShowPulse(false);
          setMobileQuickPrSeen();
        }
        onTrigger();
      }}
      className={cn(
        "relative flex h-7 items-center gap-1 rounded-md border px-1.5 text-xs font-medium transition-colors",
        "border-emerald-500/40 bg-emerald-600 text-white active:bg-emerald-500",
        showPulse && "animate-mobile-pr-pulse",
      )}
    >
      <GitPullRequest className="size-3.5" />
      PR
      {showPulse && (
        <span className="absolute inset-0 rounded-md border border-emerald-400/60 animate-ping pointer-events-none" />
      )}
    </button>
  );
}

function SessionPage() {
  const { repoId, sessionId } = Route.useParams();
  const isRepolessRoute = isNoRepoSessionRoute({ repoId });
  const search = Route.useSearch() as { conversationId?: string };
  const draftConversationId = normalizeRuntimeConversationDraftId(search.conversationId);
  const activeDraftStorageId = buildSessionDraftStorageId(sessionId, draftConversationId);
  const shortcuts = useShortcutLabels();
  const forkBootstrapEntry = usePendingForkBootstrapEntry(sessionId)
  // Read optimistic title stashed during instant navigation.  Pure read in
  // useMemo (no side effects) so React strict-mode double-invocation is safe.
  // Cleanup lives in a separate useEffect.
  const optimisticTitle = useMemo<string>(() => {
    return sessionStorage.getItem(optimisticSessionTitleStorageKey(sessionId))
      ?? forkBootstrapEntry?.title
      ?? ""
  }, [forkBootstrapEntry?.title, sessionId]);
  const optimisticAgentType = useMemo<SessionAgentIdentity>(() => {
    const raw = sessionStorage.getItem(optimisticAgentTypeStorageKey(sessionId))
      ?? forkBootstrapEntry?.agentType
    return normalizeSessionAgentIdentity(raw)
  }, [forkBootstrapEntry?.agentType, sessionId]);
  useEffect(() => {
    sessionStorage.removeItem(optimisticSessionTitleStorageKey(sessionId))
    sessionStorage.removeItem(optimisticAgentTypeStorageKey(sessionId))
  }, [sessionId]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [acceptedMessageMarkerVersion, bumpAcceptedMessageMarkerVersion] = useReducer((version: number) => version + 1, 0);
  const hasAcceptedUserMessageMarker = useMemo(
    () => readAcceptedUserMessageMarker(sessionId),
    [acceptedMessageMarkerVersion, sessionId],
  );
  const markSessionAsHavingAcceptedUserMessage = useCallback(() => {
    writeAcceptedUserMessageMarker(sessionId);
    bumpAcceptedMessageMarkerVersion();
    queryClient.setQueryData(["session", sessionId], (old: unknown) => markSessionRowHasHistory({ row: old }));
    queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
      if (!Array.isArray(old)) return old;
      let changed = false;
      const next = old.map((item) => {
        if (!item || typeof item !== "object" || Array.isArray(item)) return item;
        if ((item as Record<string, unknown>).id !== sessionId) return item;
        const markedItem = markSessionRowHasHistory({ row: item });
        changed = changed || markedItem !== item;
        return markedItem;
      });
      return changed ? next : old;
    });
  }, [queryClient, sessionId]);
  const reviewPromptQuery = useQuery<{
    canManage: boolean;
    reviewPrompt: string | null;
    defaultReviewPrompt: string;
  }>({
    queryKey: ["repo-review-prompt", repoId],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.VITE_API_URL || ""}/api/repos/${repoId}/review-prompt`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to load review prompt");
      }
      return res.json() as Promise<{
        canManage: boolean;
        reviewPrompt: string | null;
        defaultReviewPrompt: string;
      }>;
    },
    enabled: !isRepolessRoute,
  });
  const effectiveReviewPrompt = reviewPromptQuery.data?.reviewPrompt
    ?? reviewPromptQuery.data?.defaultReviewPrompt
    ?? DEFAULT_REVIEW_PROMPT;
  const cachedSessionDetailRecord = queryClient.getQueryData<unknown>(["session", sessionId]);
  const shouldForceAuthoritativeSessionRefetchOnMount =
    !hasAuthoritativeSessionRecordMetadata(cachedSessionDetailRecord);
  const fetchAuthoritativeSessionRecord = useCallback(async (targetSessionId: string) => {
    const res = await api.api.sessions[":id"].$get({
      param: { id: targetSessionId },
    });
    if (!res.ok) {
      const err = new Error("Failed to fetch session");
      (err as Error & { status: number }).status = res.status;
      throw err;
    }
    const incoming = await res.json() as Record<string, unknown>
    const existing = queryClient.getQueryData(["session", targetSessionId]) as Record<string, unknown> | undefined
    return mergeSessionRecordPreservingHasHistory({
      incoming,
      existing,
    })
  }, [queryClient]);
  const { data: authSession } = authClient.useSession();
  const canViewConnectionFlow = authSession?.user?.email?.endsWith(ADMIN_EMAIL_DOMAIN) ?? false;
  const [hasContent, setHasContent] = useState(false);
  const [draftText, setDraftText] = useState(() => loadChatDraftSync(sessionId, draftConversationId));
  const draftTextRef = useRef(loadChatDraftSync(sessionId, draftConversationId));
  const [draftSessionContextRefs, setDraftSessionContextRefs] = useState<AgentRuntimeSessionContextRef[]>([]);
  const draftSessionContextRefsRef = useRef<AgentRuntimeSessionContextRef[]>([]);
  const [draftRequestedSkillRefs, setDraftRequestedSkillRefs] = useState<AgentRuntimeRequestedSkillRef[]>([]);
  const draftRequestedSkillRefsRef = useRef<AgentRuntimeRequestedSkillRef[]>([]);
  const [draftPreferredMcpServerRefs, setDraftPreferredMcpServerRefs] = useState<AgentRuntimePreferredMcpServerRef[]>([]);
  const draftPreferredMcpServerRefsRef = useRef<AgentRuntimePreferredMcpServerRef[]>([]);
  const [draftScratchPadRefs, setDraftScratchPadRefs] = useState<AgentRuntimeScratchPadRef[]>([]);
  const draftScratchPadRefsRef = useRef<AgentRuntimeScratchPadRef[]>([]);
  const [draftAttachedPullRequest, setDraftAttachedPullRequest] = useState<AgentRuntimeAttachedPullRequest | null>(null);
  const draftAttachedPullRequestRef = useRef<AgentRuntimeAttachedPullRequest | null>(null);
  const draftCursorPositionRef = useRef(0);

  // Scratch pad queries — used to populate the @notes picker with content previews
  // and to resolve note content at send time. The "__general__" sentinel is used
  // for the user-wide general pad (not tied to any session).
  const SCRATCH_PAD_GENERAL_SESSION_ID = "__general__";
  const sessionScratchPadQuery = useScratchPadDocument({ sessionId });
  const generalScratchPadQuery = useScratchPadDocument({ sessionId: null });

  const scratchPadNoteItems = useMemo((): NotesMentionItem[] => {
    const items: NotesMentionItem[] = [];
    const sessionContent = sessionScratchPadQuery.data?.content ?? "";
    items.push({
      id: `notes-session-${sessionId}`,
      sessionId,
      label: "Thread Notes",
      preview: sessionContent,
      isEmpty: sessionContent.trim().length === 0,
    });
    const generalContent = generalScratchPadQuery.data?.content ?? "";
    items.push({
      id: "notes-general",
      sessionId: SCRATCH_PAD_GENERAL_SESSION_ID,
      label: "General Notes",
      preview: generalContent,
      isEmpty: generalContent.trim().length === 0,
    });
    return items;
  }, [sessionId, sessionScratchPadQuery.data?.content, generalScratchPadQuery.data?.content]);

  const [pendingBootstrapActive, setPendingBootstrapActive] = useState(false);
  const [pendingBootstrapSnapshot, setPendingBootstrapSnapshot] = useState<PendingBootstrapDraft>(EMPTY_PENDING_BOOTSTRAP_DRAFT);
  const [pendingBootstrapClientMessageId, setPendingBootstrapClientMessageId] = useState<string | null>(null);
  const [pendingBootstrapPrToAdopt, setPendingBootstrapPrToAdopt] = useState<PendingBootstrapPrAdoption | null>(null);
  const pendingBootstrapPrToAdoptRef = useRef(pendingBootstrapPrToAdopt);
  pendingBootstrapPrToAdoptRef.current = pendingBootstrapPrToAdopt;
  const [pendingBootstrapTextVersion, setPendingBootstrapTextVersion] = useState(0);
  const hydratedDraftSnapshotRef = useRef<ThreadDraftSnapshot>(EMPTY_THREAD_DRAFT);
  const hasLocalDraftInputRef = useRef(false);
  const suppressLocalDraftInputTrackingRef = useRef(false);
  const activeDraftSessionRef = useRef(activeDraftStorageId);
  const activeDraftBaseSessionRef = useRef(sessionId);
  const [suppressRevertBanner, setSuppressRevertBanner] = useState(false);
  const [ptyWsStatus, setPtyWsStatus] = useState<PtyWsStatus>("disconnected");
  const editorRef = useRef<PromptEditorHandle>(null);
  const [editorMountVersion, setEditorMountVersion] = useState(0);
  const draftHydratedSessionRef = useRef<string | null>(null);
  const draftSnapshotUpdatedAtRef = useRef(0);
  const [draftHydrationVersion, setDraftHydrationVersion] = useState(0);
  const maybeBuildPrAdoptionSuccessStatus = useCallback((input: {
    attachedPullRequest: AgentRuntimeAttachedPullRequest;
    linkedSessionTitle?: string | null;
    operationId?: string | null;
  }) => {
    const successAttachmentKey = buildPrAdoptionSuccessAttachmentKey({
      attachedPullRequest: input.attachedPullRequest,
    });
    if (readAcknowledgedPrAdoptionSuccessAttachment(sessionId) === successAttachmentKey) {
      return null;
    }
    return buildPrAdoptionSuccessStatus(input);
  }, [sessionId]);
  const shouldSuppressAttachedPullRequestSuccessStatus = useCallback((attachedPullRequest: AgentRuntimeAttachedPullRequest | null) => {
    const prToAdopt = pendingBootstrapPrToAdoptRef.current;
    if (!attachedPullRequest || !prToAdopt) {
      return false;
    }
    return buildPrAdoptionSuccessAttachmentKey({ attachedPullRequest }) === buildPrAdoptionSuccessAttachmentKey({
      attachedPullRequest: {
        number: prToAdopt.number,
        title: prToAdopt.title,
        url: prToAdopt.url,
        branchName: prToAdopt.branchName,
      },
    });
  }, []);
  const maybeBuildVisiblePrAdoptionSuccessStatus = useCallback((input: {
    attachedPullRequest: AgentRuntimeAttachedPullRequest;
    linkedSessionTitle?: string | null;
    operationId?: string | null;
  }) => {
    if (shouldSuppressAttachedPullRequestSuccessStatus(input.attachedPullRequest)) {
      return null;
    }
    return maybeBuildPrAdoptionSuccessStatus(input);
  }, [maybeBuildPrAdoptionSuccessStatus, shouldSuppressAttachedPullRequestSuccessStatus]);
  const maybeBuildHydratedPrAdoptionSuccessStatus = useCallback((input: {
    attachedPullRequest: AgentRuntimeAttachedPullRequest;
    linkedSessionTitle?: string | null;
    operationId?: string | null;
  }) => {
    const successAttachmentKey = buildPrAdoptionSuccessAttachmentKey({
      attachedPullRequest: input.attachedPullRequest,
    });
    if (readConsumedPrAdoptionAttachment(sessionId) === successAttachmentKey) {
      return null;
    }
    return maybeBuildVisiblePrAdoptionSuccessStatus(input);
  }, [maybeBuildVisiblePrAdoptionSuccessStatus, sessionId]);
  const dropConsumedHydratedAttachedPullRequest = useCallback((attachedPullRequest: AgentRuntimeAttachedPullRequest | null) => {
    if (!attachedPullRequest) {
      return null;
    }
    const successAttachmentKey = buildPrAdoptionSuccessAttachmentKey({
      attachedPullRequest,
    });
    return readConsumedPrAdoptionAttachment(sessionId) === successAttachmentKey
      ? null
      : attachedPullRequest;
  }, [sessionId]);
  const dropConsumedHydratedPrLinkedSessionContextRefs = useCallback((input: {
    contextRefs: AgentRuntimeSessionContextRef[];
    attachedPullRequest: AgentRuntimeAttachedPullRequest | null;
  }) => {
    const hasPrLinkedSessionContext = input.contextRefs.some((ref) => ref.origin === "pr_linked_session");
    if (!hasPrLinkedSessionContext) {
      return input.contextRefs;
    }

    const consumedSuccessAttachmentKey = readConsumedPrAdoptionAttachment(sessionId);
    if (!consumedSuccessAttachmentKey) {
      return input.contextRefs;
    }

    if (input.attachedPullRequest) {
      const attachedPullRequestKey = buildPrAdoptionSuccessAttachmentKey({
        attachedPullRequest: input.attachedPullRequest,
      });
      if (attachedPullRequestKey !== consumedSuccessAttachmentKey) {
        return input.contextRefs;
      }
    }

    const filteredContextRefs = input.contextRefs.filter((ref) => ref.origin !== "pr_linked_session");
    return filteredContextRefs.length === input.contextRefs.length
      ? input.contextRefs
      : filteredContextRefs;
  }, [sessionId]);
  const sanitizePendingBootstrapDraft = useCallback((draft: PendingBootstrapDraft): PendingBootstrapDraft => {
    const attachedPullRequest = dropConsumedHydratedAttachedPullRequest(draft.attachedPullRequest);
    const contextRefs = dropConsumedHydratedPrLinkedSessionContextRefs({
      contextRefs: draft.contextRefs,
      attachedPullRequest,
    });
    if (attachedPullRequest === draft.attachedPullRequest && contextRefs === draft.contextRefs) {
      return draft;
    }
    return {
      ...draft,
      contextRefs,
      attachedPullRequest,
    };
  }, [dropConsumedHydratedAttachedPullRequest, dropConsumedHydratedPrLinkedSessionContextRefs]);
  const runWithoutLocalDraftInputTracking = useCallback((callback: () => void) => {
    suppressLocalDraftInputTrackingRef.current = true;
    try {
      callback();
    } finally {
      suppressLocalDraftInputTrackingRef.current = false;
    }
  }, []);
  const sendQueuedForUploadsRef = useRef(false);
  const [pendingAttachedPullRequestObservation, setPendingAttachedPullRequestObservation] = useState<PendingAttachedPullRequestObservation | null>(null);
  const pendingImplicitContextRefsRef = useRef<AgentRuntimeSessionContextRef[]>([]);
  const historyRef = useRef<PromptHistoryEntry[]>([]);
  const historyIndexRef = useRef(-1);
  const savedInputRef = useRef("");
  // Track which previewable runtime processes have already accepted a start
  // request for the current sandbox lifecycle. We only record keys after the
  // websocket send succeeds so reconnect races do not permanently suppress the
  // eager startup request for that lifecycle.
  const previewRuntimeAcceptedStartRequestKeysRef = useRef<Set<string>>(new Set());
  // Separately remember which lifecycle/process pairs actually reached
  // `running`. If a process later falls back to `idle`/`error` after having been
  // healthy once, we clear its accepted-start marker so the eager-start effect
  // can recover it without forcing the user to hit the manual restart action.
  const previewRuntimeObservedRunningKeysRef = useRef<Set<string>>(new Set());
  const chatScrollRootRef = useRef<HTMLDivElement | null>(null);
  const [chatScrollRootElement, setChatScrollRootElement] = useState<HTMLDivElement | null>(null);
  const [mobileComposerContainerElement, setMobileComposerContainerElement] = useState<HTMLDivElement | null>(null);
  const handleChatScrollRootRef = useCallback((element: HTMLDivElement | null) => {
    chatScrollRootRef.current = element;
    setChatScrollRootElement(element);
  }, []);
  const handleMobileComposerContainerRef = useCallback((element: HTMLDivElement | null) => {
    setMobileComposerContainerElement(element);
  }, []);
  const mergedBannerShownRef = useRef(false);
  const resumeSessionPromiseRef = useRef<{ sessionId: string; promise: Promise<unknown> } | null>(null);
  const deferredInputResumeFrameRef = useRef<number | null>(null);
  const prAdoptionPrepareRef = useRef<{ token: string; promise: Promise<boolean> } | null>(null);
  const bootstrapPrAdoptionStartedRef = useRef(false);
  const continueSessionSourceConversationRef = useRef<ContinueSessionSourceConversation>({
    conversationId: null,
    title: null,
  });
  const continueInNewSessionDisabledReasonRef = useRef<string | null>(null);
  const speculativeResumeDebugKeyRef = useRef<string | null>(null);
  const [resumePendingSessionId, setResumePendingSessionId] = useState<string | null>(null);
  const [resumeUiPendingState, setResumeUiPendingState] = useState<ResumeUiPendingState | null>(null);
  const [retryPendingState, setRetryPendingState] = useState<SandboxRetryPendingState | null>(null);
  // Tracks whether the session was recently unarchived (Cord-level unarchive).
  // When a session is archived, its Daytona sandbox is moved to cold storage.
  // Restoring from cold storage takes 10–20 seconds (vs ~5 s for a normal resume),
  // so we use this flag to show more specific "Restoring sandbox" status messages
  // until the sandbox reaches "running" state.
  const [sessionRecentlyUnarchived, setSessionRecentlyUnarchived] = useState(false);
  const [bareImageBannerDismissed, setBareImageBannerDismissed] = useState(false);
  const activeRouteSessionIdRef = useRef(sessionId);
  activeRouteSessionIdRef.current = sessionId;
  const localForkRuntimeConversationInFlightRef = useRef<{ sessionId: string; requestId: string } | null>(null);
  const [localForkRuntimeConversationInFlight, setLocalForkRuntimeConversationInFlight] = useState<{ sessionId: string; requestId: string } | null>(null);
  const sessionPageMountedRef = useRef(true);
  useEffect(() => {
    sessionPageMountedRef.current = true;
    return () => {
      sessionPageMountedRef.current = false;
    };
  }, []);
  const isCurrentMountedSessionPage = useCallback((targetSessionId: string) => {
    return sessionPageMountedRef.current && activeRouteSessionIdRef.current === targetSessionId;
  }, []);
  const sandboxStatusRef = useRef<string>("none");
  const isMobileLayout = useMobileLayout();

  useLayoutEffect(() => {
    const scrollRoot = chatScrollRootElement;
    if (!scrollRoot) return;

    if (!isMobileLayout) {
      scrollRoot.style.removeProperty(SHARED_TIMELINE_BOTTOM_OBSTRUCTION_CSS_VAR);
      return;
    }

    const composerContainer = mobileComposerContainerElement;
    if (!composerContainer) {
      scrollRoot.style.removeProperty(SHARED_TIMELINE_BOTTOM_OBSTRUCTION_CSS_VAR);
      return;
    }

    const syncMobileBottomObstruction = () => {
      const composerHeightPx = Math.ceil(composerContainer.getBoundingClientRect().height);
      scrollRoot.style.setProperty(
        SHARED_TIMELINE_BOTTOM_OBSTRUCTION_CSS_VAR,
        `${composerHeightPx}px`,
      );
    };

    syncMobileBottomObstruction();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(syncMobileBottomObstruction);
      resizeObserver.observe(composerContainer);
    }

    return () => {
      resizeObserver?.disconnect();
      scrollRoot.style.removeProperty(SHARED_TIMELINE_BOTTOM_OBSTRUCTION_CSS_VAR);
    };
  }, [chatScrollRootElement, isMobileLayout, mobileComposerContainerElement]);

  const { preferences: userPreferences } = useUserPreferences();
  const { evictSessionPreviewFrame } = usePersistentSessionPreviewFrameController();
  const previewSessionUiMemorySnapshot = useSyncExternalStore(
    subscribePersistentSessionPreviewUiMemory,
    getPersistentSessionPreviewUiMemorySnapshot,
  );
  const previewKey = previewSessionUiMemorySnapshot.previewKeyBySessionId[sessionId] ?? 0;
  const hasOpenedPreviewForSession = previewSessionUiMemorySnapshot.hasOpenedPreviewBySessionId[sessionId] === true;
  const selectedPreviewEndpointId = previewSessionUiMemorySnapshot.selectedPreviewEndpointIdBySessionId[sessionId] ?? null;
  const rememberedCurrentPreviewUrl = previewSessionUiMemorySnapshot.currentUrlBySessionId[sessionId] ?? null;
  const rememberedNavigationPreviewUrl = previewSessionUiMemorySnapshot.navigationUrlBySessionId[sessionId] ?? null;
  const handleReloadPreview = useCallback(() => {
    setPersistentSessionPreviewReloadKey({
      sessionId,
      previewKey: previewKey + 1,
    });
  }, [previewKey, sessionId]);

  const clearDeferredInputResumeFrame = useCallback(() => {
    if (deferredInputResumeFrameRef.current === null) return;
    cancelAnimationFrame(deferredInputResumeFrameRef.current);
    deferredInputResumeFrameRef.current = null;
  }, []);

  useEffect(() => () => {
    clearDeferredInputResumeFrame();
  }, [clearDeferredInputResumeFrame]);

  const toggleTerminal = useSessionUI((s) => s.toggleTerminal);
  const terminalVisible = useSessionUI((s) => s.terminalVisible);
  const toggleRightSidebar = useSessionUI((s) => s.toggleRightSidebar);
  const toggleLeftSidebar = useSessionUI((s) => s.toggleLeftSidebar);
  const openPreview = useSessionUI((s) => s.openPreview);
  const openDevServerLogs = useSessionUI((s) => s.openDevServerLogs);
  const closeContentPanel = useSessionUI((s) => s.closeContentPanel);
  const contentPanelMode = useSessionUI((s) => s.contentPanelMode);
  const setMobileSurface = useSessionUI((s) => s.setMobileSurface);
  const shouldKeepPreviewWarm = hasOpenedPreviewForSession || contentPanelMode === "preview";
  const resetEphemeral = useSessionUI((s) => s.resetEphemeral);
  const {
    attachments,
    readyAttachments,
    hasUploading: attachmentsUploading,
    hasFailures: attachmentsFailed,
    isUploadInFlight: isAttachmentUploadInFlight,
    addFiles: addImageAttachmentsInternal,
    removeAttachment: removeAttachmentInternal,
    retryAttachment: retryAttachmentInternal,
    clearReady: clearReadyAttachments,
    clearAll: clearAllAttachments,
    restoreAttachments,
  } = useImageAttachments(sessionId);

  const markPreviewOpenedForSession = useCallback((targetSessionId: string) => {
    markPersistentSessionPreviewOpened(targetSessionId);
  }, []);

  const clearPersistentPreviewStateForSession = useCallback((targetSessionId: string) => {
    clearPersistentSessionPreviewUiMemory(targetSessionId);
  }, []);

  const setSelectedPreviewEndpointIdForSession = useCallback((targetSessionId: string, endpointId: string | null) => {
    setPersistentSessionSelectedPreviewEndpointId({
      sessionId: targetSessionId,
      endpointId,
    });
  }, []);

  const hasPersistedPreviewSessionState = hasOpenedPreviewForSession
    || previewKey !== 0
    || selectedPreviewEndpointId != null
    || rememberedCurrentPreviewUrl != null
    || rememberedNavigationPreviewUrl != null;

  useEffect(() => {
    if (!hasPersistedPreviewSessionState) {
      return;
    }

    touchPersistentSessionPreviewUiMemory(sessionId);
  }, [hasPersistedPreviewSessionState, rememberedCurrentPreviewUrl, rememberedNavigationPreviewUrl, sessionId]);

  useEffect(() => {
    if (contentPanelMode !== "preview") {
      return;
    }

    markPreviewOpenedForSession(sessionId);
  }, [contentPanelMode, markPreviewOpenedForSession, sessionId]);

  const sessionScopeKey = useMemo(
    () => {
      const userId = authSession?.user?.id?.trim();
      if (!userId) return null;
      const organizationId = authSession?.session?.activeOrganizationId?.trim() || "no-org";
      return `${organizationId}:${userId}`;
    },
    [authSession?.session?.activeOrganizationId, authSession?.user?.id]
  );

  const currentForegroundOwnershipTabId = useMemo(() => getForegroundOwnershipTabId(), []);
  const [foregroundLeaseSnapshot, setForegroundLeaseSnapshot] = useState<
    Record<string, SessionForegroundLease>
  >({});
  const [isPageVisible, setIsPageVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible",
  );

  useEffect(() => {
    if (!sessionScopeKey) {
      setForegroundLeaseSnapshot({});
      return;
    }
    const syncSnapshot = () => {
      setForegroundLeaseSnapshot(getForegroundSessionLeaseSnapshot(sessionScopeKey));
    };
    syncSnapshot();
    return subscribeForegroundSessionLeaseSnapshot(syncSnapshot);
  }, [sessionScopeKey]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const handleVisibilityChange = () => setIsPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const handleBranchUpdated = async (event: Event) => {
      const detail = (event as CustomEvent<SessionBranchUpdatedDetail>).detail;
      if (!detail || detail.sessionId !== sessionId || !detail.branch) return;

      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["session", sessionId] }),
        queryClient.cancelQueries({ queryKey: ["sessions"] }),
      ]);

      // Update the session detail cache with the new branch name
      queryClient.setQueryData(["session", sessionId], (old: unknown) => {
        if (!old || typeof old !== "object") return old;
        const current = old as Record<string, unknown>;
        return {
          ...current,
          branch: detail.branch,
          ...(detail.baseBranch !== undefined ? { baseBranch: detail.baseBranch } : {}),
          ...(detail.clearBranchSwitchInProgress === true
            ? { branchSwitchInProgress: false }
            : {}),
        };
      });

      // Update all session list caches so sidebar reflects the new branch
      queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
        if (!Array.isArray(old)) return old;
        let changed = false;
        const next = old.map((item) => {
          if (!item || typeof item !== "object") return item;
          const row = item as Record<string, unknown>;
          if (row.id !== sessionId) return item;
          changed = true;
          return {
            ...row,
            branch: detail.branch,
            ...(detail.baseBranch !== undefined ? { baseBranch: detail.baseBranch } : {}),
            ...(detail.clearBranchSwitchInProgress === true
              ? { branchSwitchInProgress: false }
              : {}),
          };
        });
        return changed ? next : old;
      });

      if (localStartNewWorkGitPendingRef.current?.newBranch === detail.branch) {
        localStartNewWorkGitPendingRef.current = null;
        setLocalStartNewWorkGitPending(null);
      }

      if (detail.clearBranchSwitchInProgress === true) {
        setStartNewWorkNotice((currentNotice) => {
          if (!currentNotice || currentNotice.newBranch === detail.branch) return currentNotice;
          const nextNotice: StartNewWorkNoticeState = {
            ...currentNotice,
            newBranch: detail.branch,
            baseBranch: detail.baseBranch ?? currentNotice.baseBranch,
          };
          writeStartNewWorkNoticeState({
            sessionId,
            notice: nextNotice,
          });
          return nextNotice;
        });
      }

    };

    window.addEventListener(SESSION_BRANCH_UPDATED_EVENT, handleBranchUpdated);
    return () => {
      window.removeEventListener(SESSION_BRANCH_UPDATED_EVENT, handleBranchUpdated);
    };
  }, [queryClient, repoId, sessionId]);

  useEffect(() => {
    const handleStartNewWorkGitCompleted = (event: Event) => {
      const detail = (event as CustomEvent<SessionStartNewWorkGitCompletedDetail>).detail;
      if (!detail || detail.sessionId !== sessionId) {
        return;
      }

      void queryClient.invalidateQueries({ queryKey: ["pr-history", sessionId] });

      const failedPendingState = localStartNewWorkGitPendingRef.current;
      localStartNewWorkGitPendingRef.current = null;
      setLocalStartNewWorkGitPending(null);
      if (detail.ok !== true) {
        const previousPr =
          failedPendingState?.previousPr ?? readStartNewWorkNoticeState(sessionId)?.previousPr ?? null;
        if (previousPr) {
          restorePreviousPullRequestCachesAfterStartNewWorkFailure({
            queryClient,
            sessionId,
            previousPr,
          });
        }
        setHandledMergedPrNumber(null);
        setStartNewWorkNotice(null);
        clearStartNewWorkNoticeState(sessionId);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["pr-status", sessionId], exact: true });
    };

    window.addEventListener(
      SESSION_START_NEW_WORK_GIT_COMPLETED_EVENT,
      handleStartNewWorkGitCompleted,
    );
    return () => {
      window.removeEventListener(
        SESSION_START_NEW_WORK_GIT_COMPLETED_EVENT,
        handleStartNewWorkGitCompleted,
      );
    };
  }, [queryClient, sessionId]);
  const foregroundLeaseOwnedByThisTab = useMemo(() => {
    if (!isPageVisible) return false;
    if (!sessionScopeKey) return true;
    return isSessionForegroundOwnedByCurrentTab({
      snapshot: foregroundLeaseSnapshot,
      sessionId,
      currentTabId: currentForegroundOwnershipTabId,
    });
  }, [currentForegroundOwnershipTabId, foregroundLeaseSnapshot, isPageVisible, sessionId, sessionScopeKey]);

  const foregroundLeaseOwnedElsewhere = useMemo(() => {
    if (!isPageVisible || !sessionScopeKey) return false;
    return isSessionForegroundOwnedElsewhere({
      snapshot: foregroundLeaseSnapshot,
      sessionId,
      currentTabId: currentForegroundOwnershipTabId,
    });
  }, [currentForegroundOwnershipTabId, foregroundLeaseSnapshot, isPageVisible, sessionId, sessionScopeKey]);
  const viewerActivityEnabled = useMemo(() => {
    return foregroundLeaseOwnedByThisTab && !foregroundLeaseOwnedElsewhere;
  }, [foregroundLeaseOwnedByThisTab, foregroundLeaseOwnedElsewhere]);

  const latestThreadViewResetSessionIdRef = useRef(sessionId);
  const threadViewResetMountedRef = useRef(true);

  useEffect(() => {
    latestThreadViewResetSessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    return () => {
      threadViewResetMountedRef.current = false;
    };
  }, []);

  const shouldContinueThreadViewResetFlow = useCallback((targetSessionId: string) => {
    return threadViewResetMountedRef.current && latestThreadViewResetSessionIdRef.current === targetSessionId;
  }, []);

  const logThreadViewResetFailure = useCallback((
    targetSessionId: string,
    error: unknown,
    options?: { surfaceToUser?: boolean },
  ) => {
    const errorMessage = error instanceof Error ? error.message : "Unknown thread reset failure";
    debugWarn("session.lifecycle", "Failed to clear thread sync session state", {
      sessionId: targetSessionId,
      errorMessage,
      surfaceToUser: options?.surfaceToUser === true,
    });
    if (options?.surfaceToUser === true && shouldContinueThreadViewResetFlow(targetSessionId)) {
      toast.error(errorMessage);
    }
  }, [shouldContinueThreadViewResetFlow]);

  const handleClearLocalThreadState = useCallback(async () => {
    if (!window.confirm("Reset this thread view and reload? This does not change server history.")) {
      return;
    }

    try {
      clearPersistableSessionSurfaceQueriesForSession({ queryClient, sessionId })
      const reloaded = await clearThreadViewAndReloadIfStillCurrent({
        sessionId,
        clearSessionState: clearThreadSyncSessionState,
        shouldReloadSession: shouldContinueThreadViewResetFlow,
        reloadWindow: () => {
          window.location.reload();
        },
      });
      if (!reloaded) {
        unblockPersistableSessionSurfaceQueriesForSession(sessionId)
      }
    } catch (error) {
      unblockPersistableSessionSurfaceQueriesForSession(sessionId)
      logThreadViewResetFailure(sessionId, error, { surfaceToUser: true });
    }
  }, [logThreadViewResetFailure, queryClient, sessionId, shouldContinueThreadViewResetFlow]);

  const resetSessionViewState = useCallback((id: string) => {
    clearPersistableSessionSurfaceQueriesForSession({ queryClient, sessionId: id })
    void clearThreadSyncSessionState(id).catch((error) => {
      logThreadViewResetFailure(id, error);
    });
    clearSessionLayoutSnapshotForSession(id)
    clearPersistentPreviewStateForSession(id)
    evictSessionPreviewFrame(id)
  }, [clearPersistentPreviewStateForSession, evictSessionPreviewFrame, logThreadViewResetFailure, queryClient]);

  const pendingForkSnapshot = peekPendingForkSnapshot(sessionId)
  const [optimisticRuntimeConversation, setOptimisticRuntimeConversation] = useState<OptimisticRuntimeConversationState | null>(null);
  const activeOptimisticRuntimeConversation = optimisticRuntimeConversation?.sessionId === sessionId
    ? optimisticRuntimeConversation
    : null;
  const optimisticCreatingRuntimeConversation = activeOptimisticRuntimeConversation?.snapshot ?? null;

  useLayoutEffect(() => {
    const restoredLayout = restoreSessionLayoutSnapshotForSession(sessionId);
    if (!restoredLayout) {
      resetEphemeral();
    }
    dispatchSharedTimelineViewportFlush();

    return () => {
      saveSessionLayoutSnapshotForSession(sessionId);
    };
  }, [
    sessionId,
    resetEphemeral,
    sessionScopeKey,
  ]);

  // A ref to track the latest attachments for persisting on session switch
  const attachmentsForDraftRef = useRef<ImageAttachment[]>([]);
  useEffect(() => {
    attachmentsForDraftRef.current = attachments;
  }, [attachments]);

  // Gate debounced persist effects until IDB load completes so we don't
  // overwrite a draft with stale/empty data during the async load window.
  const draftIdbLoadedRef = useRef(false);
  const draftRemoteLoadedRef = useRef(false);
  const [draftPersistenceReady, setDraftPersistenceReady] = useState(false);
  const draftPersistenceReadyRef = useRef(false);
  const localDraftPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remoteDraftPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateDraftPersistenceReady = useCallback(() => {
    setDraftPersistenceReady(
      hasLocalDraftInputRef.current ||
      (draftIdbLoadedRef.current && draftRemoteLoadedRef.current),
    )
  }, [])

  const markLocalDraftInput = useCallback(() => {
    if (!suppressLocalDraftInputTrackingRef.current) {
      hasLocalDraftInputRef.current = true
    }
    updateDraftPersistenceReady()
  }, [updateDraftPersistenceReady])

  const clearLocalDraftPersistTimer = useCallback(() => {
    if (localDraftPersistTimerRef.current === null) return;
    clearTimeout(localDraftPersistTimerRef.current);
    localDraftPersistTimerRef.current = null;
  }, []);

  const clearRemoteDraftPersistTimer = useCallback(() => {
    if (remoteDraftPersistTimerRef.current === null) return;
    clearTimeout(remoteDraftPersistTimerRef.current);
    remoteDraftPersistTimerRef.current = null;
  }, []);

  const clearDraftPersistTimers = useCallback(() => {
    clearLocalDraftPersistTimer();
    clearRemoteDraftPersistTimer();
  }, [clearLocalDraftPersistTimer, clearRemoteDraftPersistTimer]);

  const scheduleLocalDraftPersist = useCallback((debounceMs: number) => {
    if (!draftIdbLoadedRef.current) return;
    clearLocalDraftPersistTimer();
    localDraftPersistTimerRef.current = setTimeout(() => {
      localDraftPersistTimerRef.current = null;
      persistChatDraft(
        activeDraftStorageId,
        draftTextRef.current,
        attachmentsForDraftRef.current,
        draftSessionContextRefsRef.current,
        draftRequestedSkillRefsRef.current,
        draftPreferredMcpServerRefsRef.current,
        draftAttachedPullRequestRef.current,
        draftCursorPositionRef.current,
      );
    }, debounceMs);
  }, [activeDraftStorageId, clearLocalDraftPersistTimer]);

  const scheduleRemoteDraftPersist = useCallback((debounceMs: number) => {
    if (!draftPersistenceReadyRef.current) return;
    clearRemoteDraftPersistTimer();
    remoteDraftPersistTimerRef.current = setTimeout(() => {
      remoteDraftPersistTimerRef.current = null;
      void saveThreadDraftSnapshot(
        sessionId,
        draftTextRef.current,
        attachmentsForDraftRef.current,
        draftSessionContextRefsRef.current,
        draftRequestedSkillRefsRef.current,
        draftPreferredMcpServerRefsRef.current,
        draftAttachedPullRequestRef.current,
        draftCursorPositionRef.current,
        { conversationId: draftConversationId },
      );
    }, debounceMs);
  }, [clearRemoteDraftPersistTimer, draftConversationId, sessionId]);

  const requestDraftPersist = useCallback((args: {
    localDebounceMs?: number;
    remoteDebounceMs?: number;
  }) => {
    if (typeof args.localDebounceMs === "number") {
      scheduleLocalDraftPersist(args.localDebounceMs);
    }
    if (typeof args.remoteDebounceMs === "number") {
      scheduleRemoteDraftPersist(args.remoteDebounceMs);
    }
  }, [scheduleLocalDraftPersist, scheduleRemoteDraftPersist]);

  const persistLatestDraftSnapshotNow = useCallback(() => {
    persistChatDraft(
      activeDraftStorageId,
      draftTextRef.current,
      attachmentsForDraftRef.current,
      draftSessionContextRefsRef.current,
      draftRequestedSkillRefsRef.current,
      draftPreferredMcpServerRefsRef.current,
      draftAttachedPullRequestRef.current,
      draftCursorPositionRef.current,
    );
    if (
      draftPersistenceReadyRef.current
      || hasLocalDraftInputRef.current
      || draftSnapshotUpdatedAtRef.current > 0
    ) {
      void saveThreadDraftSnapshot(
        sessionId,
        draftTextRef.current,
        attachmentsForDraftRef.current,
        draftSessionContextRefsRef.current,
        draftRequestedSkillRefsRef.current,
        draftPreferredMcpServerRefsRef.current,
        draftAttachedPullRequestRef.current,
        draftCursorPositionRef.current,
        { conversationId: draftConversationId },
      );
    }
  }, [activeDraftStorageId, draftConversationId, sessionId]);

  const addImageAttachments = useCallback((files: File[]) => {
    markLocalDraftInput()
    return addImageAttachmentsInternal(files)
  }, [addImageAttachmentsInternal, markLocalDraftInput])

  const removeAttachment = useCallback((attachmentId: string) => {
    markLocalDraftInput()
    removeAttachmentInternal(attachmentId)
  }, [markLocalDraftInput, removeAttachmentInternal])

  const retryAttachment = useCallback((attachmentId: string) => {
    markLocalDraftInput()
    return retryAttachmentInternal(attachmentId)
  }, [markLocalDraftInput, retryAttachmentInternal])

  const readLegacyPendingBootstrapDraft = useCallback(() => {
    return {
      text: sessionStorage.getItem(initialMessageStorageKey(sessionId)),
      clientMessageId: null,
      contextRefs: parseStoredSessionContextRefs(
        sessionStorage.getItem(initialContextRefsStorageKey(sessionId))
      ),
      requestedSkillRefs: [],
      preferredMcpServerRefs: [],
      attachedPullRequest: null,
      prToAdopt: null,
    }
  }, [sessionId])

  const readPendingBootstrapDraft = useCallback(() => {
    const rawBootstrapDraft = sessionStorage.getItem(initialBootstrapDraftStorageKey(sessionId))
    try {
      const bootstrapDraft = normalizePendingBootstrapDraft(
        JSON.parse(rawBootstrapDraft ?? "null") as unknown,
      );
      if (hasPendingBootstrapDraftIntent({ draft: bootstrapDraft })) {
        return bootstrapDraft;
      }

      return readLegacyPendingBootstrapDraft()
    } catch {
      if (rawBootstrapDraft !== null) {
        sessionStorage.removeItem(initialBootstrapDraftStorageKey(sessionId))
      }

      return readLegacyPendingBootstrapDraft()
    }
  }, [readLegacyPendingBootstrapDraft, sessionId])

  const persistConsumedThreadDraftClear = useCallback((args: {
    consumedDraft: ThreadDraftSnapshot;
    observationTimestamp: number;
  }): number => {
    const updatedAt = Math.max(
      Date.now(),
      args.consumedDraft.updatedAt + CONSUMED_THREAD_DRAFT_CLEAR_VERSION_INCREMENT_MS,
      args.observationTimestamp + CONSUMED_THREAD_DRAFT_CLEAR_VERSION_INCREMENT_MS,
    );
    if (args.consumedDraft.attachedPullRequest) {
      const successAttachmentKey = buildPrAdoptionSuccessAttachmentKey({
        attachedPullRequest: args.consumedDraft.attachedPullRequest,
      });
      writeConsumedPrAdoptionAttachment({
        sessionId,
        successAttachmentKey,
      });
      writeAcknowledgedPrAdoptionSuccessAttachment({
        sessionId,
        successAttachmentKey,
      });
    }
    persistChatDraft(activeDraftStorageId, "", [], [], [], [], null, 0, { updatedAt });
    void saveThreadDraftSnapshot(
      sessionId,
      "",
      [],
      [],
      [],
      [],
      null,
      0,
      { conversationId: draftConversationId, updatedAt },
    );
    primeRemoteDraftPersistVersion(sessionId, updatedAt, draftConversationId);
    return updatedAt;
  }, [activeDraftStorageId, draftConversationId, sessionId]);

  useEffect(() => {
    draftPersistenceReadyRef.current = draftPersistenceReady
  }, [draftPersistenceReady])

  useEffect(() => {
    // Save old session's draft (text + attachments) before clearing state.
    // Capture ref values before clearAllAttachments triggers re-renders.
    const previousDraftStorageId = activeDraftSessionRef.current;
    const previousBaseSessionId = activeDraftBaseSessionRef.current;
    const previousWasDefaultConversation = previousDraftStorageId === previousBaseSessionId;
    const previousConversationId = previousWasDefaultConversation
      ? "default"
      : previousDraftStorageId.startsWith(`${previousBaseSessionId}:`)
        ? previousDraftStorageId.slice(previousBaseSessionId.length + 1)
        : "default";
    const previousDraftText = draftTextRef.current;
    const previousAttachments = attachmentsForDraftRef.current;
    const previousDraftContextRefs = draftSessionContextRefsRef.current;
    const previousDraftRequestedSkillRefs = draftRequestedSkillRefsRef.current;
    const previousDraftPreferredMcpServerRefs = draftPreferredMcpServerRefsRef.current;
    const previousDraftAttachedPullRequest = draftAttachedPullRequestRef.current;
    const previousDraftCursorPosition = draftCursorPositionRef.current;
    const shouldPersistPreviousRemoteDraft =
      hasLocalDraftInputRef.current ||
      draftSnapshotUpdatedAtRef.current > 0 ||
      (draftIdbLoadedRef.current && draftRemoteLoadedRef.current)

    clearDraftPersistTimers();

    // Clear attachments — they'll be restored from IDB below if needed
    clearAllAttachments();

    if (previousDraftStorageId !== activeDraftStorageId) {
      persistChatDraft(
        previousDraftStorageId,
        previousDraftText,
        previousAttachments,
        previousDraftContextRefs,
        previousDraftRequestedSkillRefs,
        previousDraftPreferredMcpServerRefs,
        previousDraftAttachedPullRequest,
        previousDraftCursorPosition,
      );
      if (shouldPersistPreviousRemoteDraft) {
        void saveThreadDraftSnapshot(
          previousBaseSessionId,
          previousDraftText,
          previousAttachments,
          previousDraftContextRefs,
          previousDraftRequestedSkillRefs,
          previousDraftPreferredMcpServerRefs,
          previousDraftAttachedPullRequest,
          previousDraftCursorPosition,
          hasLocalDraftInputRef.current
            ? { conversationId: previousConversationId }
            : { conversationId: previousConversationId, updatedAt: draftSnapshotUpdatedAtRef.current },
        )
      }
    }
    activeDraftSessionRef.current = activeDraftStorageId;
    activeDraftBaseSessionRef.current = sessionId;
    draftIdbLoadedRef.current = false;
    draftRemoteLoadedRef.current = false;
    hasLocalDraftInputRef.current = false;
    draftSnapshotUpdatedAtRef.current = 0;
    draftCursorPositionRef.current = 0;
    hydratedDraftSnapshotRef.current = EMPTY_THREAD_DRAFT;
    setDraftPersistenceReady(false)

    // Start with sync localStorage fallback for instant text display
    const syncText = loadChatDraftSync(sessionId, draftConversationId);
    const pendingBootstrapDraft = draftConversationId === "default"
      ? sanitizePendingBootstrapDraft(readPendingBootstrapDraft())
      : EMPTY_PENDING_BOOTSTRAP_DRAFT;
    const hasPendingBootstrapIntent = hasPendingBootstrapDraftIntent({
      draft: pendingBootstrapDraft,
    })
    setPendingBootstrapSnapshot(pendingBootstrapDraft);
    setPendingBootstrapActive(hasPendingBootstrapIntent);
    setPendingBootstrapClientMessageId(pendingBootstrapDraft.clientMessageId);
    setPendingBootstrapPrToAdopt(pendingBootstrapDraft.prToAdopt);
    const hydratedPendingBootstrapAttachedPullRequest = dropConsumedHydratedAttachedPullRequest(
      pendingBootstrapDraft.attachedPullRequest,
    );
    const initialDraftText = syncText.length > 0 ? syncText : (pendingBootstrapDraft.text ?? "");
    setDraftText(initialDraftText);
    draftTextRef.current = initialDraftText;
    draftSessionContextRefsRef.current = pendingBootstrapDraft.contextRefs;
    setDraftSessionContextRefs(pendingBootstrapDraft.contextRefs);
    draftRequestedSkillRefsRef.current = pendingBootstrapDraft.requestedSkillRefs;
    setDraftRequestedSkillRefs(pendingBootstrapDraft.requestedSkillRefs);
    draftPreferredMcpServerRefsRef.current = pendingBootstrapDraft.preferredMcpServerRefs;
    setDraftPreferredMcpServerRefs(pendingBootstrapDraft.preferredMcpServerRefs);
    draftAttachedPullRequestRef.current = hydratedPendingBootstrapAttachedPullRequest;
    setDraftAttachedPullRequest(hydratedPendingBootstrapAttachedPullRequest);
    setPrAdoptionStatus((current) => {
      const nextStatus = hydratedPendingBootstrapAttachedPullRequest
        ? maybeBuildHydratedPrAdoptionSuccessStatus({
            attachedPullRequest: hydratedPendingBootstrapAttachedPullRequest,
            linkedSessionTitle: findPrLinkedSessionTitle(pendingBootstrapDraft.contextRefs),
          })
        : null;
      return samePrAdoptionStatus(current, nextStatus) ? current : nextStatus;
    });
    runWithoutLocalDraftInputTracking(() => {
      editorRef.current?.clear();
    });
    setHasContent(false);
    draftHydratedSessionRef.current = null;

    // Then load the full draft from local IDB and remote DO storage.
    let cancelled = false;
    let bestHydratedUpdatedAt = -1;

    const maybeHydrateDraftSnapshot = (candidate: ThreadDraftSnapshot) => {
      if (cancelled) return;
      if (hasLocalDraftInputRef.current) return;
      if (candidate.updatedAt <= bestHydratedUpdatedAt) return;
      if (
        hasPendingBootstrapIntent
        && candidate.text.trim().length === 0
        && candidate.attachments.length === 0
        && candidate.contextRefs.length === 0
        && candidate.requestedSkillRefs.length === 0
        && candidate.preferredMcpServerRefs.length === 0
        && candidate.attachedPullRequest === null
      ) {
        return;
      }

      const mergedCandidate = mergeMissingThreadDraftLinkedMetadata({
        candidate,
        hydratedSnapshot: hydratedDraftSnapshotRef.current,
      });
      const hydratedAttachedPullRequest = dropConsumedHydratedAttachedPullRequest(
        mergedCandidate.attachedPullRequest,
      );
      const resolvedCandidate = hydratedAttachedPullRequest === mergedCandidate.attachedPullRequest
        ? mergedCandidate
        : {
            ...mergedCandidate,
            attachedPullRequest: hydratedAttachedPullRequest,
          };
      const hydratedContextRefs = dropConsumedHydratedPrLinkedSessionContextRefs({
        contextRefs: resolvedCandidate.contextRefs,
        attachedPullRequest: resolvedCandidate.attachedPullRequest,
      });
      let hydratedCandidate = hydratedContextRefs === resolvedCandidate.contextRefs
        ? resolvedCandidate
        : {
            ...resolvedCandidate,
            contextRefs: hydratedContextRefs,
          };
      const consumedObservation = findConsumedThreadDraftObservation({
        draft: hydratedCandidate,
        observations: buildThreadDraftConsumptionObservations({
          timelineItems: useAgentRuntime.getState().snapshot?.timeline ?? [],
        }),
      });
      if (consumedObservation) {
        const consumedClearUpdatedAt = persistConsumedThreadDraftClear({
          consumedDraft: hydratedCandidate,
          observationTimestamp: consumedObservation.timestamp,
        });
        hydratedCandidate = {
          ...EMPTY_THREAD_DRAFT,
          updatedAt: consumedClearUpdatedAt,
        };
      }

      bestHydratedUpdatedAt = hydratedCandidate.updatedAt;
      draftSnapshotUpdatedAtRef.current = hydratedCandidate.updatedAt;

      clearAllAttachments();
      if (hydratedCandidate.attachments.length > 0) {
        restoreAttachments(hydratedCandidate.attachments);
      }

      if (draftTextRef.current !== hydratedCandidate.text) {
        draftTextRef.current = hydratedCandidate.text;
      }
      draftSessionContextRefsRef.current = hydratedCandidate.contextRefs;
      setDraftSessionContextRefs(hydratedCandidate.contextRefs);
      draftRequestedSkillRefsRef.current = hydratedCandidate.requestedSkillRefs;
      setDraftRequestedSkillRefs(hydratedCandidate.requestedSkillRefs);
      draftPreferredMcpServerRefsRef.current = hydratedCandidate.preferredMcpServerRefs;
      setDraftPreferredMcpServerRefs(hydratedCandidate.preferredMcpServerRefs);
      draftAttachedPullRequestRef.current = hydratedCandidate.attachedPullRequest;
      setDraftAttachedPullRequest(hydratedCandidate.attachedPullRequest);
      draftCursorPositionRef.current = hydratedCandidate.cursorPosition;
      hydratedDraftSnapshotRef.current = hydratedCandidate;
      setPrAdoptionStatus((current) => {
        const nextStatus = hydratedCandidate.attachedPullRequest
          ? maybeBuildHydratedPrAdoptionSuccessStatus({
              attachedPullRequest: hydratedCandidate.attachedPullRequest,
              linkedSessionTitle: findPrLinkedSessionTitle(hydratedCandidate.contextRefs),
            })
          : null;
        return samePrAdoptionStatus(current, nextStatus) ? current : nextStatus;
      });

      draftHydratedSessionRef.current = null;
      setDraftHydrationVersion((version) => version + 1);
    };

    // Wait for any in-flight IDB persist for this session to complete before
    // reading, so that a rapid A→B→A switch doesn't read stale data written
    // by the save that was queued when we first left session A.
    void waitForPendingLocalDraftPersist(activeDraftStorageId)
      .then(() => loadSessionDraft(activeDraftStorageId))
      .then((draft) => {
        if (cancelled) return;
        if (draftConversationId === "default") {
          primeRemoteDraftPersistVersion(sessionId, draft.updatedAt)
        }

        maybeHydrateDraftSnapshot({
          text: draft.text,
          attachments: draft.attachments,
          contextRefs: draft.contextRefs,
          requestedSkillRefs: draft.requestedSkillRefs,
          preferredMcpServerRefs: draft.preferredMcpServerRefs,
          attachedPullRequest: draft.attachedPullRequest,
          cursorPosition: draft.cursorPosition,
          updatedAt: draft.updatedAt,
        });

        // Clean up pre-migration localStorage entry now that we've migrated
        try {
          localStorage.removeItem(chatDraftStorageKey(sessionId, draftConversationId));
        } catch {
          // ignore
        }
      })
      .catch(() => {
        // loadSessionDraft already falls back to an empty snapshot on expected
        // failures, so reaching here indicates an unexpected error in the
        // surrounding promise chain. Treat it as an empty draft and still
        // unblock persistence readiness below.
      })
      .finally(() => {
        if (cancelled) return;
        draftIdbLoadedRef.current = true;
        updateDraftPersistenceReady()
        if (hasLocalDraftInputRef.current) {
          scheduleLocalDraftPersist(LOCAL_DRAFT_PERSIST_DEBOUNCE_MS)
        }
      });

    // Skip the remote draft fetch when the local snapshot already reflects the
    // latest known remote persist version for this session. Rapid revisits often
    // already hydrated the same snapshot from IDB, and re-fetching it every time
    // shows up as avoidable thread-draft traffic in perf traces.
    {
      void waitForPendingRemoteDraftPersist(sessionId, draftConversationId)
      .then(() => loadThreadDraftSnapshotIfNewer({
        sessionId,
        conversationId: draftConversationId,
        minUpdatedAtExclusive: draftSnapshotUpdatedAtRef.current,
      }))
      .then((remoteDraft) => {
        if (cancelled) return
        if (!remoteDraft) return
        primeRemoteDraftPersistVersion(sessionId, remoteDraft.updatedAt, draftConversationId)
        maybeHydrateDraftSnapshot(remoteDraft)
      })
      .finally(() => {
        if (cancelled) return
        draftRemoteLoadedRef.current = true
        updateDraftPersistenceReady()
      })
    }

    return () => {
      cancelled = true;
    };
  }, [
    clearAllAttachments,
    clearDraftPersistTimers,
    activeDraftStorageId,
    draftConversationId,
    dropConsumedHydratedAttachedPullRequest,
    dropConsumedHydratedPrLinkedSessionContextRefs,
    maybeBuildHydratedPrAdoptionSuccessStatus,
    persistConsumedThreadDraftClear,
    restoreAttachments,
    runWithoutLocalDraftInputTracking,
    sanitizePendingBootstrapDraft,
    scheduleLocalDraftPersist,
    sessionId,
  ]);

  useEffect(() => {
    draftSessionContextRefsRef.current = draftSessionContextRefs;
  }, [draftSessionContextRefs]);

  useEffect(() => {
    draftRequestedSkillRefsRef.current = draftRequestedSkillRefs;
  }, [draftRequestedSkillRefs]);

  useEffect(() => {
    draftPreferredMcpServerRefsRef.current = draftPreferredMcpServerRefs;
  }, [draftPreferredMcpServerRefs]);

  useEffect(() => {
    draftAttachedPullRequestRef.current = draftAttachedPullRequest;
  }, [draftAttachedPullRequest]);

  useEffect(() => {
    return () => {
      clearDraftPersistTimers();
      persistChatDraft(
        activeDraftSessionRef.current,
        draftTextRef.current,
        attachmentsForDraftRef.current,
        draftSessionContextRefsRef.current,
        draftRequestedSkillRefsRef.current,
        draftPreferredMcpServerRefsRef.current,
        draftAttachedPullRequestRef.current,
        draftCursorPositionRef.current,
      );
      if (
        activeDraftBaseSessionRef.current === sessionId &&
        (
          draftPersistenceReadyRef.current ||
          hasLocalDraftInputRef.current ||
          draftSnapshotUpdatedAtRef.current > 0
        )
      ) {
        void saveThreadDraftSnapshot(
          activeDraftBaseSessionRef.current,
          draftTextRef.current,
          attachmentsForDraftRef.current,
          draftSessionContextRefsRef.current,
          draftRequestedSkillRefsRef.current,
          draftPreferredMcpServerRefsRef.current,
          draftAttachedPullRequestRef.current,
          draftCursorPositionRef.current,
          hasLocalDraftInputRef.current
            ? { conversationId: draftConversationId }
            : { conversationId: draftConversationId, updatedAt: draftSnapshotUpdatedAtRef.current },
        )
      }
    };
  }, [clearDraftPersistTimers]);

  const {
    data: session,
    error: sessionQueryError,
    isError: isSessionQueryError,
  } = useQuery({
    queryKey: ["session", sessionId],
    queryFn: () => fetchAuthoritativeSessionRecord(sessionId),
    placeholderData: () => {
      if (cachedSessionDetailRecord && typeof cachedSessionDetailRecord === "object") {
        return cachedSessionDetailRecord as Record<string, unknown>;
      }

      return findCachedSessionListRecordForSession({
        queryClient,
        sessionId,
        viewerUserId: authSession?.user?.id ?? null,
      });
    },
    // Client-created sessions seed a placeholder row into the cache before the
    // create-session POST settles. Force an immediate refetch on mount when the
    // cache entry is still that optimistic placeholder so create-from bootstrap
    // effects do not wait a full stale-time window before seeing the real row.
    refetchOnMount: shouldForceAuthoritativeSessionRefetchOnMount ? "always" : true,
    // During optimistic navigation the session may not exist in the DB yet
    // (POST still in flight). Retry on 404 so the branch badge and other
    // server data appear as soon as creation completes.  Don't retry real
    // server errors.
    retry: (failureCount, error) => {
      if (failureCount >= 3) return false;
      return (error as Error & { status?: number }).status === 404;
    },
    retryDelay: 1000,
  });

  const { data: repos } = useQuery({
    queryKey: ["repos"],
    queryFn: async () => {
      const res = await api.api.repos.$get();
      if (!res.ok) return [];
      return normalizeArrayResponse<{
        id: string;
        provider?: "github" | "gitea" | string;
        githubFullName: string;
        displayName?: string | null;
        repositoryFullName?: string | null;
        defaultBranch: string;
        deployEnabled?: boolean;
        isCordNewProject?: boolean;
      }>({
        value: await res.json(),
      });
    },
  });
  const repo = isRepolessRoute ? undefined : (repos ?? []).find((candidate) => candidate.id === repoId);
  const repoProvider = repo?.provider === "github" || repo?.provider === "gitea"
    ? repo.provider
    : "unsupported";

  const sessionNotFound =
    isSessionQueryError &&
    (sessionQueryError as Error & { status?: number }).status === 404;
  const sessionForbidden =
    isSessionQueryError &&
    (sessionQueryError as Error & { status?: number }).status === 403;
  const authoritativeSessionAvailable = hasAuthoritativeSessionRecordMetadata(session);
  useThreadRuntimeSessionRowMirror(
    session && typeof session === "object"
      ? (() => {
          const sessionRecord = session as Record<string, unknown>
          return {
            id: sessionId,
            ...(Object.prototype.hasOwnProperty.call(sessionRecord, "status")
              ? { status: typeof sessionRecord.status === "string" ? sessionRecord.status : null }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(sessionRecord, "lifecycleStatus")
              ? {
                  lifecycleStatus:
                    typeof sessionRecord.lifecycleStatus === "string"
                      ? sessionRecord.lifecycleStatus
                      : null,
                }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(sessionRecord, "prState")
              ? {
                  prState:
                    sessionRecord.prState === null || typeof sessionRecord.prState === "string"
                      ? sessionRecord.prState
                      : null,
                }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(sessionRecord, "createdAt")
              ? { createdAt: typeof sessionRecord.createdAt === "string" ? sessionRecord.createdAt : null }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(sessionRecord, "updatedAt")
              ? { updatedAt: typeof sessionRecord.updatedAt === "string" ? sessionRecord.updatedAt : null }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(sessionRecord, "runtimeLifecycle")
              ? { runtimeLifecycle: sessionRecord.runtimeLifecycle }
              : {}),
          }
        })()
      : null,
  )
  const persistedSessionModelIntentFromRecord = useMemo(
    () => readSessionIntentFromSessionRecord(session),
    [session],
  )
  const persistedSessionModelIntentConversationIdFromRecord = useMemo(() => {
    if (!session || typeof session !== "object") return null;
    const value = (session as Record<string, unknown>).sessionModelIntentConversationId;
    return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
  }, [session]);
  const sessionHistoryPresence = useMemo(() => {
    if (pendingForkSnapshot || forkBootstrapEntry) {
      return "has_history" as const
    }

    if (hasAcceptedUserMessageMarker) {
      return "has_history" as const
    }

    const rawHasHistory =
      session && typeof session === "object"
        ? (session as Record<string, unknown>).hasHistory
        : null

    if (typeof rawHasHistory === "boolean") {
      return rawHasHistory ? "has_history" as const : "no_history" as const
    }

    if (!authoritativeSessionAvailable && optimisticTitle) {
      return "no_history" as const
    }

    return "unknown" as const
  }, [
    authoritativeSessionAvailable,
    forkBootstrapEntry,
    hasAcceptedUserMessageMarker,
    optimisticTitle,
    pendingForkSnapshot,
    session,
  ])
  const fetchedSessionTitle = (() => {
    const rawTitle = (session as Record<string, unknown>)?.title;
    if (typeof rawTitle !== "string") return "";
    return rawTitle.trim();
  })();
  const sessionParentSessionId = (() => {
    const rawParentSessionId = (session as Record<string, unknown>)?.parentSessionId;
    if (typeof rawParentSessionId !== "string") {
      return forkBootstrapEntry?.parentSessionId ?? null
    }
    const trimmed = rawParentSessionId.trim();
    return trimmed.length > 0 ? trimmed : forkBootstrapEntry?.parentSessionId ?? null;
  })();
  const sessionSupportsFork = (() => {
    const raw = (session as Record<string, unknown>)?.supportsFork;
    return typeof raw === "boolean" ? raw : null;
  })();
  const sessionAgentType = normalizeSessionAgentIdentity(
    (session as Record<string, unknown>)?.agentType,
  );
  useEffect(() => {
    if (!search.conversationId) return;
    if (
      sessionAgentType === "unknown" ||
      sessionAgentType === "opencode" ||
      sessionAgentType === "claude_code"
    ) return;
    void navigate({
      to: "/$repoId/$sessionId",
      params: { repoId, sessionId },
      search: { conversationId: undefined },
      replace: true,
    });
  }, [navigate, repoId, search.conversationId, sessionAgentType, sessionId]);
  const sessionCreatedAt = (() => {
    const rawCreatedAt = (session as Record<string, unknown>)?.createdAt
    if (typeof rawCreatedAt !== "string") {
      return null
    }

    const trimmedCreatedAt = rawCreatedAt.trim()
    return trimmedCreatedAt.length > 0 ? trimmedCreatedAt : null
  })()

  useEffect(() => {
    if (!forkBootstrapEntry) return
    if (!authoritativeSessionAvailable) return
    clearPendingForkBootstrapRequestInFlight(sessionId)
    clearPendingForkSnapshot(sessionId)
    clearPendingForkBootstrapEntry(sessionId)
  }, [authoritativeSessionAvailable, forkBootstrapEntry, sessionId])

  useEffect(() => {
    if (!forkBootstrapEntry) return
    if (authoritativeSessionAvailable) return
    if (forkBootstrapEntry.status !== "pending" && forkBootstrapEntry.status !== "reconciling") {
      return
    }
    if (isPendingForkBootstrapRequestInFlight(sessionId)) return

    let cancelled = false

    const runReconciliation = async () => {
      if (!isForkBootstrapWithinReconciliationWindow(forkBootstrapEntry)) {
        patchPendingForkBootstrapEntry(sessionId, (current) => {
          if (!current) return current
          return {
            ...current,
            status: "failed",
            errorMessage: current.errorMessage ?? FORK_BOOTSTRAP_INTERRUPTED_ERROR_MESSAGE,
          }
        })
        queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
          if (!Array.isArray(old)) return old
          return old.filter((item) => {
            if (!item || typeof item !== "object") return true
            return (item as Record<string, unknown>).id !== sessionId
          })
        })
        return
      }

      if (forkBootstrapEntry.status !== "reconciling") {
        patchPendingForkBootstrapEntry(sessionId, (current) => {
          if (!current || current.status !== "pending") return current
          return {
            ...current,
            status: "reconciling",
            errorMessage: null,
          }
        })
      }

      while (!cancelled && isForkBootstrapWithinReconciliationWindow(forkBootstrapEntry)) {
        try {
          const resolvedSession = await fetchAuthoritativeSessionRecord(sessionId)
          if (cancelled) return

          const cacheSnapshot = captureSessionCacheMutationSnapshot({
            queryClient,
            sessionId,
            repoId: forkBootstrapEntry.repositoryId,
          })
          queryClient.setQueryData(["session", sessionId], resolvedSession)
          const record = resolvedSession as Record<string, unknown>
          const createdAt =
            typeof record.createdAt === "string" && record.createdAt.trim().length > 0
              ? record.createdAt
              : new Date().toISOString()
          const restoredCacheItem: CachedSessionListItem = {
            id: sessionId,
            repositoryId: forkBootstrapEntry.repositoryId,
            parentSessionId:
              typeof record.parentSessionId === "string" ? record.parentSessionId : forkBootstrapEntry.parentSessionId,
            title:
              typeof record.title === "string" && record.title.trim().length > 0
                ? record.title
                : forkBootstrapEntry.title,
            createdAt,
            lifecycleStatus:
              typeof record.lifecycleStatus === "string" && record.lifecycleStatus.trim().length > 0
                ? record.lifecycleStatus
                : "active",
          }
          applyOptimisticSessionMembershipToSessionListCaches({
            queryClient,
            snapshot: cacheSnapshot,
            sessionId,
            item: restoredCacheItem,
          })
          return
        } catch (error) {
          const status = (error as Error & { status?: number }).status
          if (status === 404) {
            await new Promise((resolve) => setTimeout(resolve, FORK_BOOTSTRAP_RECONCILE_POLL_INTERVAL_MS))
            continue
          }
          if (status === 403) break
          await new Promise((resolve) => setTimeout(resolve, FORK_BOOTSTRAP_RECONCILE_POLL_INTERVAL_MS))
        }
      }

      if (cancelled) return
      patchPendingForkBootstrapEntry(sessionId, (current) => {
        if (!current) return current
        return {
          ...current,
          status: "failed",
          errorMessage: current.errorMessage ?? FORK_BOOTSTRAP_INTERRUPTED_ERROR_MESSAGE,
        }
      })
      queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
        if (!Array.isArray(old)) return old
        return old.filter((item) => {
          if (!item || typeof item !== "object") return true
          return (item as Record<string, unknown>).id !== sessionId
        })
      })
    }

    void runReconciliation()

    return () => {
      cancelled = true
    }
  }, [
    authoritativeSessionAvailable,
    fetchAuthoritativeSessionRecord,
    forkBootstrapEntry,
    queryClient,
    sessionId,
  ])

  useEffect(() => {
    if (!fetchedSessionTitle) return;

    queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
      if (!Array.isArray(old)) return old;

      let changed = false;
      const next = old.map((item) => {
        if (!item || typeof item !== "object") return item;
        const row = item as Record<string, unknown>;
        if (row.id !== sessionId) return item;
        if (row.title === fetchedSessionTitle) return item;
        changed = true;
        return {
          ...row,
          title: fetchedSessionTitle,
        };
      });

      return changed ? next : old;
    });
  }, [fetchedSessionTitle, queryClient, sessionId]);

  const fetchOpenCodeAccessSnapshot = useCallback(async (): Promise<OpenCodeAccessSnapshot | null> => {
    const res = await api.api.sessions[":id"]["opencode-access"].$get({
      param: { id: sessionId },
    });

    if (!res.ok) {
      console.error(`[cord] opencode-access failed (${res.status})`);
      return null;
    }

    const raw = await res.json() as {
      url?: unknown;
      username?: unknown;
      password?: unknown;
      sessionId?: unknown;
      claudeRunnerSessionId?: unknown;
      workspaceRoot?: unknown;
      directorySlug?: unknown;
    };

    return {
      url: typeof raw.url === "string" ? raw.url : null,
      username: typeof raw.username === "string" ? raw.username : "opencode",
      password: typeof raw.password === "string" ? raw.password : null,
      sessionId: typeof raw.sessionId === "string" ? raw.sessionId : null,
      claudeRunnerSessionId: typeof raw.claudeRunnerSessionId === "string" ? raw.claudeRunnerSessionId : null,
      workspaceRoot: typeof raw.workspaceRoot === "string" ? raw.workspaceRoot : null,
      directorySlug: typeof raw.directorySlug === "string" ? raw.directorySlug : null,
    };
  }, [sessionId]);

  const fetchRecoveryStatusSnapshot = useCallback(async (): Promise<RecoveryStatusSnapshot | null> => {
    const baseUrl = import.meta.env.VITE_API_URL || "";
    const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/recovery-status`, {
      method: "GET",
      credentials: "include",
    });
    const raw = await res.json().catch(() => null) as unknown;
    if (!res.ok) {
      console.error(`[cord] recovery-status failed (${res.status})`);
      return normalizeRecoveryStatusSnapshot(raw);
    }
    return normalizeRecoveryStatusSnapshot(raw);
  }, [sessionId]);

  const fetchConnectionFlowLinkConfig = useCallback(async (): Promise<ConnectionFlowLinkConfig | null> => {
    const res = await api.api.sessions[":id"]["connection-flow-links"].$get({
      param: { id: sessionId },
    });

    if (!res.ok) {
      console.error(`[cord] connection-flow-links failed (${res.status})`);
      return null;
    }

    return normalizeConnectionFlowLinkConfig(await res.json());
  }, [sessionId]);

  const fetchSessionRuntimeSnapshot = useCallback(async (): Promise<ConnectionFlowRuntimeSnapshot | null> => {
    const baseUrl = import.meta.env.VITE_API_URL || "";
    const statusUrl = buildSessionStatusUrl({
      baseUrl,
      sessionId,
      includeDebug: true,
    });

    try {
      const res = await fetch(statusUrl, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        return null;
      }
      const raw = await res.json() as unknown;
      return normalizeConnectionFlowRuntimeSnapshotPayload(raw);
    } catch {
      return null;
    }
  }, [sessionId]);

  const openOpenCodeTunnelUrl = useCallback(async () => {
    // Prefer explicit API origin when configured, but fall back to same-origin
    // so local Vite proxy setups (localhost:5173 -> /api -> localhost:8787)
    // still launch OpenCode correctly without extra env wiring.
    const apiBaseUrlRaw = import.meta.env.VITE_API_URL || window.location.origin;
    const apiBaseUrl = apiBaseUrlRaw.replace(/\/+$/, "");
    if (!apiBaseUrl) {
      console.error("[cord] Missing VITE_API_URL for OpenCode web UI launcher");
      return;
    }

    const frontendBaseUrlRaw = window.location.origin;
    const frontendBaseUrl = frontendBaseUrlRaw.replace(/\/+$/, "");
    const openCodeAccess = await fetchOpenCodeAccessSnapshot();

    try {
      const launchUrl = buildOpenCodeWebUiLaunchUrl({
        frontendBaseUrl,
        apiBaseUrl,
        sessionId,
        version: OPENCODE_WEB_UI_VERSION,
        opencodeSessionId: openCodeAccess?.sessionId ?? null,
        directorySlug: openCodeAccess?.directorySlug ?? null,
      });
      window.open(launchUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("[cord] Failed to build vendored OpenCode web UI URL", error);
    }
  }, [fetchOpenCodeAccessSnapshot, sessionId]);

  useEffect(() => {
    if ((!sessionNotFound && !sessionForbidden) || optimisticTitle || forkBootstrapEntry) return;
    navigate({
      to: "/$repoId",
      params: { repoId },
      replace: true,
    });
  }, [forkBootstrapEntry, navigate, optimisticTitle, repoId, sessionForbidden, sessionNotFound]);

  const [sessionActionToast, setSessionActionToast] = useState<SessionActionOutcomeNotice | null>(null);
  const [sessionActionBlockingCount, setSessionActionBlockingCount] = useState(0);
  const [dismissedRuntimeErrorKey, setDismissedRuntimeErrorKey] = useState<string | null>(null);
  const [dismissedSandboxErrorKey, setDismissedSandboxErrorKey] = useState<string | null>(null);

  // Local pending session-action state used to keep action buttons / composer
  // blocked while a command is being acknowledged, even when the UI is also
  // showing a synthetic queued-message row. Some actions expose that row in the
  // timeline, while others stay hidden.
  const [pendingSessionActions, setPendingSessionActions] = useState<PendingSessionAction[]>([]);

  // Registry of cleanup functions for pending action-message timers and store
  // subscriptions.  Cleared on unmount to prevent leaks.
  const actionMessageCleanupRef = useRef(new Set<() => void>());
  const sessionActionBlockingGenerationRef = useRef(0);
  useEffect(() => {
    const registry = actionMessageCleanupRef.current;
    return () => {
      sessionActionBlockingGenerationRef.current += 1;
      for (const cleanup of registry) cleanup();
      registry.clear();
    };
  }, []);
  const clearPendingSessionActions = useCallback(() => {
    const registry = actionMessageCleanupRef.current;
    for (const cleanup of registry) cleanup();
    registry.clear();
    setSessionActionBlockingCount(0);
    setPendingSessionActions([]);
  }, []);
  useEffect(() => {
    sessionActionBlockingGenerationRef.current += 1;
    clearPendingSessionActions();
  }, [clearPendingSessionActions, sessionId]);

  const lastMessageRejectionTimestampRef = useRef(0);

  const removeSessionFromAllSessionListCaches = useCallback((id: string) => {
    queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
      if (!Array.isArray(old)) return old;

      let changed = false;
      const next = old.filter((item) => {
        if (!item || typeof item !== "object") return true;
        const row = item as Record<string, unknown>;
        if (row.id !== id) return true;
        changed = true;
        return false;
      });

      return changed ? next : old;
    });
  }, [queryClient]);

  const removeSessionFromCaches = useCallback(
    (id: string) => {
      removeSessionFromAllSessionListCaches(id);
      queryClient.removeQueries({ queryKey: ["session", id] });
    },
    [queryClient, removeSessionFromAllSessionListCaches],
  );

  const navigateToList = useCallback(() => {
    navigate({ to: "/$repoId", params: { repoId } });
  }, [navigate, repoId]);

  const deleteSession = useMutation({
    mutationFn: async () => {
      const res = await api.api.sessions[":id"].$delete({
        param: { id: sessionId },
      });
      if (res.status === 404) return;
      if (!res.ok) {
        const err = new Error("Failed to delete session");
        (err as Error & { status?: number }).status = res.status;
        throw err;
      }
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["sessions"] });
      await queryClient.cancelQueries({ queryKey: ["session", sessionId] });
      const snapshot = captureSessionCacheMutationSnapshot({
        queryClient,
        sessionId,
        repoId,
      });
      applyOptimisticSessionMembershipToSessionListCaches({
        queryClient,
        snapshot,
        sessionId,
        item: null,
      });
      queryClient.removeQueries({ queryKey: ["session", sessionId], exact: true });
      // Navigate away immediately — don't wait for the API response
      navigateToList();
      return { snapshot };
    },
    onSuccess: () => {
      cleanupSessionLocalStorage(sessionId);
      resetSessionViewState(sessionId);
      invalidateSessionMembershipCaches();
    },
    onError: (_err, _vars, context) => {
      toast.error("Failed to delete thread. Please try again.");
      if (context?.snapshot) {
        restoreSessionCacheMutationSnapshot({
          queryClient,
          sessionId,
          snapshot: context.snapshot,
        });
      }
      invalidateSessionMembershipCaches();
    },
  });

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingRuntimeConversationId, setEditingRuntimeConversationId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [branchCopied, setBranchCopied] = useState(false);
  const branchCopiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [showPrPopover, setShowPrPopover] = useState(false);
  const [showThreadActionsMenu, setShowThreadActionsMenu] = useState(false);
  const [runtimeConversationPendingClose, setRuntimeConversationPendingClose] = useState<RuntimeConversationSnapshot | null>(null);
  const suppressNextTitleBlurCommitRef = useRef(false);
  const [showConnectionFlow, setShowConnectionFlow] = useState(false);
  const [showSandboxDiagnostics, setShowSandboxDiagnostics] = useState(false);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [showRecoverySshDialog, setShowRecoverySshDialog] = useState(false);
  const [recoverySshAccess, setRecoverySshAccess] = useState<RecoverySshAccessSnapshot | null>(null);
  const [recoverySshAccessExpired, setRecoverySshAccessExpired] = useState(false);
  const [recoveryGithubAccess, setRecoveryGithubAccess] = useState<RecoveryGithubAccessSnapshot | null>(null);
  const [recoveryGithubAccessExpired, setRecoveryGithubAccessExpired] = useState(false);
  // Progressive disclosure for the recovery dialog. By default we only show the
  // "Reconnect" primary action; deeper shell/attach/GitHub options stay collapsed
  // so the dialog reads as a simple "try this first" flow instead of an
  // overwhelming wall of advanced recovery commands.
  //
  // The "OpenCode attach" path only appears for opencode sessions. It lets the
  // user point their LOCAL opencode TUI at the remote server running in the
  // sandbox, so they keep their familiar local interface. SSH is the universal
  // fallback (and the only path for claude_code sessions, which have no
  // comparable local attach flow).
  const [recoveryShowOpenCodeAttach, setRecoveryShowOpenCodeAttach] = useState(false);
  const [recoveryShowShellAccess, setRecoveryShowShellAccess] = useState(false);
  const [recoveryShowGithubAccess, setRecoveryShowGithubAccess] = useState(false);
  const [editorTransientBusy, setEditorTransientBusy] = useState(false);
  const [abortRequestsPendingByConversationId, setAbortRequestsPendingByConversationId] = useState<Record<string, AbortRequestPendingState>>({});
  const [continuationAdoptionPending, setContinuationAdoptionPending] = useState(() =>
    readContinuationAdoptionPending({ sessionId })
  );
  const pendingComposerSecondaryActionKeyRef = useRef<"undo" | "redo" | "compact" | "review-tab" | "review-branch" | null>(null);
  const [pendingComposerSecondaryActionKey, setPendingComposerSecondaryActionKey] = useState<
    "undo" | "redo" | "compact" | "review-tab" | "review-branch" | null
  >(null);
  const [pendingComposerSecondaryActionSettlesWithSessionAction, setPendingComposerSecondaryActionSettlesWithSessionAction] = useState(false);
  // Hide the reverted tail as soon as the server accepts undo/edit-from-message
  // so the composer prefill does not briefly duplicate the still-visible chat row.
  const [optimisticRevertMessageId, setOptimisticRevertMessageId] = useState<string | null>(null);
  const [optimisticRevertHiddenMessageIds, setOptimisticRevertHiddenMessageIds] = useState<string[]>([]);
  // Restore should reveal the hidden tail immediately even before the runtime
  // clears its canonical revert marker; failed restores roll this override back.
  const [optimisticRevertRestorePending, setOptimisticRevertRestorePending] = useState(false);
  // Tracks the background PR adoption flow so the UI can show progress
  // without freezing the editor. `null` means no adoption in progress.
  const [prAdoptionStatus, setPrAdoptionStatus] = useState<PrAdoptionStatus | null>(null);
  const [recoveryPatchDownloadPending, setRecoveryPatchDownloadPending] = useState(false);
  const [showRecoveryVersions, setShowRecoveryVersions] = useState(false);
  const [recoveryVersionsLoading, setRecoveryVersionsLoading] = useState(false);
  const [recoveryVersions, setRecoveryVersions] = useState<Array<{
    capturedAtMs: number;
    capturedAt: string;
    isCurrent: boolean;
  }>>([]);
  const recoveryVersionsRef = useRef<HTMLDivElement>(null);
  const threadActionsMenuRef = useRef<HTMLDivElement>(null);
  // Keep the header subscribed to pushed PR lifecycle data plus any cached
  // mergeability hints, but avoid issuing a fresh GitHub mergeability read on
  // every thread switch.
  const { data: prStatus, refetch: refetchPrStatus } = usePrStatus(sessionId, {
    enabled: false,
  });

  useEffect(() => {
    setShowPrPopover(false);
  }, [sessionId]);

  useEffect(() => {
    pendingComposerSecondaryActionKeyRef.current = null;
    setPendingComposerSecondaryActionKey(null);
    setPendingComposerSecondaryActionSettlesWithSessionAction(false);
    setOptimisticRevertMessageId(null);
    setOptimisticRevertHiddenMessageIds([]);
    setOptimisticRevertRestorePending(false);
  }, [sessionId]);

  useEffect(() => {
    setPrAdoptionStatus(null);
    setContinuationAdoptionPending(readContinuationAdoptionPending({ sessionId }));
    setPendingAttachedPullRequestObservation(null);
    lastProcessedPrAdoptionIdRef.current = null;
  }, [sessionId]);

  useEffect(() => {
    const syncPendingState = (event?: Event) => {
      const detail = event instanceof CustomEvent
        ? event.detail as ContinuationAdoptionPendingChangedDetail | undefined
        : undefined;
      if (detail && detail.sessionId !== sessionId) return;
      setContinuationAdoptionPending(readContinuationAdoptionPending({ sessionId }));
    };
    syncPendingState();
    window.addEventListener(CONTINUATION_ADOPTION_PENDING_CHANGED_EVENT, syncPendingState);
    return () => {
      window.removeEventListener(CONTINUATION_ADOPTION_PENDING_CHANGED_EVENT, syncPendingState);
    };
  }, [sessionId]);

  // Reset recovery versions state when navigating to a different session so
  // the dropdown never shows stale versions from a previous session.
  useEffect(() => {
    setShowRecoveryVersions(false);
    setRecoveryVersions([]);
    setRecoveryVersionsLoading(false);
  }, [sessionId]);

  const sessionBranchSwitchInProgress =
    (session as Record<string, unknown>)?.branchSwitchInProgress === true;
  const branchSwitchPendingBeforeStartNewWork =
    sessionBranchSwitchInProgress ||
    continuationAdoptionPending ||
    prAdoptionStatus?.status === "adopting";

  const sessionSandboxId =
    typeof (session as Record<string, unknown>)?.sandboxId === "string"
      ? ((session as Record<string, unknown>).sandboxId as string)
      : null;
  const sessionRuntimeGeneration =
    typeof (session as Record<string, unknown>)?.runtimeGeneration === "number"
      && Number.isFinite((session as Record<string, unknown>).runtimeGeneration)
      ? Math.max(0, Math.floor((session as Record<string, unknown>).runtimeGeneration as number))
      : 0;

  const sessionBranch =
    typeof (session as Record<string, unknown>)?.branch === "string"
      ? ((session as Record<string, unknown>).branch as string)
      : null;
  const sessionBaseBranch =
    typeof (session as Record<string, unknown>)?.baseBranch === "string"
      ? ((session as Record<string, unknown>).baseBranch as string)
      : null;
  const lifecycleStatus =
    ((session as Record<string, unknown>)?.lifecycleStatus as string | undefined) ?? "active";
  const sessionSuspendedAt =
    typeof (session as Record<string, unknown>)?.suspendedAt === "string"
      ? ((session as Record<string, unknown>).suspendedAt as string)
      : null;
  const rawCanManageSession = (session as Record<string, unknown>)?.canManage;
  const canManageSession = typeof rawCanManageSession === "boolean" ? rawCanManageSession : true;
  const canManageSessionKnown = typeof rawCanManageSession === "boolean";
  const sessionSandboxProviderId =
    typeof (session as Record<string, unknown>)?.sandboxProviderId === "string"
      ? ((session as Record<string, unknown>).sandboxProviderId as string)
      : null;
  const parentRelationType =
    typeof (session as Record<string, unknown>)?.parentRelationType === "string"
      ? ((session as Record<string, unknown>).parentRelationType as string)
      : null;
  const archivedAt =
    ((session as Record<string, unknown>)?.archivedAt as string | null | undefined) ?? null;
  const sessionArchived = Boolean(archivedAt);
  const recoverySshAccessActive = recoverySshAccess && !recoverySshAccessExpired
    ? recoverySshAccess
    : null;
  const canOpenRecoverySshDialog =
    canManageSession
    && sessionSandboxProviderId === "daytona"
    && !sessionArchived
    && lifecycleStatus !== "deleted"
    && lifecycleStatus !== "deleting";

  const requestRecoverySshAccess = useMutation({
    mutationFn: async (input?: { previousToken?: string | null }): Promise<RecoverySshAccessSnapshot> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const requestBody = input?.previousToken
        ? { previousToken: input.previousToken }
        : {};
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/recovery-ssh-access`, {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const rawBody = await res.json().catch(() => null) as unknown;
      if (!res.ok) {
        throw buildSessionRequestError({
          fallbackMessage: "Failed to generate recovery SSH access",
          responseBody: rawBody,
          status: res.status,
        });
      }

      const normalized = normalizeRecoverySshAccessSnapshot(rawBody);
      if (!normalized) {
        throw new Error("Recovery SSH access response was invalid.");
      }

      return normalized;
    },
    onSuccess: (nextAccess) => {
      setRecoverySshAccess(nextAccess);
      setRecoverySshAccessExpired(false);
      queryClient.invalidateQueries({ queryKey: ["session-recovery-status", sessionId] });
      toast.success("Recovery SSH access ready", {
        description: nextAccess.revocationScope === "all_tokens"
          ? "Generated a new short-lived SSH command and revoked existing recovery SSH tokens for this sandbox."
          : nextAccess.previousTokenRevoked
            ? "Generated a new short-lived SSH command and revoked the previous one from this tab."
          : "Use the command in your local terminal to connect directly to the sandbox.",
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to generate recovery SSH access");
    },
  });

  const requestRecoveryGithubAccess = useMutation({
    mutationFn: async (): Promise<{ access: RecoveryGithubAccessSnapshot; targetSessionId: string }> => {
      const targetSessionId = sessionId;
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${targetSessionId}/recovery-github-access`, {
        method: "POST",
        credentials: "include",
      });
      const rawBody = await res.json().catch(() => null) as unknown;
      if (!res.ok) {
        throw buildSessionRequestError({
          fallbackMessage: "Failed to generate recovery GitHub access",
          responseBody: rawBody,
          status: res.status,
        });
      }

      const normalized = normalizeRecoveryGithubAccessSnapshot(rawBody);
      if (!normalized) {
        throw new Error("Recovery GitHub access response was invalid.");
      }
      return { access: normalized, targetSessionId };
    },
    onSuccess: ({ access: nextAccess, targetSessionId }) => {
      if (activeRouteSessionIdRef.current !== targetSessionId) return;
      setRecoveryGithubAccess(nextAccess);
      setRecoveryGithubAccessExpired(false);
      toast.success("Recovery GitHub access ready", {
        description: "Paste the snippet into your recovered shell before running git push or gh commands.",
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to generate recovery GitHub access");
    },
  });

  const copyRecoverySshCommand = useCallback(async () => {
    if (!recoverySshAccessActive) {
      return;
    }

    try {
      await navigator.clipboard.writeText(recoverySshAccessActive.sshCommand);
      toast.success("SSH command copied", {
        description: "Paste it into your local terminal to connect to the sandbox.",
      });
    } catch {
      toast.error("Failed to copy SSH command");
    }
  }, [recoverySshAccessActive]);

  const copyRecoveryOpenCodeAttachCommand = useCallback(async (command: string) => {
    try {
      await navigator.clipboard.writeText(command);
      toast.success("Command copied", {
        description: "Paste into your local terminal to connect directly to the session.",
      });
    } catch {
      toast.error("Failed to copy command");
    }
  }, []);

  const copyRecoveryGithubSnippet = useCallback(async () => {
    if (!recoveryGithubAccess) return;
    try {
      await navigator.clipboard.writeText(recoveryGithubAccess.shellSnippet);
      toast.success("GitHub recovery snippet copied", {
        description: "Paste it into your recovered shell before git push or gh commands.",
      });
    } catch {
      toast.error("Failed to copy GitHub recovery snippet");
    }
  }, [recoveryGithubAccess]);

  useLayoutEffect(() => {
    if (!sessionArchived) {
      return;
    }

    clearSessionLayoutSnapshotForSession(sessionId);
    clearPersistentPreviewStateForSession(sessionId);
    evictSessionPreviewFrame(sessionId);

    if (contentPanelMode === "preview") {
      closeContentPanel();
    }
  }, [clearPersistentPreviewStateForSession, closeContentPanel, contentPanelMode, evictSessionPreviewFrame, sessionArchived, sessionId]);

  const persistedSandboxStatus =
    ((session as Record<string, unknown>)?.status as string | undefined) ?? null;
  const recoveryPatch =
    (session as Record<string, unknown>)?.recoveryPatch &&
      typeof (session as Record<string, unknown>).recoveryPatch === "object"
      ? (session as Record<string, unknown>).recoveryPatch as Record<string, unknown>
      : null;
  const recoveryPatchAvailable = recoveryPatch?.available === true;
  const runtimeLifecycleSummary =
    (session as Record<string, unknown>)?.runtimeLifecycle &&
      typeof (session as Record<string, unknown>).runtimeLifecycle === "object"
      ? (session as Record<string, unknown>).runtimeLifecycle as Record<string, unknown>
      : null;
  const sessionRuntimeLifecycle = useMemo(
    () => normalizeConnectionFlowRuntimeLifecycleDebugSnapshotPayload(runtimeLifecycleSummary),
    [runtimeLifecycleSummary],
  );
  const recoveryPatchRestoreState =
    typeof runtimeLifecycleSummary?.recoveryPatchRestoreState === "string"
      ? runtimeLifecycleSummary.recoveryPatchRestoreState
      : null;
  const recoveryPatchRestoreError =
    typeof runtimeLifecycleSummary?.recoveryPatchRestoreError === "string"
      ? runtimeLifecycleSummary.recoveryPatchRestoreError
      : null;
  const recoveryPatchRestoredAt =
    typeof runtimeLifecycleSummary?.recoveryPatchRestoredAt === "number" &&
      Number.isFinite(runtimeLifecycleSummary.recoveryPatchRestoredAt)
      ? runtimeLifecycleSummary.recoveryPatchRestoredAt
      : null;
  const [showRecoverySuccessBadge, setShowRecoverySuccessBadge] = useState(false);
  const showQuickPrButton = userPreferences.gitPullRequests.showQuickPrButton;
  const showYoloButton = userPreferences.gitPullRequests.showYoloButton;
  const sessionPullRequest =
    (session as Record<string, unknown>)?.pullRequest &&
      typeof (session as Record<string, unknown>).pullRequest === "object"
      ? ((session as Record<string, unknown>).pullRequest as Record<string, unknown>)
      : null;
  const sessionPrNumber =
    typeof sessionPullRequest?.number === "number" && Number.isFinite(sessionPullRequest.number)
      ? Math.floor(sessionPullRequest.number)
      : typeof (session as Record<string, unknown>)?.prNumber === "number" &&
          Number.isFinite((session as Record<string, unknown>).prNumber)
      ? Math.floor((session as Record<string, unknown>).prNumber as number)
      : null;
  const sessionPrUrl =
    typeof sessionPullRequest?.url === "string"
      ? sessionPullRequest.url
      : typeof (session as Record<string, unknown>)?.prUrl === "string"
      ? ((session as Record<string, unknown>).prUrl as string)
      : null;
  const sessionPrVisitHref = buildSessionPullRequestVisitHref({
    sessionId,
    repoProvider,
    pullRequestNumber: sessionPrNumber,
    rawPullRequestUrl: sessionPrUrl,
  });
  /**
   * Stable callback for child components (RuntimeChatPane, SharedTimelineList)
   * to transform raw PR URLs into provider-aware visit hrefs. For GitHub repos
   * this is a pass-through; for Gitea repos it routes through the OAuth proxy.
   */
  const buildPrVisitHrefForChildren = useCallback(
    (args: { rawPrUrl: string | null; prNumber: number | null }): string | null =>
      buildSessionPullRequestVisitHref({
        sessionId,
        repoProvider,
        pullRequestNumber: args.prNumber,
        rawPullRequestUrl: args.rawPrUrl,
      }),
    [sessionId, repoProvider],
  );
  const sessionPrState =
    (typeof sessionPullRequest?.state === "string"
      ? sessionPullRequest.state
      : ((session as Record<string, unknown>)?.prState as string | undefined)) ?? null;
  const sessionPrRelationship =
    sessionPullRequest?.relationship === "active_owner" || sessionPullRequest?.relationship === "historical"
      ? sessionPullRequest.relationship
      : typeof sessionPullRequest?.isActiveOwner === "boolean"
        ? (sessionPullRequest.isActiveOwner ? "active_owner" : "historical")
        : authoritativeSessionAvailable ? "active_owner" : "historical";
  const sessionPrIsActiveOwner =
    typeof sessionPullRequest?.isActiveOwner === "boolean"
      ? sessionPullRequest.isActiveOwner
      : sessionPrRelationship === "active_owner";
  const sessionPrActiveOwnerSession =
    sessionPullRequest?.activeOwnerSession && typeof sessionPullRequest.activeOwnerSession === "object"
      ? (sessionPullRequest.activeOwnerSession as Record<string, unknown>)
      : null;
  const sessionPrActiveOwnerSessionId =
    typeof sessionPrActiveOwnerSession?.id === "string" && sessionPrActiveOwnerSession.id.trim().length > 0
      ? sessionPrActiveOwnerSession.id
      : null;
  const sessionPrActiveOwnerSessionTitle =
    typeof sessionPrActiveOwnerSession?.title === "string" && sessionPrActiveOwnerSession.title.trim().length > 0
      ? sessionPrActiveOwnerSession.title
      : null;
  const prStatusMatchesCanonicalSession =
    sessionPrNumber !== null &&
    (prStatus?.prNumber === null ||
      prStatus?.prNumber === undefined ||
      prStatus.prNumber === sessionPrNumber);
  const currentPrStatus =
    prStatusMatchesCanonicalSession ? prStatus : undefined;
  const effectivePrState =
    currentPrStatus?.prState ?? sessionPrState;
  const historicalSessionPullRequest =
    sessionPrRelationship === "historical" && sessionPrNumber !== null;
  const canReclaimHistoricalPrOwnership =
    historicalSessionPullRequest &&
    (effectivePrState === "open" || effectivePrState === "draft") &&
    canManageSession &&
    !sessionArchived;
  const sessionSuspended =
    lifecycleStatus === "suspended" || lifecycleStatus === "suspending";
  const canContinueNewSession =
    canManageSession && (lifecycleStatus === "active" || sessionSuspended);
  const invalidateContinuityPickerCaches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getRepoSessionPickerQueryKey(repoId) });
    queryClient.invalidateQueries({ queryKey: getRepoOpenPullsQueryKey(repoId) });
  }, [queryClient, repoId]);
  const invalidateSessionMembershipCaches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
    invalidateContinuityPickerCaches();
  }, [invalidateContinuityPickerCaches, queryClient, repoId, sessionId]);

  useEffect(() => {
    if (!authoritativeSessionAvailable) return;
    if (lifecycleStatus !== "deleting" && lifecycleStatus !== "deleted") return;

    removeSessionFromCaches(sessionId);
    resetSessionViewState(sessionId);
    cleanupSessionLocalStorage(sessionId);
    navigateToList();
    invalidateSessionMembershipCaches();
  }, [
    authoritativeSessionAvailable,
    invalidateSessionMembershipCaches,
    lifecycleStatus,
    navigateToList,
    removeSessionFromCaches,
    resetSessionViewState,
    sessionId,
  ]);
  const syncCanonicalPullRequestCache = useCallback((
    targetSessionId: string,
    sessionPayload: Record<string, unknown>,
  ) => {
    const payloadPullRequest =
      sessionPayload.pullRequest && typeof sessionPayload.pullRequest === "object"
        ? (sessionPayload.pullRequest as Record<string, unknown>)
        : null;
    const prNumber =
      typeof payloadPullRequest?.number === "number" && Number.isFinite(payloadPullRequest.number)
        ? Math.floor(payloadPullRequest.number)
        : typeof sessionPayload.prNumber === "number" && Number.isFinite(sessionPayload.prNumber)
          ? Math.floor(sessionPayload.prNumber)
          : null;
    const prState =
      payloadPullRequest?.state === "draft" ||
      payloadPullRequest?.state === "open" ||
      payloadPullRequest?.state === "merged" ||
      payloadPullRequest?.state === "closed"
        ? payloadPullRequest.state
        : sessionPayload.prState === "draft" ||
            sessionPayload.prState === "open" ||
            sessionPayload.prState === "merged" ||
            sessionPayload.prState === "closed"
          ? sessionPayload.prState
          : null;
    const prUrl =
      typeof payloadPullRequest?.url === "string"
        ? payloadPullRequest.url
        : typeof sessionPayload.prUrl === "string"
          ? sessionPayload.prUrl
          : null;

    if (!prNumber || !prState) {
      queryClient.removeQueries({ queryKey: ["pr-status", targetSessionId], exact: true });
      return;
    }

    applyPrStatusCacheUpdate(queryClient, {
      sessionId: targetSessionId,
      prNumber,
      prUrl,
      prState,
      timestamp: Date.now(),
    });
  }, [queryClient]);
  // Only merged PRs are terminal and require "start new work" to create a
  // fresh branch. Closed PRs are NOT terminal — the branch still has all
  // the unmerged work, so the user can keep working (and potentially reopen
  // the PR). Renaming from mergedOrClosedPr → mergedPr for clarity.
  const mergedPr =
    sessionPrIsActiveOwner && effectivePrState === "merged";
  const [handledMergedPrNumber, setHandledMergedPrNumber] = useState<number | null>(null);
  // Once start-new-work succeeds for a given merged PR, we record its number
  // in handledMergedPrNumber so that stale cache refetches that briefly
  // reintroduce merged state do not re-show the continuation banner or
  // re-trigger background branch prep.
  const mergedPrNeedsContinuation =
    mergedPr && handledMergedPrNumber !== sessionPrNumber;
  // Continuation in a *new* thread follows the backend `/sessions/:id/continue`
  // semantics: both merged and closed PRs are terminal and can seed context
  // without relying on source-session git/runtime state.
  const terminalPrForContinuation =
    effectivePrState === "merged" || effectivePrState === "closed";
  useEffect(() => {
    setHandledMergedPrNumber(null);
  }, [sessionId]);

  const dbDoIdRaw = (session as Record<string, unknown>)?.doId;
  const dbSandboxIdRaw = (session as Record<string, unknown>)?.sandboxId;
  const dbSandboxDashboardUrlRaw = (session as Record<string, unknown>)?.sandboxDashboardUrl;
  const dbDoId = typeof dbDoIdRaw === "string" && dbDoIdRaw.length > 0 ? dbDoIdRaw : null;
  const dbSandboxId = typeof dbSandboxIdRaw === "string" && dbSandboxIdRaw.length > 0 ? dbSandboxIdRaw : null;
  const dbSandboxDashboardUrl =
    typeof dbSandboxDashboardUrlRaw === "string" && dbSandboxDashboardUrlRaw.length > 0
      ? dbSandboxDashboardUrlRaw
      : null;

  const downloadRecoveryPatch = useCallback(async (args?: { versionMs?: number }) => {
    if (!canManageSession || recoveryPatchDownloadPending) return;
    setRecoveryPatchDownloadPending(true);
    try {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const versionQuery = args?.versionMs ? `?version=${args.versionMs}` : "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/recovery-patch${versionQuery}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const details = await res.text().catch(() => "");
        throw new Error(details || "Failed to download recovery bundle");
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const contentDisposition = res.headers.get("content-disposition") ?? "";
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
      const downloadName = filenameMatch?.[1] ?? `${sessionId}-recovery.tgz`;
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = downloadName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download recovery bundle");
    } finally {
      setRecoveryPatchDownloadPending(false);
    }
  }, [canManageSession, recoveryPatchDownloadPending, sessionId]);

  const fetchRecoveryVersionsControllerRef = useRef<AbortController | null>(null);
  const fetchRecoveryVersions = useCallback(async () => {
    if (!canManageSession) return;
    // Abort any in-flight request to avoid stale responses on rapid toggles.
    fetchRecoveryVersionsControllerRef.current?.abort();
    const controller = new AbortController();
    fetchRecoveryVersionsControllerRef.current = controller;
    setRecoveryVersionsLoading(true);
    try {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/recovery-patches`, {
        credentials: "include",
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error("Failed to load recovery bundle versions");
      }
      const data: unknown = await res.json();
      const dataRecord = (data !== null && typeof data === "object") ? data as Record<string, unknown> : {};
      const rawVersions: unknown[] = Array.isArray(dataRecord.versions) ? dataRecord.versions : [];
      const parsed = rawVersions
        .filter(
          (v): v is Record<string, unknown> =>
            v !== null && typeof v === "object",
        )
        .filter(
          (v): v is Record<string, unknown> & { capturedAtMs: number; capturedAt: string } =>
            typeof v.capturedAtMs === "number" && typeof v.capturedAt === "string",
        )
        .map((v) => ({
          capturedAtMs: v.capturedAtMs as number,
          capturedAt: v.capturedAt as string,
          isCurrent: v.isCurrent === true,
        }));
      setRecoveryVersions(parsed);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      toast.error(err instanceof Error ? err.message : "Failed to load recovery bundle versions");
    } finally {
      setRecoveryVersionsLoading(false);
    }
  }, [canManageSession, sessionId]);

  const toggleRecoveryVersions = useCallback(() => {
    setShowRecoveryVersions((prev) => {
      if (!prev) {
        void fetchRecoveryVersions();
      }
      return !prev;
    });
  }, [fetchRecoveryVersions]);

  // Close recovery versions dropdown on outside click
  useEffect(() => {
    if (!showRecoveryVersions) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (recoveryVersionsRef.current && !recoveryVersionsRef.current.contains(e.target as Node)) {
        setShowRecoveryVersions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showRecoveryVersions]);

  useEffect(() => {
    if (
      recoveryPatchRestoreState !== "succeeded" ||
      typeof recoveryPatchRestoredAt !== "number"
    ) {
      setShowRecoverySuccessBadge(false);
      return;
    }
    const remainingMs = RECOVERY_SUCCESS_BADGE_TTL_MS - (Date.now() - recoveryPatchRestoredAt);
    if (remainingMs <= 0) {
      setShowRecoverySuccessBadge(false);
      return;
    }
    setShowRecoverySuccessBadge(true);
    const timeoutId = window.setTimeout(() => {
      setShowRecoverySuccessBadge(false);
    }, remainingMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [recoveryPatchRestoreState, recoveryPatchRestoredAt]);
  useEffect(() => {
    return () => {
      if (branchCopiedTimeoutRef.current) {
        clearTimeout(branchCopiedTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setShowRecoverySshDialog(false);
    setRecoverySshAccess(null);
    setRecoverySshAccessExpired(false);
    setRecoveryGithubAccess(null);
    setRecoveryGithubAccessExpired(false);
  }, [sessionId]);

  useEffect(() => {
    if (!recoverySshAccess) {
      setRecoverySshAccessExpired(false);
      return;
    }

    const expiresAtMs = Date.parse(recoverySshAccess.expiresAt);
    if (!Number.isFinite(expiresAtMs)) {
      return;
    }

    const remainingMs = expiresAtMs - Date.now();
    if (remainingMs <= 0) {
      setRecoverySshAccessExpired(true);
      return;
    }

    setRecoverySshAccessExpired(false);
    const timeoutId = window.setTimeout(() => {
      setRecoverySshAccessExpired(true);
    }, remainingMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [recoverySshAccess]);

  useEffect(() => {
    if (!recoveryGithubAccess) {
      setRecoveryGithubAccessExpired(false);
      return;
    }

    const expiresAtMs = Date.parse(recoveryGithubAccess.expiresAt);
    if (!Number.isFinite(expiresAtMs)) {
      return;
    }

    const remainingMs = expiresAtMs - Date.now();
    if (remainingMs <= 0) {
      setRecoveryGithubAccessExpired(true);
      return;
    }

    setRecoveryGithubAccessExpired(false);
    const timeoutId = window.setTimeout(() => {
      setRecoveryGithubAccessExpired(true);
    }, remainingMs);
    return () => window.clearTimeout(timeoutId);
  }, [recoveryGithubAccess]);

  const copySessionBranch = useCallback(async (event?: ReactMouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    const branchNameForClipboard = localStartNewWorkGitPendingRef.current?.newBranch ?? sessionBranch;
    if (!branchNameForClipboard) return;

    try {
      await navigator.clipboard.writeText(branchNameForClipboard);
    } catch {
      return;
    }

    setBranchCopied(true);
    if (branchCopiedTimeoutRef.current) {
      clearTimeout(branchCopiedTimeoutRef.current);
    }
    branchCopiedTimeoutRef.current = setTimeout(() => {
      setBranchCopied(false);
      branchCopiedTimeoutRef.current = null;
    }, 1500);
  }, [sessionBranch]);

  const resumeSession = useMutation({
    mutationFn: async (input?: { speculative?: boolean }) => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const speculative = input?.speculative === true;
      if (speculative) {
        logSpeculativeResumeDebug({
          baseUrl,
          sessionId,
          phase: "request_started",
          reason: "speculative_resume_request_started",
          payload: {
            lifecycleStatus,
            suspendedAt: sessionSuspendedAt,
          },
        });
      }
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/resume`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ speculative }),
      });
      if (!res.ok) {
        const message = await readErrorMessageFromResponse({
          response: res,
          fallbackMessage: "Failed to resume session",
        });
        if (speculative) {
          logSpeculativeResumeDebug({
            baseUrl,
            sessionId,
            phase: "request_failed",
            reason: "speculative_resume_request_failed",
            payload: {
              httpStatus: res.status,
              message,
            },
          });
        }
        throw new Error(message);
      }
      const payload = await res.json().catch(() => ({ ok: true }));
      if (speculative) {
        logSpeculativeResumeDebug({
          baseUrl,
          sessionId,
          phase: "request_succeeded",
          reason: "speculative_resume_request_succeeded",
          payload: {
            response: payload,
          },
        });
      }
      return payload;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
      invalidateContinuityPickerCaches();
    },
  });

  const suspendSession = useMutation({
    mutationFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/suspend`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const details = await res.text().catch(() => "");
        throw new Error(details || "Failed to suspend session");
      }
      return res.json().catch(() => ({ ok: true }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
      invalidateContinuityPickerCaches();
    },
  });

  const formatContinueBlockedDescription = useCallback((details: Record<string, unknown> | null | undefined) => {
    const actionRequired = typeof details?.actionRequired === "string"
      ? details.actionRequired.trim()
      : "Commit or push your changes, then try again.";
    const summaryParts: string[] = [];
    const stagedCount = typeof details?.stagedCount === "number" ? details.stagedCount : 0;
    const unstagedCount = typeof details?.unstagedCount === "number" ? details.unstagedCount : 0;
    const untrackedCount = typeof details?.untrackedCount === "number" ? details.untrackedCount : 0;
    const ahead = typeof details?.ahead === "number" ? details.ahead : 0;
    const hasLocalOnlyCommits = details?.hasLocalOnlyCommits === true;

    if (stagedCount > 0) summaryParts.push(`${stagedCount} staged`);
    if (unstagedCount > 0) summaryParts.push(`${unstagedCount} unstaged`);
    if (untrackedCount > 0) summaryParts.push(`${untrackedCount} untracked`);
    if (ahead > 0) summaryParts.push(`${ahead} ahead`);
    if (hasLocalOnlyCommits) summaryParts.push("local-only commits");

    if (summaryParts.length === 0) {
      return actionRequired;
    }

    return `${actionRequired} (${summaryParts.join(", ")})`;
  }, []);

  const isContextOnlyContinuationFallbackError = useCallback((error: SessionRequestError | null | undefined) => {
    return isContinuationContextOnlyFallbackStatus(error?.status) &&
      isContinuationContextOnlyFallbackMessage(error?.message);
  }, []);

  const continueSession = useMutation({
    mutationFn: async (input: ContinueSessionMutationInput): Promise<ContinueSessionResponse> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${input.sourceSessionId}/continue`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: input.optimisticSessionId,
          title: input.optimisticTitle,
        }),
      });
      const rawBody = await res.json().catch(() => null) as unknown;
      if (
        !res.ok ||
        !rawBody ||
        typeof rawBody !== "object" ||
        Array.isArray(rawBody) ||
        !("session" in rawBody)
      ) {
        const errorBody = rawBody && typeof rawBody === "object" && !Array.isArray(rawBody)
          ? rawBody as SessionApiErrorBody
          : null;
        const error = buildSessionRequestError({
          fallbackMessage: "Failed to continue in a new session",
          responseBody: rawBody,
          status: res.status,
        });
        if (errorBody?.error === "NO_PROVIDER_CONFIGURED") {
          error.code = "NO_PROVIDER_CONFIGURED";
        }
        throw error;
      }
      return rawBody as ContinueSessionResponse;
    },
    onSuccess: (data, variables) => {
      const nextSessionId = typeof data.session.id === "string" ? data.session.id : null;
      writeContinuationAdoptionPending({ sessionId: variables.optimisticSessionId, pending: false });
      if (!nextSessionId) {
        restoreSessionCacheMutationSnapshot({
          queryClient,
          sessionId: variables.optimisticSessionId,
          snapshot: variables.cacheSnapshot,
        });
        sessionStorage.removeItem(optimisticSessionTitleStorageKey(variables.optimisticSessionId));
        sessionStorage.removeItem(optimisticAgentTypeStorageKey(variables.optimisticSessionId));
        sessionStorage.removeItem(initialBootstrapDraftStorageKey(variables.optimisticSessionId));
        navigate({
          to: "/$repoId/$sessionId",
          params: {
            repoId: variables.sourceRepositoryId,
            sessionId: variables.sourceSessionId,
          },
        });
        toast.error("Continue succeeded but the new session metadata was missing");
        return;
      }
      if (nextSessionId !== variables.optimisticSessionId) {
        writeContinuationAdoptionPending({ sessionId: nextSessionId, pending: false });
      }

      const isContextOnly = data.contextOnly === true;
      const nextRepositoryId = typeof data.session.repositoryId === "string"
        ? data.session.repositoryId
        : variables.sourceRepositoryId;
      const nextSession: Record<string, unknown> = {
        ...data.session,
        // Continued sessions are freshly created and have no transcript rows yet.
        // Seeding this before navigation keeps the empty-thread composer centered
        // on first paint instead of waiting for the session-detail refetch.
        hasHistory: false,
      };

      queryClient.setQueryData(["session", nextSessionId], nextSession);
      if (!isContextOnly) {
        syncCanonicalPullRequestCache(nextSessionId, nextSession as Record<string, unknown>);
      }
      applyOptimisticSessionMembershipToSessionListCaches({
        queryClient,
        snapshot: variables.cacheSnapshot,
        sessionId: nextSessionId,
        item: nextSession as CachedSessionListItem,
      });
      queryClient.invalidateQueries({ queryKey: ["session", nextSessionId] });
      queryClient.invalidateQueries({ queryKey: ["session", variables.sourceSessionId] });
      queryClient.invalidateQueries({ queryKey: getRepoSessionPickerQueryKey(variables.sourceRepositoryId) });
      queryClient.invalidateQueries({ queryKey: getRepoOpenPullsQueryKey(variables.sourceRepositoryId) });

      const nextTitle = typeof data.session.title === "string" ? data.session.title : "Continued session";
      const nextAgentType = typeof data.session.agentType === "string" ? data.session.agentType : null;
      sessionStorage.setItem(optimisticSessionTitleStorageKey(nextSessionId), nextTitle);
      if (nextAgentType) {
        sessionStorage.setItem(optimisticAgentTypeStorageKey(nextSessionId), nextAgentType);
      }

      const sourceTitle = data.sourceSession.title?.trim() || variables.sourceSessionTitle;
      const sourceConversation = variables.sourceConversation;
      const sourceContextTitle = buildContinuationSourceContextRefTitle({
        sourceSessionTitle: sourceTitle,
        sourceConversation,
      });
      const continuationBootstrapDraft = buildContinuationBootstrapDraft({
        sourceSessionId: variables.sourceSessionId,
        sourceRepositoryId: variables.sourceRepositoryId,
        sourceContextTitle,
        sourceConversation,
        contextRefLocalId: crypto.randomUUID(),
        origin: isContextOnly ? "context" : "continue_new_session",
      });
      sessionStorage.setItem(
        initialBootstrapDraftStorageKey(nextSessionId),
        JSON.stringify(continuationBootstrapDraft),
      );

      if (isContextOnly) {
        toast.success("Continued in a new thread", {
          description: data.sourceSession.archivedAt
            ? "Archived thread was attached as read-only context."
            : `PR was already ${data.sourceSession.prState} — new thread starts fresh with context from the source`,
        });
      } else if (typeof data.sourceSession.prNumber === "number") {
        toast.success("Continued in a new thread", {
          description: data.sourceSession.branch
            ? `Continuing on ${data.sourceSession.branch} with PR #${data.sourceSession.prNumber}`
            : `Continuing with PR #${data.sourceSession.prNumber}`,
        });
      } else {
        toast.success("Continued in a new thread", {
          description: data.sourceSession.branch
            ? `Continuing on branch ${data.sourceSession.branch}`
            : "Carried forward context from the source session",
        });
      }

      navigate({
        to: "/$repoId/$sessionId",
        params: { repoId: nextRepositoryId, sessionId: nextSessionId },
      });
    },
    onError: async (err, variables) => {
      writeContinuationAdoptionPending({ sessionId: variables.optimisticSessionId, pending: false });
      const sessionError = err as SessionRequestError;
      const navigateBackToSourceSession = () => {
        navigate({
          to: "/$repoId/$sessionId",
          params: {
            repoId: variables.sourceRepositoryId,
            sessionId: variables.sourceSessionId,
          },
        });
      };
      if (sessionError?.code === "NO_PROVIDER_CONFIGURED") {
        restoreSessionCacheMutationSnapshot({
          queryClient,
          sessionId: variables.optimisticSessionId,
          snapshot: variables.cacheSnapshot,
        });
        sessionStorage.removeItem(optimisticSessionTitleStorageKey(variables.optimisticSessionId));
        sessionStorage.removeItem(optimisticAgentTypeStorageKey(variables.optimisticSessionId));
        sessionStorage.removeItem(initialBootstrapDraftStorageKey(variables.optimisticSessionId));
        navigateBackToSourceSession();
        toast.error("No AI provider configured", {
          description: "Add an API key in Settings before continuing this session.",
          action: { label: "Settings", onClick: () => navigate({ to: "/settings", search: { tab: "api-keys" as any } }) },
        });
        return;
      }

      if (sessionError?.code === "UNPUBLISHED_CONTINUATION_WORK") {
        restoreSessionCacheMutationSnapshot({
          queryClient,
          sessionId: variables.optimisticSessionId,
          snapshot: variables.cacheSnapshot,
        });
        sessionStorage.removeItem(optimisticSessionTitleStorageKey(variables.optimisticSessionId));
        sessionStorage.removeItem(optimisticAgentTypeStorageKey(variables.optimisticSessionId));
        sessionStorage.removeItem(initialBootstrapDraftStorageKey(variables.optimisticSessionId));
        navigateBackToSourceSession();
        toast.error("Continue blocked by local changes", {
          description: formatContinueBlockedDescription(sessionError.details),
        });
        return;
      }

      if (isContextOnlyContinuationFallbackError(sessionError)) {
        const nextSessionId = variables.optimisticSessionId;
        const sourceConversation = variables.sourceConversation;
        const sourceContextTitle = buildContinuationSourceContextRefTitle({
          sourceSessionTitle: variables.sourceSessionTitle,
          sourceConversation,
        });
        const continuationBootstrapDraft = buildContinuationBootstrapDraft({
          sourceSessionId: variables.sourceSessionId,
          sourceRepositoryId: variables.sourceRepositoryId,
          sourceContextTitle,
          sourceConversation,
          contextRefLocalId: crypto.randomUUID(),
          origin: "context",
        });
        sessionStorage.setItem(
          initialBootstrapDraftStorageKey(nextSessionId),
          JSON.stringify(continuationBootstrapDraft),
        );

        try {
          const baseUrl = import.meta.env.VITE_API_URL || "";
          const createRes = await fetch(`${baseUrl}/api/sessions`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              repositoryId: variables.sourceRepositoryId,
              title: variables.optimisticTitle,
              agentType: variables.sourceAgentType,
              id: nextSessionId,
            }),
          });
          const createdBody = await createRes.json().catch(() => null) as Record<string, unknown> | null;
          if (!createRes.ok) {
            throw buildSessionRequestError({
              fallbackMessage: "Failed to continue thread with context",
              responseBody: createdBody,
              status: createRes.status,
            });
          }

          const createdSessionId = typeof createdBody?.id === "string" ? createdBody.id : nextSessionId;
          if (createdSessionId !== nextSessionId) {
            writeContinuationAdoptionPending({ sessionId: createdSessionId, pending: false });
          }
          const createdRepositoryId = typeof createdBody?.repositoryId === "string"
            ? createdBody.repositoryId
            : variables.sourceRepositoryId;
          const createdSession: Record<string, unknown> = {
            ...(createdBody ?? {}),
            id: createdSessionId,
            repositoryId: createdRepositoryId,
            // Fallback-created context-only sessions are also brand-new. Make the
            // empty-thread layout deterministic until the detail query confirms it.
            hasHistory: false,
          };
          if (createdSessionId !== nextSessionId) {
            sessionStorage.setItem(
              initialBootstrapDraftStorageKey(createdSessionId),
              JSON.stringify(continuationBootstrapDraft),
            );
            sessionStorage.removeItem(initialBootstrapDraftStorageKey(nextSessionId));
          }

          queryClient.setQueryData(["session", createdSessionId], createdSession);
          applyOptimisticSessionMembershipToSessionListCaches({
            queryClient,
            snapshot: variables.cacheSnapshot,
            sessionId: createdSessionId,
            item: createdSession as CachedSessionListItem,
          });
          queryClient.invalidateQueries({ queryKey: ["sessions"] });
          queryClient.invalidateQueries({ queryKey: getRepoSessionPickerQueryKey(createdRepositoryId) });
          queryClient.invalidateQueries({ queryKey: getRepoOpenPullsQueryKey(createdRepositoryId) });
          toast.success("Started a new thread with source context", {
            description: "The source is still busy, so this thread was attached as read-only context.",
          });
          navigate({
            to: "/$repoId/$sessionId",
            params: { repoId: createdRepositoryId, sessionId: createdSessionId },
          });
        } catch (fallbackError) {
          restoreSessionCacheMutationSnapshot({
            queryClient,
            sessionId: variables.optimisticSessionId,
            snapshot: variables.cacheSnapshot,
          });
          sessionStorage.removeItem(initialBootstrapDraftStorageKey(nextSessionId));
          sessionStorage.removeItem(optimisticSessionTitleStorageKey(nextSessionId));
          sessionStorage.removeItem(optimisticAgentTypeStorageKey(nextSessionId));
          navigateBackToSourceSession();
          toast.error(
            fallbackError instanceof Error
              ? fallbackError.message
              : "Failed to continue thread with context",
          );
        }
        return;
      }

      restoreSessionCacheMutationSnapshot({
        queryClient,
        sessionId: variables.optimisticSessionId,
        snapshot: variables.cacheSnapshot,
      });
      sessionStorage.removeItem(optimisticSessionTitleStorageKey(variables.optimisticSessionId));
      sessionStorage.removeItem(optimisticAgentTypeStorageKey(variables.optimisticSessionId));
      sessionStorage.removeItem(initialBootstrapDraftStorageKey(variables.optimisticSessionId));
      navigateBackToSourceSession();
      toast.error(err instanceof Error ? err.message : "Failed to continue in a new session");
    },
  });

  const reclaimPrOwnership = useMutation({
    mutationFn: async (): Promise<{
      ok: true;
      alreadyOwner?: boolean;
      previousActiveOwnerSessionId?: string | null;
      session: Record<string, unknown>;
    }> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/reclaim-pr-ownership`, {
        method: "POST",
        credentials: "include",
      });
      const rawBody = await res.json().catch(() => null) as unknown;
      if (
        !res.ok ||
        !rawBody ||
        typeof rawBody !== "object" ||
        Array.isArray(rawBody) ||
        (rawBody as Record<string, unknown>).ok !== true ||
        !(rawBody as Record<string, unknown>).session ||
        typeof (rawBody as Record<string, unknown>).session !== "object"
      ) {
        throw buildSessionRequestError({
          fallbackMessage: "Failed to move PR ownership to this thread",
          responseBody: rawBody,
          status: res.status,
        });
      }
      return rawBody as {
        ok: true;
        alreadyOwner?: boolean;
        previousActiveOwnerSessionId?: string | null;
        session: Record<string, unknown>;
      };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["session", sessionId], (old: unknown) => {
        if (!old || typeof old !== "object" || Array.isArray(old)) {
          return data.session;
        }
        return {
          ...(old as Record<string, unknown>),
          ...data.session,
        };
      });
      syncCanonicalPullRequestCache(sessionId, data.session);
      queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
        if (!Array.isArray(old)) return old;
        return old.map((item) => {
          if (!item || typeof item !== "object") return item;
          const row = item as Record<string, unknown>;
          if (row.id !== sessionId) return item;
          return {
            ...row,
            ...data.session,
          };
        });
      });
      queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
      invalidateContinuityPickerCaches();

      const previousOwnerSessionId =
        typeof data.previousActiveOwnerSessionId === "string" &&
          data.previousActiveOwnerSessionId.trim().length > 0
          ? data.previousActiveOwnerSessionId
          : sessionPrActiveOwnerSessionId;
      if (previousOwnerSessionId) {
        queryClient.invalidateQueries({ queryKey: ["session", previousOwnerSessionId] });
      }

      toast.success(
        data.alreadyOwner === true
          ? "This thread already owns the PR"
          : "PR ownership moved back to this thread",
      );
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to move PR ownership to this thread");
    },
  });


  // --- Start New Work: archive current merged/closed PR and switch to a fresh branch ---
  const [startNewWorkNotice, setStartNewWorkNotice] = useState<StartNewWorkNoticeState | null>(
    () => readStartNewWorkNoticeState(sessionId),
  );
  const [localStartNewWorkWaitingForUnarchive, setLocalStartNewWorkWaitingForUnarchive] = useState<StartNewWorkTrigger | null>(null);
  const localStartNewWorkWaitingForUnarchiveRef = useRef<StartNewWorkTrigger | null>(null);
  const [localStartNewWorkGitPending, setLocalStartNewWorkGitPending] = useState<LocalStartNewWorkGitPendingState | null>(null);
  const localStartNewWorkGitPendingRef = useRef<LocalStartNewWorkGitPendingState | null>(null);
  const unarchivePromiseRef = useRef<Promise<unknown> | null>(null);

  const applyLocalStartNewWorkWaitingForUnarchive = useCallback((
    nextState: StartNewWorkTrigger | null,
  ) => {
    localStartNewWorkWaitingForUnarchiveRef.current = nextState;
    setLocalStartNewWorkWaitingForUnarchive(nextState);
  }, []);

  const clearLocalStartNewWorkWaitingForUnarchive = useCallback(() => {
    applyLocalStartNewWorkWaitingForUnarchive(null);
  }, [applyLocalStartNewWorkWaitingForUnarchive]);

  const applyLocalStartNewWorkGitPending = useCallback((
    nextState: LocalStartNewWorkGitPendingState | null,
  ) => {
    localStartNewWorkGitPendingRef.current = nextState;
    setLocalStartNewWorkGitPending(nextState);
  }, []);

  const clearLocalStartNewWorkGitPending = useCallback(() => {
    applyLocalStartNewWorkGitPending(null);
  }, [applyLocalStartNewWorkGitPending]);

  useEffect(() => {
    setStartNewWorkNotice(readStartNewWorkNoticeState(sessionId));
    clearLocalStartNewWorkWaitingForUnarchive();
    clearLocalStartNewWorkGitPending();
    unarchivePromiseRef.current = null;
  }, [clearLocalStartNewWorkGitPending, clearLocalStartNewWorkWaitingForUnarchive, sessionId]);

  const startNewWork = useMutation({
    mutationFn: async (_input: StartNewWorkMutationInput): Promise<StartNewWorkResponse> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/start-new-work`, {
        method: "POST",
        credentials: "include",
      });
      const rawBody = await res.json().catch(() => null) as unknown;
      if (
        !res.ok ||
        !rawBody ||
        typeof rawBody !== "object" ||
        Array.isArray(rawBody) ||
        !(rawBody as Record<string, unknown>).ok
      ) {
        throw buildSessionRequestError({
          fallbackMessage: "Failed to start new work",
          responseBody: rawBody,
          status: res.status,
        });
      }
      return rawBody as StartNewWorkResponse;
    },
    // Retry transient server errors (502/503/network) up to 2 times.
    // Client errors (4xx like 409 lock conflict) are not retried because
    // they indicate a precondition that won't clear on its own quickly.
    // 2 retries = reasonable chance at surviving a transient blip without
    // making the user wait too long on a genuine failure.
    retry: (failureCount, error) => {
      const MAX_START_NEW_WORK_RETRIES = 2; // 2 retries = 3 total attempts
      // Standard HTTP client-error range. These are precondition or validation
      // failures for this mutation, so retrying only amplifies duplicate
      // Keep Working calls after the server has already consumed the PR.
      const HTTP_CLIENT_ERROR_MIN_STATUS = 400;
      const HTTP_SERVER_ERROR_MIN_STATUS = 500;
      if (failureCount >= MAX_START_NEW_WORK_RETRIES) return false;
      const status = (error as SessionRequestError | undefined)?.status;
      if (
        typeof status === "number" &&
        status >= HTTP_CLIENT_ERROR_MIN_STATUS &&
        status < HTTP_SERVER_ERROR_MIN_STATUS
      ) return false;
      // Lock conflicts are non-transient — retrying won't help.
      if (isBranchSwitchInProgressError(error)) return false;
      return true;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: ~1.5s, ~3s.  These delays are short enough
      // that the user won't think the UI is frozen, but long enough to
      // let transient sandbox/DB issues clear.
      const START_NEW_WORK_RETRY_BASE_DELAY_MS = 1_500;
      return START_NEW_WORK_RETRY_BASE_DELAY_MS * Math.pow(2, attemptIndex);
    },
    onSuccess: (data, variables) => {
      const sessionPayload = data.session as Record<string, unknown>;
      const normalizedSessionPayload: Record<string, unknown> = {
        ...sessionPayload,
        ...(data.gitPending
          ? {}
          : {
              branch: data.newBranch,
              baseBranch: data.baseBranch,
            }),
        prNumber: null,
        prUrl: null,
        prState: null,
        pullRequest: null,
      };

      // Update caches to reflect cleared PR state. While gitPending is true,
      // do NOT optimistically make the fresh branch canonical: the backend only
      // persists it after the sandbox has restored (if needed) and git setup
      // succeeds. The pending branch is still displayed through
      // localStartNewWorkGitPending, but leaving the canonical branch untouched
      // keeps the UI in the correct "restoring/preparing" state until the DO
      // emits start_new_work_git_completed.
      queryClient.setQueryData(["session", sessionId], normalizedSessionPayload);
      queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
        if (!Array.isArray(old)) return old;
        return old.map((item) => {
          if (!item || typeof item !== "object") return item;
          const row = item as Record<string, unknown>;
          if (row.id !== sessionId) return item;
          return {
            ...row,
            ...normalizedSessionPayload,
          };
        });
      });
      syncCanonicalPullRequestCache(sessionId, normalizedSessionPayload);
      // Immediately remove the pr-status cache so effectivePrState becomes
      // null right away. Without this, the stale "merged" state from the
      // pr-status query can cause the merged banner to persist momentarily
      // even after start-new-work succeeds.
      queryClient.removeQueries({ queryKey: ["pr-status", sessionId], exact: true });
      queryClient.invalidateQueries({ queryKey: ["pr-history", sessionId] });
      invalidateContinuityPickerCaches();

      setHandledMergedPrNumber(data.previousPr.number);

      const nextStartNewWorkNotice: StartNewWorkNoticeState = {
        newBranch: data.newBranch,
        baseBranch: data.baseBranch,
        trigger: variables.trigger,
        timestamp: variables.requestedAt,
        previousPr: data.previousPr,
      };
      setStartNewWorkNotice(nextStartNewWorkNotice);
      writeStartNewWorkNoticeState({
        sessionId,
        notice: nextStartNewWorkNotice,
      });

      applyLocalStartNewWorkGitPending(
        data.gitPending
          ? {
              newBranch: data.newBranch,
              baseBranch: data.baseBranch,
              trigger: variables.trigger,
              requestedAt: variables.requestedAt,
              previousPr: data.previousPr,
            }
          : null,
      );

      if (variables.trigger !== "background") {
        toast.success(data.gitPending ? "Preparing fresh branch" : "Ready for new work", {
          description: data.gitPending
            ? `Anything you send now will queue until ${data.newBranch} is ready.`
            : `Created branch ${data.newBranch} from ${data.baseBranch}`,
        });
      }
    },
    onError: (err, variables) => {
      if (isBranchSwitchInProgressError(err)) {
        showBranchSwitchInProgressToast({ actionLabel: "starting new work again" });
        return;
      }
      clearLocalStartNewWorkGitPending();
      clearLocalStartNewWorkWaitingForUnarchive();
      if ((err as SessionRequestError | undefined)?.code === "START_NEW_WORK_NOT_ELIGIBLE") {
        queryClient.removeQueries({ queryKey: ["pr-status", sessionId], exact: true });
        void queryClient.invalidateQueries({ queryKey: ["session", sessionId], exact: true });
        void queryClient.invalidateQueries({ queryKey: ["pr-history", sessionId], exact: true });
        if (variables.trigger === "manual") {
          toast.info("Checking whether this thread is already ready for new work…");
        }
        return;
      }
      // Auto/background triggers own the follow-up UX so we do not double-toast
      // while the user is typing or while the send path is already explaining
      // why the message could not be dispatched.
      if (variables.trigger !== "manual") return;
      toast.error(err instanceof Error ? err.message : "Failed to start new work");
    },
  });

  // Ref to expose startNewWork to the handleSend callback without stale closures
  const startNewWorkRef = useRef(startNewWork);
  startNewWorkRef.current = startNewWork;
  const startNewWorkPromiseRef = useRef<StartNewWorkPromiseEntry | null>(null);
  const startNewWorkGitPendingRef = useRef(false);
  const backgroundStartNewWorkRequestedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    startNewWorkPromiseRef.current = null;
    backgroundStartNewWorkRequestedKeyRef.current = null;
  }, [sessionId]);

  const beginStartNewWork = useCallback((trigger: StartNewWorkTrigger) => {
    if (startNewWorkPromiseRef.current) {
      return startNewWorkPromiseRef.current.promise;
    }

    const existingGitPendingState = localStartNewWorkGitPendingRef.current;
    if (existingGitPendingState) {
      return Promise.resolve({
        ok: true,
        newBranch: existingGitPendingState.newBranch,
        baseBranch: existingGitPendingState.baseBranch,
        gitPending: true,
        previousPr: existingGitPendingState.previousPr,
        session:
          session && typeof session === "object"
            ? session as Record<string, unknown>
            : {},
      } satisfies StartNewWorkResponse);
    }

    const pendingUnarchivePromise = unarchivePromiseRef.current;
    const requestedAt = Date.now();
    const promise = (async () => {
      if (pendingUnarchivePromise) {
        applyLocalStartNewWorkWaitingForUnarchive(trigger);
        try {
          await pendingUnarchivePromise;
        } catch (error) {
          const wrappedError =
            error instanceof Error ? error : new Error("Failed to unarchive thread");
          (
            wrappedError as Error & {
              continuationPreparationStep?: "unarchive";
            }
          ).continuationPreparationStep = "unarchive";
          throw wrappedError;
        } finally {
          clearLocalStartNewWorkWaitingForUnarchive();
        }
      }

      return startNewWorkRef.current.mutateAsync({ trigger, requestedAt });
    })();
    const entry: StartNewWorkPromiseEntry = { promise, trigger };
    startNewWorkPromiseRef.current = entry;

    void promise.finally(() => {
      if (startNewWorkPromiseRef.current?.promise === promise) {
        startNewWorkPromiseRef.current = null;
      }
    });

    return promise;
  }, [applyLocalStartNewWorkWaitingForUnarchive, clearLocalStartNewWorkWaitingForUnarchive, session]);

  const maybeStartNewWorkInBackground = useCallback(() => {
    if (!mergedPrNeedsContinuation) return;
    if (sessionArchived && !unarchivePromiseRef.current) return;
    const backgroundRequestKey = `${sessionId}:${sessionPrNumber ?? "unknown"}`;
    if (backgroundStartNewWorkRequestedKeyRef.current === backgroundRequestKey) return;
    if (localStartNewWorkGitPendingRef.current) return;

    backgroundStartNewWorkRequestedKeyRef.current = backgroundRequestKey;
    void beginStartNewWork("background").catch(() => {
      // The mutation's own error handling covers surfaced failures. We keep the
      // one-shot gate set so typing does not spam repeated background retries.
    });
  }, [beginStartNewWork, mergedPrNeedsContinuation, sessionArchived, sessionId, sessionPrNumber]);

  const maybeStartNewWorkFromDraftInput = useCallback((input: {
    text?: string;
    hasContextRefs?: boolean;
  }) => {
    if (suppressLocalDraftInputTrackingRef.current) return;
    if (!hasLocalDraftInputRef.current) return;

    const normalizedText = typeof input.text === "string" ? input.text.trim() : "";
    if (normalizedText.length > 0 && parseSlashCommandInput(normalizedText)) {
      return;
    }

    if (normalizedText.length === 0 && input.hasContextRefs !== true) return;
    maybeStartNewWorkInBackground();
  }, [maybeStartNewWorkInBackground]);

  const ensureStartNewWorkReady = useCallback(async (): Promise<boolean> => {
    if (localStartNewWorkGitPendingRef.current) {
      return true;
    }

    if (startNewWorkGitPendingRef.current) {
      return true;
    }

    if (!mergedPrNeedsContinuation) return true;

    if (startNewWorkPromiseRef.current) {
      const existingPromiseEntry = startNewWorkPromiseRef.current;
      try {
        await existingPromiseEntry.promise;
        return true;
      } catch (err) {
        if (existingPromiseEntry.trigger === "manual") {
          // Manual clicks already surface a dedicated failure toast.
          return false;
        }
        toast.error("Message not sent — could not prepare a fresh branch", {
          description: err instanceof Error ? err.message : "Please try Keep Working manually, then send again.",
        });
        return false;
      }
    }

    const promise = beginStartNewWork("auto");
    try {
      await promise;
      return true;
    } catch (err) {
      // The mutation's onError handler fires for the original attempt,
      // but when auto-triggered during send the user has no other
      // feedback that their message was not sent.  Show a specific hint
      // so the dropped message is not a mystery.
      toast.error("Message not sent — could not prepare a fresh branch", {
        description: err instanceof Error ? err.message : "Please try Keep Working manually, then send again.",
      });
      return false;
    }
  }, [beginStartNewWork, mergedPrNeedsContinuation]);

  const ensureSessionWritableForSend = useCallback(async (): Promise<boolean> => {
    if (!unarchivePromiseRef.current) {
      return true;
    }

    try {
      await unarchivePromiseRef.current;
      return true;
    } catch (err) {
      toast.error("Message not sent — could not unarchive the thread", {
        description: err instanceof Error ? err.message : "Please unarchive the thread and try again.",
      });
      return false;
    }
  }, []);

  const prHistory = useQuery({
    queryKey: ["pr-history", sessionId],
    enabled: authoritativeSessionAvailable,
    queryFn: async (): Promise<PrHistoryEntry[]> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/pr-history`, {
        credentials: "include",
      });
      if (!res.ok) return [];
      return res.json() as Promise<PrHistoryEntry[]>;
    },
  });

  const prHistoryEntries = prHistory.data ?? [];

  const archiveSession = useMutation({
    mutationFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/archive`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const responseBody = await res.json().catch(() => null);
        throw createSessionActionRequestErrorFromResponse({
          action: "archive thread",
          responseBody,
          fallbackMessage: "Failed to archive thread",
          status: res.status,
        });
      }
      return res.json().catch(() => ({ ok: true }));
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["sessions"] });
      await queryClient.cancelQueries({ queryKey: ["session", sessionId] });

      const snapshot = captureSessionCacheMutationSnapshot({
        queryClient,
        sessionId,
        repoId,
      });
      const sourceItem = selectSessionCacheMutationSource({
        snapshot,
        sessionId,
      });
      const archivedAt = new Date().toISOString();

      if (sourceItem) {
        const archivedItem: CachedSessionListItem = {
          ...sourceItem,
          archivedAt,
        };

        queryClient.setQueryData(["session", sessionId], (old: unknown) => {
          if (!old || typeof old !== "object") return old;
          return {
            ...(old as Record<string, unknown>),
            archivedAt,
          };
        });
        applyOptimisticSessionMembershipToSessionListCaches({
          queryClient,
          snapshot,
          sessionId,
          item: archivedItem,
        });
      }

      navigateToList();
      return { snapshot, navigatedToList: true };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        restoreSessionCacheMutationSnapshot({
          queryClient,
          sessionId,
          snapshot: context.snapshot,
        });
      }
      invalidateSessionMembershipCaches();
      if (context?.navigatedToList) {
        navigate({
          to: "/$repoId/$sessionId",
          params: { repoId, sessionId },
        });
      }
    },
    onSuccess: () => {
      invalidateSessionMembershipCaches();
    },
  });

  const unarchiveSession = useMutation({
    mutationFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/unarchive`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const responseBody = await res.json().catch(() => null);
        throw createSessionActionRequestErrorFromResponse({
          action: "unarchive thread",
          responseBody,
          fallbackMessage: "Failed to unarchive thread",
          status: res.status,
        });
      }
      return res.json().catch(() => ({ ok: true }));
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["sessions"] });
      await queryClient.cancelQueries({ queryKey: ["session", sessionId] });

      const snapshot = captureSessionCacheMutationSnapshot({
        queryClient,
        sessionId,
        repoId,
      });
      const sourceItem = selectSessionCacheMutationSource({
        snapshot,
        sessionId,
      });

      if (sourceItem) {
        const activeItem: CachedSessionListItem = {
          ...sourceItem,
          archivedAt: null,
        };

        queryClient.setQueryData(["session", sessionId], (old: unknown) => {
          if (!old || typeof old !== "object") return old;
          return {
            ...(old as Record<string, unknown>),
            archivedAt: null,
          };
        });
        applyOptimisticSessionMembershipToSessionListCaches({
          queryClient,
          snapshot,
          sessionId,
          item: activeItem,
        });
      }

      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        restoreSessionCacheMutationSnapshot({
          queryClient,
          sessionId,
          snapshot: context.snapshot,
        });
      }
      invalidateSessionMembershipCaches();
    },
    onSuccess: (data) => {
      invalidateSessionMembershipCaches();
      if (!(data && typeof data === "object" && (data as { alreadyUnarchived?: unknown }).alreadyUnarchived === true)) {
        setSessionRecentlyUnarchived(true);
      }
    },
  });

  const beginUnarchiveSession = useCallback(() => {
    if (unarchivePromiseRef.current) {
      return unarchivePromiseRef.current;
    }

    const promise = unarchiveSession.mutateAsync();
    unarchivePromiseRef.current = promise;

    void promise.finally(() => {
      if (unarchivePromiseRef.current === promise) {
        unarchivePromiseRef.current = null;
      }
    });

    return promise;
  }, [unarchiveSession]);

  const handleStartNewWork = useCallback(() => {
    if (
      unarchiveSession.isPending ||
      startNewWork.isPending ||
      localStartNewWorkWaitingForUnarchiveRef.current ||
      localStartNewWorkGitPendingRef.current
    ) {
      return;
    }
    if (branchSwitchPendingBeforeStartNewWork) {
      showBranchSwitchInProgressToast({ actionLabel: "starting new work again" });
      return;
    }
    captureAnalyticsEvent({
      event: ANALYTICS_EVENTS.startNewWorkRequested,
      properties: buildPrAnalyticsProperties({
        sessionId,
        repositoryId: repoId,
        agentType: sessionAgentType,
        source: "thread_page",
        prState: effectivePrState,
        properties: {
          trigger: "manual",
        },
      }),
    });
    const startFlow = async () => {
      if (sessionArchived) {
        applyLocalStartNewWorkWaitingForUnarchive("manual");
        try {
          await beginUnarchiveSession();
        } catch (error) {
          const wrappedError =
            error instanceof Error ? error : new Error("Failed to unarchive thread");
          (
            wrappedError as Error & {
              continuationPreparationStep?: "unarchive";
            }
          ).continuationPreparationStep = "unarchive";
          throw wrappedError;
        } finally {
          clearLocalStartNewWorkWaitingForUnarchive();
        }
      }
      return beginStartNewWork("manual");
    };
    void startFlow().catch((error) => {
      const continuationPreparationStep =
        error && typeof error === "object" && "continuationPreparationStep" in error
          ? (error as { continuationPreparationStep?: unknown }).continuationPreparationStep
          : null;
      if (continuationPreparationStep !== "unarchive") {
        return;
      }
      toast.error(error instanceof Error ? error.message : "Failed to unarchive thread");
    });
  }, [applyLocalStartNewWorkWaitingForUnarchive, beginStartNewWork, beginUnarchiveSession, branchSwitchPendingBeforeStartNewWork, clearLocalStartNewWorkWaitingForUnarchive, effectivePrState, repoId, sessionAgentType, sessionArchived, sessionId, startNewWork.isPending, unarchiveSession.isPending]);

  const handleContinueInNewSession = useCallback(() => {
    if (continueSession.isPending) return;
    const disabledReason = continueInNewSessionDisabledReasonRef.current;
    if (disabledReason) {
      toast.error(disabledReason);
      return;
    }
    captureAnalyticsEvent({
      event: ANALYTICS_EVENTS.continueInNewSessionRequested,
      properties: buildThreadAnalyticsProperties({
        sessionId,
        repositoryId: repoId,
        agentType: sessionAgentType,
        source: "thread_page",
        properties: {
          lifecycle_status: lifecycleStatus,
        },
      }),
    });
    const optimisticSessionId = crypto.randomUUID();
    const optimisticTitle = formatSessionTitle();
    const optimisticAgentType = toKnownAgentType(sessionAgentType) ?? "opencode";
    const sourceConversation = continueSessionSourceConversationRef.current;
    const sourceSessionTitle = fetchedSessionTitle || optimisticTitle || "Untitled session";
    const sourceContextTitle = buildContinuationSourceContextRefTitle({
      sourceSessionTitle,
      sourceConversation,
    });
    const contextOnlyContinuationExpected = sessionArchived || terminalPrForContinuation;
    const continuationBootstrapDraft = buildContinuationBootstrapDraft({
      sourceSessionId: sessionId,
      sourceRepositoryId: repoId,
      sourceContextTitle,
      sourceConversation,
      contextRefLocalId: crypto.randomUUID(),
      origin: contextOnlyContinuationExpected ? "context" : "continue_new_session",
    });
    void queryClient.cancelQueries({ queryKey: ["sessions"] });
    const cacheSnapshot = captureSessionCacheMutationSnapshot({
      queryClient,
      sessionId: optimisticSessionId,
      repoId,
    });
    const placeholder = {
      ...buildOptimisticEmptyThreadSessionRecord({
        sessionId: optimisticSessionId,
        repositoryId: repoId,
        title: optimisticTitle,
        agentType: optimisticAgentType,
      }),
      parentSessionId: sessionId,
      parentRelationType: "continue",
      archivedAt: null,
    } satisfies CachedSessionListItem & { hasHistory: false };
    applyOptimisticSessionMembershipToSessionListCaches({
      queryClient,
      snapshot: cacheSnapshot,
      sessionId: optimisticSessionId,
      item: placeholder,
    });
    queryClient.setQueryData(["session", optimisticSessionId], placeholder);
    sessionStorage.setItem(optimisticSessionTitleStorageKey(optimisticSessionId), optimisticTitle);
    sessionStorage.setItem(optimisticAgentTypeStorageKey(optimisticSessionId), optimisticAgentType);
    sessionStorage.setItem(
      initialBootstrapDraftStorageKey(optimisticSessionId),
      JSON.stringify(continuationBootstrapDraft),
    );
    writeContinuationAdoptionPending({
      sessionId: optimisticSessionId,
      pending: !contextOnlyContinuationExpected,
    });
    continueSession.mutate({
      sourceSessionId: sessionId,
      sourceRepositoryId: repoId,
      sourceSessionTitle,
      sourceAgentType: optimisticAgentType,
      sourceConversation,
      optimisticSessionId,
      optimisticTitle,
      cacheSnapshot,
    });
    navigate({
      to: "/$repoId/$sessionId",
      params: { repoId, sessionId: optimisticSessionId },
    });
  }, [continueSession, fetchedSessionTitle, lifecycleStatus, navigate, optimisticTitle, queryClient, repoId, sessionAgentType, sessionArchived, sessionId, terminalPrForContinuation]);

  const requestDeleteThread = useCallback((source: string) => {
    if (!canManageSession || deleteSession.isPending) return;
    if (!confirm("Delete this thread? This cannot be undone.")) return;
    captureAnalyticsEvent({
      event: ANALYTICS_EVENTS.threadDeleteRequested,
      properties: buildPrAnalyticsProperties({
        sessionId,
        repositoryId: repoId,
        agentType: sessionAgentType,
        source,
        prState: effectivePrState,
        properties: {
          lifecycle_status: lifecycleStatus,
        },
      }),
    });
    deleteSession.mutate();
  }, [canManageSession, deleteSession, effectivePrState, lifecycleStatus, repoId, sessionAgentType, sessionId]);

  const readSessionTitleCacheState = useCallback(() => {
    const readState = (row: unknown) => {
      if (!row || typeof row !== "object") return null;
      const record = row as Record<string, unknown>;
      return {
        title: typeof record.title === "string" ? record.title : null,
        activityHubTitleUpdatedAt:
          typeof record.activityHubTitleUpdatedAt === "number" && Number.isFinite(record.activityHubTitleUpdatedAt)
            ? Math.floor(record.activityHubTitleUpdatedAt)
            : typeof record.titleUpdatedAt === "string" && Number.isFinite(Date.parse(record.titleUpdatedAt))
            ? Math.floor(Date.parse(record.titleUpdatedAt))
            : null,
        pendingLocalTitleValue:
          typeof record.pendingLocalTitleValue === "string" ? record.pendingLocalTitleValue : null,
      };
    };

    const detailState = readState(queryClient.getQueryData(["session", sessionId]));
    if (detailState) return detailState;

    const sessionLists = queryClient.getQueriesData({ queryKey: ["sessions"] });
    for (const [, sessions] of sessionLists) {
      if (!Array.isArray(sessions)) continue;
      const found = sessions.find((item) => {
        if (!item || typeof item !== "object") return false;
        return (item as Record<string, unknown>).id === sessionId;
      });
      const foundState = readState(found);
      if (foundState) return foundState;
    }

    return {
      title: null,
      activityHubTitleUpdatedAt: null,
      pendingLocalTitleValue: null,
    };
  }, [queryClient, sessionId]);
  const applySessionTitleToCaches = useCallback(
    (args: {
      nextTitle: string | null;
      activityHubTitleUpdatedAt?: number | null;
      pendingLocalTitleValue?: string | null;
    }) => {
      const { nextTitle, activityHubTitleUpdatedAt, pendingLocalTitleValue } = args;
      queryClient.setQueryData(["session", sessionId], (old: unknown) => {
        if (!old || typeof old !== "object") return old;
        const row = old as Record<string, unknown>;
        const currentTitleUpdatedAt =
          typeof row.activityHubTitleUpdatedAt === "number" && Number.isFinite(row.activityHubTitleUpdatedAt)
            ? Math.floor(row.activityHubTitleUpdatedAt)
            : null;
        const currentPendingLocalTitleValue =
          typeof row.pendingLocalTitleValue === "string" ? row.pendingLocalTitleValue : null;
        if (
          row.title === nextTitle &&
          currentTitleUpdatedAt === (activityHubTitleUpdatedAt ?? null) &&
          currentPendingLocalTitleValue === (pendingLocalTitleValue ?? null)
        ) {
          return old;
        }
        return {
          ...row,
          title: nextTitle,
          activityHubTitleUpdatedAt: activityHubTitleUpdatedAt ?? null,
          pendingLocalTitleValue: pendingLocalTitleValue ?? null,
        };
      });

      queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
        if (!Array.isArray(old)) return old;

        let changed = false;
        const next = old.map((item) => {
          if (!item || typeof item !== "object") return item;
          const row = item as Record<string, unknown>;
          if (row.id !== sessionId) return item;

          const currentTitleUpdatedAt =
            typeof row.activityHubTitleUpdatedAt === "number" && Number.isFinite(row.activityHubTitleUpdatedAt)
              ? Math.floor(row.activityHubTitleUpdatedAt)
              : null;
          const currentPendingLocalTitleValue =
            typeof row.pendingLocalTitleValue === "string" ? row.pendingLocalTitleValue : null;
          if (
            row.title === nextTitle &&
            currentTitleUpdatedAt === (activityHubTitleUpdatedAt ?? null) &&
            currentPendingLocalTitleValue === (pendingLocalTitleValue ?? null)
          ) {
            return item;
          }

          changed = true;
          return {
            ...row,
            title: nextTitle,
            activityHubTitleUpdatedAt: activityHubTitleUpdatedAt ?? null,
            pendingLocalTitleValue: pendingLocalTitleValue ?? null,
          };
        });

        return changed ? next : old;
      });
    },
    [queryClient, sessionId],
  );

  const updateTitle = useMutation({
    mutationFn: async ({ title }: { title: string; previousTitle: string | null }) => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new Error("Failed to update title");
      return res.json();
    },
    onMutate: async ({ title, previousTitle }) => {
      await queryClient.cancelQueries({ queryKey: ["session", sessionId] })
      await queryClient.cancelQueries({ queryKey: ["sessions"] })
      setIsEditingTitle(false);
      const previousCacheState = readSessionTitleCacheState();
      applySessionTitleToCaches({
        nextTitle: title,
        activityHubTitleUpdatedAt: previousCacheState.activityHubTitleUpdatedAt,
        pendingLocalTitleValue: title,
      });
      return {
        previousTitle,
        previousActivityHubTitleUpdatedAt: previousCacheState.activityHubTitleUpdatedAt,
        previousPendingLocalTitleValue: previousCacheState.pendingLocalTitleValue,
      };
    },
    onSuccess: (data, variables) => {
      const resolvedTitle =
        data && typeof data === "object" && typeof (data as Record<string, unknown>).title === "string"
          ? ((data as Record<string, unknown>).title as string).trim() || variables.title
          : variables.title;
      const nextActivityHubTitleUpdatedAt =
        data && typeof data === "object" && typeof (data as Record<string, unknown>).titleUpdatedAt === "string"
          ? Date.parse((data as Record<string, unknown>).titleUpdatedAt as string)
          : data && typeof data === "object" && typeof (data as Record<string, unknown>).updatedAt === "string"
          ? Date.parse((data as Record<string, unknown>).updatedAt as string)
          : NaN;
      const normalizedActivityHubTitleUpdatedAt = Number.isFinite(nextActivityHubTitleUpdatedAt)
        ? Math.floor(nextActivityHubTitleUpdatedAt)
        : null
      recordSessionTitleTimestamp({
        sessionId,
        timestamp: normalizedActivityHubTitleUpdatedAt,
      })
      applySessionTitleToCaches({
        nextTitle: resolvedTitle,
        activityHubTitleUpdatedAt: normalizedActivityHubTitleUpdatedAt,
        pendingLocalTitleValue: null,
      });
      queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
      invalidateContinuityPickerCaches();
    },
    onError: (_error, _variables, context) => {
      if (context) {
        const currentCacheState = readSessionTitleCacheState()
        const titleWasAuthoritativelyUpdated =
          currentCacheState.pendingLocalTitleValue === null &&
          currentCacheState.title !== context.previousTitle
        if (titleWasAuthoritativelyUpdated) {
          setIsEditingTitle(false)
          return
        }
        applySessionTitleToCaches({
          nextTitle: context.previousTitle,
          activityHubTitleUpdatedAt: context.previousActivityHubTitleUpdatedAt,
          pendingLocalTitleValue: context.previousPendingLocalTitleValue,
        });
      }
      setIsEditingTitle(false);
    },
  });

  const startEditingTitle = () => {
    const currentTitle =
      ((session as Record<string, unknown>)?.title as string) ?? "";
    setEditTitle(currentTitle);
    setEditingRuntimeConversationId(null);
    suppressNextTitleBlurCommitRef.current = false;
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.select(), 0);
  };

  const commitTitle = () => {
    if (suppressNextTitleBlurCommitRef.current) {
      suppressNextTitleBlurCommitRef.current = false;
      return;
    }
    if (updateTitle.isPending) return;

    const previousTitleRaw = (session as Record<string, unknown>)?.title;
    const previousTitle = typeof previousTitleRaw === "string" ? previousTitleRaw : null;
    const currentTitle = previousTitle?.trim() ?? "";
    const trimmed = editTitle.trim();

    if (trimmed && trimmed !== currentTitle) {
      setIsEditingTitle(false);
      updateTitle.mutate({ title: trimmed, previousTitle });
    } else {
      setIsEditingTitle(false);
    }
  };

  const forkBootstrapPending =
    forkBootstrapEntry?.status === "pending" || forkBootstrapEntry?.status === "reconciling"
  const forkBootstrapFailed = forkBootstrapEntry?.status === "failed"
  const forkBootstrapTransportReady = !forkBootstrapEntry || forkBootstrapEntry.status === "ready"
  const shouldConnectSessionWs = useMemo(() => {
    const hasOptimisticIdentity =
      optimisticTitle.trim().length > 0 || optimisticAgentType !== "unknown"

    return (
      forkBootstrapTransportReady && (
        (!isSessionQueryError && (Boolean(session) || hasOptimisticIdentity)) ||
        (sessionNotFound && hasOptimisticIdentity)
      )
    )
  }, [
    forkBootstrapTransportReady,
    isSessionQueryError,
    optimisticAgentType,
    optimisticTitle,
    session,
    sessionNotFound,
  ])

  useEffect(() => {
    if (!shouldConnectSessionWs) return;

    return claimForegroundSessionLease({
      scopeKey: sessionScopeKey,
      sessionId,
    });
  }, [sessionScopeKey, sessionId, shouldConnectSessionWs]);

  const {
    transportConnectEnabled,
    transportStandbyInAnotherTab,
  } = useMemo(() => deriveSessionTransportOwnershipState({
    shouldConnectSessionWs,
    foregroundLeaseOwnedByThisTab,
    foregroundLeaseOwnedElsewhere,
    hasSessionScopeKey: Boolean(sessionScopeKey),
    isPageVisible,
    sessionArchived,
  }), [
    foregroundLeaseOwnedByThisTab,
    foregroundLeaseOwnedElsewhere,
    isPageVisible,
    sessionArchived,
    sessionScopeKey,
    shouldConnectSessionWs,
  ])

  const runtimeConversationTabsEnabled =
    sessionAgentType === "opencode" ||
    optimisticAgentType === "opencode" ||
    sessionAgentType === "claude_code" ||
    optimisticAgentType === "claude_code";
  const activityStoreConversationsById = useRuntimeConversationActivity(
    (state) => state.bySessionId[sessionId] ?? EMPTY_RUNTIME_CONVERSATIONS_BY_ID,
  );
  const requestedConversationIdForInitialSync = runtimeConversationTabsEnabled
    ? draftConversationId
    : "default";
  const cachedRuntimeConversationsForInitialSync = queryClient.getQueryData<RuntimeConversationSnapshot[]>([
    "runtime-conversations",
    sessionId,
  ]);
  const requestedConversationKnownForInitialSync =
    requestedConversationIdForInitialSync === "default" ||
    optimisticCreatingRuntimeConversation?.conversationId === requestedConversationIdForInitialSync ||
    activityStoreConversationsById[requestedConversationIdForInitialSync]?.archivedAt === null ||
    (Array.isArray(cachedRuntimeConversationsForInitialSync) &&
      cachedRuntimeConversationsForInitialSync.some(
        (conversation) =>
          conversation.conversationId === requestedConversationIdForInitialSync &&
          conversation.archivedAt === null,
      ));
  const initialThreadSyncConversationId = requestedConversationKnownForInitialSync
    ? requestedConversationIdForInitialSync
    : "default";
  const optimisticRuntimeSeedForThreadSync =
    optimisticCreatingRuntimeConversation?.conversationId === initialThreadSyncConversationId
      ? activeOptimisticRuntimeConversation?.seed ?? null
      : pendingForkSnapshot;
  const {
    status,
    hasConnected,
    firstConnectionAttemptStartedAt,
    initReady: sessionDoInitReady,
    sessionStatus,
    wsStatusChangedAt,
    wsDegradedSinceAt,
    wsTransportProbeState,
    wsTransportProbeAt,
    refreshStatusFromProbe,
    forceReconnect: forceThreadSyncReconnect,
    remoteWsDiagnostics,
    lastWsCloseDiagnostics,
    lastWsErrorDiagnostics,
    send,
    sendChatMessage,
    stageServerOrchestratedChatMessage,
    setActiveConversationId,
    cancelQueuedMessage,
    agentType: wsAgentType,
    connectionCount,
    branchSwitchInProgress,
    startNewWorkGitPending,
    sandboxStatus,
    sandboxProgress,
    sandboxStartupKind: serverSandboxStartupKind,
    bootSource,
    queuedMessages,
    ptyUrl,
    vscodeUrl,
    previewUrl,
    previewEndpoints,
    runtimeProcesses,
    devServerStatus,
    devServerError,
    runtimeProcessesConfigured,
    codeServerStatus,
    codeServerError,
    activeCredentialSource,
    nextCredentialSource,
    credentialIssue,
    credentialStateRefreshedAt,
    lastCredentialFallbackAt,
    runtimeLastErrorCode,
    runtimeLastErrorMessage,
    chatGptOauthDebug,
    conversationEpoch: wsConversationEpoch,
    activeEditMessageOperation: wsActiveEditMessageOperation,
    lastMessageRejection,
    transportLoading,
    foregroundActivationLoading,
    hideTransportLoadingWhenRenderable,
    isCatchingUp,
    syncReadyReceived,
    runtimeMaterialization,
    syncNewDebug,
    transcriptFreshness,
    sessionActionNotice: wsSessionActionNotice,
    queuedSessionCommandNotice: wsQueuedSessionCommandNotice,
    yoloFlowStep,
    yoloFlowError,
    prAdoption: wsPrAdoption,
    prAdoptionFreshSession,
    sessionModelIntent: wsSessionModelIntent,
    sessionModelIntentConversationId: wsSessionModelIntentConversationId,
    shouldRefreshSessionSurfaceAfterPrefill: shouldRefreshSessionSurfaceOnMount,
  } = useThreadSync(sessionId, {
    connectEnabled: transportConnectEnabled,
    initialBranchSwitchInProgress: sessionBranchSwitchInProgress,
    viewerActivityEnabled: viewerActivityEnabled && !sessionArchived,
    readOnlyTransport: sessionArchived,
    optimisticRuntimeSeed: optimisticRuntimeSeedForThreadSync,
    activeConversationId: initialThreadSyncConversationId,
  });
  const foregroundActivationLoadingVisible =
    transportConnectEnabled &&
    foregroundActivationLoading &&
    sessionHistoryPresence !== "no_history" &&
    runtimeMaterialization !== "authoritative";
  const chatTransportLoading = transportLoading || foregroundActivationLoadingVisible;
  const runtimeConversationTabsAvailable =
    runtimeConversationTabsEnabled || wsAgentType === "opencode" || wsAgentType === "claude_code";
  const runtimeConversationsQuery = useQuery({
    queryKey: ["runtime-conversations", sessionId],
    enabled: (wsAgentType === "opencode" || wsAgentType === "claude_code") && sessionDoInitReady,
    queryFn: async (): Promise<RuntimeConversationSnapshot[]> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/runtime-conversations`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to load chat tabs");
      }
      const body = await res.json() as { runtimeConversations?: unknown };
      return Array.isArray(body.runtimeConversations)
        ? body.runtimeConversations.filter((item): item is RuntimeConversationSnapshot => {
            return Boolean(
              item &&
              typeof item === "object" &&
              typeof (item as RuntimeConversationSnapshot).conversationId === "string",
            );
          })
        : [];
    },
  });
  const allRuntimeConversationsQuery = useQuery({
    queryKey: ["runtime-conversations", sessionId, "include-archived"],
    enabled: (wsAgentType === "opencode" || wsAgentType === "claude_code") && sessionDoInitReady,
    refetchInterval: (query) => {
      const conversations = query.state.data;
      return conversations?.some((conversation) => conversation.archivedAt !== null && runtimeConversationIsRunning(conversation))
        ? ARCHIVED_RUNTIME_CONVERSATION_REFETCH_INTERVAL_MS
        : false;
    },
    queryFn: async (): Promise<RuntimeConversationSnapshot[]> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/runtime-conversations?includeArchived=true`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to load chat sessions");
      }
      const body = await res.json() as { runtimeConversations?: unknown };
      return Array.isArray(body.runtimeConversations)
        ? body.runtimeConversations.filter((item): item is RuntimeConversationSnapshot => {
            return Boolean(
              item &&
              typeof item === "object" &&
              typeof (item as RuntimeConversationSnapshot).conversationId === "string",
            );
          })
        : [];
    },
  });
  const requestedConversationId = runtimeConversationTabsAvailable ? draftConversationId : "default";
  useEffect(() => {
    setOptimisticRuntimeConversation(null);
  }, [sessionId]);
  // Default ("Main") tab snapshot used whenever we have no richer information
  // about the session's runtime conversations. Memoized so every render doesn't
  // produce a new object identity — which is what caused the downstream
  // `runtimeConversations` literal array and its write-back effect to churn on
  // every render (each call to `replaceSessionSnapshots` would hit the
  // reference-based `sameKeys` guard with fresh objects and update zustand).
  const defaultRuntimeConversation = useMemo<RuntimeConversationSnapshot>(() => ({
    conversationId: "default",
    agentType: wsAgentType === "claude_code" || sessionAgentType === "claude_code" || optimisticAgentType === "claude_code"
      ? "claude_code"
      : "opencode",
    providerSessionId: null,
    title: "Main",
    isDefault: true,
    statusKind: null,
    createdAt: MIN_RUNTIME_CONVERSATION_CREATED_AT_MS,
    updatedAt: MIN_RUNTIME_CONVERSATION_CREATED_AT_MS,
    archivedAt: null,
  }), [optimisticAgentType, sessionAgentType, wsAgentType]);
  // The sidebar maintains a live store of runtime-conversation snapshots (via
  // the multiplex activity websocket) for every tracked session. When the user
  // opens a multi-tab session from the sidebar the zustand store already has
  // the full tab list, but the per-session `runtime-conversations` HTTP query
  // is gated on the session DO being init-ready and the websocket advertising
  // an agent type — so the top-bar tabs would otherwise blank out into a
  // single-tab header for a beat on every switch. Seed from the activity store
  // so the tabs render instantly and the HTTP response just takes over when it
  // arrives.
  const activityStoreConversations = useMemo<RuntimeConversationSnapshot[]>(() => {
    return Object.values(activityStoreConversationsById)
      .filter((conversation) => conversation.archivedAt === null)
      .sort((a, b) => {
        if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
        return a.createdAt - b.createdAt;
      });
  }, [activityStoreConversationsById]);
  // Merge the three possible sources into a single stable tuple. The source
  // tag lets the write-back effect below distinguish authoritative HTTP-query
  // data (worth syncing into the shared activity store) from data we *already*
  // read out of that same store (which would otherwise cause a render/write
  // feedback loop — see the effect for details).
  const { runtimeConversations, runtimeConversationsSource, runtimeConversationsIncludeOptimistic } = useMemo<{
    runtimeConversations: RuntimeConversationSnapshot[];
    runtimeConversationsSource: RuntimeConversationsSource;
    runtimeConversationsIncludeOptimistic: boolean;
  }>(() => {
    const appendOptimisticConversation = (conversations: RuntimeConversationSnapshot[]) => {
      if (!optimisticCreatingRuntimeConversation) {
        return { conversations, includeOptimistic: false };
      }
      if (conversations.some((conversation) => conversation.conversationId === optimisticCreatingRuntimeConversation.conversationId)) {
        return { conversations, includeOptimistic: false };
      }
      return {
        conversations: [...conversations, optimisticCreatingRuntimeConversation],
        includeOptimistic: true,
      };
    };

    if (!runtimeConversationTabsAvailable) {
      return {
        runtimeConversations: [defaultRuntimeConversation],
        runtimeConversationsSource: "default",
        runtimeConversationsIncludeOptimistic: false,
      };
    }
    if (runtimeConversationsQuery.data && runtimeConversationsQuery.data.length > 0) {
      const next = appendOptimisticConversation(runtimeConversationsQuery.data);
      return {
        runtimeConversations: next.conversations,
        runtimeConversationsSource: "query",
        runtimeConversationsIncludeOptimistic: next.includeOptimistic,
      };
    }
    if (activityStoreConversations.length > 0) {
      const next = appendOptimisticConversation(activityStoreConversations);
      return {
        runtimeConversations: next.conversations,
        runtimeConversationsSource: "activity-store",
        runtimeConversationsIncludeOptimistic: next.includeOptimistic,
      };
    }
    if (runtimeConversationsQuery.isSuccess) {
      const next = appendOptimisticConversation([defaultRuntimeConversation]);
      return {
        runtimeConversations: next.conversations,
        runtimeConversationsSource: "query",
        runtimeConversationsIncludeOptimistic: next.includeOptimistic,
      };
    }
    if (optimisticCreatingRuntimeConversation) {
      return {
        runtimeConversations: [defaultRuntimeConversation, optimisticCreatingRuntimeConversation],
        runtimeConversationsSource: "default",
        runtimeConversationsIncludeOptimistic: true,
      };
    }
    if (requestedConversationId !== "default") {
      return {
        runtimeConversations: [
          defaultRuntimeConversation,
          {
            ...defaultRuntimeConversation,
            conversationId: requestedConversationId,
            title: "Loading chat…",
            isDefault: false,
          },
        ],
        runtimeConversationsSource: "loading-placeholder",
        runtimeConversationsIncludeOptimistic: false,
      };
    }
    return {
      runtimeConversations: [defaultRuntimeConversation],
      runtimeConversationsSource: "default",
      runtimeConversationsIncludeOptimistic: false,
    };
  }, [
    activityStoreConversations,
    defaultRuntimeConversation,
    optimisticCreatingRuntimeConversation,
    requestedConversationId,
    runtimeConversationTabsAvailable,
    runtimeConversationsQuery.data,
    runtimeConversationsQuery.isSuccess,
  ]);
  // When the requested (URL) conversation is unknown, we synthesize a
  // "Loading chat…" placeholder tab so the tab strip doesn't collapse to a
  // single entry while we wait on query/activity data. But that tab is NOT
  // validated as belonging to this session yet — adopting it as the UI's
  // active conversation would render the default-conversation runtime under
  // the stale/deep-linked tab and cause downstream call-sites (queued
  // messages, permission replies, abort requests, etc.) to send the
  // unvalidated conversation id to the server. Until validation happens via
  // `query` or `activity-store`, we intentionally keep the default tab as
  // the active conversation for both UI and thread-sync so there is no
  // divergence between what the user sees and what we actually drive.
  const isRequestedConversationValidated =
    runtimeConversationsSource === "query" ||
    runtimeConversationsSource === "activity-store" ||
    optimisticCreatingRuntimeConversation?.conversationId === requestedConversationId;
  const activeConversation = (
    isRequestedConversationValidated
      ? runtimeConversations.find(
          (conversation) => conversation.conversationId === requestedConversationId,
        )
      : undefined
  ) ?? runtimeConversations.find((conversation) => conversation.isDefault) ?? runtimeConversations[0];
  const activeConversationId = activeConversation?.conversationId ?? "default";
  const persistedSessionModelIntent = useMemo(() => {
    if (
      persistedSessionModelIntentConversationIdFromRecord &&
      persistedSessionModelIntentConversationIdFromRecord !== "default" &&
      persistedSessionModelIntentConversationIdFromRecord !== activeConversationId
    ) {
      return undefined;
    }
    return persistedSessionModelIntentFromRecord;
  }, [
    activeConversationId,
    persistedSessionModelIntentConversationIdFromRecord,
    persistedSessionModelIntentFromRecord,
  ]);
  const persistedSessionModelIntentConversationId = useMemo(() => {
    if (persistedSessionModelIntent === undefined) {
      return null;
    }
    return persistedSessionModelIntentConversationIdFromRecord ?? "default";
  }, [persistedSessionModelIntent, persistedSessionModelIntentConversationIdFromRecord]);
  // A deep-linked or freshly selected tab can briefly be missing from validated
  // query/activity data. During that window the active runtime intentionally
  // remains on the default/previous validated tab; block continuation rather
  // than snapshot that fallback as the new thread's source context.
  const continueSourceConversationPending =
    runtimeConversationTabsAvailable &&
    requestedConversationId !== "default" &&
    requestedConversationId !== activeConversationId;
  const activeContinuationSourceConversation = useMemo<ContinueSessionSourceConversation>(() => {
    if (!runtimeConversationTabsAvailable) {
      return { conversationId: null, title: null };
    }
    const conversationId = activeConversationId.trim();
    if (conversationId.length === 0) {
      return { conversationId: null, title: null };
    }
    const title = typeof activeConversation?.title === "string" && activeConversation.title.trim().length > 0
      ? activeConversation.title.trim()
      : activeConversation?.isDefault === true
        ? "Main"
        : null;
    return { conversationId, title };
  }, [activeConversation?.isDefault, activeConversation?.title, activeConversationId, runtimeConversationTabsAvailable]);
  continueSessionSourceConversationRef.current = activeContinuationSourceConversation;
  // Kept for backwards compatibility with existing call-sites; now always
  // equal to `activeConversationId` because we no longer diverge UI state
  // from thread-sync state. Left as a named binding to minimize churn at
  // the (many) downstream use-sites and document intent.
  const activeConversationIdForThreadSync = activeConversationId;
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;
  const focusedOptimisticConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    const optimisticConversationId = optimisticCreatingRuntimeConversation?.conversationId ?? null;
    if (!optimisticConversationId || activeConversationId !== optimisticConversationId) return;
    if (focusedOptimisticConversationIdRef.current === optimisticConversationId) return;
    focusedOptimisticConversationIdRef.current = optimisticConversationId;
    const timeoutId = window.setTimeout(() => {
      editorRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [activeConversationId, optimisticCreatingRuntimeConversation?.conversationId]);
  const runtimeConversationViewedEventIds = useRuntimeConversationActivity((state) => state.viewedEventIds);
  const activeRuntimeConversationActivity = useRuntimeConversationActivity(
    (state) => state.bySessionId[sessionId]?.[activeConversationId] ?? null,
  );
  const activeRuntimeConversationStateSnapshot = selectRuntimeConversationSnapshotForControlState({
    querySnapshot: activeConversation,
    activitySnapshot: activeRuntimeConversationActivity,
  });
  // The session-level history hint stays "has_history" for threads that already
  // have a main-chat transcript. Blank side-chat tabs need their own empty hint
  // so the shared empty-state machinery centers the composer without hiding the
  // already-visible session header or right sidebar.
  const activeRuntimeConversationKnownEmpty =
    runtimeConversationTabsAvailable &&
    activeConversationId !== "default" &&
    runtimeConversationIsLocallyKnownEmpty({ conversation: activeRuntimeConversationStateSnapshot });
  const activeChatHistoryPresence = activeRuntimeConversationKnownEmpty
    ? "no_history"
    : sessionHistoryPresence;
  const allRuntimeConversations = useMemo(() => {
    const source = allRuntimeConversationsQuery.data && allRuntimeConversationsQuery.data.length > 0
      ? allRuntimeConversationsQuery.data
      : runtimeConversations;
    return [...source].sort((a, b) => {
      if (a.archivedAt === null && b.archivedAt !== null) return -1;
      if (a.archivedAt !== null && b.archivedAt === null) return 1;
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      return a.createdAt - b.createdAt;
    });
  }, [allRuntimeConversationsQuery.data, runtimeConversations]);
  const backgroundRunningRuntimeConversationCount = useMemo(() => {
    return allRuntimeConversations.filter((conversation) => {
      if (conversation.archivedAt === null) return false;
      const statusSnapshot = selectRuntimeConversationSnapshotForControlState({
        querySnapshot: conversation,
        activitySnapshot: activityStoreConversationsById[conversation.conversationId] ?? null,
      });
      return runtimeConversationIsRunning(statusSnapshot);
    }).length;
  }, [activityStoreConversationsById, allRuntimeConversations]);
  useEffect(() => {
    setActiveConversationId(activeConversationIdForThreadSync);
  }, [activeConversationIdForThreadSync, setActiveConversationId]);
  useEffect(() => {
    // Only write back when the data is *authoritative*: the HTTP query fetched
    // directly from the SessionDO. Skipping the other sources is important:
    //
    //  - "activity-store": `runtimeConversations` already IS the zustand
    //    store's snapshot list. `replaceSessionSnapshots` would map those
    //    snapshots onto fresh object identities and the store's `sameKeys`
    //    fast-path is reference-based, so an equal-revision write always
    //    emits a state change — recomputing `activityStoreConversations`,
    //    re-firing this effect, writing again → infinite loop.
    //  - "loading-placeholder" / "default": these are synthetic, zero-revision
    //    snapshots. Writing them either no-ops against real WS-supplied data
    //    (thanks to the store's `currentRevision > incomingRevision` guard) or
    //    pollutes the shared store with placeholder rows that the multiplex
    //    activity WS will overwrite anyway. Either way there's nothing to
    //    publish that other subscribers don't already have.
    if (runtimeConversationsSource !== "query") return;
    useRuntimeConversationActivity.getState().replaceSessionSnapshots({
      sessionId,
      snapshots: runtimeConversations
        .filter((conversation) => !runtimeConversationsIncludeOptimistic || conversation.conversationId !== optimisticCreatingRuntimeConversation?.conversationId)
        .map((conversation) => ({
        sessionId,
        conversationId: conversation.conversationId,
        agentType: conversation.agentType,
        providerSessionId: conversation.providerSessionId,
        title: conversation.title,
        isDefault: conversation.isDefault,
        statusKind: conversation.statusKind,
        activityState: conversation.activityState ?? null,
        activityEventSequence: conversation.activityEventSequence ?? null,
        lastActivityEventId: conversation.lastActivityEventId ?? null,
        lastFinishedEventId: conversation.lastFinishedEventId ?? null,
        lastFinishedAt: conversation.lastFinishedAt ?? null,
        lastFinishReason: conversation.lastFinishReason ?? null,
        rawTailId: conversation.rawTailId ?? null,
        terminalRawCursor: conversation.terminalRawCursor ?? null,
        terminalRawCursorObservedAt: conversation.terminalRawCursorObservedAt ?? null,
        activityObservedAt: conversation.activityObservedAt ?? null,
        createdAt: conversation.createdAt,
        updatedAt: conversation.updatedAt,
        archivedAt: conversation.archivedAt,
      })),
    });
  }, [optimisticCreatingRuntimeConversation?.conversationId, runtimeConversations, runtimeConversationsIncludeOptimistic, runtimeConversationsSource, sessionId]);
  useEffect(() => {
    useRuntimeConversationActivity.getState().markViewed({
      sessionId,
      conversationId: activeConversationId,
    });
  }, [activeConversationId, sessionId, runtimeConversations]);
  useEffect(() => {
    if (!runtimeConversationTabsAvailable) {
      const runtimeConversationTabsKnownUnavailable =
        sessionAgentType !== "unknown" || wsAgentType !== "unknown";
      if (!runtimeConversationTabsKnownUnavailable) return;
      if (search.conversationId === undefined) return;
      void navigate({
        to: "/$repoId/$sessionId",
        params: { repoId, sessionId },
        search: { conversationId: undefined },
        replace: true,
      });
      return;
    }
    if (!runtimeConversationsQuery.isSuccess) return;
    const canonicalConversationId = activeConversation?.isDefault ? undefined : activeConversationId;
    if (search.conversationId === canonicalConversationId) return;
    void navigate({
      to: "/$repoId/$sessionId",
      params: { repoId, sessionId },
      search: { conversationId: canonicalConversationId },
      replace: true,
    });
  }, [activeConversation?.isDefault, activeConversationId, navigate, optimisticAgentType, repoId, requestedConversationId, runtimeConversationTabsAvailable, runtimeConversationsQuery.isSuccess, search.conversationId, sessionAgentType, sessionId, wsAgentType]);

  const applyRuntimeConversationTitleToCaches = useCallback((args: {
    conversationId: string;
    nextTitle: string | null;
    updatedAt?: number | null;
    force?: boolean;
  }) => {
    const updatedAt = args.updatedAt ?? Date.now();
    let updatedSnapshot: RuntimeConversationSnapshot | null = null;
    let skippedAsStale = false;
    queryClient.setQueryData<RuntimeConversationSnapshot[]>(
      ["runtime-conversations", sessionId],
      (existing) => {
        if (!existing) return existing;
        let changed = false;
        const next = existing.map((conversation) => {
          if (conversation.conversationId !== args.conversationId) return conversation;
          if (!args.force && conversation.updatedAt > updatedAt) {
            skippedAsStale = true;
            updatedSnapshot = conversation;
            return conversation;
          }
          if (conversation.title === args.nextTitle && conversation.updatedAt === updatedAt) {
            updatedSnapshot = conversation;
            return conversation;
          }
          changed = true;
          updatedSnapshot = {
            ...conversation,
            title: args.nextTitle,
            updatedAt,
          };
          return updatedSnapshot;
        });
        return changed ? next : existing;
      },
    );

    if (skippedAsStale) return;

    if (!updatedSnapshot) {
      const fallbackSnapshot = runtimeConversations.find(
        (conversation) => conversation.conversationId === args.conversationId,
      );
      if (!args.force && fallbackSnapshot && fallbackSnapshot.updatedAt > updatedAt) return;
      if (fallbackSnapshot) {
        updatedSnapshot = {
          ...fallbackSnapshot,
          title: args.nextTitle,
          updatedAt,
        };
      }
    }

    if (!updatedSnapshot) return;
    const activitySnapshot = useRuntimeConversationActivity
      .getState()
      .bySessionId[sessionId]?.[args.conversationId];
    if (activitySnapshot) {
      if (!args.force && activitySnapshot.updatedAt > updatedAt) return;
      useRuntimeConversationActivity.getState().applySnapshots([
        {
          ...activitySnapshot,
          title: args.nextTitle,
          updatedAt: args.force ? Math.max(updatedAt, activitySnapshot.updatedAt) : updatedAt,
          timestamp: args.force ? Date.now() : updatedAt,
        },
      ]);
      return;
    }
    useRuntimeConversationActivity.getState().applySnapshots([
      toRuntimeConversationActivitySnapshot({
        sessionId,
        conversation: updatedSnapshot,
        timestamp: updatedAt,
      }),
    ]);
  }, [queryClient, runtimeConversations, sessionId]);

  const updateRuntimeConversationTitle = useMutation({
    mutationFn: async (input: {
      conversationId: string;
      title: string;
      previousTitle: string | null;
    }): Promise<RuntimeConversationSnapshot> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(
        `${baseUrl}/api/sessions/${sessionId}/runtime-conversations/${encodeURIComponent(input.conversationId)}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: input.title, previousTitle: input.previousTitle }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { error?: unknown; runtimeConversation?: unknown } | null;
        const error = new Error(typeof body?.error === "string" ? body.error : "Failed to update chat tab title") as Error & {
          runtimeConversation?: RuntimeConversationSnapshot;
        };
        if (
          body?.runtimeConversation &&
          typeof body.runtimeConversation === "object" &&
          typeof (body.runtimeConversation as RuntimeConversationSnapshot).conversationId === "string"
        ) {
          error.runtimeConversation = body.runtimeConversation as RuntimeConversationSnapshot;
        }
        throw error;
      }
      const body = await res.json() as { runtimeConversation?: RuntimeConversationSnapshot };
      if (!body.runtimeConversation?.conversationId) {
        throw new Error("Chat tab rename response was missing a conversation id");
      }
      return body.runtimeConversation;
    },
    onMutate: async ({ conversationId, title, previousTitle }) => {
      await queryClient.cancelQueries({ queryKey: ["runtime-conversations", sessionId] });
      setEditingRuntimeConversationId(null);
      const previousQueryConversation = queryClient
        .getQueryData<RuntimeConversationSnapshot[]>(["runtime-conversations", sessionId])
        ?.find((conversation) => conversation.conversationId === conversationId);
      const previousActivityConversation = useRuntimeConversationActivity
        .getState()
        .bySessionId[sessionId]?.[conversationId];
      const previousUpdatedAt = previousQueryConversation?.updatedAt ?? previousActivityConversation?.updatedAt;
      const optimisticUpdatedAt = Date.now();
      applyRuntimeConversationTitleToCaches({
        conversationId,
        nextTitle: title,
        updatedAt: optimisticUpdatedAt,
      });
      return { conversationId, previousTitle, previousUpdatedAt, optimisticTitle: title, optimisticUpdatedAt };
    },
    onSuccess: (conversation) => {
      applyRuntimeConversationTitleToCaches({
        conversationId: conversation.conversationId,
        nextTitle: conversation.title,
        updatedAt: conversation.updatedAt,
      });
      queryClient.invalidateQueries({ queryKey: ["runtime-conversations", sessionId] });
    },
    onError: (error, _variables, context) => {
      const conflictConversation = error instanceof Error
        ? (error as Error & { runtimeConversation?: RuntimeConversationSnapshot }).runtimeConversation
        : undefined;
      if (context) {
        if (conflictConversation?.conversationId === context.conversationId) {
          applyRuntimeConversationTitleToCaches({
            conversationId: conflictConversation.conversationId,
            nextTitle: conflictConversation.title,
            updatedAt: conflictConversation.updatedAt,
            force: true,
          });
        } else if ((queryClient
          .getQueryData<RuntimeConversationSnapshot[]>(["runtime-conversations", sessionId])
          ?.find((conversation) => conversation.conversationId === context.conversationId)
          ?.title
          ?? useRuntimeConversationActivity
            .getState()
            .bySessionId[sessionId]?.[context.conversationId]
            ?.title
          ?? null) === context.optimisticTitle) {
          applyRuntimeConversationTitleToCaches({
            conversationId: context.conversationId,
            nextTitle: context.previousTitle,
            updatedAt: context.previousUpdatedAt,
            force: true,
          });
        }
      }
      toast.error(error instanceof Error ? error.message : "Failed to update chat tab title");
      setEditingRuntimeConversationId(null);
    },
  });

  const startEditingRuntimeConversationTitle = useCallback((conversation: RuntimeConversationSnapshot) => {
    if (!canManageSession || sessionArchived || lifecycleStatus === "deleted" || lifecycleStatus === "deleting") {
      return;
    }
    if (conversation.isDefault) {
      startEditingTitle();
      return;
    }
    if (runtimeConversationsSource === "loading-placeholder") return;
    setIsEditingTitle(false);
    setEditTitle(conversation.title ?? "");
    suppressNextTitleBlurCommitRef.current = false;
    setEditingRuntimeConversationId(conversation.conversationId);
    setTimeout(() => titleInputRef.current?.select(), 0);
  }, [canManageSession, lifecycleStatus, runtimeConversationsSource, sessionArchived, startEditingTitle]);

  const commitRuntimeConversationTitle = useCallback((conversation: RuntimeConversationSnapshot) => {
    if (suppressNextTitleBlurCommitRef.current) {
      suppressNextTitleBlurCommitRef.current = false;
      return;
    }
    if (conversation.isDefault) {
      commitTitle();
      return;
    }
    if (updateRuntimeConversationTitle.isPending) return;

    const previousTitle = typeof conversation.title === "string" ? conversation.title : null;
    const currentTitle = previousTitle?.trim() ?? "";
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== currentTitle) {
      setEditingRuntimeConversationId(null);
      updateRuntimeConversationTitle.mutate({
        conversationId: conversation.conversationId,
        title: trimmed,
        previousTitle,
      });
    } else {
      setEditingRuntimeConversationId(null);
    }
  }, [commitTitle, editTitle, updateRuntimeConversationTitle]);

  const createRuntimeConversation = useMutation({
    mutationFn: async (input?: {
      title?: string | null;
      conversationId?: string;
      optimisticSnapshot?: RuntimeConversationSnapshot;
      previousConversationId?: string;
    }): Promise<RuntimeConversationSnapshot> => {
      if (sessionArchived) {
        throw new Error("Archived threads must be unarchived before creating a new chat tab");
      }
      if (requiresExplicitResume) {
        throw new Error(MANUAL_RESUME_REQUIRED_REASON);
      }
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/runtime-conversations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: input?.title ?? null,
          creationKind: "blank",
          ...(input?.conversationId ? { conversationId: input.conversationId } : {}),
        }),
      });
      if (!res.ok) {
        throw new Error(await readRuntimeConversationMutationError(res));
      }
      const body = await res.json() as { runtimeConversation?: RuntimeConversationSnapshot };
      if (!body.runtimeConversation?.conversationId) {
        throw new Error("Chat tab create response was missing a conversation id");
      }
      return body.runtimeConversation;
    },
    onMutate: (input) => {
      if (input?.optimisticSnapshot) {
        setOptimisticRuntimeConversation({
          sessionId,
          snapshot: input.optimisticSnapshot,
          creationKind: "blank",
          previousConversationId: input.previousConversationId,
        });
      }
    },
    onSuccess: (conversation, input) => {
      setOptimisticRuntimeConversation((current) => {
        if (!current) return null;
        if (!input?.conversationId || current.snapshot.conversationId === input.conversationId) return null;
        return current;
      });
      queryClient.setQueryData<RuntimeConversationSnapshot[]>(
        ["runtime-conversations", sessionId],
        (existing) => {
          const current = existing ?? runtimeConversations;
          if (current.some((item) => item.conversationId === conversation.conversationId)) {
            return current.map((item) => item.conversationId === conversation.conversationId ? conversation : item);
          }
          return [...current, conversation];
        },
      );
      queryClient.setQueryData<RuntimeConversationSnapshot[]>(
        ["runtime-conversations", sessionId, "include-archived"],
        (existing) => {
          const current = existing ?? allRuntimeConversations;
          const withoutDuplicate = current.filter((item) => item.conversationId !== conversation.conversationId);
          return [...withoutDuplicate, conversation];
        },
      );
      void navigate({
        to: "/$repoId/$sessionId",
        params: { repoId, sessionId },
        search: { conversationId: conversation.isDefault ? undefined : conversation.conversationId },
        replace: true,
      });
    },
    onError: (error, input) => {
      const failedConversationId = input?.conversationId ?? null;
      setOptimisticRuntimeConversation((current) => {
        if (!current) return null;
        if (!failedConversationId || current.snapshot.conversationId === failedConversationId) return null;
        return current;
      });
      if (failedConversationId && activeConversationIdRef.current === failedConversationId) {
        const previousConversationId = input?.previousConversationId && input.previousConversationId !== "default"
          ? input.previousConversationId
          : undefined;
        void navigate({
          to: "/$repoId/$sessionId",
          params: { repoId, sessionId },
          search: { conversationId: previousConversationId },
          replace: true,
        });
      }
      toast.error(error instanceof Error ? error.message : "Failed to create chat tab");
    },
  });

  const createBlankRuntimeConversationOptimistically = useCallback(() => {
    if (sessionArchived || createRuntimeConversation.isPending) return;
    const conversationId = createClientRuntimeConversationId();
    const optimisticSnapshot = buildOptimisticRuntimeConversationSnapshot({
      conversationId,
      agentType: activeConversation?.agentType ?? defaultRuntimeConversation.agentType,
      timestamp: Date.now(),
    });
    setOptimisticRuntimeConversation({
      sessionId,
      snapshot: optimisticSnapshot,
      creationKind: "blank",
      previousConversationId: activeConversationId,
    });
    void navigate({
      to: "/$repoId/$sessionId",
      params: { repoId, sessionId },
      search: { conversationId },
      replace: true,
    });
    createRuntimeConversation.mutate({
      title: null,
      conversationId,
      optimisticSnapshot,
      previousConversationId: activeConversationId,
    });
  }, [
    activeConversation?.agentType,
    activeConversationId,
    createRuntimeConversation,
    defaultRuntimeConversation.agentType,
    navigate,
    repoId,
    sessionArchived,
    sessionId,
  ]);
  const visibleRuntimeConversations = useMemo(() => {
    if (!runtimeConversationTabsAvailable || !optimisticCreatingRuntimeConversation) {
      return runtimeConversations;
    }
    if (runtimeConversations.some((conversation) => conversation.conversationId === optimisticCreatingRuntimeConversation.conversationId)) {
      return runtimeConversations;
    }
    return [...runtimeConversations, optimisticCreatingRuntimeConversation];
  }, [optimisticCreatingRuntimeConversation, runtimeConversationTabsAvailable, runtimeConversations]);
  const visibleAllRuntimeConversations = useMemo(() => {
    if (!runtimeConversationTabsAvailable || !optimisticCreatingRuntimeConversation) {
      return allRuntimeConversations;
    }
    if (allRuntimeConversations.some((conversation) => conversation.conversationId === optimisticCreatingRuntimeConversation.conversationId)) {
      return allRuntimeConversations;
    }
    return [...allRuntimeConversations, optimisticCreatingRuntimeConversation];
  }, [allRuntimeConversations, optimisticCreatingRuntimeConversation, runtimeConversationTabsAvailable]);
  const createLocalForkRuntimeConversation = useMutation({
    mutationFn: async (input: {
      conversationId?: string
      sourceConversationId: string
      title: string
      messageID?: string
    }): Promise<RuntimeConversationSnapshot> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/runtime-conversations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: input.title,
          creationKind: "fork",
          ...(input.conversationId ? { conversationId: input.conversationId } : {}),
          sourceConversationId: input.sourceConversationId,
          ...(input.messageID ? { messageID: input.messageID } : {}),
        }),
      });
      if (!res.ok) {
        throw new Error(await readRuntimeConversationMutationError(res));
      }
      const body = await res.json() as { runtimeConversation?: RuntimeConversationSnapshot };
      if (!body.runtimeConversation?.conversationId) {
        throw new Error("Fork response was missing a conversation id");
      }
      return body.runtimeConversation;
    },
  });
  const restoreRuntimeConversation = useMutation({
    mutationFn: async (conversationId: string): Promise<RuntimeConversationSnapshot> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(
        `${baseUrl}/api/sessions/${sessionId}/runtime-conversations/${encodeURIComponent(conversationId)}/restore`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      if (!res.ok) {
        throw new Error(await readRuntimeConversationMutationError(res));
      }
      const body = await res.json() as { runtimeConversation?: RuntimeConversationSnapshot | null };
      if (!body.runtimeConversation?.conversationId) {
        throw new Error("Chat session restore response was missing a conversation id");
      }
      return body.runtimeConversation;
    },
    onSuccess: (conversation) => {
      queryClient.setQueryData<RuntimeConversationSnapshot[]>(
        ["runtime-conversations", sessionId],
        (existing) => {
          const current = existing ?? runtimeConversations;
          const withoutDuplicate = current.filter((item) => item.conversationId !== conversation.conversationId);
          return [...withoutDuplicate, conversation];
        },
      );
      queryClient.setQueryData<RuntimeConversationSnapshot[]>(
        ["runtime-conversations", sessionId, "include-archived"],
        (existing) => {
          const current = existing ?? allRuntimeConversations;
          return current.map((item) => item.conversationId === conversation.conversationId ? conversation : item);
        },
      );
      useRuntimeConversationActivity.getState().applySnapshots([{
        sessionId,
        conversationId: conversation.conversationId,
        agentType: conversation.agentType,
        providerSessionId: conversation.providerSessionId,
        title: conversation.title,
        isDefault: conversation.isDefault,
        statusKind: conversation.statusKind,
        activityState: conversation.activityState ?? null,
        activityEventSequence: conversation.activityEventSequence ?? null,
        lastActivityEventId: conversation.lastActivityEventId ?? null,
        lastFinishedEventId: conversation.lastFinishedEventId ?? null,
        lastFinishedAt: conversation.lastFinishedAt ?? null,
        lastFinishReason: conversation.lastFinishReason ?? null,
        activityObservedAt: conversation.activityObservedAt ?? null,
        createdAt: conversation.createdAt,
        updatedAt: conversation.updatedAt,
        archivedAt: conversation.archivedAt,
        timestamp: Date.now(),
      }]);
      void navigate({
        to: "/$repoId/$sessionId",
        params: { repoId, sessionId },
        search: { conversationId: conversation.isDefault ? undefined : conversation.conversationId },
        replace: true,
      });
      void queryClient.invalidateQueries({ queryKey: ["runtime-conversations", sessionId, "include-archived"] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to restore chat session");
    },
  });
  const closeRuntimeConversation = useMutation({
    mutationFn: async (input: { conversation: RuntimeConversationSnapshot; showUndo?: boolean; stopBeforeClose?: boolean }): Promise<RuntimeConversationSnapshot> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const stopQuery = input.stopBeforeClose ? "?stop=true" : "";
      const res = await fetch(
        `${baseUrl}/api/sessions/${sessionId}/runtime-conversations/${encodeURIComponent(input.conversation.conversationId)}${stopQuery}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      if (!res.ok) {
        throw new Error(await readRuntimeConversationMutationError(res));
      }
      const body = await res.json().catch(() => ({})) as { runtimeConversation?: RuntimeConversationSnapshot };
      return body.runtimeConversation ?? { ...input.conversation, archivedAt: Date.now(), updatedAt: Date.now() };
    },
    onMutate: async (input) => {
      const conversationId = input.conversation.conversationId;
      const runtimeConversationsQueryKey = ["runtime-conversations", sessionId];
      const allRuntimeConversationsQueryKey = ["runtime-conversations", sessionId, "include-archived"];
      const previousRuntimeConversations = queryClient.getQueryData<RuntimeConversationSnapshot[]>(runtimeConversationsQueryKey);
      const previousAllRuntimeConversations = queryClient.getQueryData<RuntimeConversationSnapshot[]>(allRuntimeConversationsQueryKey);
      const previousActivitySession = useRuntimeConversationActivity.getState().bySessionId[sessionId] ?? null;
      const activeWasClosed = conversationId === activeConversationId;

      await Promise.all([
        queryClient.cancelQueries({ queryKey: runtimeConversationsQueryKey }),
        queryClient.cancelQueries({ queryKey: allRuntimeConversationsQueryKey }),
      ]);

      const archivedAt = Date.now();
      const closedConversationSnapshot: RuntimeConversationSnapshot = {
        ...input.conversation,
        archivedAt,
        updatedAt: Math.max(input.conversation.updatedAt, archivedAt),
      };
      const nextRuntimeConversations = runtimeConversations.filter((item) => item.conversationId !== conversationId);

      queryClient.setQueryData<RuntimeConversationSnapshot[]>(
        runtimeConversationsQueryKey,
        (existing) => (existing ?? runtimeConversations).filter((item) => item.conversationId !== conversationId),
      );
      queryClient.setQueryData<RuntimeConversationSnapshot[]>(
        allRuntimeConversationsQueryKey,
        (existing) => {
          const current = existing ?? allRuntimeConversations;
          let foundConversation = false;
          const next = current.map((item) => {
            if (item.conversationId !== conversationId) return item;
            foundConversation = true;
            return closedConversationSnapshot;
          });
          return foundConversation ? next : [...next, closedConversationSnapshot];
        },
      );
      useRuntimeConversationActivity.getState().replaceSessionSnapshots({
        sessionId,
        force: true,
        snapshots: nextRuntimeConversations.map((conversation) => toRuntimeConversationActivitySnapshot({
          sessionId,
          conversation,
          timestamp: archivedAt,
        })),
      });
      setRuntimeConversationPendingClose(null);
      if (activeWasClosed) {
        void navigate({
          to: "/$repoId/$sessionId",
          params: { repoId, sessionId },
          search: { conversationId: undefined },
          replace: true,
        });
      }

      return {
        conversationId,
        activeWasClosed,
        previousRuntimeConversations,
        previousAllRuntimeConversations,
        previousActivitySession,
      };
    },
    onSuccess: (closedConversation, input) => {
      const conversationId = input.conversation.conversationId;
      queryClient.setQueryData<RuntimeConversationSnapshot[]>(
        ["runtime-conversations", sessionId],
        (existing) => existing?.filter((item) => item.conversationId !== conversationId),
      );
      queryClient.setQueryData<RuntimeConversationSnapshot[]>(
        ["runtime-conversations", sessionId, "include-archived"],
        (existing) => {
          const current = existing ?? allRuntimeConversations;
          let foundConversation = false;
          const next = current.map((item) => {
            if (item.conversationId !== conversationId) return item;
            foundConversation = true;
            return closedConversation;
          });
          return foundConversation ? next : [...next, closedConversation];
        },
      );
      const currentActivitySession = useRuntimeConversationActivity.getState().bySessionId[sessionId] ?? {};
      useRuntimeConversationActivity.getState().replaceSessionSnapshots({
        sessionId,
        force: true,
        snapshots: Object.values(currentActivitySession).filter((conversation) => conversation.conversationId !== conversationId),
      });
      if (input.showUndo !== false) {
        const label = input.conversation.title?.trim() || "Chat session";
        toast.success(`${label} closed`, {
          duration: RUNTIME_CONVERSATION_CLOSE_UNDO_TOAST_DURATION_MS,
          action: {
            label: "Undo",
            onClick: () => restoreRuntimeConversation.mutate(conversationId),
          },
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["runtime-conversations", sessionId, "include-archived"] });
    },
    onError: (error, _input, context) => {
      if (context) {
        queryClient.setQueryData(["runtime-conversations", sessionId], context.previousRuntimeConversations);
        queryClient.setQueryData(["runtime-conversations", sessionId, "include-archived"], context.previousAllRuntimeConversations);
        useRuntimeConversationActivity.getState().replaceSessionSnapshots({
          sessionId,
          force: true,
          snapshots: Object.values(context.previousActivitySession ?? {}),
        });
        if (context.activeWasClosed && activeConversationIdRef.current === "default") {
          void navigate({
            to: "/$repoId/$sessionId",
            params: { repoId, sessionId },
            search: { conversationId: context.conversationId },
            replace: true,
          });
        }
      }
      toast.error(error instanceof Error ? error.message : "Failed to close chat session");
    },
  });
  startNewWorkGitPendingRef.current = startNewWorkGitPending;
  const startNewWorkWaitingForUnarchive = localStartNewWorkWaitingForUnarchive !== null;
  const startNewWorkPreparationPending =
    startNewWorkWaitingForUnarchive ||
    startNewWork.isPending ||
    localStartNewWorkGitPending !== null ||
    startNewWorkGitPending;
  const startNewWorkPreparationActive =
    mergedPrNeedsContinuation ||
    startNewWorkWaitingForUnarchive ||
    localStartNewWorkGitPending !== null ||
    startNewWorkGitPending;
  const mergedThreadContinuationState = deriveMergedThreadContinuationState({
    mergedPr: mergedPrNeedsContinuation,
    sessionArchived,
    unarchivePending: unarchiveSession.isPending,
    startNewWorkWaitingForUnarchive,
    startNewWorkPreparationPending:
      startNewWork.isPending || localStartNewWorkGitPending !== null || startNewWorkGitPending,
  });
  const displaySessionBranch = localStartNewWorkGitPending?.newBranch ?? sessionBranch;
  const displaySessionBaseBranch = localStartNewWorkGitPending?.baseBranch ?? sessionBaseBranch;
  const displayBranchTreeUrl =
    displaySessionBranch &&
    repoProvider === "github" &&
    repo?.githubFullName &&
    localStartNewWorkGitPending === null
      ? `https://github.com/${repo.githubFullName}/tree/${encodeURIComponent(displaySessionBranch)}`
      : null;

  useEffect(() => {
    const pendingState = localStartNewWorkGitPendingRef.current;
    if (!pendingState) {
      return;
    }
    if (startNewWorkGitPending) {
      return;
    }
    if (sessionBranch !== pendingState.newBranch) {
      return;
    }
    clearLocalStartNewWorkGitPending();
  }, [clearLocalStartNewWorkGitPending, sessionBranch, startNewWorkGitPending]);
  const runtimeTimelineItems = useAgentRuntime((state) => state.snapshot?.timeline ?? null);
  // Zustand selectors must return stable references. Derive filtered arrays in
  // component memoization instead of manufacturing a fresh snapshot per render.
  const renderedAttachedPullRequests = useMemo(() => {
    return runtimeTimelineItems?.flatMap((item) => {
      if (item.kind !== "user_message" || !item.attachedPullRequest) {
        return [];
      }
      return [item.attachedPullRequest];
    }) ?? [];
  }, [runtimeTimelineItems]);
  const hasObservedAttachedPullRequestInConversation = useCallback((attachedPullRequest: AgentRuntimeAttachedPullRequest | null) => {
    return hasObservedAttachedPullRequest({
      attachedPullRequest,
      queuedMessages,
      renderedAttachedPullRequests,
    });
  }, [queuedMessages, renderedAttachedPullRequests]);
  useEffect(() => {
    if (!shouldRefreshSessionSurfaceOnMount) {
      return;
    }

    let cancelled = false;

    void Promise.allSettled([
      queryClient.invalidateQueries({ queryKey: ["session", sessionId], exact: true }),
      queryClient.invalidateQueries({
        queryKey: getPrStatusQueryKey({ sessionId }),
        exact: true,
      }),
      queryClient.invalidateQueries({
        queryKey: getGitStatusQueryKey({ sessionId }),
        exact: true,
      }),
      queryClient.invalidateQueries({
        queryKey: getGitLogQueryKey({ sessionId }),
        exact: true,
      }),
      invalidateSessionRepositoryFileQueries(queryClient, sessionId),
    ]).finally(() => {
      if (cancelled) {
        return;
      }

      getThreadSyncManager().recordSessionSurfaceRefresh(sessionId);
    });

    return () => {
      cancelled = true;
    };
  }, [queryClient, sessionId, shouldRefreshSessionSurfaceOnMount]);
  const connectionFlowQuery = useQuery({
    queryKey: ["session-connection-flow", sessionId],
    enabled: showThreadActionsMenu && showConnectionFlow && canViewConnectionFlow,
    queryFn: async () => {
      const [runtimeSnapshot, openCodeAccess, connectionFlowLinkConfig] = await Promise.all([
        fetchSessionRuntimeSnapshot(),
        fetchOpenCodeAccessSnapshot(),
        fetchConnectionFlowLinkConfig(),
      ]);
      return {
        runtimeSnapshot,
        openCodeAccess,
        connectionFlowLinkConfig,
        fetchedAt: Date.now(),
      };
    },
    staleTime: CONNECTION_FLOW_STALE_MS,
    refetchOnWindowFocus: false,
    refetchInterval: showThreadActionsMenu && showConnectionFlow && canViewConnectionFlow ? CONNECTION_FLOW_REFETCH_INTERVAL_MS : false,
  });
  const recoveryRuntimeAccessQuery = useQuery({
    queryKey: ["session-recovery-runtime-access", sessionId],
    enabled: showRecoverySshDialog && canOpenRecoverySshDialog,
    queryFn: fetchOpenCodeAccessSnapshot,
    staleTime: RECOVERY_RUNTIME_ACCESS_STALE_MS,
    refetchOnWindowFocus: false,
  });
  const recoveryStatusQuery = useQuery({
    queryKey: ["session-recovery-status", sessionId],
    enabled: showRecoverySshDialog && canOpenRecoverySshDialog,
    queryFn: fetchRecoveryStatusSnapshot,
    staleTime: RECOVERY_RUNTIME_ACCESS_STALE_MS,
    refetchOnWindowFocus: false,
  });
  const runtimeSnapshot = connectionFlowQuery.data?.runtimeSnapshot ?? null;
  const openCodeAccessSnapshot = recoveryRuntimeAccessQuery.data ?? connectionFlowQuery.data?.openCodeAccess ?? null;
  const connectionFlowLinkConfig = connectionFlowQuery.data?.connectionFlowLinkConfig ?? null;
  const runtimeHistoryDebug = runtimeSnapshot?.history ?? null;
  const runtimeReplicationDebug = runtimeSnapshot?.replication ?? null;
  const connectionFlowRuntimeLifecycle = runtimeSnapshot?.runtimeLifecycle ?? sessionRuntimeLifecycle;
  const runtimeDoSessionId = runtimeSnapshot?.doSessionId ?? null;
  const runtimeDoSandboxId = runtimeSnapshot?.doSandboxId ?? null;
  const runtimeDoOpenCodeSessionId = runtimeSnapshot?.doOpenCodeSessionId ?? null;
  const sandboxDashboardUrl = resolveSandboxDashboardUrl({
    runtimeSnapshot,
    connectionFlowLinkConfig,
    sessionFallbackDashboardUrl: dbSandboxDashboardUrl,
  });
  const cloudflareAccountId =
    asNonEmptyString(connectionFlowLinkConfig?.cloudflareAccountId);
  const cloudflareSessionDoNamespaceId =
    asNonEmptyString(connectionFlowLinkConfig?.cloudflareSessionDoNamespaceId);
  const connectionFlowNeonTablesUrl =
    asNonEmptyString(connectionFlowLinkConfig?.neonTablesUrl)
    ?? asNonEmptyString(CONNECTION_FLOW_NEON_TABLES_URL)
    ?? CONNECTION_FLOW_NEON_FALLBACK_URL;
  const cloudflareDurableObjectUrl = buildConnectionFlowCloudflareDoUrl(dbDoId, {
    accountId: cloudflareAccountId,
    namespaceId: cloudflareSessionDoNamespaceId,
  });
  const sandboxIdMismatch =
    Boolean(dbSandboxId) &&
    Boolean(runtimeDoSandboxId) &&
    dbSandboxId !== runtimeDoSandboxId;
  const doSessionMismatch =
    Boolean(runtimeDoSessionId) &&
    runtimeDoSessionId !== sessionId;
  useEffect(() => {
    setBareImageBannerDismissed(false);
  }, [bootSource, sessionId, sessionRuntimeGeneration, sessionSandboxId]);

  // ---------------------------------------------------------------------------
  // Build status for bare-image banner
  // ---------------------------------------------------------------------------
  // When the session started from the bare base image, fetch the builds list
  // so the banner can show live build progress (e.g. "Build queued…").
  // Polls while a build is pending/building, stops once resolved.
  // Uses the same query key as the settings page BuildStatusTab so the cache
  // is shared — navigating between session and settings won't refetch.
  const bareImageBuildsQuery = useQuery({
    queryKey: ["builds", repoId],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/builds?repoId=${repoId}`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`Failed to fetch builds (${res.status})`);
      return res.json() as Promise<{
        builds: Array<{
          id: string;
          status: "pending" | "building" | "ready" | "failed" | "cancelled" | "superseded";
          buildPhase?: string | null;
          statusMessage?: string | null;
          createdAt: string;
        }>;
        buildHealth?: {
          status: "failing" | "blocked";
          consecutiveFailures: number;
          lastFailureMessage: string | null;
          lastFailurePhase: string | null;
          lastFailedAt: string | null;
          blockedAt: string | null;
        } | null;
      }>;
    },
    enabled: !isRepolessRoute && bootSource === "bare" && !bareImageBannerDismissed,
    refetchInterval: (query) => {
      const builds = query.state.data?.builds;
      const hasInFlight = builds?.some(
        (b) => b.status === "pending" || b.status === "building"
      );
      return hasInFlight ? BARE_BANNER_BUILD_POLL_MS : false;
    },
  });
  useInvalidateReposQueryWhenBuildsSettle({
    builds: bareImageBuildsQuery.data?.builds,
    enabled: !isRepolessRoute && bootSource === "bare" && !bareImageBannerDismissed,
  });

  // Build-settings lookup drives the bare-image banner variant selection: the
  // banner shows a "set up your dev environment" prompt when no meaningful
  // build/runtime config is saved, and a build-status message when one is.
  // This query shares the same cache key as the settings page so navigating
  // between the two does not refetch. Only runs for bare sessions — no other
  // caller of this page needs the data.
  const bareImageBuildSettingsQuery = useQuery({
    queryKey: ["build-settings", repoId],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/repos/${repoId}/build-settings`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`Failed to fetch build settings (${res.status})`);
      return res.json() as Promise<{
        rootDirectory: string | null;
        installCommand: string | null;
        buildCommand: string | null;
        buildHooks: string[];
        runtimeProcesses: unknown[];
      }>;
    },
    enabled: !isRepolessRoute && bootSource === "bare" && !bareImageBannerDismissed,
  });
  const bareImageHasBuildConfig = useMemo(() => {
    const data = bareImageBuildSettingsQuery.data;
    if (!data) {
      // Assume config exists while loading — prevents a flash of the stronger
      // "set up your dev environment" copy before we know the real state.
      return true;
    }
    const rootDirectory = data.rootDirectory?.trim() ?? "";
    const install = data.installCommand?.trim() ?? "";
    const build = data.buildCommand?.trim() ?? "";
    const hooks = Array.isArray(data.buildHooks) ? data.buildHooks : [];
    const runtimeProcesses = Array.isArray(data.runtimeProcesses) ? data.runtimeProcesses : [];
    const hasRunnableRuntimeProcess = runtimeProcesses.some((process) => {
      if (!process || typeof process !== "object") return false;
      const row = process as {
        command?: unknown;
        runner?: { kind?: unknown; files?: unknown };
      };
      if (row.runner?.kind === "docker_compose") {
        return Array.isArray(row.runner.files) && row.runner.files.length > 0;
      }
      return typeof row.command === "string" && row.command.trim().length > 0;
    });
    return (
      rootDirectory.length > 0 ||
      install.length > 0 ||
      build.length > 0 ||
      hooks.length > 0 ||
      hasRunnableRuntimeProcess
    );
  }, [bareImageBuildSettingsQuery.data]);

  /**
   * Derive a simple summary of the most relevant in-flight or latest build
   * to show in the bare-image banner. null when data is not yet loaded.
   */
  const bareImageBuildSummary = useMemo(() => {
    const builds = bareImageBuildsQuery.data?.builds;
    if (!builds) return null;
    // Find the first in-flight build (pending or building)
    const inFlight = builds.find(
      (b) => b.status === "pending" || b.status === "building"
    );
    if (inFlight) {
      return {
        status: inFlight.status as "pending" | "building",
        phase: inFlight.buildPhase ?? null,
        buildId: inFlight.id,
      };
    }
    // If no in-flight build, check if there's a ready build
    const ready = builds.find((b) => b.status === "ready");
    if (ready) {
      return { status: "ready" as const, phase: null, buildId: ready.id };
    }
    // If latest build failed
    const failed = builds.find((b) => b.status === "failed");
    if (failed) {
      return { status: "failed" as const, phase: null, buildId: failed.id };
    }
    return null;
  }, [bareImageBuildsQuery.data]);

  /**
   * Build health circuit breaker state — non-null when the backend has
   * detected repeated deterministic failures for the current build scope.
   * Used to show enhanced banners explaining why sessions are bare/stale.
   */
  const buildHealthData = useMemo(() => {
    return bareImageBuildsQuery.data?.buildHealth ?? null;
  }, [bareImageBuildsQuery.data]);

  // For prebuilt sessions, fetch health data to show stale-build warnings.
  // Only queries when boot was prebuilt and the bare builds query didn't run.
  const [staleBuildBannerDismissed, setStaleBuildBannerDismissed] = useState(false);
  const buildHealthRebuildMutation = useMutation({
    mutationFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/builds/repo/${repoId}/rebuild`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        let message = `Failed to queue rebuild (${res.status})`;
        try {
          const body = await res.json() as { error?: unknown };
          if (typeof body.error === "string" && body.error.trim().length > 0) {
            message = body.error;
          }
        } catch {
          // Keep the status-based fallback when the server does not return JSON.
        }
        throw new Error(message);
      }
      return res.json() as Promise<{ id: string; status: string }>;
    },
    onSuccess: () => {
      setBareImageBannerDismissed(false);
      setStaleBuildBannerDismissed(false);
      void queryClient.invalidateQueries({ queryKey: ["builds", repoId] });
      toast.success("Build queued");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to queue rebuild");
    },
  });
  const prebuiltHealthQuery = useQuery({
    queryKey: ["builds", repoId],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/builds?repoId=${repoId}`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`Failed to fetch builds (${res.status})`);
      return res.json() as Promise<{
        builds: Array<{ id: string; status: string }>;
        buildHealth?: {
          status: "failing" | "blocked";
          consecutiveFailures: number;
          lastFailureMessage: string | null;
          lastFailurePhase: string | null;
          lastFailedAt: string | null;
          blockedAt: string | null;
        } | null;
      }>;
    },
    enabled: !isRepolessRoute && bootSource === "prebuilt" && !staleBuildBannerDismissed,
    // Only fetch once — stale build warnings are not time-critical
    staleTime: PREBUILT_BUILD_HEALTH_STALE_MS,
  });
  const prebuiltBuildHealthData = useMemo(() => {
    return prebuiltHealthQuery.data?.buildHealth ?? null;
  }, [prebuiltHealthQuery.data]);

  useEffect(() => {
    if (wsSessionModelIntent === undefined) {
      return;
    }
    const intentConversationId = wsSessionModelIntentConversationId ?? "default";
    if (intentConversationId !== "default" && intentConversationId !== activeConversationId) {
      return;
    }
    if (shouldIgnoreStaleSessionModelIntentFromServer({
      sessionId,
      conversationId: intentConversationId,
      sessionModelIntent: wsSessionModelIntent,
    })) {
      return;
    }

    applyStoredSessionIntentToLocalStorage(sessionId, wsSessionModelIntent, intentConversationId);
    queryClient.setQueryData(["session", sessionId], (current: unknown) => {
      if (!current || typeof current !== "object") {
        return current;
      }

      return {
        ...(current as Record<string, unknown>),
        sessionModelIntent: wsSessionModelIntent,
        sessionModelIntentConversationId: intentConversationId,
      };
    });
  }, [activeConversationId, queryClient, sessionId, wsSessionModelIntent, wsSessionModelIntentConversationId]);
  sandboxStatusRef.current = sandboxStatus;
  const transportUnavailableInActiveTab =
    transportConnectEnabled && (status !== "connected" || wsDegradedSinceAt !== null)
  // During start-new-work background git ops, the DO holds the branch switch
  // lock (branchSwitchInProgress=true) but we want the UI to remain interactive.
  // Messages sent during this time are queued (not rejected) in the DO, so
  // the composer should stay enabled. We only treat branchSwitchInProgress as
  // blocking when it is NOT a start-new-work background operation.
  const effectiveBranchSwitchInProgress =
    (branchSwitchInProgress && !startNewWorkGitPending) ||
    (transportUnavailableInActiveTab
      && sessionBranchSwitchInProgress
      && !startNewWorkGitPending);
  const transportUnavailableForInteractiveActions =
    transportStandbyInAnotherTab || transportUnavailableInActiveTab
  const transportUnavailableForComposerSend = transportStandbyInAnotherTab
  const transportInteractiveActionUnavailableReason = transportStandbyInAnotherTab
    ? "Another focused tab currently owns the live session connection. Focus this tab to take over before sending session actions."
    : "Session transport is reconnecting; requests may be delayed."
  /**
   * Healthy sandbox auth state rarely changes between remounts. Cache it briefly
   * so session switches do not immediately retrigger the check, while keeping the
   * unhealthy path on the existing repair polling loop.
   */
  const SANDBOX_AUTH_HEALTH_STALE_TIME_MS = 30 * 1000;
  // branchSwitchPending gates interactions that truly cannot happen during
  // a branch switch (e.g. git operations, sandbox mutations).
  // PR adoption is intentionally excluded: messages can queue during adoption.
  const branchSwitchPending =
    effectiveBranchSwitchInProgress ||
    continuationAdoptionPending ||
    startNewWorkPreparationPending;
  const archiveTogglePending = archiveSession.isPending || unarchiveSession.isPending;
  const archiveToggleDisabled =
    !canManageSession ||
    deleteSession.isPending ||
    archiveTogglePending ||
    branchSwitchPending ||
    resumeSession.isPending ||
    suspendSession.isPending;
  const runtimeInteractionEnabled = !sessionArchived && sandboxStatus === "running";
  const effectivePtyUrl = sessionArchived ? null : ptyUrl;
  const effectiveVscodeUrl = sessionArchived ? null : vscodeUrl;
  const effectiveBasePreviewUrl = sessionArchived ? null : previewUrl;
  const effectivePreviewEndpoints = sessionArchived ? [] : previewEndpoints;
  const effectiveRuntimeProcesses = sessionArchived ? [] : runtimeProcesses;
  const effectiveRuntimeProcessesConfigured = sessionArchived ? false : runtimeProcessesConfigured;
  const previewFrameEnabled = shouldEnableSessionPreviewFrame({
    sessionArchived,
    shouldKeepPreviewWarm,
    autoLoadPreviewIframes: userPreferences.preview.autoLoadIframes,
    sandboxStatus,
    previewUrl: effectiveBasePreviewUrl,
  });

  const sandboxAuthHealth = useQuery({
    queryKey: ["sandbox-auth-health", sessionId, sessionSandboxId, sessionRuntimeGeneration],
    enabled: canManageSession && runtimeInteractionEnabled && !branchSwitchPending,
    staleTime: SANDBOX_AUTH_HEALTH_STALE_TIME_MS,
    refetchInterval: (query) => {
      const data = query.state.data as SandboxAuthHealthResponse | undefined;
      return data?.authReady === false ? SANDBOX_AUTH_HEALTH_REFETCH_INTERVAL_MS : false;
    },
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<SandboxAuthHealthResponse> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/sandbox-auth-health`, {
        method: "GET",
        credentials: "include",
      });
      const rawBody = await res.json().catch(() => null) as unknown;
      if (!res.ok) {
        throw buildSessionRequestError({
          fallbackMessage: "Failed to inspect sandbox auth health",
          responseBody: rawBody,
          status: res.status,
        });
      }
      return normalizeSandboxAuthHealthResponse(rawBody);
    },
  });

  const repairSandboxAuth = useMutation({
    mutationFn: async (): Promise<SandboxAuthHealthResponse> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/repair-sandbox-auth`, {
        method: "POST",
        credentials: "include",
      });
      const rawBody = await res.json().catch(() => null) as unknown;
      if (!res.ok) {
        throw buildSessionRequestError({
          fallbackMessage: "Failed to repair sandbox auth",
          responseBody: rawBody,
          status: res.status,
        });
      }
      return normalizeSandboxAuthHealthResponse(rawBody);
    },
    onSuccess: async () => {
      await sandboxAuthHealth.refetch();
      toast.success("Sandbox auth repaired", {
        description: "Git and GitHub credentials were reloaded without recreating the sandbox.",
      });
    },
    onError: (error) => {
      const requestError = error instanceof Error ? error as SessionRequestError : null;
      const friendlyMessage = requestError?.code === "SANDBOX_AUTH_BROKER_SANDBOX_STOPPED"
        ? "The sandbox is stopped. Resume or retry the sandbox instead of repairing auth."
        : requestError?.code === "SANDBOX_AUTH_BROKER_SANDBOX_UNROUTABLE"
          ? "The sandbox is unreachable right now. Retry or resume the sandbox to recover."
          : requestError?.message ?? "Failed to repair sandbox auth";
      toast.error(friendlyMessage);
      if (
        requestError?.code === "SANDBOX_AUTH_BROKER_SANDBOX_STOPPED" ||
        requestError?.code === "SANDBOX_AUTH_BROKER_SANDBOX_UNROUTABLE"
      ) {
        void sandboxAuthHealth.refetch();
      }
    },
  });

  const sandboxAuthNeedsRepair =
    runtimeInteractionEnabled &&
    sandboxAuthHealth.data?.authReady === false;
  const sandboxAuthHealthError =
    sandboxAuthHealth.error instanceof Error
      ? sandboxAuthHealth.error as SessionRequestError
      : null;
  const sandboxAuthErrorCode =
    sandboxAuthHealth.data?.errorCode ?? sandboxAuthHealthError?.code ?? null;
  const sandboxAuthProviderUnavailable =
    sandboxAuthErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_STOPPED" ||
    sandboxAuthErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_UNROUTABLE";
  const sandboxAuthBannerVisible =
    runtimeInteractionEnabled &&
    canManageSession &&
    !branchSwitchPending &&
    (sandboxAuthNeedsRepair || sandboxAuthHealth.isError);
  const sandboxAuthBannerCanRepair =
    runtimeInteractionEnabled &&
    !branchSwitchPending &&
    !sandboxAuthProviderUnavailable &&
    sandboxAuthHealthError?.code !== "SANDBOX_AUTH_BROKER_SANDBOX_UNAVAILABLE" &&
    sandboxAuthHealthError?.code !== "SANDBOX_AUTH_BROKER_TARGET_MISSING";

  const restoreRejectedQueuedMessageToComposer = useCallback((input: NonNullable<MessageRejectionEvent["recovery"]>) => {
    const existingText = editorRef.current?.getText() ?? draftTextRef.current;
    const mergedText = (() => {
      if (existingText.trim().length === 0) return input.text;
      if (input.text.trim().length === 0) return existingText;
      return `${existingText.replace(/\s+$/, "")}\n\n${input.text}`;
    })();

    const existingContextRefs = draftSessionContextRefsRef.current;
    const mergedContextRefs = dedupeSessionContextRefs([
      ...existingContextRefs,
      ...(input.contextRefs ?? []),
    ]);
    const mergedRequestedSkillRefs = dedupeRequestedSkillRefs([
      ...draftRequestedSkillRefsRef.current,
      ...(input.requestedSkillRefs ?? []),
    ]);
    const mergedPreferredMcpServerRefs = dedupePreferredMcpServerRefs([
      ...draftPreferredMcpServerRefsRef.current,
      ...(input.preferredMcpServerRefs ?? []),
    ]);
    const nextAttachedPullRequest = input.attachedPullRequest ?? draftAttachedPullRequestRef.current;
    const nextAttachedPullRequestKey = nextAttachedPullRequest
      ? buildPrAdoptionSuccessAttachmentKey({ attachedPullRequest: nextAttachedPullRequest })
      : null;
    const pendingObservedAttachedPullRequestKey = pendingAttachedPullRequestObservation
      ? buildPrAdoptionSuccessAttachmentKey({
          attachedPullRequest: pendingAttachedPullRequestObservation.attachedPullRequest,
        })
      : null;
    const attachmentDrafts = buildRuntimeUserMessageDraftAttachments(input.attachments);

    if (
      nextAttachedPullRequestKey !== null
      && nextAttachedPullRequestKey === pendingObservedAttachedPullRequestKey
    ) {
      setPendingAttachedPullRequestObservation(null);
    }

    markLocalDraftInput();
    if (attachmentDrafts.length > 0) {
      restoreAttachments(attachmentDrafts);
    }
    pendingImplicitContextRefsRef.current = [];
    draftHydratedSessionRef.current = null;
    draftSessionContextRefsRef.current = mergedContextRefs;
    draftRequestedSkillRefsRef.current = mergedRequestedSkillRefs;
    draftPreferredMcpServerRefsRef.current = mergedPreferredMcpServerRefs;
    draftAttachedPullRequestRef.current = nextAttachedPullRequest;
    setDraftSessionContextRefs(mergedContextRefs);
    setDraftRequestedSkillRefs(mergedRequestedSkillRefs);
    setDraftPreferredMcpServerRefs(mergedPreferredMcpServerRefs);
    setDraftAttachedPullRequest(nextAttachedPullRequest);
    setPrAdoptionStatus((current) => {
      const nextStatus = nextAttachedPullRequest
        ? maybeBuildVisiblePrAdoptionSuccessStatus({
            attachedPullRequest: nextAttachedPullRequest,
            linkedSessionTitle: findPrLinkedSessionTitle(mergedContextRefs),
          })
        : null;
      return samePrAdoptionStatus(current, nextStatus) ? current : nextStatus;
    });
    editorRef.current?.setStructuredContent({
      text: mergedText,
      sessionContextRefs: mergedContextRefs,
      requestedSkillRefs: mergedRequestedSkillRefs,
      preferredMcpServerRefs: mergedPreferredMcpServerRefs,
    });
    editorRef.current?.focusAtEnd();
    setHasContent(
      mergedText.trim().length > 0
      || mergedContextRefs.length > 0
      || mergedRequestedSkillRefs.length > 0
      || mergedPreferredMcpServerRefs.length > 0
      || attachmentDrafts.length > 0
      || nextAttachedPullRequest !== null,
    );
    draftTextRef.current = mergedText;
  }, [markLocalDraftInput, pendingAttachedPullRequestObservation, restoreAttachments]);

  const sessionActionSonnerIdRef = useRef<string | number | null>(null);
  const seenQueuedSessionCommandNoticeIdsRef = useRef<Set<string>>(new Set());
  const seenStorageMountWarningToastKeysRef = useRef<Set<string>>(new Set());

  const sendSessionSocketAction = useCallback((message: { type: string; [key: string]: unknown }, failureMessage: string) => {
    const accepted = send(message);
    if (!accepted) {
      toast.error(failureMessage);
    }
    return accepted;
  }, [send]);

  const closeRuntimeConversationKeepingRun = useCallback((conversation: RuntimeConversationSnapshot) => {
    closeRuntimeConversation.mutate({ conversation });
    setRuntimeConversationPendingClose(null);
  }, [closeRuntimeConversation]);

  const requestRuntimeConversationClose = useCallback((conversation: RuntimeConversationSnapshot) => {
    const statusSnapshot = selectRuntimeConversationSnapshotForControlState({
      querySnapshot: conversation,
      activitySnapshot: activityStoreConversationsById[conversation.conversationId] ?? null,
    });
    if (runtimeConversationIsRunning(statusSnapshot)) {
      setRuntimeConversationPendingClose(conversation);
      return;
    }
    closeRuntimeConversationKeepingRun(conversation);
  }, [activityStoreConversationsById, closeRuntimeConversationKeepingRun]);

  const stopAndCloseRuntimeConversation = useCallback((conversation: RuntimeConversationSnapshot) => {
    closeRuntimeConversation.mutate({ conversation, stopBeforeClose: true });
    setRuntimeConversationPendingClose(null);
  }, [closeRuntimeConversation]);

  const openRuntimeConversationFromList = useCallback((conversation: RuntimeConversationSnapshot) => {
    if (conversation.archivedAt !== null) {
      restoreRuntimeConversation.mutate(conversation.conversationId);
      return;
    }
    useRuntimeConversationActivity.getState().applySnapshots([
      toRuntimeConversationActivitySnapshot({
        sessionId,
        conversation,
        timestamp: Date.now(),
      }),
    ]);
    useRuntimeConversationActivity.getState().markViewed({
      sessionId,
      conversationId: conversation.conversationId,
    });
    void navigate({
      to: "/$repoId/$sessionId",
      params: { repoId, sessionId },
      search: { conversationId: conversation.isDefault ? undefined : conversation.conversationId },
      replace: true,
    });
  }, [navigate, repoId, restoreRuntimeConversation, sessionId]);

  const visibleSessionActionNotice = useMemo(() => {
    if (!wsSessionActionNotice) return null;
    const targetUserId = typeof wsSessionActionNotice.targetUserId === "string"
      ? wsSessionActionNotice.targetUserId
      : null;
    if (targetUserId && authSession?.user?.id !== targetUserId) return null;
    return runtimeConversationScopedNoticeTargetsActiveConversation({
      conversationId: wsSessionActionNotice.conversationId,
      runtimeConversationTabsAvailable,
      activeConversationId,
    })
      ? wsSessionActionNotice
      : null;
  }, [activeConversationId, authSession?.user?.id, runtimeConversationTabsAvailable, wsSessionActionNotice]);

  useEffect(() => {
    if (visibleSessionActionNotice) {
      setSessionActionToast(visibleSessionActionNotice);
      return;
    }
    setSessionActionToast((current) => {
      if (!current) return current;
      return runtimeConversationScopedNoticeTargetsActiveConversation({
        conversationId: current.conversationId,
        runtimeConversationTabsAvailable,
        activeConversationId,
      })
        ? current
        : null;
    });
  }, [activeConversationId, runtimeConversationTabsAvailable, visibleSessionActionNotice]);

  useEffect(() => {
    seenQueuedSessionCommandNoticeIdsRef.current.clear();
  }, [sessionId]);

  useEffect(() => {
    seenStorageMountWarningToastKeysRef.current.clear();
  }, [sessionId]);

  useEffect(() => {
    for (const progressEntry of sandboxProgress) {
      if (progressEntry.step !== STORAGE_MOUNT_WARNING_STEP) {
        continue;
      }

      const progressTimestamp =
        typeof progressEntry.timestamp === "number" && Number.isFinite(progressEntry.timestamp)
          ? progressEntry.timestamp
          : 0;
      if (Date.now() - progressTimestamp > STORAGE_MOUNT_WARNING_FRESHNESS_WINDOW_MS) {
        continue;
      }

      const dedupeKey = [
        STORAGE_MOUNT_WARNING_STEP,
        String(progressTimestamp),
        progressEntry.message,
      ].join(":");
      if (seenStorageMountWarningToastKeysRef.current.has(dedupeKey)) {
        continue;
      }

      seenStorageMountWarningToastKeysRef.current.add(dedupeKey);
      while (seenStorageMountWarningToastKeysRef.current.size > STORAGE_MOUNT_WARNING_DEDUPE_LIMIT) {
        const oldestSeenWarningKey = seenStorageMountWarningToastKeysRef.current.values().next().value;
        if (typeof oldestSeenWarningKey !== "string") {
          break;
        }
        seenStorageMountWarningToastKeysRef.current.delete(oldestSeenWarningKey);
      }

      const warningMessage = typeof progressEntry.message === "string" && progressEntry.message.trim().length > 0
        ? progressEntry.message
        : "Persistent storage mounts were skipped for this sandbox startup.";
      toast.error(warningMessage);
    }
  }, [sandboxProgress]);

  useEffect(() => {
    if (!wsQueuedSessionCommandNotice) return;
    if (seenQueuedSessionCommandNoticeIdsRef.current.has(wsQueuedSessionCommandNotice.id)) {
      return;
    }
    seenQueuedSessionCommandNoticeIdsRef.current.add(wsQueuedSessionCommandNotice.id);
    while (
      seenQueuedSessionCommandNoticeIdsRef.current.size >
      QUEUED_SESSION_COMMAND_NOTICE_DEDUPE_LIMIT
    ) {
      const oldestSeenNoticeId = seenQueuedSessionCommandNoticeIdsRef.current.values().next().value;
      if (typeof oldestSeenNoticeId !== "string") {
        break;
      }
      seenQueuedSessionCommandNoticeIdsRef.current.delete(oldestSeenNoticeId);
    }
    const elapsedMs = Date.now() - wsQueuedSessionCommandNotice.timestamp;
    if (elapsedMs > QUEUED_SESSION_COMMAND_NOTICE_FRESHNESS_WINDOW_MS) {
      return;
    }
    if (wsQueuedSessionCommandNotice.outcome === "rejected") {
      setPendingSessionActions((prev) => prev.filter((action) => action.clientActionId !== wsQueuedSessionCommandNotice.clientActionId));
      toast.error(wsQueuedSessionCommandNotice.message);
      return;
    }

    setPendingSessionActions((prev) => prev.map((action) => {
      if (action.clientActionId !== wsQueuedSessionCommandNotice.clientActionId || !action.queuedMessage) {
        return action;
      }
      action.cancelScheduledRemoval?.();
      return {
        ...action,
        queuedMessage: {
          ...action.queuedMessage,
          status: "submit_uncertain",
          source: "local",
          serverObserved: true,
          deliveryPhase: "server_uncertain",
          blocker: "submission_uncertain",
          statusText: wsQueuedSessionCommandNotice.message,
          statusIcon: "waiting",
          statusVisibility: "immediate",
        },
      };
    }));
    toast.warning(wsQueuedSessionCommandNotice.message);
  }, [wsQueuedSessionCommandNotice]);

  useEffect(() => {
    setSessionActionToast(null);
    seenQueuedSessionCommandNoticeIdsRef.current.clear();
  }, [sessionId]);

  useEffect(() => {
    setRetryPendingState(null);
  }, [sessionId]);

  useEffect(() => {
    setSessionRecentlyUnarchived(false);
  }, [sessionId]);

  const sandboxRecoverableInactive =
    lifecycleStatus === "active" &&
    (sandboxStatus === "stopped" ||
      sandboxStatus === "error" ||
      (sandboxStatus === "none" && status === "connected"));
  const sandboxResumeNeeded = sessionSuspended || sandboxRecoverableInactive;
  const resumePending = resumePendingSessionId === sessionId;
  const resumeActionPending =
    resumePending || resumeUiPendingState?.sessionId === sessionId;
  // Whether the sandbox is currently in a startup/loading state. Duplicated
  // early (also used later for UI) so that sandboxRestoringFromArchive can be
  // derived before the branch-switch-notice and MergedPr-banner code that
  // depends on it.
  const sandboxLoadingEarly =
    sandboxStatus === "creating" ||
    sandboxStatus === "starting_services" ||
    sandboxStatus === "starting_agent" ||
    sandboxStatus === "starting_opencode" ||
    sandboxStatus === "connecting_sse";
  // True only when a cold-storage restore has actually been requested or is in
  // progress after a Cord-level unarchive. A passive plain unarchive can leave
  // the sandbox stopped until the user resumes it later, so "recently
  // unarchived" alone is intentionally not enough to say restore is happening.
  const localArchiveRestoreStartupActive =
    sessionRecentlyUnarchived &&
    (sandboxResumeNeeded || sandboxLoadingEarly) &&
    (sandboxLoadingEarly || resumeActionPending || startNewWorkPreparationPending);
  const sandboxRestoringFromArchive =
    serverSandboxStartupKind === "restore" || localArchiveRestoreStartupActive;
  const recoveryIsOpenCodeSession =
    sessionAgentType === "opencode" || optimisticAgentType === "opencode" || wsAgentType === "opencode";
  const recoveryOpenCodeAccessIncomplete = recoveryIsOpenCodeSession && (
    !openCodeAccessSnapshot?.url ||
    !openCodeAccessSnapshot.password ||
    !normalizeRecoveryRuntimeCliSessionId({ value: openCodeAccessSnapshot.sessionId })
  );
  const refetchRecoveryRuntimeAccess = recoveryRuntimeAccessQuery.refetch;
  const refetchRecoveryStatus = recoveryStatusQuery.refetch;
  useEffect(() => {
    if (!showRecoverySshDialog || !canOpenRecoverySshDialog || (!sandboxResumeNeeded && !recoveryOpenCodeAccessIncomplete)) return;
    const intervalId = window.setInterval(() => {
      void refetchRecoveryRuntimeAccess();
      void refetchRecoveryStatus();
    }, RECOVERY_RUNTIME_ACCESS_STALE_MS);
    return () => window.clearInterval(intervalId);
  }, [canOpenRecoverySshDialog, recoveryOpenCodeAccessIncomplete, refetchRecoveryRuntimeAccess, refetchRecoveryStatus, sandboxResumeNeeded, showRecoverySshDialog]);

  const sandboxAvailableForPr = sandboxStatus === "running" || sandboxResumeNeeded
  const requiresExplicitResume = sandboxResumeNeeded && mergedPrNeedsContinuation;
  const continuationAllowsComposerWhileArchived =
    mergedThreadContinuationState.kind === "unarchiving" ||
    mergedThreadContinuationState.kind === "waiting_for_unarchive" ||
    mergedThreadContinuationState.kind === "preparing_branch";
  const composerReadOnly = sessionArchived && !continuationAllowsComposerWhileArchived;
  const interactionBlockedReason = sessionArchived
    ? ARCHIVED_THREAD_READ_ONLY_REASON
    : branchSwitchPending
      ? BRANCH_SWITCH_BLOCKED_REASON
      : resumeActionPending && sandboxResumeNeeded
        ? sandboxRestoringFromArchive
          ? "Restoring sandbox from cold storage..."
          : "Resuming sandbox so the agent can continue..."
        : null;
  // Auto-resume should stay non-blocking for the composer so users can keep typing
  // and queue sends while the sandbox starts in the background. PR adoption
  // should also stay non-blocking: the DO queues chat sends until the branch
  // switch completes, so disabling the editor here regresses the promised
  // "send now, deliver after switch" behavior.
  const composerBranchSwitchBlocked =
    continuationAdoptionPending ||
    (effectiveBranchSwitchInProgress
      && !startNewWorkPreparationPending
      && prAdoptionStatus?.status !== "adopting"
      && prAdoptionStatus?.status !== "preparing");
  const composerInteractionBlockedReason = composerReadOnly
    ? ARCHIVED_THREAD_READ_ONLY_REASON
    : composerBranchSwitchBlocked
      ? BRANCH_SWITCH_BLOCKED_REASON
      : null;

  const requestResume = useCallback(
    async (options?: { force?: boolean; speculative?: boolean }) => {
      const targetSessionId = sessionId;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
      if (sessionArchived) {
        toast.error(ARCHIVED_THREAD_RESUME_REASON);
        return false;
      }
      if (branchSwitchPending) {
        showBranchSwitchInProgressToast({ actionLabel: "starting the sandbox" });
        return false;
      }
      if (resumeActionPending) {
        return activeRouteSessionIdRef.current === targetSessionId;
      }
      if (!sandboxResumeNeeded && !options?.force) return true;
      if (requiresExplicitResume && !options?.force) return false;
      if (resumeSessionPromiseRef.current?.sessionId === targetSessionId) {
        try {
          await resumeSessionPromiseRef.current.promise;
          return activeRouteSessionIdRef.current === targetSessionId;
        } catch {
          return false;
        }
      }
      let resumeEntry: { sessionId: string; promise: Promise<unknown> } | null = null;
      try {
        resumeEntry = {
          sessionId: targetSessionId,
          promise: resumeSession.mutateAsync({ speculative: options?.speculative }),
        };
        resumeSessionPromiseRef.current = resumeEntry;
        setResumePendingSessionId(targetSessionId);
        await resumeEntry.promise;
        if (activeRouteSessionIdRef.current === targetSessionId) {
          setResumeUiPendingState({
            requestedAt: Date.now(),
            sessionId: targetSessionId,
          });
        }
        return activeRouteSessionIdRef.current === targetSessionId;
      } catch (error) {
        if (activeRouteSessionIdRef.current === targetSessionId) {
          setResumeUiPendingState((current) =>
            current?.sessionId === targetSessionId ? null : current,
          );
        }
        if (activeRouteSessionIdRef.current === targetSessionId) {
          const message =
            error instanceof Error && error.message.trim().length > 0
              ? error.message
              : "Could not start the sandbox. Please try again.";
          toast.error(message);
        }
        return false;
      } finally {
        if (resumeSessionPromiseRef.current === resumeEntry) {
          resumeSessionPromiseRef.current = null;
        }
        setResumePendingSessionId((current) => current === targetSessionId ? null : current);
      }
    },
    [
      branchSwitchPending,
      requiresExplicitResume,
      resumeActionPending,
      resumeSession,
      sandboxResumeNeeded,
      sessionArchived,
      sessionId,
    ],
  );

  const speculativeResumeRuntimeStateRef = useRef<SpeculativeResumeRuntimeState | null>(null);
  speculativeResumeRuntimeStateRef.current = {
    baseUrl: import.meta.env.VITE_API_URL || "",
    sessionId,
    branchSwitchPending,
    lifecycleStatus,
    requiresExplicitResume,
    resumeActionPending,
    sandboxRecoverableInactive,
    sandboxResumeNeeded,
    sandboxStatus,
    sessionArchived,
    sessionSuspended,
    status,
    suspendedAt: sessionSuspendedAt,
    requestResume,
  };

  /**
   * Triggers a sandbox resume and then polls until the sandbox actually reaches
   * the "running" state.  `requestResume` only waits for the backend to
   * *accept* the resume request – the sandbox still needs tens of seconds to
   * cold-start.  Without this extra wait, session actions (command, summarize,
   * revert …) hit the DO before the runtime is up and get a 503.
   *
   * Used by every action handler that needs a live sandbox (review, undo, redo,
   * compact, fork, edit). Fire-and-forget callers (input interaction,
   * preview click) should keep using plain `requestResume`.
   */
  const resumeAndWaitForRunning = useCallback(
    async (options?: { force?: boolean }): Promise<boolean> => {
      const targetSessionId = sessionId;

      // Already running – nothing to do.
      if (sandboxStatusRef.current === "running" && !sandboxResumeNeeded) {
        return true;
      }

      const resumed = await requestResume(options);
      if (!resumed) return false;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;

      // Fast path: sandbox may already be running by the time the HTTP round
      // trip completes (e.g. the resume was already in progress).
      if (sandboxStatusRef.current === "running") return true;

      // Poll until the reactive sandboxStatusRef (kept in sync with the thread
      // sync state on every render) flips to "running" or a terminal state.
      const startTime = Date.now();
      while (Date.now() - startTime < RESUME_WAIT_RUNNING_TIMEOUT_MS) {
        if (activeRouteSessionIdRef.current !== targetSessionId) return false;

        const current = sandboxStatusRef.current;
        if (current === "running") return true;
        if (current === "error") {
          if (activeRouteSessionIdRef.current === targetSessionId) {
            toast.error("Sandbox failed to start. Please try again.");
          }
          return false;
        }

        await new Promise((resolve) => setTimeout(resolve, RESUME_WAIT_RUNNING_POLL_MS));
      }

      // Timed out waiting for sandbox to reach "running".
      if (activeRouteSessionIdRef.current === targetSessionId) {
        toast.error("Sandbox is taking too long to start. Please try again.");
      }
      return false;
    },
    [requestResume, sandboxResumeNeeded, sessionId],
  );

  const handleInputInteraction = useCallback(() => {
    if (sessionArchived) return;
    if (branchSwitchPending) return;
    if (!sessionDoInitReady && !sessionSuspended && sandboxStatus === "none") return;
    if (!sandboxResumeNeeded) return;
    if (deferredInputResumeFrameRef.current !== null) return;
    deferredInputResumeFrameRef.current = requestAnimationFrame(() => {
      deferredInputResumeFrameRef.current = null;
      void requestResume();
    });
  }, [
    branchSwitchPending,
    requestResume,
    sandboxResumeNeeded,
    sandboxStatus,
    sessionArchived,
    sessionDoInitReady,
    sessionSuspended,
  ]);

  useEffect(() => {
    if (sandboxResumeNeeded && !sessionArchived && !branchSwitchPending) {
      return;
    }
    clearDeferredInputResumeFrame();
  }, [branchSwitchPending, clearDeferredInputResumeFrame, sandboxResumeNeeded, sessionArchived]);

  // Speculative auto-resume: if the user stays on a recently-suspended
  // session for 5 seconds without interacting, start the sandbox in the
  // background so it's ready when they need it.
  // Keep the effect dependencies to the actual resume gate. Volatile thread
  // sync fields (connection status, sandbox status diagnostics, mutation
  // object identity) still flow through the ref above for logs and the final
  // callback, but they must not reset the dwell timer on every foreground sync
  // render.
  useEffect(() => {
    const scheduledState = speculativeResumeRuntimeStateRef.current;
    if (!scheduledState) return;
    const getCurrentScheduledSessionState = () => {
      const latestState = speculativeResumeRuntimeStateRef.current;
      if (latestState?.sessionId === scheduledState.sessionId) return latestState;
      return scheduledState;
    };
    const logDecision = (
      phase: SpeculativeResumeDebugPhase,
      reason: string,
      payload?: Record<string, unknown>,
    ) => {
      const currentScheduledState = getCurrentScheduledSessionState();
      const debugKey = JSON.stringify({
        phase,
        reason,
        sessionId: currentScheduledState.sessionId,
        branchSwitchPending: currentScheduledState.branchSwitchPending,
        requiresExplicitResume: currentScheduledState.requiresExplicitResume,
        resumeActionPending: currentScheduledState.resumeActionPending,
        sandboxResumeNeeded: currentScheduledState.sandboxResumeNeeded,
        sessionArchived: currentScheduledState.sessionArchived,
        suspendedAt: currentScheduledState.suspendedAt,
      });
      if (speculativeResumeDebugKeyRef.current === debugKey) return;
      speculativeResumeDebugKeyRef.current = debugKey;
      logSpeculativeResumeDebug({
        baseUrl: currentScheduledState.baseUrl,
        sessionId: currentScheduledState.sessionId,
        phase,
        reason,
        payload: buildSpeculativeResumeDebugPayload({ state: currentScheduledState, payload }),
      });
    };

    if (!scheduledState.sandboxResumeNeeded) return;
    if (scheduledState.sessionArchived) {
      logDecision("blocked", "session_archived");
      return;
    }
    if (scheduledState.branchSwitchPending) {
      logDecision("blocked", "branch_switch_pending");
      return;
    }
    if (scheduledState.requiresExplicitResume) {
      logDecision("blocked", "requires_explicit_resume");
      return;
    }
    if (scheduledState.resumeActionPending) {
      logDecision("blocked", "resume_already_pending");
      return;
    }

    const eligibility = evaluateSpeculativeResumeEligibility({ suspendedAt: scheduledState.suspendedAt });
    if (!eligibility.eligible) {
      logDecision("blocked", eligibility.reason ?? "not_eligible", eligibility);
      return;
    }

    logDecision("scheduled", "eligible", {
      ...eligibility,
      dwellMs: SPECULATIVE_RESUME_DWELL_MS,
    });

    let timerFired = false;
    const timer = setTimeout(() => {
      timerFired = true;
      const currentScheduledState = getCurrentScheduledSessionState();
      logSpeculativeResumeDebug({
        baseUrl: currentScheduledState.baseUrl,
        sessionId: currentScheduledState.sessionId,
        phase: "timer_fired",
        reason: "dwell_timer_elapsed",
        payload: buildSpeculativeResumeDebugPayload({
          state: currentScheduledState,
          payload: {
            ...eligibility,
            dwellMs: SPECULATIVE_RESUME_DWELL_MS,
          },
        }),
      });
      void currentScheduledState.requestResume({ speculative: true });
    }, SPECULATIVE_RESUME_DWELL_MS);

    return () => {
      clearTimeout(timer);
      if (timerFired) return;
      const currentScheduledState = getCurrentScheduledSessionState();
      logSpeculativeResumeDebug({
        baseUrl: currentScheduledState.baseUrl,
        sessionId: currentScheduledState.sessionId,
        phase: "timer_cancelled",
        reason: "effect_cleanup_before_dwell_elapsed",
        payload: buildSpeculativeResumeDebugPayload({
          state: currentScheduledState,
          payload: {
            ...eligibility,
            dwellMs: SPECULATIVE_RESUME_DWELL_MS,
          },
        }),
      });
    };
  }, [
    sessionId,
    sandboxResumeNeeded,
    sessionArchived,
    branchSwitchPending,
    requiresExplicitResume,
    resumeActionPending,
    sessionSuspendedAt,
  ]);

  useEffect(() => {
    if (!sandboxResumeNeeded) {
      speculativeResumeDebugKeyRef.current = null;
    }
  }, [sandboxResumeNeeded, sessionId]);

  const sendInteractiveSessionSocketAction = useCallback(async (
    args: {
      actionLabel: string;
      failureMessage: string;
      message: { type: string; [key: string]: unknown };
    },
  ): Promise<boolean> => {
    if (sessionArchived) {
      toast.error(ARCHIVED_THREAD_READ_ONLY_REASON);
      return false;
    }
    if (branchSwitchPending) {
      showBranchSwitchInProgressToast({ actionLabel: args.actionLabel });
      return false;
    }
    const resumed = await resumeAndWaitForRunning({ force: true });
    if (!resumed) {
      return false;
    }
    return sendSessionSocketAction(args.message, args.failureMessage);
  }, [branchSwitchPending, resumeAndWaitForRunning, sendSessionSocketAction, sessionArchived]);

  const retrySandboxPending = retryPendingState?.sessionId === sessionId;

  // Git status and Quick PR state
  const { data: gitStatus } = useGitStatus(sessionId, runtimeInteractionEnabled, {
    visible: false,
    sessionSuspended: sandboxResumeNeeded,
  });
  const prFlowSnapshot = useSessionPrFlow((state) => state.snapshots[sessionId] ?? null);
  const [, forcePrFlowRepoReadinessRecheck] = useReducer((version: number) => version + 1, 0)
  const archiveConfirmationHasSessionChanges = useMemo(() => {
    if (gitStatus) {
      return hasSessionChangesForArchiveConfirmation({
        stagedCount: gitStatus.staged.length,
        unstagedCount: gitStatus.unstaged.length,
        untrackedCount: gitStatus.untracked.length,
        commitsAheadOfBase: gitStatus.commitsAheadOfBase,
      });
    }

    if (prFlowSnapshot?.repoReadiness) {
      return prFlowSnapshot.repoReadiness.hasQuickPrSourceChanges;
    }

    return null;
  }, [gitStatus, prFlowSnapshot]);
  const manualCreatePrActive =
    prFlowSnapshot?.activeOperation?.kind === "manual_create_pr" &&
    (prFlowSnapshot.activeOperation.status === "accepted" ||
      prFlowSnapshot.activeOperation.status === "running");
  const derivedQuickPrUi = useMemo(() => deriveQuickPrUiState(prFlowSnapshot), [prFlowSnapshot]);
  // True when the Quick PR workflow is actively executing (e.g. staging,
  // committing, pushing).  Used to block message dispatch on the server and
  // show an informative queue status on the client.
  const quickPrExecuting = Boolean(
    derivedQuickPrUi &&
      derivedQuickPrUi.step !== "idle" &&
      derivedQuickPrUi.step !== "queued" &&
      derivedQuickPrUi.step !== "done" &&
      derivedQuickPrUi.step !== "error",
  );
  const dismissedQuickPrOperationId = useSessionUI(
    (s) => s.dismissedQuickPrOperationIds[sessionId] ?? null,
  );
  const dismissStoredQuickPrOperation = useSessionUI((s) => s.dismissQuickPrOperation);
  const clearDismissedQuickPrOperation = useSessionUI(
    (s) => s.clearDismissedQuickPrOperation,
  );
  const [quickPrRequestPending, setQuickPrRequestPending] = useState(false);
  const [quickPrRequestError, setQuickPrRequestError] = useState<string | null>(null);
  const [quickPrSyncRequestPending, setQuickPrSyncRequestPending] = useState(false);
  const gitOperationLoading = useSessionUI((s) => s.gitOperationLoading);
  const mergeRequestState = useSessionPrActionState(
    (state) => state.mergeRequests[sessionId] ?? null,
  )
  const startMergeRequest = useSessionPrActionState((state) => state.startMergeRequest)
  const settleMergeRequest = useSessionPrActionState((state) => state.settleMergeRequest)
  const markMergeRequestIndeterminate = useSessionPrActionState(
    (state) => state.markMergeRequestIndeterminate,
  )
  const clearMergeRequestState = useSessionPrActionState((state) => state.clearMergeRequestState)
  const rawResolveConflictActionState = useSessionPrActionState(
    (state) => state.resolveConflictActions[sessionId] ?? null,
  )
  const resolveConflictActionState = useMemo(() => {
    if (!rawResolveConflictActionState || !runtimeConversationTabsAvailable) {
      return rawResolveConflictActionState
    }
    if (!rawResolveConflictActionState.conversationId) {
      return activeConversationId === "default" ? rawResolveConflictActionState : null
    }
    return rawResolveConflictActionState.conversationId === activeConversationId
      ? rawResolveConflictActionState
      : null
  }, [activeConversationId, rawResolveConflictActionState, runtimeConversationTabsAvailable])
  const requestResolveConflict = useSessionPrActionState(
    (state) => state.requestResolveConflict,
  )
  const markResolveConflictIndeterminate = useSessionPrActionState(
    (state) => state.markResolveConflictIndeterminate,
  )
  const clearSessionActionState = useSessionPrActionState((state) => state.clearSessionActionState)
  const threadRuntimeEntry = useThreadRuntime((state) => state.entries[sessionId])
  const activeThreadRuntimeEntry = useMemo(() => {
    if (!threadRuntimeEntry || !runtimeConversationTabsAvailable) return threadRuntimeEntry
    const activityState = activeRuntimeConversationStateSnapshot?.activityState ?? null
    if (activityState !== "running" && activityState !== "error" && activityState !== "idle") {
      return threadRuntimeEntry
    }
    return {
      ...threadRuntimeEntry,
      state: activityState,
      lastActivityEventId:
        activeRuntimeConversationStateSnapshot?.lastActivityEventId ??
        threadRuntimeEntry.lastActivityEventId,
      lastFinishedEventId:
        activeRuntimeConversationStateSnapshot?.lastFinishedEventId ??
        threadRuntimeEntry.lastFinishedEventId,
      lastFinishedAt:
        activeRuntimeConversationStateSnapshot?.lastFinishedAt ??
        threadRuntimeEntry.lastFinishedAt,
      lastFinishReason:
        activeRuntimeConversationStateSnapshot?.lastFinishReason ??
        threadRuntimeEntry.lastFinishReason,
      rawTailId:
        activeRuntimeConversationStateSnapshot?.rawTailId ??
        threadRuntimeEntry.rawTailId,
      terminalRawCursor:
        activeRuntimeConversationStateSnapshot?.terminalRawCursor ??
        threadRuntimeEntry.terminalRawCursor,
      terminalRawCursorObservedAt:
        activeRuntimeConversationStateSnapshot?.terminalRawCursorObservedAt ??
        threadRuntimeEntry.terminalRawCursorObservedAt,
    }
  }, [
    activeRuntimeConversationStateSnapshot,
    runtimeConversationTabsAvailable,
    threadRuntimeEntry,
  ])
  const threadActivityState = activeThreadRuntimeEntry?.state ?? "idle"
  const setOptimisticWorkRequestPending = useThreadRuntime(
    (state) => state.setOptimisticWorkRequestPending,
  )
  const setOptimisticYoloStartPending = useThreadRuntime(
    (state) => state.setOptimisticYoloStartPending,
  )
  const yoloFlowActive = isYoloFlowStepActive(yoloFlowStep)
  const yoloStartOptimisticPending = threadRuntimeEntry?.optimisticYoloStartPending ?? false
  const yoloFlowBusy =
    yoloFlowActive || isThreadRuntimeYoloSpinnerActive(threadRuntimeEntry)
  const [yoloStartPending, setYoloStartPending] = useState(false);
  const yoloStartRequestedRef = useRef(false);
  const yoloLastErrorRef = useRef<string | null>(null);
  const yoloStartPendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearedPrStatusAfterResolveTimestampRef = useRef<number | null>(null)

  useEffect(() => {
    if (!derivedQuickPrUi) return;
    setQuickPrRequestPending(false);
    setQuickPrRequestError(null);
    if (
      dismissedQuickPrOperationId &&
      dismissedQuickPrOperationId !== derivedQuickPrUi.operationId
    ) {
      clearDismissedQuickPrOperation(sessionId);
    }
  }, [
    clearDismissedQuickPrOperation,
    derivedQuickPrUi,
    dismissedQuickPrOperationId,
    sessionId,
  ]);

  useEffect(() => {
    setQuickPrSyncRequestPending(false);
  }, [sessionId]);

  useEffect(() => {
    clearedPrStatusAfterResolveTimestampRef.current = null
  }, [sessionId])

  const quickPrUi = useMemo(() => {
    if (
      derivedQuickPrUi &&
      derivedQuickPrUi.operationId !== dismissedQuickPrOperationId
    ) {
      return derivedQuickPrUi;
    }
    if (quickPrRequestPending) {
      return {
        operationId: "pending",
        operationKind: sessionPrNumber ? "update_pr" as const : "quick_pr" as const,
        step: "checking" as const,
        error: null,
        errorCode: null,
        behind: null,
        result: null,
      };
    }
    if (quickPrRequestError) {
      return {
        operationId: "request_error",
        operationKind: sessionPrNumber ? "update_pr" as const : "quick_pr" as const,
        step: "error" as const,
        error: quickPrRequestError,
        errorCode: null,
        behind: null,
        result: null,
      };
    }
    return null;
  }, [
    derivedQuickPrUi,
    dismissedQuickPrOperationId,
    quickPrRequestError,
    quickPrRequestPending,
    sessionPrNumber,
  ]);

  const quickPrStep = quickPrUi?.step ?? "idle";
  const quickPrError = quickPrUi?.error ?? null;
  const quickPrErrorCode = quickPrUi?.errorCode ?? null;
  const quickPrBehind = quickPrUi?.behind ?? null;
  const quickPrResult = quickPrUi?.result ?? null;
  const quickPrSyncPending = quickPrSyncRequestPending;
  const rawPrFlowRepoReadiness = prFlowSnapshot?.repoReadiness ?? null
  const prFlowRepoReadinessSnapshotTimestamp = getSessionPrFlowSnapshotTimestamp(prFlowSnapshot)
  const prFlowRepoReadinessExpiresAt =
    rawPrFlowRepoReadiness && prFlowRepoReadinessSnapshotTimestamp !== null
      ? prFlowRepoReadinessSnapshotTimestamp + PR_FLOW_REPO_READINESS_FALLBACK_MAX_AGE_MS
      : null
  const prFlowRepoReadinessBranchMatches = Boolean(
    rawPrFlowRepoReadiness &&
      (!sessionBranch ||
        rawPrFlowRepoReadiness.currentBranch === sessionBranch ||
        rawPrFlowRepoReadiness.headBranch === sessionBranch),
  )
  const prFlowRepoReadiness =
    rawPrFlowRepoReadiness &&
    prFlowRepoReadinessSnapshotTimestamp !== null &&
    Date.now() - prFlowRepoReadinessSnapshotTimestamp <= PR_FLOW_REPO_READINESS_FALLBACK_MAX_AGE_MS &&
    prFlowRepoReadinessBranchMatches
      ? rawPrFlowRepoReadiness
      : null
  useEffect(() => {
    if (prFlowRepoReadinessExpiresAt === null) return

    const delayMs = prFlowRepoReadinessExpiresAt - Date.now()
    if (delayMs <= 0) return

    const timer = setTimeout(() => {
      forcePrFlowRepoReadinessRecheck()
    }, delayMs)

    return () => {
      clearTimeout(timer)
    }
  }, [forcePrFlowRepoReadinessRecheck, prFlowRepoReadinessExpiresAt])
  const hasUncommittedChanges = gitStatus
    ? gitStatus.staged.length > 0 ||
      gitStatus.unstaged.length > 0 ||
      gitStatus.untracked.length > 0
    : prFlowRepoReadiness?.hasUncommittedChanges === true
  const commitsAheadOfBaseRaw = gitStatus?.commitsAheadOfBase ?? prFlowRepoReadiness?.commitsAheadOfBase
  const commitsAheadOfBase =
    typeof commitsAheadOfBaseRaw === "number" && Number.isFinite(commitsAheadOfBaseRaw)
      ? Math.max(0, commitsAheadOfBaseRaw)
      : 0
  const hasBranchCommits = commitsAheadOfBase > 0
  const hasQuickPrSourceChanges = hasUncommittedChanges || hasBranchCommits
  const hasUnpushedCommits = gitStatus
    ? gitStatus.ahead > 0
    : prFlowRepoReadiness?.hasUnpushedCommits === true
  const hasLocalOnlyContinuationCommits = Boolean(
    gitStatus
      ? gitStatus.hasUpstream === false && commitsAheadOfBase > 0
      : prFlowRepoReadiness?.hasUpstream === false && commitsAheadOfBase > 0,
  )
  const hasUnpublishedContinuationWork =
    hasUncommittedChanges ||
    hasUnpushedCommits ||
    hasLocalOnlyContinuationCommits
  const unpublishedContinuationWorkDisabledReason = hasUncommittedChanges
    ? "Commit and push your changes before continuing in a new thread."
    : hasUnpushedCommits || hasLocalOnlyContinuationCommits
      ? "Push your commits before continuing in a new thread."
      : null
  const hasPendingPrUpdates = hasUncommittedChanges || hasUnpushedCommits
  const attachedPullRequestActionState = useMemo(() => {
    if (effectivePrState !== "open" && effectivePrState !== "draft") return null
    return deriveAttachedPullRequestActionStateMachine({
      prState: effectivePrState,
      canManageSession,
      sessionPrIsActiveOwner,
      sandboxAvailableForPr,
      hasPendingPrUpdates,
      quickPrUi,
      currentPrStatus,
      mergeRequestState,
      resolveConflictState: resolveConflictActionState,
      threadActivityState,
    })
  }, [
    canManageSession,
    currentPrStatus,
    effectivePrState,
    hasPendingPrUpdates,
    mergeRequestState,
    quickPrUi,
    resolveConflictActionState,
    sandboxAvailableForPr,
    sessionPrIsActiveOwner,
    threadActivityState,
  ])
  useEffect(() => {
    if (attachedPullRequestActionState?.shouldPollRemoteStatus !== true) {
      return
    }

    void refetchPrStatus()
    const pollTimer = window.setInterval(() => {
      void refetchPrStatus()
    }, PR_STATUS_CONFLICT_POLL_INTERVAL_MS)

    return () => {
      window.clearInterval(pollTimer)
    }
  }, [attachedPullRequestActionState?.shouldPollRemoteStatus, refetchPrStatus, sessionId])
  const mergeRequestInFlight = mergeRequestState?.phase === "requested"

  useEffect(() => {
    if (
      sessionPrNumber === null ||
      effectivePrState === "merged" ||
      effectivePrState === "closed"
    ) {
      clearSessionActionState(sessionId)
    }
  }, [clearSessionActionState, effectivePrState, sessionId, sessionPrNumber])

  useEffect(() => {
    const resolvePhase = resolveConflictActionState?.phase ?? "idle"
    const resolveTimestamp = resolveConflictActionState?.timestamp

    if (resolvePhase !== "started" && resolvePhase !== "dispatched") {
      return
    }

    if (typeof resolveTimestamp !== "number" || !Number.isFinite(resolveTimestamp)) {
      return
    }

    const nextTimestamp = Math.floor(resolveTimestamp)
    if (
      typeof clearedPrStatusAfterResolveTimestampRef.current === "number" &&
      nextTimestamp <= clearedPrStatusAfterResolveTimestampRef.current
    ) {
      return
    }

    clearedPrStatusAfterResolveTimestampRef.current = nextTimestamp
    queryClient.removeQueries({ queryKey: getPrStatusQueryKey({ sessionId }), exact: true })
    clearMergeRequestState(sessionId)
  }, [clearMergeRequestState, queryClient, resolveConflictActionState?.phase, resolveConflictActionState?.timestamp, sessionId])

  const dismissQuickPr = useCallback(() => {
    setQuickPrRequestPending(false);
    setQuickPrRequestError(null);
    if (derivedQuickPrUi) {
      dismissStoredQuickPrOperation(sessionId, derivedQuickPrUi.operationId);
      return;
    }
    clearDismissedQuickPrOperation(sessionId);
  }, [
    clearDismissedQuickPrOperation,
    derivedQuickPrUi,
    dismissStoredQuickPrOperation,
    sessionId,
  ]);

  const resolveArchiveConfirmationHasSessionChanges = useCallback(async (): Promise<boolean | null> => {
    if (typeof archiveConfirmationHasSessionChanges === "boolean") {
      return archiveConfirmationHasSessionChanges;
    }

    if (effectivePrState !== "merged") {
      return null;
    }

    try {
      const freshGitStatus = await fetchGitStatus(sessionId);
      queryClient.setQueryData(["git-status", sessionId], freshGitStatus);

      return hasSessionChangesForArchiveConfirmation({
        stagedCount: freshGitStatus.staged.length,
        unstagedCount: freshGitStatus.unstaged.length,
        untrackedCount: freshGitStatus.untracked.length,
        commitsAheadOfBase: freshGitStatus.commitsAheadOfBase,
      });
    } catch {
      return null;
    }
  }, [
    archiveConfirmationHasSessionChanges,
    effectivePrState,
    queryClient,
    sessionId,
  ]);

  const handleArchiveToggle = useCallback(async () => {
    try {
      if (sessionArchived) {
        await beginUnarchiveSession();
        captureAnalyticsEvent({
          event: ANALYTICS_EVENTS.threadUnarchived,
          properties: buildPrAnalyticsProperties({
            sessionId,
            repositoryId: repoId,
            agentType: sessionAgentType,
            source: "thread_page",
            prState: effectivePrState,
            properties: {
              trigger: "explicit",
            },
          }),
        });
      } else {
        const hasSessionChanges = await resolveArchiveConfirmationHasSessionChanges();

        if (
          isRunningSandboxForArchiveConfirmation({
            liveSandboxStatus: sandboxStatus,
            persistedSandboxStatus,
            lifecycleStatus,
            prState: effectivePrState,
            hasSessionChanges,
          })
          && !window.confirm(RUNNING_SANDBOX_ARCHIVE_CONFIRMATION_MESSAGE)
        ) {
          return;
        }
        await archiveSession.mutateAsync();
        captureAnalyticsEvent({
          event: ANALYTICS_EVENTS.threadArchived,
          properties: buildPrAnalyticsProperties({
            sessionId,
            repositoryId: repoId,
            agentType: sessionAgentType,
            source: "thread_page",
            prState: effectivePrState,
            properties: {
              had_session_changes: hasSessionChanges,
              lifecycle_status: lifecycleStatus,
            },
          }),
        });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update archive state");
    }
  }, [
    archiveSession,
    effectivePrState,
    lifecycleStatus,
    persistedSandboxStatus,
    repoId,
    resolveArchiveConfirmationHasSessionChanges,
    sandboxStatus,
    sessionAgentType,
    sessionArchived,
    sessionId,
    beginUnarchiveSession,
  ]);

  const clearYoloStartPendingTimeout = useCallback(() => {
    if (!yoloStartPendingTimeoutRef.current) return;
    clearTimeout(yoloStartPendingTimeoutRef.current);
    yoloStartPendingTimeoutRef.current = null;
  }, []);

  const markSidebarWorkRequested = useCallback((args: { sessionId: string }) => {
    setOptimisticWorkRequestPending({
      sessionId: args.sessionId,
      pending: true,
      timeoutMs: SIDEBAR_WORK_REQUEST_PENDING_TIMEOUT_MS,
    });
  }, [setOptimisticWorkRequestPending]);

  const postPrAction = useCallback(
    async (
      endpoint: "quick-pr" | "merge-pr" | "resolve-merge-conflict" | "yolo",
      payload: Record<string, unknown> = {},
      options?: {
        allowClaimRetry?: boolean;
        throwIndeterminateErrors?: boolean;
      },
    ): Promise<{ ok: boolean; error?: string }> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const allowClaimRetry = options?.allowClaimRetry !== false;
      const throwIndeterminateErrors = options?.throwIndeterminateErrors === true;

      try {
        const bodyPayload = runtimeConversationTabsAvailable
          ? { conversationId: activeConversationId, ...payload }
          : payload;
        const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/${endpoint}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bodyPayload),
        });

        if (!res.ok) {
          let errBody: { error?: string; requiresClaim?: boolean } | null = null;
          try {
            errBody = await res.json() as { error?: string; requiresClaim?: boolean };
          } catch {
            errBody = null;
          }

          if (allowClaimRetry && res.status === 409 && errBody?.requiresClaim) {
            const confirmed = window.confirm(
              "This session belongs to another user. Claim PR actions under your git provider identity?",
            );
            if (confirmed) {
              return postPrAction(endpoint, { ...payload, claim: true }, {
                allowClaimRetry: false,
                throwIndeterminateErrors,
              });
            }
          }

          const requestError = createSessionActionRequestError({
            action: endpoint.replaceAll("-", " "),
            details: errBody?.error || null,
            status: res.status,
          });

          if (throwIndeterminateErrors && requestError.indeterminate === true) {
            throw requestError;
          }

          if (requestError.indeterminate === true) {
            void refreshStatusFromProbe();
          }

          return {
            ok: false,
            error: requestError.message,
          };
        }

        return { ok: true };
      } catch (error) {
        if (shouldRefreshStatusAfterSessionActionError(error)) {
          void refreshStatusFromProbe();
        }

        if (throwIndeterminateErrors && isIndeterminateSessionActionError(error)) {
          throw error;
        }

        return {
          ok: false,
          error: getSessionActionErrorMessage(error, `Failed to ${endpoint.replaceAll("-", " ")}`),
        };
      }
    },
    [activeConversationId, refreshStatusFromProbe, runtimeConversationTabsAvailable, sessionId],
  );

  const triggerQuickPr = useCallback(async () => {
    const targetSessionId = sessionId;
    if (activeRouteSessionIdRef.current !== targetSessionId) return;
    if (derivedQuickPrUi && (derivedQuickPrUi.step === "error" || derivedQuickPrUi.step === "done")) {
      dismissStoredQuickPrOperation(sessionId, derivedQuickPrUi.operationId);
    } else {
      clearDismissedQuickPrOperation(sessionId);
    }
    setQuickPrRequestError(null);
    setQuickPrRequestPending(true);
    capturePrAnalyticsEvent({
      event: ANALYTICS_EVENTS.quickPrRequested,
      source: "thread_page",
      properties: {
        session_archived: sessionArchived,
      },
    });
    const result = await postPrAction("quick-pr");
    if (activeRouteSessionIdRef.current !== targetSessionId) return;
    if (!result.ok) {
      setQuickPrRequestPending(false);
      setQuickPrRequestError(result.error || "Quick PR failed");
      capturePrAnalyticsEvent({
        event: ANALYTICS_EVENTS.quickPrRequestFailed,
        source: "thread_page",
        properties: {
          error_message: result.error || "Quick PR failed",
        },
      });
    }
  }, [
    capturePrAnalyticsEvent,
    clearDismissedQuickPrOperation,
    derivedQuickPrUi,
    dismissStoredQuickPrOperation,
    postPrAction,
    sessionArchived,
    sessionId,
  ]);

  const triggerSyncFromQuickPrError = useCallback(async () => {
    const targetSessionId = sessionId;
    if (activeRouteSessionIdRef.current !== targetSessionId) return;
    if (quickPrSyncRequestPending || gitOperationLoading) {
      return;
    }
    setQuickPrSyncRequestPending(true);
    const ui = useSessionUI.getState();
    ui.setGitOperationError(null);
    ui.setGitOperationLoading(true);
    let gitResultHandled = false;
    try {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const doRequest = async (claim: boolean) => {
        return fetch(`${baseUrl}/api/sessions/${sessionId}/git-exec`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            operation: "sync",
            ...(runtimeConversationTabsAvailable ? { conversationId: activeConversationId } : {}),
            ...(claim ? { claim: true } : {}),
          }),
        });
      };

      let res = await doRequest(false);
      if (!res.ok) {
        let payload: { error?: string; requiresClaim?: boolean } | null = null;
        try {
          payload = await res.json() as { error?: string; requiresClaim?: boolean };
        } catch {
          payload = null;
        }

        if (res.status === 409 && payload?.requiresClaim) {
          const confirmed = window.confirm(
            "This session belongs to another user. Claim push/PR actions under your git provider identity?",
          );
          if (confirmed) {
            res = await doRequest(true);
            if (!res.ok) {
              try {
                payload = await res.json() as { error?: string; requiresClaim?: boolean };
              } catch {
                payload = null;
              }
            }
          } else {
            return;
          }
        }

        if (!res.ok) {
          const result = normalizeSessionGitExecResult({
            message: payload,
            fallbackOperation: "sync",
          });
          if (result) {
            applySessionGitExecResult({ sessionId: targetSessionId, result });
            gitResultHandled = true;
            toast.error(result.error || "Sync failed");
            return;
          }

          ui.setGitOperationError(payload?.error || "Failed to start sync");
          toast.error(payload?.error || "Failed to start sync");
          return;
        }
      }

      const result = await readSessionGitExecResult({
        response: res,
        requestedOperation: "sync",
      });
      applySessionGitExecResult({ sessionId: targetSessionId, result });
      gitResultHandled = true;

      if (!result.ok) {
        toast.error(result.error || "Sync failed");
        return;
      }

      dismissQuickPr();
      capturePrAnalyticsEvent({
        event: ANALYTICS_EVENTS.quickPrSyncRequested,
        source: "quick_pr_error",
      });
      toast.success("Sync complete. Retry Quick PR now.");
    } catch (error) {
      ui.setGitOperationError(error instanceof Error ? error.message : "Failed to sync");
      toast.error(error instanceof Error ? error.message : "Failed to sync");
    } finally {
      if (!gitResultHandled) {
        ui.setGitOperationLoading(false);
      }
      if (activeRouteSessionIdRef.current === targetSessionId) {
        setQuickPrSyncRequestPending(false);
      }
    }
  }, [activeConversationId, capturePrAnalyticsEvent, dismissQuickPr, gitOperationLoading, quickPrSyncRequestPending, runtimeConversationTabsAvailable, sessionId]);

  const triggerMergePr = useCallback(async () => {
    if (mergeRequestState?.phase === "requested") return
    const requestId = crypto.randomUUID();
    startMergeRequest({ sessionId, requestId })
    capturePrAnalyticsEvent({
      event: ANALYTICS_EVENTS.mergePrRequested,
      source: "thread_page",
      properties: {
        merge_method: "squash",
      },
    });
    try {
      const result = await postPrAction("merge-pr", { method: "squash", requestId }, {
        throwIndeterminateErrors: true,
      });
      if (!result.ok) {
        settleMergeRequest({
          sessionId,
          requestId,
          ok: false,
          error: result.error || "Failed to merge PR",
        })
        capturePrAnalyticsEvent({
          event: ANALYTICS_EVENTS.mergePrRequestFailed,
          source: "thread_page",
          properties: {
            error_message: result.error || "Failed to merge PR",
            merge_method: "squash",
          },
        });
      }
    } catch (error) {
      const errorMessage = getSessionActionErrorMessage(error, "Failed to merge PR")
      if (isIndeterminateSessionActionError(error)) {
        markMergeRequestIndeterminate({
          sessionId,
          message: errorMessage,
        })
      } else {
        settleMergeRequest({
          sessionId,
          requestId,
          ok: false,
          error: errorMessage,
        })
      }
      capturePrAnalyticsEvent({
        event: ANALYTICS_EVENTS.mergePrRequestFailed,
        source: "thread_page",
        properties: {
          error_message: errorMessage,
          merge_method: "squash",
        },
      });
    }
  }, [
    capturePrAnalyticsEvent,
    markMergeRequestIndeterminate,
    mergeRequestState?.phase,
    postPrAction,
    sessionId,
    settleMergeRequest,
    startMergeRequest,
  ]);

  const triggerYoloFlow = useCallback(async () => {
    const targetSessionId = sessionId;
    if (activeRouteSessionIdRef.current !== targetSessionId) return;
    if (yoloFlowBusy || yoloStartRequestedRef.current) return;
    yoloStartRequestedRef.current = true;
    setYoloStartPending(true);
    setOptimisticYoloStartPending({
      sessionId: targetSessionId,
      pending: true,
      timeoutMs: YOLO_START_PENDING_TIMEOUT_MS,
    });
    capturePrAnalyticsEvent({
      event: ANALYTICS_EVENTS.yoloRequested,
      source: "thread_page",
      properties: {
        yolo_step_at_request: yoloFlowStep,
      },
    });
    clearYoloStartPendingTimeout();
    yoloStartPendingTimeoutRef.current = setTimeout(() => {
      setOptimisticYoloStartPending({ sessionId: targetSessionId, pending: false });
      if (activeRouteSessionIdRef.current !== targetSessionId) return;
      setYoloStartPending(false);
      toast.error("YOLO is taking longer than expected to start. Try again.");
    }, YOLO_START_PENDING_TIMEOUT_MS);
    dismissQuickPr();
    try {
      const result = await postPrAction("yolo");
      if (!result.ok) {
        setOptimisticYoloStartPending({ sessionId: targetSessionId, pending: false });
        if (activeRouteSessionIdRef.current !== targetSessionId) return;
        clearYoloStartPendingTimeout();
        setYoloStartPending(false);
        yoloStartRequestedRef.current = false;
        capturePrAnalyticsEvent({
          event: ANALYTICS_EVENTS.yoloRequestFailed,
          source: "thread_page",
          properties: {
            error_message: result.error || "Failed to start YOLO",
            yolo_step_at_request: yoloFlowStep,
          },
        });
        toast.error(result.error || "Failed to start YOLO");
        return;
      }
      if (activeRouteSessionIdRef.current !== targetSessionId) return;
    } catch (error) {
      setOptimisticYoloStartPending({ sessionId: targetSessionId, pending: false });
      if (activeRouteSessionIdRef.current !== targetSessionId) return;
      clearYoloStartPendingTimeout();
      setYoloStartPending(false);
      yoloStartRequestedRef.current = false;
      capturePrAnalyticsEvent({
        event: ANALYTICS_EVENTS.yoloRequestFailed,
        source: "thread_page",
        properties: {
          error_message: error instanceof Error ? error.message : "Failed to start YOLO",
          yolo_step_at_request: yoloFlowStep,
        },
      });
      toast.error(error instanceof Error ? error.message : "Failed to start YOLO");
    }
  }, [
    clearYoloStartPendingTimeout,
    dismissQuickPr,
    postPrAction,
    sessionId,
    setOptimisticYoloStartPending,
    yoloFlowBusy,
    yoloFlowStep,
  ]);

  useEffect(() => {
    clearYoloStartPendingTimeout();
    yoloStartRequestedRef.current = false;
    yoloLastErrorRef.current = null;
    setYoloStartPending(false);
    setQuickPrRequestPending(false);
    setQuickPrRequestError(null);
  }, [clearYoloStartPendingTimeout, sessionId]);

  useEffect(() => {
    if (!yoloStartPending) return;
    if (yoloStartOptimisticPending) return;
    clearYoloStartPendingTimeout();
    setYoloStartPending(false);
  }, [clearYoloStartPendingTimeout, yoloStartOptimisticPending, yoloStartPending]);

  useEffect(() => {
    return () => {
      clearYoloStartPendingTimeout();
    };
  }, [clearYoloStartPendingTimeout]);

  useEffect(() => {
    if (yoloFlowStep !== "error") {
      yoloLastErrorRef.current = null;
      return;
    }
    const resolvePhase = resolveConflictActionState?.phase ?? "idle"
    if (
      resolvePhase === "requested" ||
      resolvePhase === "started" ||
      resolvePhase === "dispatched" ||
      resolvePhase === "indeterminate"
    ) {
      return
    }
    const message =
      yoloFlowError
      || quickPrError
      || "YOLO failed";
    if (yoloLastErrorRef.current === message) return;
    const nowMs = Date.now()
    const yoloErrorUpdatedAt = threadRuntimeEntry?.yoloFlowUpdatedAt ?? null
    const yoloErrorIsFresh =
      typeof yoloErrorUpdatedAt === "number" &&
      Number.isFinite(yoloErrorUpdatedAt) &&
      nowMs - Math.floor(yoloErrorUpdatedAt) <= YOLO_ERROR_TOAST_FRESH_WINDOW_MS
    if (!yoloStartRequestedRef.current && !yoloErrorIsFresh) {
      yoloLastErrorRef.current = message;
      return;
    }
    pruneYoloErrorToastDedupe(nowMs)
    const dedupeTimestamp = typeof yoloErrorUpdatedAt === "number" && Number.isFinite(yoloErrorUpdatedAt)
      ? Math.floor(yoloErrorUpdatedAt)
      : 0
    const dedupeKey = `${sessionId}:${dedupeTimestamp}:${message}`
    if (yoloErrorToastDedupe.has(dedupeKey)) {
      yoloLastErrorRef.current = message;
      return;
    }
    yoloErrorToastDedupe.set(dedupeKey, nowMs)
    yoloLastErrorRef.current = message;
    yoloStartRequestedRef.current = false;
    toast.error(message);
  }, [quickPrError, resolveConflictActionState?.phase, sessionId, threadRuntimeEntry?.yoloFlowUpdatedAt, yoloFlowError, yoloFlowStep]);

  useEffect(() => {
    if (yoloFlowStep !== "done") return;
    if (!yoloStartRequestedRef.current) return;
    yoloStartRequestedRef.current = false;
    navigateToList();
  }, [navigateToList, yoloFlowStep]);

  const triggerResolveMergeConflict = useCallback(async () => {
    if (
      resolveConflictActionState?.phase === "requested" ||
      resolveConflictActionState?.phase === "started" ||
      resolveConflictActionState?.phase === "dispatched"
    ) {
      return
    }
    const requestId = crypto.randomUUID();
    const clientMessageId = buildResolveMergeConflictClientMessageId({ requestId });
    const targetConversationId = activeConversationId;
    const instructionText = buildResolveMergeConflictInstructionText({
      baseBranch: sessionBaseBranch ?? repo?.defaultBranch ?? "main",
    });
    requestResolveConflict({ sessionId, requestId, conversationId: activeConversationId })
    // Use sendChatMessage (normal WebSocket path) instead of
    // stageServerOrchestratedChatMessage so the message immediately enters
    // pendingLocalDispatches. This triggers the "dispatching" work state and
    // shows the spinner/STOP button optimistically — matching the behavior of
    // regular user messages.
    const optimisticStageResult = sendChatMessage({
      type: "message",
      text: instructionText,
      clientMessageId,
      conversationId: targetConversationId,
    });
    if (optimisticStageResult.accepted) {
      useSessionPrActionState.getState().setResolveConflictFromServer({
        sessionId,
        requestId,
        conversationId: targetConversationId,
        state: "dispatched",
        baseBranch: sessionBaseBranch ?? repo?.defaultBranch ?? "main",
        dispatchMode: sandboxResumeNeeded ? "queued" : "forwarded",
      })
      markSidebarWorkRequested({ sessionId })
      if (sandboxResumeNeeded) {
        void requestResume({ force: false })
      }
    }
    capturePrAnalyticsEvent({
      event: ANALYTICS_EVENTS.resolveMergeConflictRequested,
      source: "thread_page",
    });
    try {
      const result = await postPrAction(
        "resolve-merge-conflict",
        { requestId, conversationId: targetConversationId },
        { throwIndeterminateErrors: true },
      );
      if (!result.ok) {
        if (optimisticStageResult.accepted) {
          cancelQueuedMessage(clientMessageId)
        }
        useSessionPrActionState.getState().setResolveConflictFromServer({
          sessionId,
          requestId,
          conversationId: targetConversationId,
          state: "failed",
          error: result.error || "Failed to start conflict resolution",
        })
        capturePrAnalyticsEvent({
          event: ANALYTICS_EVENTS.resolveMergeConflictRequestFailed,
          source: "thread_page",
          properties: {
            error_message: result.error || "Failed to start conflict resolution",
          },
        });
      }
    } catch (error) {
      const errorMessage = getSessionActionErrorMessage(error, "Failed to start conflict resolution")
      if (isIndeterminateSessionActionError(error)) {
        markResolveConflictIndeterminate({
          sessionId,
          message: errorMessage,
        })
      } else {
        if (optimisticStageResult.accepted) {
          cancelQueuedMessage(clientMessageId)
        }
        useSessionPrActionState.getState().setResolveConflictFromServer({
          sessionId,
          requestId,
          conversationId: targetConversationId,
          state: "failed",
          error: errorMessage,
        })
      }
      capturePrAnalyticsEvent({
        event: ANALYTICS_EVENTS.resolveMergeConflictRequestFailed,
        source: "thread_page",
        properties: {
          error_message: errorMessage,
        },
      });
    }
  }, [
    capturePrAnalyticsEvent,
    activeConversationId,
    cancelQueuedMessage,
    markSidebarWorkRequested,
    markResolveConflictIndeterminate,
    postPrAction,
    requestResolveConflict,
    requestResume,
    resolveConflictActionState?.phase,
    repo?.defaultBranch,
    sandboxResumeNeeded,
    sendChatMessage,
    sessionBaseBranch,
    sessionId,
  ]);

  const markThreadViewed = useThreadRuntime((s) => s.markViewed);
  const currentThreadActivityEventId = useThreadRuntime(
    (s) => s.entries[sessionId]?.lastActivityEventId ?? null,
  );
  const currentThreadFinishedEventId = useThreadRuntime(
    (s) => s.entries[sessionId]?.lastFinishedEventId ?? null,
  );
  const threadRuntimeBroadcastTabIdRef = useRef(buildThreadRuntimeBroadcastTabId());
  const threadRuntimeBroadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const lastBroadcastViewedEventIdBySessionRef = useRef<Record<string, number>>({});

  // Open a tab immediately on click (to avoid popup blocker) with a loading
  // page, then navigate it to code-server URL once ready.
  const codeServerTabRef = useRef<{ sessionId: string; tab: Window } | null>(null);
  const resumeThenStartCodeServerRef = useRef<string | null>(null);
  const codeServerRedirectStorageKey = `cord:vscode:redirect:${sessionId}`;
  const codeServerRedirectChannelName = `cord:vscode:${sessionId}`;

  const deliverCodeServerNavigation = useCallback((args: {
    tab: Window | null;
    url: string;
  }) => {
    try {
      window.localStorage.setItem(codeServerRedirectStorageKey, args.url);
    } catch {
      // Ignore storage failures; direct popup messaging can still succeed.
    }

    try {
      const channel = new BroadcastChannel(codeServerRedirectChannelName);
      channel.postMessage({ type: "cord_vscode_redirect", url: args.url });
      channel.close();
    } catch {
      // BroadcastChannel is not guaranteed in every browser context.
    }

    if (!args.tab || args.tab.closed) {
      return;
    }

    try {
      args.tab.postMessage({ type: "cord_vscode_redirect", url: args.url }, window.location.origin);
    } catch {
      // Fall through to direct navigation attempts.
    }

    try {
      args.tab.location.replace(args.url);
      return;
    } catch {
      // Some browsers are stricter about opener-driven popup navigation.
    }

    try {
      args.tab.location.href = args.url;
    } catch {
      // If even direct location assignment fails, the popup can still consume
      // the storage or BroadcastChannel redirect signal on its own.
    }
  }, [codeServerRedirectChannelName, codeServerRedirectStorageKey]);

  const startCodeServerFromUi = useCallback(() => {
    if (sessionArchived) {
      return;
    }

    if (codeServerStatus === "starting") {
      return;
    }

    // Provider-native port URLs can exist before code-server is actually
    // listening, so always go through the start/wrapper flow on click.
    try {
      window.localStorage.removeItem(codeServerRedirectStorageKey);
    } catch {
      // Ignore storage cleanup failures; they only affect popup fallback state.
    }

    const tab = window.open("about:blank", "_blank");
    if (tab) {
      tab.document.write(`<!DOCTYPE html>
<html><head><title>Starting VS Code...</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{background:#111;color:#e4e4e7;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh}
.c{text-align:center}
.spinner{width:32px;height:32px;border:3px solid #333;border-top-color:#a1a1aa;border-radius:50%;animation:spin .8s linear infinite;margin:0 auto 20px}
@keyframes spin{to{transform:rotate(360deg)}}
h1{font-size:16px;font-weight:500;margin-bottom:8px}
p{font-size:13px;color:#71717a;max-width:420px;line-height:1.4}
</style>
<script>
(() => {
  const redirectStorageKey = ${JSON.stringify(codeServerRedirectStorageKey)};
  const redirectChannelName = ${JSON.stringify(codeServerRedirectChannelName)};
  const allowedOrigin = ${JSON.stringify(window.location.origin)};
  const redirect = (url) => {
    if (typeof url !== "string" || url.length === 0) return;
    window.location.replace(url);
  };

  try {
    const existingUrl = window.localStorage.getItem(redirectStorageKey);
    if (existingUrl) {
      redirect(existingUrl);
      return;
    }
  } catch {
    // Ignore storage access failures in restricted browser modes.
  }

  window.addEventListener("message", (event) => {
    if (event.origin !== allowedOrigin) return;
    const data = event.data;
    if (!data || data.type !== "cord_vscode_redirect" || typeof data.url !== "string") return;
    redirect(data.url);
  });

  window.addEventListener("storage", (event) => {
    if (event.key !== redirectStorageKey || typeof event.newValue !== "string") return;
    redirect(event.newValue);
  });

  try {
    if (typeof BroadcastChannel === "function") {
      const channel = new BroadcastChannel(redirectChannelName);
      channel.onmessage = (event) => {
        const data = event.data;
        if (!data || data.type !== "cord_vscode_redirect" || typeof data.url !== "string") return;
        redirect(data.url);
      };
    }
  } catch {
    // BroadcastChannel is best-effort only.
  }
})();
</script></head><body>
<div class="c"><div class="spinner"></div><h1>Starting VS Code</h1><p>Preparing the editor in your sandbox. This can take up to 30 seconds.</p></div>
</body></html>`);
      tab.document.close();
      codeServerTabRef.current = { sessionId, tab };

      if (effectiveVscodeUrl) {
        window.setTimeout(() => {
          if (codeServerTabRef.current?.sessionId !== sessionId) {
            return;
          }
          deliverCodeServerNavigation({
            tab: codeServerTabRef.current?.tab ?? null,
            url: effectiveVscodeUrl,
          });
        }, CODE_SERVER_POPUP_NAVIGATION_FALLBACK_DELAY_MS);
      }
    }

    send({ type: "start_code_server" });
  }, [
    codeServerRedirectChannelName,
    codeServerRedirectStorageKey,
    codeServerStatus,
    deliverCodeServerNavigation,
    effectiveVscodeUrl,
    send,
    sessionArchived,
    sessionId,
  ]);

  useEffect(() => {
    if (codeServerStatus === "running" && codeServerTabRef.current?.sessionId === sessionId && effectiveVscodeUrl) {
      deliverCodeServerNavigation({
        tab: codeServerTabRef.current.tab,
        url: effectiveVscodeUrl,
      });
      codeServerTabRef.current = null;
    }
    if (codeServerStatus === "error") {
      try {
        window.localStorage.removeItem(codeServerRedirectStorageKey);
      } catch {
        // Ignore storage cleanup failures; popup will still show the error state.
      }
      if (codeServerTabRef.current?.sessionId === sessionId) {
        codeServerTabRef.current.tab.close();
        codeServerTabRef.current = null;
      }
    }
  }, [
    codeServerRedirectStorageKey,
    codeServerStatus,
    deliverCodeServerNavigation,
    effectiveVscodeUrl,
    sessionId,
  ]);

  useEffect(() => {
    if (codeServerTabRef.current && codeServerTabRef.current.sessionId !== sessionId) {
      codeServerTabRef.current.tab.close();
      codeServerTabRef.current = null;
    }
    if (resumeThenStartCodeServerRef.current && resumeThenStartCodeServerRef.current !== sessionId) {
      resumeThenStartCodeServerRef.current = null;
    }
  }, [sessionId]);

  useEffect(() => {
    if (resumeThenStartCodeServerRef.current !== sessionId) return;
    if (sandboxStatus !== "running") return;

    resumeThenStartCodeServerRef.current = null;
    startCodeServerFromUi();
  }, [sandboxStatus, sessionId, startCodeServerFromUi]);

  const ptyConnectedReportedRef = useRef(false);
  useEffect(() => {
    ptyConnectedReportedRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    if (ptyConnectedReportedRef.current) return;
    if (sessionArchived) return;
    if (!transportConnectEnabled) return;
    if (status !== "connected") return;
    if (ptyWsStatus !== "connected") return;
    send({ type: "pty_connected" });
    ptyConnectedReportedRef.current = true;
  }, [ptyWsStatus, send, sessionArchived, status, transportConnectEnabled]);

  // Reset Quick PR display state after a short delay once flow completes successfully
  const quickPrOpenedRef = useRef<string | null>(null);
  useEffect(() => {
    if (quickPrStep === "done" && quickPrResult?.prUrl) {
      // Prevent double-resetting for the same PR
      if (quickPrOpenedRef.current === quickPrResult.prUrl) return;
      quickPrOpenedRef.current = quickPrResult.prUrl;
      // Reset after a short delay to allow UI to show success state
      const timer = setTimeout(() => {
        dismissQuickPr();
        quickPrOpenedRef.current = null;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [dismissQuickPr, quickPrStep, quickPrResult]);

  useEffect(() => {
    lastBroadcastViewedEventIdBySessionRef.current = {};

    if (!sessionScopeKey) {
      if (threadRuntimeBroadcastChannelRef.current) {
        threadRuntimeBroadcastChannelRef.current.close();
        threadRuntimeBroadcastChannelRef.current = null;
      }
      return;
    }

    const channel = createThreadRuntimeBroadcastChannel();
    if (!channel) {
      if (threadRuntimeBroadcastChannelRef.current) {
        threadRuntimeBroadcastChannelRef.current.close();
        threadRuntimeBroadcastChannelRef.current = null;
      }
      return;
    }

    threadRuntimeBroadcastChannelRef.current = channel;
    return () => {
      if (threadRuntimeBroadcastChannelRef.current === channel) {
        threadRuntimeBroadcastChannelRef.current = null;
      }
      channel.close();
    };
  }, [sessionScopeKey]);

  useEffect(() => {
    const markIfVisible = () => {
      if (document.visibilityState !== "visible" || !sessionScopeKey) return;
      const viewedEventId =
        markThreadViewed(sessionId) ??
        useThreadRuntime.getState().entries[sessionId]?.lastViewedEventId ??
        0;

      if (typeof viewedEventId !== "number" || viewedEventId <= 0) return;

      const lastBroadcast = lastBroadcastViewedEventIdBySessionRef.current[sessionId] ?? 0;
      if (viewedEventId <= lastBroadcast) return;

      const channel = threadRuntimeBroadcastChannelRef.current;
      if (!channel) return;

      try {
        channel.postMessage({
          type: "thread_mark_viewed",
          scopeKey: sessionScopeKey,
          tabId: threadRuntimeBroadcastTabIdRef.current,
          timestamp: Date.now(),
          sessionId,
          viewedEventId,
        });
        lastBroadcastViewedEventIdBySessionRef.current[sessionId] = viewedEventId;
      } catch {
        // Ignore cross-tab broadcast failures.
      }
    };

    markIfVisible();
    document.addEventListener("visibilitychange", markIfVisible);
    return () => document.removeEventListener("visibilitychange", markIfVisible);
  }, [
    sessionScopeKey,
    currentThreadActivityEventId,
    currentThreadFinishedEventId,
    markThreadViewed,
    sessionId,
  ]);

  useEffect(() => {
    if (!lastMessageRejection) return;
    if (lastMessageRejection.timestamp <= lastMessageRejectionTimestampRef.current) return;
    lastMessageRejectionTimestampRef.current = lastMessageRejection.timestamp;
    if (lastMessageRejection.retryable && lastMessageRejection.recovery) {
      restoreRejectedQueuedMessageToComposer(lastMessageRejection.recovery);
    }
    // User-initiated cancellations are silent — no error toast needed.
    if (lastMessageRejection.reason === "CANCELLED") return;
    const msg =
      normalizeAgentErrorMessage({
        value: lastMessageRejection.message,
        fallbackMessage: "Message was rejected",
      }) ?? "Message was rejected";
    if (lastMessageRejection.reason === "NO_PROVIDER_CONFIGURED" || msg === "NO_PROVIDER_CONFIGURED") {
      toast.error("No AI provider configured", {
        description: "Add an API key in Settings to start a thread.",
        action: { label: "Settings", onClick: () => navigate({ to: "/settings", search: { tab: "api-keys" as any } }) },
      });
    } else {
      toast.error(msg);
    }
  }, [lastMessageRejection, navigate, restoreRejectedQueuedMessageToComposer]);

  // Server-driven PR adoption completion effect.
  // When the DO reports that the background adoption operation has succeeded
  // or failed, transition the local prAdoptionStatus accordingly.
  const lastProcessedPrAdoptionIdRef = useRef<string | null>(null);
  const invalidatePrAdoptionSuccessQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
    queryClient.invalidateQueries({ queryKey: ["pr-status", sessionId] });
    queryClient.invalidateQueries({ queryKey: getRepoOpenPullsQueryKey(repoId) });
    queryClient.invalidateQueries({ queryKey: ["git-status", sessionId] });
    queryClient.invalidateQueries({ queryKey: ["git-log", sessionId] });
    void invalidateSessionRepositoryFileQueries(queryClient, sessionId);
  }, [queryClient, repoId, sessionId]);
  useEffect(() => {
    if (!wsPrAdoption) return;
    const outcomeKey = buildPrAdoptionOutcomeKey({
      operationId: wsPrAdoption.operationId,
      status: wsPrAdoption.status,
    });
    // Avoid processing the same operation twice
    if (outcomeKey && lastProcessedPrAdoptionIdRef.current === outcomeKey) return;

    const acknowledgedOutcomeKey = readAcknowledgedPrAdoptionOutcome(sessionId);
    const outcomeAlreadyAcknowledged =
      outcomeKey !== null && acknowledgedOutcomeKey === outcomeKey;

    if (wsPrAdoption.status === "succeeded") {
      lastProcessedPrAdoptionIdRef.current = outcomeKey;
      setPendingBootstrapPrToAdopt((current) => {
        if (!current) return current;
        return current.number === wsPrAdoption.prNumber
          && current.title === wsPrAdoption.title
          && current.url === wsPrAdoption.url
          && current.branchName === wsPrAdoption.branchName
          ? null
          : current;
      });
      const adoptedPr: AgentRuntimeAttachedPullRequest = {
        number: wsPrAdoption.prNumber,
        url: wsPrAdoption.url,
        title: wsPrAdoption.title,
        branchName: wsPrAdoption.branchName,
      };
      const successAttachmentKey = buildPrAdoptionSuccessAttachmentKey({
        attachedPullRequest: adoptedPr,
      });
      const successAlreadyAcknowledged =
        readAcknowledgedPrAdoptionSuccessAttachment(sessionId) ===
        successAttachmentKey;
      const attachmentAlreadyPresentInConversation = hasObservedAttachedPullRequestInConversation(adoptedPr);
      const attachmentPendingObservation = pendingAttachedPullRequestObservation !== null
        && buildPrAdoptionSuccessAttachmentKey({
          attachedPullRequest: pendingAttachedPullRequestObservation.attachedPullRequest,
        }) === successAttachmentKey;
      // If the first user message already carried this PR tag, suppress any
      // reconnect-time success UI for it.
      const attachmentAlreadyConsumedByFirstMessage =
        readConsumedPrAdoptionAttachment(sessionId) === successAttachmentKey;
      invalidatePrAdoptionSuccessQueries();
      if (attachmentAlreadyPresentInConversation || attachmentAlreadyConsumedByFirstMessage) {
        writeConsumedPrAdoptionAttachment({
          sessionId,
          successAttachmentKey,
        });
        writeAcknowledgedPrAdoptionSuccessAttachment({
          sessionId,
          successAttachmentKey,
        });
        if (outcomeKey) {
          writeAcknowledgedPrAdoptionOutcome({ sessionId, outcomeKey });
        }
        setPrAdoptionStatus(null);
        return;
      }
      if (outcomeAlreadyAcknowledged || successAlreadyAcknowledged) {
        setPrAdoptionStatus(null);
        return;
      }
      if (attachmentPendingObservation) {
        setPrAdoptionStatus(null);
        return;
      }
      // Set the attached PR if not already set (e.g., after reconnect)
      if (!sameAttachedPullRequest(draftAttachedPullRequestRef.current ?? undefined, adoptedPr)) {
        draftAttachedPullRequestRef.current = adoptedPr;
        setDraftAttachedPullRequest(adoptedPr);
      }
      const nextSuccessStatus = maybeBuildPrAdoptionSuccessStatus({
        attachedPullRequest: adoptedPr,
        linkedSessionTitle: null,
        operationId: wsPrAdoption.operationId,
      });
      if (!nextSuccessStatus) {
        return;
      }
      setPrAdoptionStatus((current) => samePrAdoptionStatus(current, nextSuccessStatus)
        ? current
        : nextSuccessStatus);
    } else if (wsPrAdoption.status === "failed") {
      lastProcessedPrAdoptionIdRef.current = outcomeKey;
      setPendingBootstrapPrToAdopt((current) => {
        if (!current) return current;
        return current.number === wsPrAdoption.prNumber
          && current.title === wsPrAdoption.title
          && current.url === wsPrAdoption.url
          && current.branchName === wsPrAdoption.branchName
          ? null
          : current;
      });
      if (outcomeAlreadyAcknowledged) {
        return;
      }
      const nextErrorStatus: PrAdoptionStatus = {
        status: "error",
        prNumber: wsPrAdoption.prNumber,
        title: wsPrAdoption.title,
        error: wsPrAdoption.errorMessage ?? "PR adoption failed",
        operationId: wsPrAdoption.operationId,
      };
      setPrAdoptionStatus((current) => samePrAdoptionStatus(current, nextErrorStatus)
        ? current
        : nextErrorStatus);
    } else if (wsPrAdoption.status === "running" || wsPrAdoption.status === "accepted") {
      // Update the local status to reflect the server's in-progress state.
      // This handles the reconnect case where the browser missed the
      // initial "preparing" → "adopting" transition.
      setPrAdoptionStatus((current) => {
        if (current?.status === "error" || current?.status === "success") return current;
        const nextAdoptingStatus: PrAdoptionStatus = {
          status: "adopting",
          prNumber: wsPrAdoption.prNumber,
          title: wsPrAdoption.title,
          operationId: wsPrAdoption.operationId,
        };
        return samePrAdoptionStatus(current, nextAdoptingStatus)
          ? current
          : nextAdoptingStatus;
      });
    }
  }, [
    invalidatePrAdoptionSuccessQueries,
    hasObservedAttachedPullRequestInConversation,
    pendingAttachedPullRequestObservation,
    repoId,
    sessionId,
    wsPrAdoption,
  ]);

  const runtimeStatusAgentType = useAgentRuntime((state) => state.snapshot?.status.agentType ?? null);
  const runtimeStatusPhase = useAgentRuntime((state) => state.snapshot?.status.phase ?? null);
  const runtimeStatusStartedAt = useAgentRuntime((state) => state.snapshot?.status.startedAt ?? null);
  const runtimeErrorType = useAgentRuntime((state) => state.snapshot?.status.error?.type ?? null);
  const runtimeErrorMessage = useAgentRuntime((state) => state.snapshot?.status.error?.message ?? null);
  const runtimeReplicationIntegrityState = useAgentRuntime(
    (state) => state.snapshot?.status.replicationIntegrity?.state ?? null,
  );
  const runtimeReplicationIntegrityReason = useAgentRuntime(
    (state) => state.snapshot?.status.replicationIntegrity?.reason ?? null,
  );
  const runtimeSupportsPromptParts = useAgentRuntime(
    (state) => state.snapshot?.capabilities.supportsPromptParts ?? null,
  );
  const runtimeSupportsImageAttachments = useAgentRuntime(
    (state) => state.snapshot?.capabilities.supportsImageAttachments ?? null,
  );
  const runtimeSupportsSlashCommands = useAgentRuntime(
    (state) => state.snapshot?.capabilities.supportsSlashCommands ?? null,
  );
  const runtimeSupportsSessionActions = useAgentRuntime(
    (state) => state.snapshot?.capabilities.supportsSessionActions ?? null,
  );
  const activeRuntimeInputPausePending = useAgentRuntime(
    (state) => hasRuntimeInputPausePendingForActiveConversation({
      pendingInteractions: state.snapshot?.pendingInteractions,
      activeConversationProviderSessionId: activeRuntimeConversationStateSnapshot?.providerSessionId ?? null,
      runtimeConversationTabsAvailable,
      conversationMetadataReady: runtimeConversationsQuery.isSuccess,
    }),
  );
  const runtimeSupportsQuestions = useAgentRuntime(
    (state) => state.snapshot?.capabilities.supportsQuestions ?? null,
  );
  const rawClaudeProjection = useAgentRuntime((state) => state.rawClaudeProjection);
  const runtimeHasConversation = useAgentRuntime((state) => {
    const snapshot = state.snapshot;
    return Boolean(
      snapshot
      && (
        snapshot.timeline.length > 0
        || snapshot.pendingInteractions.length > 0
      )
    );
  });
  const runtimeRevertMessageId = useAgentRuntime((state) => state.snapshot?.actions.revertMessageId ?? null);
  const runtimeUndoTargetMessageId = useAgentRuntime((state) => getAgentRuntimeUndoTargetMessageId(state.snapshot));
  const optimisticRevertTargetPresentInRuntimeTimeline = useAgentRuntime((state) => {
    if (!optimisticRevertMessageId) return false;
    return state.snapshot?.timeline.some((item) => getTimelineMessageTargetId(item) === optimisticRevertMessageId) ?? false;
  });
  const optimisticRevertHiddenTailPresentInRuntimeTimeline = useAgentRuntime((state) => {
    if (optimisticRevertHiddenMessageIds.length === 0) return false;
    const hiddenMessageIds = new Set(optimisticRevertHiddenMessageIds);
    return state.snapshot?.timeline.some((item) => {
      const targetId = getTimelineMessageTargetId(item);
      return Boolean(targetId && hiddenMessageIds.has(targetId));
    }) ?? false;
  });
  const runtimeConversationEpoch = useAgentRuntime((state) => state.snapshot?.conversationEpoch ?? null);
  const runtimeActionOutcome = useAgentRuntime((state) => state.snapshot?.sessionActionOutcome ?? null);
  const runtimeAbortAvailabilityEnabled = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.abort?.enabled ?? null,
  );
  const topRuntimeInteractionKind = useAgentRuntime(
    (state) => state.snapshot?.pendingInteractions[0]?.kind ?? null,
  );
  const topRuntimeInteractionRequestId = useAgentRuntime(
    (state) => state.snapshot?.pendingInteractions[0]?.requestId ?? null,
  );
  const runtimeActiveEditMessageOperation = useAgentRuntime(
    (state) => state.snapshot?.activeEditOperation ?? null,
  );
  const runtimeUndoAvailabilityAvailable = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.undo?.available ?? null,
  );
  const runtimeUndoAvailabilityEnabled = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.undo?.enabled ?? null,
  );
  const runtimeUndoAvailabilityDisabledReason = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.undo?.disabledReason ?? null,
  );
  const runtimeRedoAvailabilityAvailable = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.redo?.available ?? null,
  );
  const runtimeRedoAvailabilityEnabled = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.redo?.enabled ?? null,
  );
  const runtimeRedoAvailabilityDisabledReason = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.redo?.disabledReason ?? null,
  );
  const runtimeCompactAvailabilityAvailable = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.compact?.available ?? null,
  );
  const runtimeCompactAvailabilityEnabled = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.compact?.enabled ?? null,
  );
  const runtimeCompactAvailabilityDisabledReason = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.compact?.disabledReason ?? null,
  );
  const runtimeForkAvailabilityAvailable = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.fork_message?.available ?? null,
  );
  const runtimeForkAvailabilityEnabled = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.fork_message?.enabled ?? null,
  );
  const runtimeForkAvailabilityDisabledReason = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.fork_message?.disabledReason ?? null,
  );
  const runtimeEditAvailabilityAvailable = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.edit_message?.available ?? null,
  );
  const runtimeEditAvailabilityEnabled = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.edit_message?.enabled ?? null,
  );
  const runtimeEditAvailabilityDisabledReason = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.edit_message?.disabledReason ?? null,
  );
  const runtimeReplyQuestionAvailabilityEnabled = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.reply_question?.enabled ?? null,
  );
  const runtimeReplyQuestionAvailabilityDisabledReason = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.reply_question?.disabledReason ?? null,
  );
  const runtimeQuestionRejectAvailabilityEnabled = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.question_reject?.enabled ?? null,
  );
  const runtimeQuestionRejectAvailabilityDisabledReason = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.question_reject?.disabledReason ?? null,
  );
  const runtimeReplyPermissionAvailabilityEnabled = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.reply_permission?.enabled ?? null,
  );
  const runtimeReplyPermissionAvailabilityDisabledReason = useAgentRuntime(
    (state) => state.snapshot?.actions.availability.reply_permission?.disabledReason ?? null,
  );
  const visibleRuntimeActionOutcome = useMemo(() => {
    if (!runtimeActionOutcome) return null;
    const targetUserId = typeof runtimeActionOutcome.targetUserId === "string"
      ? runtimeActionOutcome.targetUserId
      : null;
    if (targetUserId && authSession?.user?.id !== targetUserId) return null;
    return runtimeConversationScopedNoticeTargetsActiveConversation({
      conversationId: runtimeActionOutcome.conversationId,
      runtimeConversationTabsAvailable,
      activeConversationId,
    })
      ? runtimeActionOutcome
      : null;
  }, [activeConversationId, authSession?.user?.id, runtimeActionOutcome, runtimeConversationTabsAvailable]);
  const runtimeReadPathActive = true;

  const interactionDockOutcome = useMemo(() => {
    if (!runtimeReadPathActive || !visibleSessionActionNotice || !topRuntimeInteractionKind || !topRuntimeInteractionRequestId) {
      return null;
    }
    const expectedKind = topRuntimeInteractionKind === "ask_user" ? "permission" : topRuntimeInteractionKind;
    if (visibleSessionActionNotice.requestKind !== expectedKind) return null;
    if (visibleSessionActionNotice.requestId !== topRuntimeInteractionRequestId) return null;
    return visibleSessionActionNotice;
  }, [runtimeReadPathActive, topRuntimeInteractionKind, topRuntimeInteractionRequestId, visibleSessionActionNotice]);

  const suppressSessionActionToast = useMemo(() => {
    if (!sessionActionToast) return false;
    if (interactionDockOutcome && sessionActionToast.id === interactionDockOutcome.id) return true;
    return false;
  }, [interactionDockOutcome, sessionActionToast]);

  useEffect(() => {
    if (!sessionActionToast) return;
    if (!suppressSessionActionToast) return;
    setSessionActionToast(null);
  }, [sessionActionToast, suppressSessionActionToast]);

  useEffect(() => {
    if (!sessionActionToast || suppressSessionActionToast) {
      if (sessionActionSonnerIdRef.current !== null) {
        toast.dismiss(sessionActionSonnerIdRef.current);
        sessionActionSonnerIdRef.current = null;
      }
      return;
    }
    const id = sessionActionToast.outcome === "invalid"
      ? toast.error(sessionActionToast.message)
      : toast.warning(sessionActionToast.message);
    sessionActionSonnerIdRef.current = id;
    return () => { toast.dismiss(id); };
  }, [sessionActionToast, suppressSessionActionToast]);

  const agentType = useMemo<SessionAgentIdentity>(() => {
    const resolvedRuntimeAgentType = normalizeSessionAgentIdentity(runtimeStatusAgentType);
    return resolveSessionAgentIdentity({
      runtime: resolvedRuntimeAgentType,
      optimistic: optimisticAgentType,
      session: sessionAgentType,
      ws: wsAgentType,
    });
  }, [optimisticAgentType, runtimeStatusAgentType, sessionAgentType, wsAgentType]);
  const recoveryRuntimeCliCommands = useMemo(() => buildRecoveryRuntimeCliCommands({
    agentType,
    claudeRunnerSessionId: openCodeAccessSnapshot?.claudeRunnerSessionId ?? null,
    openCodeSessionId: runtimeDoOpenCodeSessionId ?? openCodeAccessSnapshot?.sessionId ?? null,
  }), [agentType, openCodeAccessSnapshot?.claudeRunnerSessionId, openCodeAccessSnapshot?.sessionId, runtimeDoOpenCodeSessionId]);
  const recoveryOpenCodeAttachCommand = useMemo(() => {
    if (agentType !== "opencode") return null;
    return buildRecoveryOpenCodeAttachCommand({ openCodeAccessSnapshot });
  }, [agentType, openCodeAccessSnapshot]);
  const recoveryStatusSnapshot = recoveryStatusQuery.data ?? null;
  const recoveryProviderRuntime = recoveryStatusSnapshot?.providerRuntime ?? null;
  const recoverySshStatus = recoveryStatusSnapshot?.sshRecovery ?? null;
  const recoveryCordRetryBlocked = Boolean(sessionRuntimeLifecycle?.recoveryBlockReason) ||
    sessionRuntimeLifecycle?.recoveryDecision?.kind === "replacement_handoff_blocked";
  const recoverySshStatusLoading = recoveryStatusQuery.isFetching && !recoveryStatusSnapshot;
  const recoverySshAccessDisabled = requestRecoverySshAccess.isPending ||
    recoverySshStatusLoading ||
    !recoverySshStatus ||
    recoverySshStatus.available === false;
  const recoveryProviderStatusLabel = recoveryProviderRuntime?.status
    ? recoveryProviderRuntime.status.replace(/_/g, " ")
    : recoveryStatusQuery.isFetching
      ? "checking"
      : "unknown";
  // User-facing copy for the recovery dialog. We deliberately avoid internal
  // names like "Cord" or "Daytona" — end users don't know what those are. The
  // heading/description pair adapts to the most specific failure we know about
  // so the user understands *what* is broken before choosing an action.
  //
  // The dialog is now state-aware: instead of always showing a "Reconnect
  // sandbox" CTA, we derive a mode and render different primary actions.
  //
  // Modes:
  //   healthy          — sandbox running + WS connected, no issues
  //   cord_disconnected — sandbox running but WS/transport unhealthy
  //   sandbox_inactive — sandbox stopped/suspended, needs start/resume
  //   recovery_blocked — Cord's automatic recovery has given up
  //   sandbox_error    — sandbox in error state
  const recoveryDialogMode: RecoveryDialogMode = (() => {
    if (recoveryCordRetryBlocked) return "recovery_blocked";
    if (sandboxStatus === "error") return "sandbox_error";
    if (
      runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_STOPPED" ||
      runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_UNROUTABLE" ||
      runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_HEALTH_TIMEOUT"
    ) {
      return "sandbox_error";
    }
    if (sandboxResumeNeeded) return "sandbox_inactive";
    if (sandboxStatus === "running" && status === "connected") return "healthy";
    if (sandboxStatus === "running") return "cord_disconnected";
    return "sandbox_inactive";
  })();

  const recoveryDialogTitle = (() => {
    switch (recoveryDialogMode) {
      case "healthy":
        return "Sandbox status";
      case "cord_disconnected":
        return "Session connection lost";
      case "sandbox_inactive":
        return sessionSuspended ? "Sandbox is suspended" : "Sandbox is not running";
      case "recovery_blocked":
        return "Automatic reconnect is blocked";
      case "sandbox_error":
        return runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_STOPPED"
          ? "Sandbox stopped unexpectedly"
          : runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_UNROUTABLE"
            ? "Sandbox is unreachable"
            : runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_HEALTH_TIMEOUT"
              ? "Sandbox needs recovery"
              : "Sandbox error";
    }
  })();

  const recoveryDialogDescription = (() => {
    switch (recoveryDialogMode) {
      case "healthy":
        return "Your sandbox is connected and running normally. Advanced recovery options are available below if needed.";
      case "cord_disconnected":
        return "The sandbox is still running, but the app lost its session connection. Reconnecting usually fixes this in a few seconds.";
      case "sandbox_inactive":
        return sessionSuspended
          ? "The sandbox was suspended to save resources. Start it to continue working."
          : "The sandbox is not currently running. Start it to continue, or use SSH for direct shell access.";
      case "recovery_blocked":
        return "We couldn't reconnect this sandbox on our own. Use SSH for direct access — it works even when reconnect is blocked. You can also retry the in-app recovery path.";
      case "sandbox_error":
        return "Start with Reconnect — this fixes most issues. The advanced options below let you connect to your sandbox directly if reconnecting doesn't work.";
    }
  })();
  // Auto-expand the SSH section when the recovery dialog opens in blocked mode,
  // since SSH is the recommended path when Cord recovery has given up.
  useEffect(() => {
    if (!showRecoverySshDialog) return;
    if (recoveryDialogMode === "recovery_blocked") {
      setRecoveryShowShellAccess(true);
    }
  }, [recoveryDialogMode, showRecoverySshDialog]);
  const resolvedForkAgentType = useMemo(
    () => toKnownAgentType(agentType),
    [agentType],
  )
  const agentIdentityResolved = agentType !== "unknown";
  const isClaudeSession = agentType === "claude_code";
  const isCursorSession = agentType === "cursor";
  const chatInputAgentType: AgentType = agentType === "cursor"
    ? "cursor"
    : isClaudeSession
      ? "claude_code"
      : agentType === "opencode"
        ? "opencode"
        : userPreferences.defaults.agentType;
  const { data: composerProvidersData } = useProviders(
    authoritativeSessionAvailable ? sessionId : null,
    runtimeInteractionEnabled,
    chatInputAgentType,
  );
  const sessionModelController = useSessionModelController({
      sessionId,
      serverSessionIntent: persistedSessionModelIntent,
      repositoryId: repoId,
      agentType: chatInputAgentType,
      providers: composerProvidersData?.providers ?? [],
    defaults: composerProvidersData?.defaults ?? {},
    preferences: userPreferences,
    sandboxRunning: runtimeInteractionEnabled,
      loadingModels: composerProvidersData?.loadingModels ?? false,
      historyPresenceHint: sessionHistoryPresence,
    });
  useRuntimeComposeModeSync({ sessionId, agentType, conversationId: activeConversationId });
  const runtimeClaudePermissionMode = useMemo(
    () => deriveClaudePermissionModeFromProjection(rawClaudeProjection),
    [rawClaudeProjection],
  );
  const runtimeClaudeBuildPermissionMode = useMemo(
    () => deriveLatestClaudeBuildPermissionModeFromProjection(rawClaudeProjection),
    [rawClaudeProjection],
  );
  const fallbackCapabilities = agentType === "unknown"
    ? UNKNOWN_AGENT_CAPABILITIES
    : getAgentCapabilities(agentType);
  const capabilities = runtimeSupportsPromptParts !== null
    ? {
        supportsPromptParts: runtimeSupportsPromptParts,
        supportsImageAttachments: runtimeSupportsImageAttachments === true,
        supportsSlashCommands: runtimeSupportsSlashCommands === true,
        supportsSessionActions: runtimeSupportsSessionActions === true,
        supportsQuestionDock: runtimeSupportsQuestions === true,
      }
    : fallbackCapabilities;

  function captureThreadAnalyticsEvent({
    event,
    properties,
    source,
  }: {
    event: string;
    properties?: AnalyticsProperties;
    source: string;
  }): void {
    captureAnalyticsEvent({
      event,
      properties: buildThreadAnalyticsProperties({
        sessionId,
        repositoryId: repoId,
        agentType,
        source,
        properties,
      }),
    });
  }

  function capturePrAnalyticsEvent({
    event,
    properties,
    source,
  }: {
    event: string;
    properties?: AnalyticsProperties;
    source: string;
  }): void {
    captureAnalyticsEvent({
      event,
      properties: buildPrAnalyticsProperties({
        sessionId,
        repositoryId: repoId,
        agentType,
        source,
        prState: effectivePrState,
        properties,
      }),
    });
  }

  function captureThreadMessageSent({
    attachmentCount,
    autoStartedNewWork,
    contextRefCount,
    hasText,
    source,
  }: {
    attachmentCount: number;
    autoStartedNewWork: boolean;
    contextRefCount: number;
    hasText: boolean;
    source: string;
  }): void {
    captureThreadAnalyticsEvent({
      event: ANALYTICS_EVENTS.threadMessageSent,
      source,
      properties: {
        attachment_count: attachmentCount,
        auto_started_new_work: autoStartedNewWork,
        context_ref_count: contextRefCount,
        has_text: hasText,
        session_archived: sessionArchived,
        sandbox_resume_needed: sandboxResumeNeeded,
      },
    });
  }
  const runtimeError = normalizeAgentErrorDetails({
    value: {
      type: runtimeErrorType,
      message: runtimeErrorMessage,
    },
    fallbackType: runtimeErrorType,
  });
  const runtimeErrorKey = runtimeError
    ? `${runtimeError.type ?? runtimeError.code ?? "runtime_error"}:${runtimeError.message}`
    : null;
  const activeConversationViewedEventId = runtimeConversationViewedEventIds[`${sessionId}:${activeConversationId}`] ?? 0;
  const activeConversationThreadRuntimeEntry = runtimeConversationTabsAvailable
    ? deriveRuntimeConversationThreadRuntimeEntry({
        conversation: activeRuntimeConversationStateSnapshot,
        lastViewedEventId: activeConversationViewedEventId,
        lastKnownSandboxStatus: sandboxStatus,
      })
    : undefined;
  const activeRuntimeStatusPhase = runtimeConversationTabsAvailable
    ? deriveRuntimeStatusPhaseForActiveConversation({
        conversation: activeRuntimeConversationStateSnapshot,
        fallbackPhase: runtimeStatusPhase,
        conversationMetadataReady: runtimeConversationsQuery.isSuccess,
      })
    : runtimeStatusPhase;
  const activeRuntimeAbortAvailabilityEnabled = runtimeConversationTabsAvailable
    ? activeRuntimeStatusPhase === "running" && runtimeAbortAvailabilityEnabled === true
    : runtimeAbortAvailabilityEnabled;
  const activeAbortRequest = abortRequestsPendingByConversationId[activeConversationId] ?? null;
  const activePersistedStatus = runtimeConversationTabsAvailable
    ? activeRuntimeStatusPhase
    : sessionStatus;
  const activeQueuedMessages = useMemo(() => filterQueuedMessagesForActiveConversation({
    queuedMessages,
    runtimeConversationTabsAvailable,
    activeConversationId,
  }), [activeConversationId, queuedMessages, runtimeConversationTabsAvailable]);
  const currentActiveThreadActivityEventId = runtimeConversationTabsAvailable
    ? (activeConversationThreadRuntimeEntry?.lastActivityEventId ?? null)
    : currentThreadActivityEventId;
  const currentActiveThreadFinishedEventId = runtimeConversationTabsAvailable
    ? (activeConversationThreadRuntimeEntry?.lastFinishedEventId ?? null)
    : currentThreadFinishedEventId;
  const activeThreadControlState = deriveSessionActiveThreadControlState({
    runtimeStatusPhase: activeRuntimeStatusPhase,
    runtimeAbortEnabled: activeRuntimeAbortAvailabilityEnabled,
    runtimeStartedAt: runtimeStatusStartedAt,
    runtimeInputPausePending: activeRuntimeInputPausePending,
    sessionStatus,
    transportConnectEnabled,
    transportStandbyInAnotherTab,
    transportReadOnly: sessionArchived,
    wsStatus: status,
    wsDegradedSinceAt,
    sandboxStatus,
    lifecycleStatus,
    persistedStatus: activePersistedStatus,
    threadRuntimeEntry: activeConversationThreadRuntimeEntry ?? activeThreadRuntimeEntry,
    transcriptFreshness,
    queuedMessages: activeQueuedMessages,
    abortRequested: activeAbortRequest !== null,
  });
  const agentError = runtimeError && runtimeErrorKey !== dismissedRuntimeErrorKey
    ? {
        type: runtimeError.type ?? runtimeError.code ?? "runtime_error",
        message: runtimeError.message,
      }
    : null;
  const dismissError = () => {
    if (!runtimeErrorKey) return;
    addDismissedSessionErrorKey({
      sessionId,
      scope: RUNTIME_ERROR_DISMISSAL_SCOPE,
      errorKey: runtimeErrorKey,
    });
    setDismissedRuntimeErrorKey(runtimeErrorKey);
  };
  const hasMessages = runtimeHasConversation;
  const revertMessageID = optimisticRevertRestorePending
    ? null
    : runtimeRevertMessageId ?? optimisticRevertMessageId;
  const undoTargetMessageID = runtimeUndoTargetMessageId;
  const rawActiveEditMessageOperation = runtimeActiveEditMessageOperation ?? wsActiveEditMessageOperation;
  const activeEditMessageOperation = (() => {
    const rawOperationConversationId = rawActiveEditMessageOperation && typeof rawActiveEditMessageOperation === "object"
      ? (rawActiveEditMessageOperation as { conversationId?: unknown }).conversationId
      : undefined;
    const operationConversationId = typeof rawOperationConversationId === "string"
      ? rawOperationConversationId.trim()
      : "";
    if (
      operationConversationId.length > 0 &&
      runtimeConversationTabsAvailable &&
      operationConversationId !== activeConversationId
    ) {
      return null;
    }
    return rawActiveEditMessageOperation;
  })();
  const conversationEpoch = runtimeConversationEpoch ?? wsConversationEpoch;

  const editMessageOperationPending = Boolean(
    rawActiveEditMessageOperation
      && rawActiveEditMessageOperation.state !== "completed"
      && rawActiveEditMessageOperation.state !== "failed"
  );
  const sessionActionPending =
    sessionActionBlockingCount > 0
    || hasBlockingPendingSessionActions({ actions: pendingSessionActions });

  useEffect(() => {
    if (!pendingComposerSecondaryActionSettlesWithSessionAction) return;
    if (sessionActionPending) return;
    pendingComposerSecondaryActionKeyRef.current = null;
    setPendingComposerSecondaryActionKey(null);
    setPendingComposerSecondaryActionSettlesWithSessionAction(false);
  }, [pendingComposerSecondaryActionSettlesWithSessionAction, sessionActionPending]);

  useEffect(() => {
    if (!optimisticRevertMessageId) return;
    if (optimisticRevertRestorePending) return;
    if (runtimeRevertMessageId) return;
    if (optimisticRevertTargetPresentInRuntimeTimeline || optimisticRevertHiddenTailPresentInRuntimeTimeline) return;
    setOptimisticRevertMessageId(null);
    setOptimisticRevertHiddenMessageIds([]);
  }, [optimisticRevertHiddenTailPresentInRuntimeTimeline, optimisticRevertMessageId, optimisticRevertRestorePending, optimisticRevertTargetPresentInRuntimeTimeline, runtimeRevertMessageId]);

  useEffect(() => {
    if (!runtimeErrorKey) {
      setDismissedRuntimeErrorKey(null);
      return;
    }

    const dismissedKeys = readDismissedSessionErrorKeys({
      sessionId,
      scope: RUNTIME_ERROR_DISMISSAL_SCOPE,
    });
    setDismissedRuntimeErrorKey(dismissedKeys.has(runtimeErrorKey) ? runtimeErrorKey : null);
  }, [runtimeErrorKey, sessionId]);

  useEffect(() => {
    if (!revertMessageID) {
      setSuppressRevertBanner(false);
    }
  }, [revertMessageID]);

  useEffect(() => {
    if (!optimisticRevertRestorePending) return;
    if (runtimeRevertMessageId) return;
    setOptimisticRevertRestorePending(false);
  }, [optimisticRevertRestorePending, runtimeRevertMessageId]);

  useEffect(() => {
    if (!activeEditMessageOperation) return;

    if (activeEditMessageOperation.state === "failed") {
      if (activeEditMessageOperation.reverted !== true) {
        setOptimisticRevertMessageId(null);
        setOptimisticRevertHiddenMessageIds([]);
      }
      setSuppressRevertBanner(false);
      toast.error(
        activeEditMessageOperation.error?.message && activeEditMessageOperation.error.message.trim().length > 0
          ? activeEditMessageOperation.error.message
          : "Failed to edit message",
      );
      return;
    }

    if (activeEditMessageOperation.state === "completed") {
      // Keep the rewind banner suppressed after a successful edit operation.
      // The runtime can report completion before the reverted OpenCode timeline
      // marker has caught up, and unsuppressing here briefly resurrects the
      // yellow "Message undone" banner even though the user already submitted
      // the replacement message.
      return;
    }

    setSuppressRevertBanner(true);
  }, [activeEditMessageOperation]);

  useEffect(() => {
    if (effectivePrState === "merged") {
      if (mergedBannerShownRef.current) return;
      mergedBannerShownRef.current = true;
      dispatchSharedTimelineTailScrollRequest({ reason: "merged_pr_banner_shown" });
      return;
    }
    mergedBannerShownRef.current = false;
  }, [effectivePrState]);

  const commandListQuery = useQuery({
    queryKey: getSessionCommandListQueryKey(sessionId),
    enabled: capabilities.supportsSlashCommands && runtimeInteractionEnabled,
    queryFn: ({ signal }) => fetchSessionCommandList({ sessionId, signal }),
    staleTime: SESSION_COMMAND_LIST_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!runtimeInteractionEnabled || !agentIdentityResolved) return;

    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    const mentionablesQueryKey = getSessionMentionablesQueryKey({
      sessionId,
      agentType: chatInputAgentType,
    });
    const commandListQueryKey = getSessionCommandListQueryKey(sessionId);

    function scheduleAttempt(nextAttempt: number) {
      if (cancelled || nextAttempt > SESSION_SKILL_STARTUP_WARMUP_MAX_ATTEMPTS) {
        return;
      }
      retryTimeout = setTimeout(() => {
        void runAttempt(nextAttempt);
      }, SESSION_SKILL_STARTUP_WARMUP_RETRY_DELAY_MS);
    }

    async function runAttempt(attempt: number): Promise<void> {
      try {
        let mentionables = await fetchSessionMentionables({
          sessionId,
          refresh: true,
          warmup: true,
        });
        if (cancelled) return;

        if (
          mentionables.runtimeSkillsCacheWarmupPending === true &&
          attempt >= SESSION_SKILL_STARTUP_WARMUP_MAX_ATTEMPTS
        ) {
          const finalMentionables = await fetchSessionMentionables({
            sessionId,
            refresh: true,
          }).catch(() => null);
          if (cancelled) return;
          if (finalMentionables && finalMentionables.fallback !== true) {
            mentionables = finalMentionables;
          }
        }

        if (mentionables.fallback !== true) {
          queryClient.setQueryData<SessionMentionablesResponse>(
            mentionablesQueryKey,
            (current) => mergeMentionablesByRuntimeSkillCacheFreshness({
              current,
              next: mentionables,
            }),
          );
        }

        const warmupStillPending = mentionables.runtimeSkillsCacheWarmupPending === true;

        if (capabilities.supportsSlashCommands) {
          const commands = await fetchSessionCommandList({ sessionId }).catch(() => null);
          if (!cancelled && commands && commands.length > 0) {
            await queryClient.cancelQueries({ queryKey: commandListQueryKey, exact: true });
            const commandListIsSafeToInstall = !warmupStillPending ||
              hasSkillSlashCommand(commands) ||
              attempt >= SESSION_SKILL_STARTUP_WARMUP_MAX_ATTEMPTS;
            if (commandListIsSafeToInstall) {
              queryClient.setQueryData<SlashCommandItem[]>(commandListQueryKey, commands);
            }
          }
        }

        if (warmupStillPending || getMentionablesRuntimeSkillsCacheRefreshedAt(mentionables) <= 0) {
          scheduleAttempt(attempt + 1);
        }
      } catch {
        scheduleAttempt(attempt + 1);
      }
    }

    void runAttempt(1);

    return () => {
      cancelled = true;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [
    agentIdentityResolved,
    capabilities.supportsSlashCommands,
    chatInputAgentType,
    queryClient,
    runtimeInteractionEnabled,
    sessionId,
  ]);

  const commandListItemsByName = useMemo(() => {
    const byName = new Map<string, SlashCommandItem>();
    for (const item of commandListQuery.data ?? []) {
      byName.set(item.name.toLowerCase(), item);
    }
    return byName;
  }, [commandListQuery.data]);

  useEffect(() => {
    if (!showThreadActionsMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!threadActionsMenuRef.current) return;
      if (!threadActionsMenuRef.current.contains(event.target as Node)) {
        setShowThreadActionsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showThreadActionsMenu]);

  useEffect(() => {
    if (showThreadActionsMenu) return;
    setShowConnectionFlow(false);
    setShowRecoveryVersions(false);
  }, [showThreadActionsMenu]);

  useEffect(() => {
    setShowThreadActionsMenu(false);
    setShowConnectionFlow(false);
  }, [sessionId]);

  const isBusy = activeThreadControlState.isCurrentResponseActive;
  const prAdoptionInProgress = prAdoptionStatus?.status === "adopting" || prAdoptionStatus?.status === "preparing";
  const busyActionDisabledReason = deriveSessionBusyActionDisabledReason({
    workState: activeThreadControlState.workState,
  });

  useEffect(() => {
    if (activeAbortRequest === null) {
      return;
    }

    const resolution = resolveStopPendingStateTransition({
      abortRequestedAt: activeAbortRequest.requestedAt,
      requestedRunStartedAt: activeAbortRequest.requestedRunStartedAt,
      requestedRunActivityEventId: activeAbortRequest.requestedRunActivityEventId,
      currentRuntimeStartedAt: runtimeStatusStartedAt,
      currentThreadActivityEventId: currentActiveThreadActivityEventId,
      currentThreadFinishedEventId: currentActiveThreadFinishedEventId,
      currentResponseActive: activeThreadControlState.isCurrentResponseActive,
      runtimeActionOutcomeAction: visibleRuntimeActionOutcome?.action ?? null,
      runtimeActionOutcomeTimestamp:
        typeof visibleRuntimeActionOutcome?.timestamp === "number" && Number.isFinite(visibleRuntimeActionOutcome.timestamp)
          ? visibleRuntimeActionOutcome.timestamp
          : null,
      lastMessageRejectionReason: lastMessageRejection?.reason ?? null,
      lastMessageRejectionTimestamp:
        typeof lastMessageRejection?.timestamp === "number" && Number.isFinite(lastMessageRejection.timestamp)
          ? lastMessageRejection.timestamp
          : null,
    });

    if (resolution.shouldClear) {
      setAbortRequestsPendingByConversationId((current) => removeAbortRequestPendingState({
        current,
        conversationId: activeAbortRequest.conversationId,
        expected: activeAbortRequest,
      }));
    }
  }, [
    activeAbortRequest,
    activeThreadControlState.isCurrentResponseActive,
    currentActiveThreadActivityEventId,
    currentActiveThreadFinishedEventId,
    lastMessageRejection,
    runtimeStatusStartedAt,
    visibleRuntimeActionOutcome,
  ]);

  useEffect(() => {
    const pendingAbortRequests = Object.values(abortRequestsPendingByConversationId);
    if (pendingAbortRequests.length === 0) {
      return;
    }

    const nowMs = Date.now();
    const hasExpiredRequest = pendingAbortRequests.some((pendingRequest) => {
      return nowMs - pendingRequest.requestedAt >= ABORT_REQUEST_PENDING_TIMEOUT_MS;
    });
    if (hasExpiredRequest) {
      setAbortRequestsPendingByConversationId((current) => {
        let changed = false;
        const next = { ...current };
        for (const [conversationId, pendingRequest] of Object.entries(current)) {
          if (nowMs - pendingRequest.requestedAt < ABORT_REQUEST_PENDING_TIMEOUT_MS) continue;
          delete next[conversationId];
          changed = true;
        }
        return changed ? next : current;
      });
      return;
    }

    const timeoutMs = Math.min(...pendingAbortRequests.map((pendingRequest) => {
      return ABORT_REQUEST_PENDING_TIMEOUT_MS - (nowMs - pendingRequest.requestedAt);
    }));
    const timeoutId = window.setTimeout(() => {
      const expiryCheckStartedAt = Date.now();
      setAbortRequestsPendingByConversationId((current) => {
        let changed = false;
        const next = { ...current };
        for (const [conversationId, pendingRequest] of Object.entries(current)) {
          if (expiryCheckStartedAt - pendingRequest.requestedAt < ABORT_REQUEST_PENDING_TIMEOUT_MS) continue;
          delete next[conversationId];
          changed = true;
        }
        return changed ? next : current;
      });
    }, timeoutMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [abortRequestsPendingByConversationId]);
  const branchSwitchNotice = useMemo(() => {
    if (mergedThreadContinuationState.kind === "unarchiving") {
      return {
        title: "Unarchiving this thread",
        description: "Making the archived thread writable again. You can send once unarchiving finishes — if this PR was merged, we will prepare a fresh branch next.",
      };
    }
    if (mergedThreadContinuationState.kind === "waiting_for_unarchive") {
      return {
        title: "Unarchiving this thread before preparing new work",
        description: "The archived thread is being unarchived first. As soon as that finishes, we will create a fresh branch for your next message.",
      };
    }
    if (startNewWorkPreparationPending) {
      // Phase 1: Sandbox is being restored from Daytona cold storage after an
      // unarchive. This is the slow part (10–20 s) and the user should know
      // why it's taking longer than a normal ~5 s resume.
      if (sandboxRestoringFromArchive) {
        return {
          title: "Restoring sandbox from cold storage",
          description: "This sandbox was archived and is being restored — this typically takes 10–20 seconds. Once it's ready, we'll create a fresh branch for your new work. You can keep typing in the meantime.",
        };
      }
      // Phase 2: Sandbox is running (or was never cold-stored). The actual
      // branch creation + git ops are the remaining work.
      const startupHint = sandboxResumeNeeded
        ? " This may take a moment while the sandbox starts up."
        : "";
      return {
        title: "Creating a fresh branch for new work",
        description: `Preparing the sandbox, syncing the base branch, and creating a new work branch. You can keep typing — your next message will wait for the branch if needed.${startupHint}`,
      };
    }
    // Non-keep-working case: user unarchived a thread (no merged PR) and the
    // sandbox is resuming from cold storage. Show an info bar so the user
    // understands why this resume is taking longer than usual.
    if (sandboxRestoringFromArchive && !prAdoptionInProgress && !continuationAdoptionPending) {
      return {
        title: "Restoring sandbox from cold storage",
        description: "This sandbox was archived and is being restored. This typically takes 10–20 seconds — a bit longer than a normal resume.",
      };
    }
    if (prAdoptionInProgress) {
      return {
        title: "Switching this session to the selected pull request",
        description: "Setting up the PR branch in the background. You can send your message now — it will be delivered once the switch completes.",
      };
    }
    if (continuationAdoptionPending) {
      return {
        title: "Switching this session to the selected source branch",
        description: "Preparing the sandbox and checking out the continuation branch now.",
      };
    }
    if (effectiveBranchSwitchInProgress) {
      return {
        title: "Branch switch in progress",
        description: "Messages, PR actions, and git actions are temporarily paused until the sandbox finishes switching branches.",
      };
    }
    return null;
  }, [effectiveBranchSwitchInProgress, continuationAdoptionPending, mergedThreadContinuationState.kind, prAdoptionInProgress, sandboxResumeNeeded, sandboxRestoringFromArchive, startNewWorkPreparationPending]);
  const continueSlashState = useMemo(() => {
    if (!canManageSession) {
      return {
        enabled: false,
        disabledReason: "Only the thread owner or an admin can continue this session",
      };
    }
    if (sessionArchived) {
      // `/continue` adopts another thread's branch into *this* thread, so it
      // requires the current thread to be writable. This differs from
      // "Start New Thread", which creates a separate session.
      return {
        enabled: false,
        disabledReason: ARCHIVED_THREAD_SLASH_CONTINUE_REASON,
      };
    }
    if (!(lifecycleStatus === "active" || sessionSuspended)) {
      return {
        enabled: false,
        disabledReason: "This session cannot be continued right now",
      };
    }
    if (branchSwitchPending) {
      return {
        enabled: false,
        disabledReason: BRANCH_SWITCH_BLOCKED_REASON,
      };
    }
    if (hasMessages) {
      return {
        enabled: false,
        disabledReason: "Only a fresh session can adopt continuation. Start a new session or use Continue in new session from the source thread",
      };
    }
    if (isBusy) {
      return {
        enabled: false,
        disabledReason: "Unavailable while agent is running",
      };
    }
    return { enabled: true, disabledReason: null };
  }, [branchSwitchPending, canManageSession, hasMessages, isBusy, lifecycleStatus, sessionArchived, sessionSuspended]);
  const prSlashState = useMemo(() => {
    if (repoProvider !== "github") {
      return {
        enabled: false,
        disabledReason: "PR adoption is only available for GitHub repositories right now",
      };
    }
    if (!canManageSession) {
      return {
        enabled: false,
        disabledReason: "Only the thread owner or an admin can switch PR context",
      };
    }
    if (sessionArchived) {
      return {
        enabled: false,
        disabledReason: ARCHIVED_THREAD_READ_ONLY_REASON,
      };
    }
    if (!(lifecycleStatus === "active" || sessionSuspended)) {
      return {
        enabled: false,
        disabledReason: "PR adoption is not available for this session right now",
      };
    }
    if (branchSwitchPending) {
      return {
        enabled: false,
        disabledReason: BRANCH_SWITCH_BLOCKED_REASON,
      };
    }
    if (prAdoptionFreshSession === false) {
      return {
        enabled: false,
        disabledReason: "Only a fresh session can adopt a pull request. Start a new session before switching repo PR context",
      };
    }
    if (isBusy) {
      return {
        enabled: false,
        disabledReason: "Unavailable while agent is running",
      };
    }
    if (prAdoptionInProgress) {
      return {
        enabled: false,
        disabledReason: "Wait for current PR adoption to finish",
      };
    }
    return { enabled: true, disabledReason: null };
  }, [branchSwitchPending, canManageSession, isBusy, lifecycleStatus, prAdoptionFreshSession, prAdoptionInProgress, repoProvider, sessionArchived, sessionSuspended]);
  // Let the actual adoption action proceed even when the fresh-session verdict
  // hasn't arrived yet (prAdoptionFreshSession === null).  The server-side
  // `adopt-pr` handler performs its own authoritative freshness check, so we
  // do not need to block optimistically on the client.  The only case where
  // `null` occurs is a brand-new session whose first status snapshot hasn't
  // landed — and brand-new sessions are always fresh.
  const prAdoptionActionState = useMemo(() => {
    if (!prSlashState.enabled) {
      return prSlashState;
    }
    return { enabled: true, disabledReason: null };
  }, [prSlashState]);
  const hasReadyAttachments = capabilities.supportsImageAttachments && readyAttachments.length > 0;
  // Allow sending even when the PR is merged/closed — the send handler will auto-trigger
  // "start new work" to create a fresh branch before dispatching the message. If that
  // preparation is already in flight, keep Enter/send enabled so the user can keep typing
  // and the submit path simply waits for the shared promise instead of forcing an idle pause.
  // Only block if the sandbox needs an explicit resume AND we can't auto-resolve it (i.e.,
  // no merged PR to reset, or user can't manage the session).
  const canAutoStartNewWork = startNewWorkPreparationActive && !composerReadOnly;
  const sendBlockedByResume = requiresExplicitResume && !canAutoStartNewWork;
  const sendBlockedByBranchSwitch =
    continuationAdoptionPending ||
    (effectiveBranchSwitchInProgress && !startNewWorkPreparationPending && !prAdoptionInProgress);
  const activeOptimisticForkPreparing =
    activeOptimisticRuntimeConversation?.creationKind === "fork" &&
    optimisticCreatingRuntimeConversation?.conversationId === activeConversationId;
  // Chat sends stay available through same-tab transport startup/reconnect gaps
  // because ThreadSyncManager buffers them in the browser-local outbox and
  // flushes them once the session transport is ready. Standby tabs still block
  // sends so the user does not interact through a tab that does not own the
  // foreground session lease.
  const canSend = !composerReadOnly && !activeOptimisticForkPreparing && deriveComposerCanSend({
    sessionActionPending,
    editMessageOperationPending,
    editorTransientBusy,
    sendBlockedByBranchSwitch,
    hasSendableContent: hasContent || hasReadyAttachments || draftAttachedPullRequest !== null,
    attachmentsReady:
      !capabilities.supportsImageAttachments || (!attachmentsUploading && !attachmentsFailed),
    sendBlockedByResume,
    transportUnavailableForInteractiveActions: transportUnavailableForComposerSend,
  });
  const continueInNewSessionDisabledReason = continueSourceConversationPending
    ? CONTINUE_NEW_THREAD_SOURCE_LOADING_REASON
    : !sessionArchived && !terminalPrForContinuation && hasUnpublishedContinuationWork
      ? unpublishedContinuationWorkDisabledReason
      : null;
  continueInNewSessionDisabledReasonRef.current = continueInNewSessionDisabledReason;
  const startNewWorkDisabledReason =
    mergedThreadContinuationState.kind === "archived"
      ? canManageSession
        ? null
        : ARCHIVED_THREAD_START_NEW_WORK_REASON
      : mergedThreadContinuationState.kind === "unarchiving" || mergedThreadContinuationState.kind === "waiting_for_unarchive"
        ? null
        : branchSwitchPending && mergedThreadContinuationState.kind !== "preparing_branch"
          ? BRANCH_SWITCH_BLOCKED_REASON
          : null;
  const startNewWorkActionLabel =
    mergedThreadContinuationState.kind === "archived"
      ? "Keep Working"
      : mergedThreadContinuationState.kind === "unarchiving" || mergedThreadContinuationState.kind === "waiting_for_unarchive"
        ? "Unarchiving…"
        : startNewWorkPreparationPending
          ? sandboxRestoringFromArchive ? "Restoring…" : "Preparing…"
          : "Keep Working";
  const startNewWorkActionIsKeepWorking =
    mergedThreadContinuationState.kind === "archived" || mergedThreadContinuationState.kind === "ready_to_start_new_work";
  const startNewWorkActionPending =
    mergedThreadContinuationState.kind === "unarchiving" ||
    mergedThreadContinuationState.kind === "waiting_for_unarchive" ||
    startNewWorkPreparationPending;
  const startNewWorkActionDescription =
    mergedThreadContinuationState.kind === "archived"
      ? "Unarchives this thread, creates a fresh branch, and keeps you in the same conversation"
      : mergedThreadContinuationState.kind === "unarchiving" || mergedThreadContinuationState.kind === "waiting_for_unarchive"
        ? "Unarchiving the thread before creating a fresh branch for your next message"
        : mergedThreadContinuationState.kind === "preparing_branch"
          ? sandboxRestoringFromArchive
            ? "Restoring the sandbox from cold storage. Once ready, a fresh branch will be created automatically."
            : "Creating a fresh branch in the background. Anything you send now will wait until it is ready."
          : "Creates a fresh branch and continues in this same thread";
  const startNewWorkStatusLabel =
    mergedThreadContinuationState.kind === "archived"
      ? "Archived"
      : mergedThreadContinuationState.kind === "unarchiving"
        ? "Unarchiving"
        : mergedThreadContinuationState.kind === "waiting_for_unarchive"
          ? "Queued"
          : mergedThreadContinuationState.kind === "preparing_branch"
            ? sandboxRestoringFromArchive ? "Restoring sandbox" : "Preparing branch"
            : null;
  const startNewWorkStatusDescription =
    mergedThreadContinuationState.kind === "archived"
      ? "This merged thread is archived. Keep Working will unarchive it and create a fresh branch for the next message."
      : mergedThreadContinuationState.kind === "unarchiving"
        ? "Unarchiving the thread now. You can keep typing; once it is writable we will continue on a fresh branch."
        : mergedThreadContinuationState.kind === "waiting_for_unarchive"
          ? "The thread is being unarchived. As soon as that finishes, fresh branch preparation will continue automatically."
          : mergedThreadContinuationState.kind === "preparing_branch"
            ? sandboxRestoringFromArchive
              ? "The sandbox was archived in cold storage and is being restored (10–20 seconds). Once ready, we'll create a fresh branch."
              : "Fresh branch prep is running in the background. The branch badge already shows the target branch, and your next message will wait if needed."
            : "This merged PR is saved in thread history. Keep Working starts a fresh branch here; New Thread starts a separate conversation."
  const derivedHistoricalStartNewWorkNotice = useMemo(() => {
    if (startNewWorkNotice !== null) return null;
    const latestHistoryEntry = prHistoryEntries[0] ?? null;
    const currentBranch = typeof sessionBranch === "string" ? sessionBranch.trim() : "";
    if (!latestHistoryEntry || currentBranch.length === 0) return null;

    const previousBranch = typeof latestHistoryEntry.branch === "string"
      ? latestHistoryEntry.branch.trim()
      : "";
    if (previousBranch.length > 0 && previousBranch === currentBranch) return null;

    const consumedAtMs = Date.parse(latestHistoryEntry.createdAt);
    if (!Number.isFinite(consumedAtMs)) return null;

    const branchAssignedAtRaw = (session as Record<string, unknown>)?.branchAssignedAt;
    const branchAssignedAtMs = typeof branchAssignedAtRaw === "string" || branchAssignedAtRaw instanceof Date
      ? Date.parse(String(branchAssignedAtRaw))
      : null;
    if (
      typeof branchAssignedAtMs !== "number" ||
      !Number.isFinite(branchAssignedAtMs) ||
      branchAssignedAtMs < consumedAtMs ||
      branchAssignedAtMs - consumedAtMs > START_NEW_WORK_HISTORY_BRANCH_ASSIGNMENT_MAX_LAG_MS
    ) {
      return null;
    }

    return {
      newBranch: currentBranch,
      baseBranch: sessionBaseBranch ?? repo?.defaultBranch ?? "base branch",
      trigger: "background" as StartNewWorkTrigger,
      timestamp: consumedAtMs,
      previousPr: {
        number: latestHistoryEntry.prNumber,
        url: latestHistoryEntry.prUrl,
        state: latestHistoryEntry.prState,
      },
    };
  }, [prHistoryEntries, repo?.defaultBranch, session, sessionBaseBranch, sessionBranch, startNewWorkNotice]);
  const visibleStartNewWorkNotice = startNewWorkNotice ?? derivedHistoricalStartNewWorkNotice;
  const startNewWorkNoticePending =
    visibleStartNewWorkNotice !== null && (
      localStartNewWorkGitPending?.newBranch === visibleStartNewWorkNotice.newBranch ||
      (
        startNewWorkGitPending &&
        (
          displaySessionBranch === visibleStartNewWorkNotice.newBranch ||
          startNewWorkNotice?.newBranch === visibleStartNewWorkNotice.newBranch
        )
      )
    );
  const startNewWorkTimelineInterstitials = useMemo(() => {
    if (!visibleStartNewWorkNotice) return [];
    const notice = visibleStartNewWorkNotice;
    const previousPrHref = buildSessionPullRequestVisitHref({
      sessionId,
      repoProvider,
      pullRequestNumber: notice.previousPr.number,
      rawPullRequestUrl: notice.previousPr.url,
    });
    return [{
      key: `start-new-work:${sessionId}:${notice.previousPr.number}:${notice.newBranch}`,
      timestamp: notice.timestamp,
      node: (
        <NewWorkStartedNotice
          newBranch={notice.newBranch}
          baseBranch={notice.baseBranch}
          previousPrNumber={notice.previousPr.number}
          previousPrUrl={previousPrHref}
          previousPrState={notice.previousPr.state}
          pending={startNewWorkNoticePending}
        />
      ),
    }];
  }, [repoProvider, sessionId, startNewWorkNoticePending, visibleStartNewWorkNotice]);
  const resumeBannerResumeDisabledReason = sessionArchived ? ARCHIVED_THREAD_RESUME_REASON : null;
  const failedAttachments = useMemo(
    () => attachments.filter((attachment) => attachment.status === "failed"),
    [attachments],
  );
  const failedAttachmentSignature = useMemo(
    () =>
      failedAttachments
        .map((attachment) => `${attachment.id}:${attachment.error ?? "Upload failed"}`)
        .sort()
        .join("|"),
    [failedAttachments],
  );
  const [dismissedAttachmentFailureSignature, setDismissedAttachmentFailureSignature] = useState<string | null>(null);
  const [retryingFailedAttachments, setRetryingFailedAttachments] = useState(false);
  const showAttachmentFailureToast =
    !attachmentsUploading &&
    failedAttachmentSignature.length > 0 &&
    failedAttachmentSignature !== dismissedAttachmentFailureSignature;
  const attachmentFailureHeadline = failedAttachments.length > 1
    ? `${failedAttachments.length} attachment uploads failed`
    : "Attachment upload failed";
  const attachmentFailureDetail =
    failedAttachments.length > 0
      ? failedAttachments[0].error ?? "Attachment upload failed."
      : "Attachment upload failed.";

  const retryAllFailedAttachments = useCallback(() => {
    if (failedAttachments.length === 0) return;
    if (retryingFailedAttachments) return;
    setRetryingFailedAttachments(true);
    setDismissedAttachmentFailureSignature(failedAttachmentSignature);
    void Promise.all(failedAttachments.map((attachment) => retryAttachment(attachment.id)))
      .finally(() => {
        setRetryingFailedAttachments(false);
      });
  }, [failedAttachmentSignature, failedAttachments, retryAttachment, retryingFailedAttachments]);

  useEffect(() => {
    if (failedAttachmentSignature.length > 0) return;
    setDismissedAttachmentFailureSignature(null);
    setRetryingFailedAttachments(false);
  }, [failedAttachmentSignature]);

  useEffect(() => {
    if (!showAttachmentFailureToast) return;
    const id = toast.error(attachmentFailureHeadline, {
      description: attachmentFailureDetail,
      duration: Infinity,
      action: {
        label: "Retry upload",
        onClick: retryAllFailedAttachments,
      },
      cancel: {
        label: "Dismiss",
        onClick: () => setDismissedAttachmentFailureSignature(failedAttachmentSignature),
      },
    });
    return () => { toast.dismiss(id); };
  }, [
    attachmentFailureDetail,
    attachmentFailureHeadline,
    failedAttachmentSignature,
    retryAllFailedAttachments,
    showAttachmentFailureToast,
  ]);

  const sandboxLoading = sandboxLoadingEarly;
  const sandboxStartupUiActive = !sessionArchived && (sandboxLoading || (resumeActionPending && sandboxResumeNeeded));
  const sandboxStartupKind = inferSandboxStartupKind({
    progress: sandboxProgress,
    bootSource,
    explicitKind: sandboxRestoringFromArchive ? "restore" : resumeActionPending ? "resume" : serverSandboxStartupKind,
  });
  const sandboxStartupLabel = getSandboxStartupUiLabel(sandboxStartupKind);
  const sandboxStatusDisplayLabel = sandboxStartupUiActive
    ? sandboxStartupLabel
    : formatSandboxStatus(sandboxStatus);

  useEffect(() => {
    if (resumeUiPendingState?.sessionId !== sessionId) {
      return;
    }

    if (sandboxStatus === "running" || sandboxStatus === "error") {
      setResumeUiPendingState((current) =>
        current?.sessionId === sessionId ? null : current,
      );
      return;
    }

    const elapsedMs = Date.now() - resumeUiPendingState.requestedAt;
    if (elapsedMs >= RESUME_UI_PENDING_TIMEOUT_MS) {
      setResumeUiPendingState((current) =>
        current?.sessionId === sessionId ? null : current,
      );
      return;
    }

    const timeoutMs = RESUME_UI_PENDING_TIMEOUT_MS - elapsedMs;
    const timeoutId = setTimeout(() => {
      setResumeUiPendingState((current) =>
        current?.sessionId === sessionId ? null : current,
      );
    }, timeoutMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [resumeUiPendingState, sandboxStatus, sessionId]);

  // Clear the "recently unarchived" flag once the sandbox is fully running.
  // At that point, the Daytona cold-storage restore is complete and all
  // downstream status messages can revert to normal (non-archive) wording.
  useEffect(() => {
    if (sessionRecentlyUnarchived && sandboxStatus === "running") {
      setSessionRecentlyUnarchived(false);
    }
  }, [sessionRecentlyUnarchived, sandboxStatus]);

  // Merge real queued messages with any visible pending session-action rows.
  // Action rows go *after* real queued messages so the "X of Y" counter in
  // SharedQueuedMessage prioritises genuine user messages.
  const effectiveQueuedMessages = useMemo(() => {
    const visiblePendingActionMessages = pendingSessionActions.flatMap((action) =>
      action.queuedMessage && action.conversationId === activeConversationId ? [action.queuedMessage] : []
    )
    return mergeQueuedMessagesWithPendingSessionActionMessages({
      queuedMessages: activeQueuedMessages,
      pendingSessionActionMessages: visiblePendingActionMessages,
    })
  }, [activeConversationId, activeQueuedMessages, pendingSessionActions]);
  useEffect(() => {
    if (hasLocalDraftInputRef.current) return;
    if (
      draftSessionContextRefs.length === 0
      && draftRequestedSkillRefs.length === 0
      && draftPreferredMcpServerRefs.length === 0
      && draftAttachedPullRequest === null
    ) {
      return;
    }

    const currentDraft: ThreadDraftSnapshot = {
      text: draftTextRef.current,
      attachments: buildDraftConsumptionAttachments(attachmentsForDraftRef.current),
      contextRefs: draftSessionContextRefs,
      requestedSkillRefs: draftRequestedSkillRefs,
      preferredMcpServerRefs: draftPreferredMcpServerRefs,
      attachedPullRequest: draftAttachedPullRequest,
      cursorPosition: draftCursorPositionRef.current,
      updatedAt: draftSnapshotUpdatedAtRef.current,
    };
    const consumedObservation = findConsumedThreadDraftObservation({
      draft: currentDraft,
      observations: buildThreadDraftConsumptionObservations({
        timelineItems: runtimeTimelineItems ?? [],
        queuedMessages: effectiveQueuedMessages,
      }),
    });
    if (!consumedObservation) return;

    const consumedClearUpdatedAt = persistConsumedThreadDraftClear({
      consumedDraft: currentDraft,
      observationTimestamp: consumedObservation.timestamp,
    });
    editorRef.current?.clear();
    setHasContent(false);
    setDraftText("");
    draftTextRef.current = "";
    draftSessionContextRefsRef.current = [];
    setDraftSessionContextRefs([]);
    draftRequestedSkillRefsRef.current = [];
    setDraftRequestedSkillRefs([]);
    draftPreferredMcpServerRefsRef.current = [];
    setDraftPreferredMcpServerRefs([]);
    draftAttachedPullRequestRef.current = null;
    setDraftAttachedPullRequest(null);
    draftCursorPositionRef.current = 0;
    clearAllAttachments();
    attachmentsForDraftRef.current = [];
    draftSnapshotUpdatedAtRef.current = consumedClearUpdatedAt;
    hydratedDraftSnapshotRef.current = {
      ...EMPTY_THREAD_DRAFT,
      updatedAt: consumedClearUpdatedAt,
    };
    setPrAdoptionStatus((current) => current?.status === "success" ? null : current);
  }, [
    clearAllAttachments,
    draftAttachedPullRequest,
    draftPreferredMcpServerRefs,
    draftRequestedSkillRefs,
    draftSessionContextRefs,
    effectiveQueuedMessages,
    persistConsumedThreadDraftClear,
    runtimeTimelineItems,
  ]);
  const [showConnectionIssue, setShowConnectionIssue] = useState(false);

  const connectionIssueDelayMs = hasConnected
    ? RECONNECT_WARNING_DELAY_MS
    : INITIAL_CONNECTING_NOTICE_DELAY_MS;
  const connectionIssueSinceAt = hasConnected
    ? wsDegradedSinceAt
    : firstConnectionAttemptStartedAt;

  useEffect(() => {
    if (!transportConnectEnabled || status === "connected" || connectionIssueSinceAt === null) {
      setShowConnectionIssue(false);
      return;
    }

    const elapsedMs = Date.now() - connectionIssueSinceAt;
    if (elapsedMs >= connectionIssueDelayMs) {
      setShowConnectionIssue(true);
      return;
    }

    setShowConnectionIssue(false);
    const timer = setTimeout(() => {
      setShowConnectionIssue(true);
    }, connectionIssueDelayMs - elapsedMs);

    return () => clearTimeout(timer);
  }, [connectionIssueDelayMs, connectionIssueSinceAt, status, transportConnectEnabled]);

  const latestSandboxErrorState = useMemo(() => {
    for (let i = sandboxProgress.length - 1; i >= 0; i -= 1) {
      const entry = sandboxProgress[i];
      if (entry.step === "error" && entry.message.trim().length > 0) {
        return {
          message: entry.message,
          timestamp: typeof entry.timestamp === "number" && Number.isFinite(entry.timestamp)
            ? entry.timestamp
            : null,
        };
      }
    }
    return {
      message: null,
      timestamp: null,
    };
  }, [sandboxProgress]);
  const latestSandboxError = latestSandboxErrorState.message;
  const sandboxErrorKey = sandboxStatus === "error"
    ? [
        runtimeLastErrorCode ?? "sandbox_error",
        latestSandboxErrorState.timestamp ?? "no_timestamp",
        latestSandboxErrorState.message ?? runtimeLastErrorMessage ?? "sandbox_error",
      ].join(":")
    : null;
  const sandboxErrorDismissed = sandboxErrorKey !== null && sandboxErrorKey === dismissedSandboxErrorKey;
  const shouldSuppressDuplicateAgentErrorBanner =
    sandboxStatus === "error" &&
    agentError !== null &&
    latestSandboxErrorState.message !== null &&
    latestSandboxErrorState.message.trim() === agentError.message.trim();

  useEffect(() => {
    if (!sandboxErrorKey) {
      setDismissedSandboxErrorKey(null);
      return;
    }

    const dismissedKeys = readDismissedSessionErrorKeys({
      sessionId,
      scope: SANDBOX_ERROR_DISMISSAL_SCOPE,
    });
    setDismissedSandboxErrorKey(dismissedKeys.has(sandboxErrorKey) ? sandboxErrorKey : null);
  }, [sandboxErrorKey, sessionId]);

  const dismissSandboxError = useCallback(() => {
    if (!sandboxErrorKey) return;
    addDismissedSessionErrorKey({
      sessionId,
      scope: SANDBOX_ERROR_DISMISSAL_SCOPE,
      errorKey: sandboxErrorKey,
    });
    setDismissedSandboxErrorKey(sandboxErrorKey);
  }, [sandboxErrorKey, sessionId]);

  useEffect(() => {
    const currentRetry = retryPendingState;
    if (!currentRetry || currentRetry.sessionId !== sessionId) return;

    if (sandboxStatus !== "error" && sandboxStatus !== "stopped") {
      setRetryPendingState(null);
      return;
    }

    if (
      latestSandboxErrorState.timestamp !== null &&
      latestSandboxErrorState.timestamp >
        (currentRetry.baselineErrorTimestamp ?? currentRetry.requestedAt)
    ) {
      setRetryPendingState(null);
      return;
    }

    if (
      latestSandboxErrorState.timestamp === null &&
      currentRetry.baselineErrorTimestamp === null &&
      latestSandboxErrorState.message !== currentRetry.baselineErrorMessage
    ) {
      setRetryPendingState(null);
    }
  }, [latestSandboxErrorState, retryPendingState, sandboxStatus, sessionId]);

  const requestSandboxRetry = useCallback(() => {
    if (retrySandboxPending) return;
    const accepted = sendSessionSocketAction(
      { type: "retry_sandbox" },
      "Could not retry sandbox startup.",
    );
    if (!accepted) return;
    setRetryPendingState({
      baselineErrorMessage: latestSandboxErrorState.message,
      baselineErrorTimestamp: latestSandboxErrorState.timestamp,
      requestedAt: Date.now(),
      sessionId,
    });
  }, [latestSandboxErrorState, retrySandboxPending, sendSessionSocketAction, sessionId]);

  const requestSessionReconnect = useCallback(() => {
    forceThreadSyncReconnect({ reason: "reconnect:recovery_dialog" });
    void refreshStatusFromProbe({ reason: "recovery_dialog_reconnect" });
  }, [forceThreadSyncReconnect, refreshStatusFromProbe]);

  const credentialUsageEffectiveModel = sessionModelController.resolved.model ?? null;
  const credentialUsageModelIntentKey = credentialUsageEffectiveModel
    ? [
        credentialUsageEffectiveModel.providerID,
        credentialUsageEffectiveModel.modelID,
        sessionModelController.resolved.variant ?? "default",
      ].join(":")
    : wsSessionModelIntent?.kind ?? "default";
  const credentialUsageQueryKey = [
    "session-credential-usage",
    sessionId,
    activeCredentialSource?.keyRowId ?? activeCredentialSource?.ref ?? "none",
    credentialUsageModelIntentKey,
  ] as const;
  const canManageCredentialUsage = canManageSessionKnown && canManageSession;
  const buildCredentialUsageEndpoint = useCallback((path: "credential-usage" | "credential-usage/refresh") => {
    const baseUrl = import.meta.env.VITE_API_URL || "";
    const params = new URLSearchParams();
    if (credentialUsageEffectiveModel) {
      params.set("modelProviderID", credentialUsageEffectiveModel.providerID);
      params.set("modelID", credentialUsageEffectiveModel.modelID);
    }
    const suffix = params.size > 0 ? `?${params.toString()}` : "";
    return `${baseUrl}/api/sessions/${sessionId}/${path}${suffix}`;
  }, [credentialUsageEffectiveModel, sessionId]);
  const buildCredentialUsageTransportResponse = useCallback((message: string): SessionCredentialUsageResponse => ({
    ok: true,
    activeSource: activeCredentialSource
      ? {
          ...activeCredentialSource,
          oauthMode: activeCredentialSource.oauthMode ?? null,
          keyRowId: activeCredentialSource.keyRowId ?? null,
        }
      : null,
    supported: true,
    stale: false,
    usage: null,
    error: {
      code: "transport_error",
      message,
    },
    fallbackRecommendation: null,
  }), [activeCredentialSource]);
  const credentialUsageQuery = useQuery({
    queryKey: credentialUsageQueryKey,
    enabled:
      canManageCredentialUsage
      && activeCredentialSource?.kind === "subscription"
      && !sessionArchived,
    staleTime: CREDENTIAL_USAGE_STALE_TIME_MS,
    refetchInterval: CREDENTIAL_USAGE_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const res = await fetch(buildCredentialUsageEndpoint("credential-usage"), {
        method: "GET",
        credentials: "include",
      });
      const body = await res.json().catch(() => null) as
        | SessionCredentialUsageResponse
        | { error?: string }
        | null;
      if (!res.ok) {
        return buildCredentialUsageTransportResponse(body && typeof body === "object" && "error" in body && typeof body.error === "string"
          ? body.error
          : "Failed to load credential usage");
      }
      return body as SessionCredentialUsageResponse;
    },
  });
  const localCredentialFallbackWarningReason = useMemo(() => {
    if (isBusy) {
      return "The agent is currently working. Restarting now will interrupt the active turn."
    }

    if (queuedMessages.length > 0) {
      return "Queued messages will be interrupted if you restart on a different credential source right now."
    }

    if (
      sandboxStatus === "creating"
      || sandboxStatus === "starting_services"
      || sandboxStatus === "starting_agent"
      || sandboxStatus === "starting_opencode"
      || sandboxStatus === "connecting_sse"
    ) {
      return "The sandbox is still starting. Restarting now will interrupt startup and switch credentials immediately."
    }

    return null
  }, [isBusy, queuedMessages.length, sandboxStatus]);
  const refreshCredentialUsage = useMutation({
    mutationFn: async () => {
      const queryKeyAtRequestStart = credentialUsageQueryKey;
      const endpointAtRequestStart = buildCredentialUsageEndpoint("credential-usage/refresh");
      const res = await fetch(endpointAtRequestStart, {
        method: "POST",
        credentials: "include",
      });
      const body = await res.json().catch(() => null) as
        | SessionCredentialUsageResponse
        | { error?: string }
        | null;
      if (!res.ok) {
        throw new Error(body && typeof body === "object" && "error" in body && typeof body.error === "string"
          ? body.error
          : "Failed to refresh credential usage");
      }
      return {
        queryKey: queryKeyAtRequestStart,
        response: body as SessionCredentialUsageResponse,
      };
    },
    onSuccess: (result) => {
      queryClient.setQueryData(result.queryKey, result.response);
      queryClient.invalidateQueries({ queryKey: ["session-credential-usage", sessionId] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to refresh credential usage");
    },
  });

  const manualCredentialFallback = useMutation({
    mutationFn: async (input: { force: boolean }) => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/manual-credential-fallback`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force: input.force }),
      });
      const body = await res.json().catch(() => null) as
        | { ok?: boolean; fromSourceLabel?: string; toSourceLabel?: string; error?: string; code?: string | null }
        | null;
      if (!res.ok || !body?.ok) {
        const error = new Error(body?.error ?? "Failed to switch credentials") as Error & {
          code?: string | null;
        };
        error.code = body?.code ?? null;
        throw error;
      }
      return body;
    },
    onSuccess: (result) => {
      toast.success("Switching credentials", {
        description:
          result.fromSourceLabel && result.toSourceLabel
            ? `${result.fromSourceLabel} → ${result.toSourceLabel}`
            : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["session-credential-usage", sessionId] });
      void refreshStatusFromProbe({ reason: "credential_fallback" });
    },
  });
  const requestManualCredentialFallback = useCallback(async (source: "sandbox_error" | "subscription_usage") => {
    let shouldForce = false;
    if (localCredentialFallbackWarningReason) {
      captureAnalyticsEvent({
        event: ANALYTICS_EVENTS.credentialFallbackConfirmationShown,
        properties: buildThreadAnalyticsProperties({
          agentType: sessionAgentType,
          properties: {
            reason: localCredentialFallbackWarningReason,
            source,
          },
          repositoryId: repoId,
          sessionId,
          source: "thread_page",
        }),
      });
      shouldForce = window.confirm(`${localCredentialFallbackWarningReason}\n\nDo you want to restart anyway and switch credentials?`);
      if (!shouldForce) {
        return;
      }
    }

    captureAnalyticsEvent({
      event: ANALYTICS_EVENTS.credentialFallbackRequested,
      properties: buildThreadAnalyticsProperties({
        agentType: sessionAgentType,
        properties: {
          forced: shouldForce,
          source,
        },
        repositoryId: repoId,
        sessionId,
        source: "thread_page",
      }),
    });

    try {
      await manualCredentialFallback.mutateAsync({ force: shouldForce });
    } catch (error) {
      const fallbackError = error as Error & { code?: string | null };
      if (fallbackError.code === "confirmation_required" && !shouldForce) {
        captureAnalyticsEvent({
          event: ANALYTICS_EVENTS.credentialFallbackConfirmationShown,
          properties: buildThreadAnalyticsProperties({
            agentType: sessionAgentType,
            properties: {
              reason: fallbackError.message,
              source,
            },
            repositoryId: repoId,
            sessionId,
            source: "thread_page",
          }),
        });
        const confirmed = window.confirm(`${fallbackError.message}\n\nDo you want to restart anyway and switch credentials?`);
        if (!confirmed) {
          return;
        }
        captureAnalyticsEvent({
          event: ANALYTICS_EVENTS.credentialFallbackRequested,
          properties: buildThreadAnalyticsProperties({
            agentType: sessionAgentType,
            properties: {
              forced: true,
              source,
            },
            repositoryId: repoId,
            sessionId,
            source: "thread_page",
          }),
        });
        try {
          await manualCredentialFallback.mutateAsync({ force: true });
          return;
        } catch (forcedError) {
          toast.error(forcedError instanceof Error ? forcedError.message : "Failed to switch credentials");
          return;
        }
      }

      toast.error(error instanceof Error ? error.message : "Failed to switch credentials");
    }
  }, [
    localCredentialFallbackWarningReason,
    manualCredentialFallback,
    repoId,
    sessionAgentType,
    sessionId,
  ]);
  const credentialFallbackBlockingWindowsKey =
    credentialUsageQuery.data?.fallbackRecommendation?.blockingWindowIds.join(",") ?? "";
  useEffect(() => {
    if (sessionArchived) {
      return;
    }

    if (credentialUsageQuery.data?.fallbackRecommendation?.shouldOffer !== true) {
      return;
    }

    captureAnalyticsEvent({
      event: ANALYTICS_EVENTS.credentialFallbackSuggested,
      properties: buildThreadAnalyticsProperties({
        agentType: sessionAgentType,
        properties: {
          blocking_windows: credentialUsageQuery.data.fallbackRecommendation.blockingWindowIds.join(","),
          next_source_ref: nextCredentialSource?.ref ?? null,
        },
        repositoryId: repoId,
        sessionId,
        source: "thread_page",
      }),
    });
  }, [
    credentialFallbackBlockingWindowsKey,
    credentialUsageQuery.data?.fallbackRecommendation?.shouldOffer,
    nextCredentialSource?.ref,
    repoId,
    sessionArchived,
    sessionAgentType,
    sessionId,
  ]);

  const credentialFallbackActionLabel = nextCredentialSource?.label ?? null;
  const shouldOfferManualCredentialFallback =
    sandboxStatus === "error"
    && credentialIssue !== null
    && nextCredentialSource !== null
    && canManageSession
    && !sessionArchived;

  // Sandbox is in a transient spin-up phase — auto-resume was triggered or the
  // sandbox is actively starting. Suppress scary "stopped" / "suspended"
  // badges during this window so the user doesn't see alarming red indicators
  // while a resume request is still accepted/in flight. A muted header startup
  // indicator now carries the non-blocking progress signal.
  const resumeGraceActiveRef = useRef(false);
  const resumeGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, forceRerender] = useState(0);

  useEffect(() => {
    if (resumeActionPending) {
      // While resume is active, mark grace as active and clear any pending timer.
      resumeGraceActiveRef.current = true;
      if (resumeGraceTimerRef.current) {
        clearTimeout(resumeGraceTimerRef.current);
        resumeGraceTimerRef.current = null;
      }
    } else if (resumeGraceActiveRef.current && !sandboxLoading) {
      // Resume just finished but sandbox hasn't entered a loading state yet.
      // Start a grace timer to keep suppressing the badge.  Clear any prior
      // timer first — sandboxStatus changes can re-trigger this branch.
      if (resumeGraceTimerRef.current) {
        clearTimeout(resumeGraceTimerRef.current);
      }
      resumeGraceTimerRef.current = setTimeout(() => {
        resumeGraceActiveRef.current = false;
        resumeGraceTimerRef.current = null;
        forceRerender((n) => n + 1);
      }, RESUME_GRACE_PERIOD_MS);
    }
    if (sandboxLoading || sandboxStatus === "running") {
      // Sandbox has moved past the gap — clear grace immediately.
      resumeGraceActiveRef.current = false;
      if (resumeGraceTimerRef.current) {
        clearTimeout(resumeGraceTimerRef.current);
        resumeGraceTimerRef.current = null;
      }
    }
    return () => {
      if (resumeGraceTimerRef.current) {
        clearTimeout(resumeGraceTimerRef.current);
        resumeGraceTimerRef.current = null;
      }
    };
  }, [resumeActionPending, sandboxLoading, sandboxStatus]);

  const sandboxTransientSpinUp = resumeActionPending || sandboxLoading || resumeGraceActiveRef.current;

  // User-facing reason explaining why file/terminal operations are blocked.
  // Threaded down to context menus so the user sees _why_ items are disabled.
  const sandboxUnavailableReason = deriveSandboxUnavailableReason({
    sandboxStatus,
    sessionArchived,
    sandboxTransientSpinUp,
    startupKind: sandboxStartupKind,
  });

  const runtimeLifecycleAvailabilityStatus = useMemo(
    () => deriveRuntimeLifecycleAvailabilityStatus({ runtimeLifecycle: sessionRuntimeLifecycle }),
    [sessionRuntimeLifecycle],
  );

  const topBarAvailabilityStatus = useMemo<TopBarAvailabilityStatus | null>(() => {
    if (!shouldConnectSessionWs) return null;

    if (!sessionArchived && transportStandbyInAnotherTab) {
      return {
        level: "info",
        label: "Standby tab",
        detail: "Another focused tab currently owns the live session connection. Focus this tab to take over live updates.",
      };
    }

    // Archived threads intentionally do not have a connectable sandbox. The
    // backend can still report the last runtime as errored/unreachable after a
    // YOLO merge-and-archive flow, but that is expected archival state rather
    // than an actionable live-sandbox failure. Keep recovery badges below
    // visible for archived sessions because they are historical diagnostics,
    // not live sandbox availability failures.
    if (!sessionArchived && sandboxStatus === "error") {
      return {
        level: "blocked",
        label: "Sandbox error",
        detail: latestSandboxError,
      };
    }

    // Only show "Sandbox stopped" when the sandbox is genuinely stuck —
    // suppress it when a resume is in-flight or the sandbox is already
    // spinning up (the header startup indicator handles that progress cue).
    if (!sessionArchived && sandboxStatus === "stopped" && !sessionSuspended && !sandboxTransientSpinUp) {
      return {
        level: "blocked",
        label: "Sandbox stopped",
      };
    }

    if (recoveryPatchRestoreState === "failed" && recoveryPatchAvailable) {
      return {
        level: "warning",
        label: "Recovery bundle available",
        detail: recoveryPatchRestoreError
          ? `${recoveryPatchRestoreError} ${canManageSession ? "Open the thread menu to download the saved recovery bundle." : "Ask a session manager to download the saved recovery bundle."}`
          : canManageSession
            ? "Automatic recovery failed. Open the thread menu to download the saved recovery bundle."
            : "Automatic recovery failed. Ask a session manager to download the saved recovery bundle.",
      };
    }

    if (showRecoverySuccessBadge) {
      return {
        level: "warning",
        label: "Recovered exact git state",
        detail: "Commits, staged changes, unstaged changes, and untracked files were restored from the saved recovery bundle.",
      };
    }

    // Suspension is an implementation detail — don't show a badge.  The user
    // will either auto-resume on interaction or use the thread-actions menu.
    // (Previously showed a gray "Suspended" info badge here.)

    if (!sessionArchived) {
      const transportAvailabilityStatus = deriveTopBarTransportAvailabilityStatus({
        showConnectionIssue,
        hasConnected,
        status,
        wsTransportProbeState,
      });
      if (transportAvailabilityStatus) {
        return transportAvailabilityStatus;
      }
    }

    if (
      !sessionArchived &&
      runtimeLifecycleAvailabilityStatus &&
      (runtimeLifecycleAvailabilityStatus.level === "blocked" || (!sessionSuspended && !sandboxTransientSpinUp))
    ) {
      return runtimeLifecycleAvailabilityStatus;
    }

    return null;
  }, [
    canManageSession,
    latestSandboxError,
    recoveryPatchAvailable,
    recoveryPatchRestoreError,
    recoveryPatchRestoreState,
    sandboxStatus,
    sandboxTransientSpinUp,
    sessionArchived,
    sessionSuspended,
    shouldConnectSessionWs,
    showRecoverySuccessBadge,
    showConnectionIssue,
    status,
    transportStandbyInAnotherTab,
    runtimeLifecycleAvailabilityStatus,
    wsTransportProbeState,
    hasConnected,
  ]);

  const cachedSidebarWarningReason = useMemo<
    "suspended" | "connection" | "error" | null
  >(() => {
    if (sessionArchived) return null;
    if (sessionSuspended || sandboxStatus === "stopped") return "suspended";
    if (transportStandbyInAnotherTab) return null;
    if (showConnectionIssue) return "connection";
    // When the sandbox is in error state, still allow the sidebar to show
    // cached file tree / git data instead of hiding everything.  The error
    // banner is displayed separately in the chat panel.
    if (sandboxStatus === "error") return "error";
    if (sandboxStatus === "running") return null;
    return null;
  }, [sandboxStatus, sessionArchived, sessionSuspended, showConnectionIssue, transportStandbyInAnotherTab]);

  const recentSandboxProgress = useMemo(() => {
    return sandboxProgress.slice(-6).reverse();
  }, [sandboxProgress]);

  const postSessionAction = useCallback(
    async (
      action: "command" | "review" | "summarize" | "revert" | "unrevert" | "edit-message",
      body?: Record<string, unknown>,
      options?: {
        localBlockingTimeoutMs?: number;
        onLocalBlockingTimeout?: () => void;
        onLateError?: (error: unknown) => void;
      },
    ) => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      if (activeRouteSessionIdRef.current !== sessionId) {
        throw new Error("Session changed before the action could run");
      }

      const localBlockingTimeoutMs = options?.localBlockingTimeoutMs;
      const shouldUseLocalBlockingTimeout =
        typeof localBlockingTimeoutMs === "number"
        && Number.isFinite(localBlockingTimeoutMs)
        && localBlockingTimeoutMs > 0;
      const blockingGeneration = sessionActionBlockingGenerationRef.current;

      let blockingReleased = false;
      const releaseBlocking = () => {
        if (blockingReleased) return;
        blockingReleased = true;
        if (!sessionPageMountedRef.current) {
          return;
        }
        if (sessionActionBlockingGenerationRef.current !== blockingGeneration) {
          return;
        }
        setSessionActionBlockingCount((count) => Math.max(0, count - 1));
      };

      setSessionActionBlockingCount((count) => count + 1);

      let localBlockingTimeoutHandle: number | null = null;
      let localBlockingTimeoutCleanup: (() => void) | null = null;

      const clearLocalBlockingTimeout = () => {
        if (localBlockingTimeoutHandle !== null) {
          window.clearTimeout(localBlockingTimeoutHandle);
          localBlockingTimeoutHandle = null;
        }
        if (localBlockingTimeoutCleanup) {
          actionMessageCleanupRef.current.delete(localBlockingTimeoutCleanup);
          localBlockingTimeoutCleanup = null;
        }
      };

      const requestConversationId = runtimeConversationTabsAvailable
        ? activeConversationId
        : null;

      const runRequest = async () => {
        try {
          const requestBody = (() => {
            const conversationBody = runtimeConversationTabsAvailable
              ? { ...(body ?? {}), conversationId: activeConversationId }
              : (body ?? {});
            if (action !== "command" && action !== "review") {
              return conversationBody;
            }
            if (body && typeof body === "object" && !Array.isArray(body)) {
              const bodyRecord = conversationBody as Record<string, unknown>;
              const rawClientActionId = bodyRecord.clientActionId;
              const existingClientActionId =
                typeof rawClientActionId === "string"
                  ? rawClientActionId.trim()
                  : "";
              if (existingClientActionId.length > 0) {
                return conversationBody;
              }
            }
            return {
              ...conversationBody,
              clientActionId: crypto.randomUUID(),
            } satisfies Record<string, unknown>;
          })();
          const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/${action}`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          });
          if (!res.ok) {
            const raw = await res.text().catch(() => "");
            let details = raw;
            if (raw) {
              try {
                const parsed = JSON.parse(raw) as unknown;
                if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                  const error = (parsed as Record<string, unknown>).error;
                  const message = (parsed as Record<string, unknown>).message;
                  if (typeof error === "string" && error.trim().length > 0) {
                    details = error;
                  } else if (error && typeof error === "object" && !Array.isArray(error)) {
                    const objectMessage = (error as Record<string, unknown>).message;
                    if (typeof objectMessage === "string" && objectMessage.trim().length > 0) {
                      details = objectMessage;
                    }
                  } else if (typeof message === "string" && message.trim().length > 0) {
                    details = message;
                  }
                }
              } catch {
                // Keep raw response text.
              }
            }
            throw createSessionActionRequestError({
              action,
              details: details || null,
              status: res.status,
            });
          }
          return await res.json().catch(() => ({ ok: true }));
        } catch (error) {
          if (shouldRefreshStatusAfterSessionActionError(error)) {
            if (sessionPageMountedRef.current) {
              void refreshStatusFromProbe();
            }
          }
          throw error;
        }
      };

      type SessionActionRequestResult =
        | { kind: "response"; payload: unknown }
        | { kind: "error"; error: unknown }
        | { kind: "local_timeout" };

      const requestResultPromise = runRequest()
        .then((payload): SessionActionRequestResult => ({ kind: "response", payload }))
        .catch((error): SessionActionRequestResult => ({ kind: "error", error }));

      try {
        const requestResult = shouldUseLocalBlockingTimeout
          ? await Promise.race<SessionActionRequestResult>([
              requestResultPromise,
              new Promise<SessionActionRequestResult>((resolve) => {
                localBlockingTimeoutCleanup = () => {
                  if (localBlockingTimeoutHandle !== null) {
                    window.clearTimeout(localBlockingTimeoutHandle);
                    localBlockingTimeoutHandle = null;
                  }
                  actionMessageCleanupRef.current.delete(localBlockingTimeoutCleanup!);
                  localBlockingTimeoutCleanup = null;
                };
                localBlockingTimeoutHandle = window.setTimeout(() => {
                  clearLocalBlockingTimeout();
                  releaseBlocking();
                  try {
                    options?.onLocalBlockingTimeout?.();
                  } catch {
                    // Best effort only — the pending action should still stop blocking.
                  }
                  void refreshStatusFromProbe();
                  resolve({ kind: "local_timeout" });
                }, localBlockingTimeoutMs);
                actionMessageCleanupRef.current.add(localBlockingTimeoutCleanup);
              }),
            ])
          : await requestResultPromise;

        if (requestResult.kind === "response") {
          return requestResult.payload;
        }

        if (requestResult.kind === "error") {
          throw requestResult.error;
        }

        void requestResultPromise.then((lateResult) => {
          if (lateResult.kind !== "error") return;
          if (isIndeterminateSessionActionError(lateResult.error)) return;
          if (!sessionPageMountedRef.current || activeRouteSessionIdRef.current !== sessionId) return;
          if (
            requestConversationId !== null &&
            activeConversationIdRef.current !== requestConversationId
          ) return;
          if (sessionActionBlockingGenerationRef.current !== blockingGeneration) return;
          options?.onLateError?.(lateResult.error);
        });

        return {
          ok: true,
          accepted: true,
          localTimeout: true,
        };
      } finally {
        clearLocalBlockingTimeout();
        releaseBlocking();
      }
    },
    [
      activeConversationId,
      refreshStatusFromProbe,
      runtimeConversationTabsAvailable,
      sessionId,
    ],
  );

  const invalidateCommandList = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: getSessionCommandListQueryKey(sessionId) });
  }, [queryClient, sessionId]);

  const getResolvedComposerSelection = useCallback(() => {
    const settings = useInputSettings.getState();
    const scopedSessionSelection = settings.getSessionModelSelectionWithScope({ sessionId, conversationId: activeConversationId });
    const sessionSelection = scopedSessionSelection.selection;
    const hasStaleExplicitSessionIntent =
      sessionModelController.sessionIntent?.kind === "explicit" &&
      sessionModelController.resolved.source !== "explicit_session_override" &&
      sessionModelController.resolved.invalidationReason !== null;

    const resolvedModel = hasStaleExplicitSessionIntent
      ? sessionModelController.resolved.model
      : sessionSelection.model ?? sessionModelController.resolved.model;
    const requestedVariant = hasStaleExplicitSessionIntent
      ? normalizeThinkingVariant(sessionModelController.resolved.variant)
      : normalizeThinkingVariant(sessionSelection.variant)
        ?? normalizeThinkingVariant(sessionModelController.resolved.variant);
    const resolvedVariant = resolvedModel
      ? resolveEffectiveThinkingVariant({
          providerID: resolvedModel.providerID,
          modelID: resolvedModel.modelID,
          requestedVariant,
          runtimeVariants: findCatalogModelVariants(
            sessionModelController.catalog,
            resolvedModel,
          ),
        })
      : null;

    return {
      settings,
      sessionSelection,
      sessionSelectionConversationId: scopedSessionSelection.conversationId,
      hasStaleExplicitSessionIntent,
      resolvedModel,
      resolvedVariant,
    };
  }, [
    activeConversationId,
    sessionId,
    sessionModelController.resolved.invalidationReason,
    sessionModelController.catalog,
    sessionModelController.resolved.model,
    sessionModelController.resolved.source,
    sessionModelController.resolved.variant,
    sessionModelController.sessionIntent,
  ]);

  const resolveOutgoingThinkingVariant = useCallback((args: {
    model: { providerID: string; modelID: string } | null;
    variant: string | null;
    runtimeVariants?: string[];
  }): string | null => {
    const normalizedVariant = normalizeThinkingVariant(args.variant);
    if (!normalizedVariant) return null;
    if (normalizedVariant === THINKING_OFF_VARIANT) return THINKING_OFF_VARIANT;
    if (!args.model) return null;

    const supportedVariants = getThinkingVariants(
      args.model.providerID,
      args.model.modelID,
      args.runtimeVariants,
    );
    return supportedVariants.includes(normalizedVariant) ? normalizedVariant : null;
  }, []);

  /** Resolve providerID + modelID for an OpenCode compact/summarize action.
   *  Returns the body to send, or null if no model could be resolved. */
  const getCompactModelBody = useCallback((): { providerID: string; modelID: string } | null => {
    const { resolvedModel } = getResolvedComposerSelection();
    if (resolvedModel?.providerID && resolvedModel?.modelID) {
      return { providerID: resolvedModel.providerID, modelID: resolvedModel.modelID };
    }
    return null;
  }, [getResolvedComposerSelection]);

  const compactModelBody = useMemo(
    () => (isClaudeSession ? undefined : getCompactModelBody()),
    [getCompactModelBody, isClaudeSession],
  );
  const compactModelUnavailableReason =
    !isClaudeSession && capabilities.supportsSessionActions && compactModelBody === null
      ? "Select a model before compacting context"
      : null;
  const forkBootstrapActionBlockReason = forkBootstrapPending
    ? "Fork setup is still running"
    : forkBootstrapFailed
      ? "Retry fork setup before using session actions"
      : null
  const sessionActionBlockReason = branchSwitchPending
    ? BRANCH_SWITCH_BLOCKED_REASON
    : sessionArchived
      ? ARCHIVED_THREAD_READ_ONLY_REASON
      : sandboxResumeNeeded
        ? SUSPENDED_THREAD_ACTION_REASON
      : editMessageOperationPending
        ? "Finish the active edit before starting another action"
      : sessionActionPending
        ? "Another session action is already running"
        : forkBootstrapActionBlockReason
          ? forkBootstrapActionBlockReason
          : null;

  const resolveFallbackActionState = useCallback((actionName: AgentRuntimeActionName): RuntimeActionUiState => {
    if (!capabilities.supportsSessionActions) {
      return {
        available: false,
        enabled: false,
        disabledReason: "Session actions unavailable",
      };
    }

    const runningReason = busyActionDisabledReason;
    const getCommandItem = (...names: string[]) => {
      for (const name of names) {
        const item = commandListItemsByName.get(name);
        if (item) return item;
      }
      return null;
    };

    if (actionName === "abort") {
      const abortEnabled = activeThreadControlState.stopMode === "enabled";
      const abortDisabledReason = activeThreadControlState.stopPending
        ? "Stopping the agent..."
        : activeThreadControlState.stopDisabledReason ?? "Nothing to abort right now";
      return {
        available: true,
        enabled: abortEnabled,
        disabledReason: abortEnabled ? null : abortDisabledReason,
      };
    }

    if (isClaudeSession) {
      const commandName =
        actionName === "compact"
          ? ["compact", "summarize"]
          : actionName === "fork_message"
            ? ["fork"]
            : [actionName.replace("_message", "")];
      const commandItem = getCommandItem(...commandName);
      if (commandItem?.enabled === false) {
        return {
          available: true,
          enabled: false,
          disabledReason: commandItem.disabledReason ?? `${commandItem.name} is unavailable right now`,
        };
      }
    }

    if (actionName === "redo") {
      return {
        available: true,
        enabled: !isBusy && Boolean(revertMessageID),
        disabledReason: isBusy ? runningReason : "No redo checkpoint available",
      };
    }

    if (actionName === "undo") {
      return {
        available: true,
        enabled: !isBusy,
        disabledReason: isBusy ? runningReason : null,
      };
    }

    if (actionName === "compact") {
      return {
        available: true,
        enabled: !isBusy && hasMessages,
        disabledReason: isBusy ? runningReason : !hasMessages ? "No conversation to compact yet" : null,
      };
    }

    if (actionName === "fork_message") {
      return {
        available: true,
        enabled: !isBusy && hasMessages,
        disabledReason: isBusy ? runningReason : "No conversation yet",
      };
    }

    return {
      available: true,
      enabled: !isBusy,
      disabledReason: isBusy ? runningReason : null,
    };
  }, [
    activeThreadControlState.stopDisabledReason,
    activeThreadControlState.stopMode,
    activeThreadControlState.stopPending,
    busyActionDisabledReason,
    capabilities.supportsSessionActions,
    commandListItemsByName,
    hasMessages,
    isBusy,
    isClaudeSession,
    revertMessageID,
  ]);

  const runtimeUndoAvailability = buildOptionalRuntimeActionAvailability({
    available: runtimeUndoAvailabilityAvailable,
    enabled: runtimeUndoAvailabilityEnabled,
    disabledReason: runtimeUndoAvailabilityDisabledReason,
  });
  const runtimeRedoAvailability = buildOptionalRuntimeActionAvailability({
    available: runtimeRedoAvailabilityAvailable,
    enabled: runtimeRedoAvailabilityEnabled,
    disabledReason: runtimeRedoAvailabilityDisabledReason,
  });
  const runtimeCompactAvailability = buildOptionalRuntimeActionAvailability({
    available: runtimeCompactAvailabilityAvailable,
    enabled: runtimeCompactAvailabilityEnabled,
    disabledReason: runtimeCompactAvailabilityDisabledReason,
  });
  const runtimeForkAvailability = buildOptionalRuntimeActionAvailability({
    available: runtimeForkAvailabilityAvailable,
    enabled: runtimeForkAvailabilityEnabled,
    disabledReason: runtimeForkAvailabilityDisabledReason,
  });
  const runtimeEditAvailability = buildOptionalRuntimeActionAvailability({
    available: runtimeEditAvailabilityAvailable,
    enabled: runtimeEditAvailabilityEnabled,
    disabledReason: runtimeEditAvailabilityDisabledReason,
  });

  const getBaseActionState = useCallback((actionName: AgentRuntimeActionName): RuntimeActionUiState => {
    const availability =
      actionName === "undo"
        ? runtimeUndoAvailability
        : actionName === "redo"
          ? runtimeRedoAvailability
          : actionName === "compact"
            ? runtimeCompactAvailability
            : actionName === "fork_message"
              ? runtimeForkAvailability
              : actionName === "edit_message"
                ? runtimeEditAvailability
                : null;
    if (availability) {
      return normalizeRuntimeActionAvailability(availability, "Action unavailable");
    }
    return resolveFallbackActionState(actionName);
  }, [
    resolveFallbackActionState,
    runtimeCompactAvailability,
    runtimeEditAvailability,
    runtimeForkAvailability,
    runtimeRedoAvailability,
    runtimeUndoAvailability,
  ]);

  const undoActionState = useMemo(() => {
    const next = applyRuntimeActionBlockReason(getBaseActionState("undo"), sessionActionBlockReason);
    if (!next.available || undoTargetMessageID) return next;
    return {
      ...next,
      enabled: false,
      disabledReason: next.disabledReason ?? "No user message available to undo",
    };
  }, [getBaseActionState, sessionActionBlockReason, undoTargetMessageID]);
  const redoActionState = useMemo(
    () => applyRuntimeActionBlockReason(getBaseActionState("redo"), sessionActionBlockReason),
    [getBaseActionState, sessionActionBlockReason],
  );
  const compactActionState = useMemo(
    () => applyRuntimeActionBlockReason(getBaseActionState("compact"), sessionActionBlockReason ?? compactModelUnavailableReason),
    [compactModelUnavailableReason, getBaseActionState, sessionActionBlockReason],
  );
  const reviewModeActionState = useMemo<RuntimeActionUiState>(() => {
    if (!capabilities.supportsSessionActions) {
      return {
        available: false,
        enabled: false,
        disabledReason: "Session actions unavailable",
      };
    }

    if (!runtimeReadPathActive) {
      return {
        available: false,
        enabled: false,
        disabledReason: "Review mode is only available in shared UI",
      };
    }

    if (!runtimeConversationTabsAvailable) {
      return {
        available: true,
        enabled: false,
        disabledReason: "Review mode needs chat tabs to be available",
      };
    }

    if (isCursorSession) {
      return {
        available: true,
        enabled: false,
        disabledReason: "Review mode is not available for Cursor sessions yet",
      };
    }

    return applyRuntimeActionBlockReason(
      {
        available: true,
        enabled: !isBusy,
        disabledReason: !isBusy ? null : busyActionDisabledReason,
      },
      sessionActionBlockReason,
    );
  }, [
    busyActionDisabledReason,
    isCursorSession,
    capabilities.supportsSessionActions,
    isBusy,
    runtimeConversationTabsAvailable,
    sessionActionBlockReason,
    runtimeReadPathActive,
  ]);
  const forkActionState = useMemo(
    () => {
      const isRuntimeForkAgent = agentType === "opencode" || agentType === "claude_code";
      const localRuntimeForkAvailable =
        isRuntimeForkAgent && runtimeConversationTabsAvailable;
      if (sessionArchived) {
        return {
          available: false,
          enabled: false,
          disabledReason: ARCHIVED_THREAD_READ_ONLY_REASON,
        };
      }
      if (sandboxResumeNeeded) {
        return {
          available: false,
          enabled: false,
          disabledReason: SUSPENDED_THREAD_ACTION_REASON,
        };
      }
      if (agentType === "opencode" && !localRuntimeForkAvailable) {
        return {
          available: true,
          enabled: false,
          disabledReason: "Chat tabs are still loading",
        };
      }
      if (agentType === "claude_code" && !localRuntimeForkAvailable && sessionSupportsFork === false) {
        return {
          available: true,
          enabled: false,
          disabledReason: "Chat tabs are still loading",
        };
      }
      if (sessionSupportsFork === false && !localRuntimeForkAvailable) {
        return {
          available: false,
          enabled: false,
          disabledReason: FORK_UNSUPPORTED_PROVIDER_REASON,
        };
      }
      let localForkDisabledReason: string | null = null;
      if (localRuntimeForkAvailable) {
        localForkDisabledReason = !hasMessages
          ? "No conversation yet"
          : runtimeConversationsQuery.isError
            ? "Chat tabs failed to load"
            : !runtimeConversationsQuery.isSuccess || !activeConversation?.providerSessionId
              ? "Chat tab is still loading"
              : null;
      }
      const baseActionState = applyRuntimeActionBlockReason(
        localRuntimeForkAvailable
          ? {
              available: true,
              enabled: localForkDisabledReason === null,
              disabledReason: localForkDisabledReason,
            }
          : getBaseActionState("fork_message"),
        sessionActionBlockReason,
      );
      if (
        localRuntimeForkAvailable
        && localForkRuntimeConversationInFlight?.sessionId === sessionId
        && baseActionState.enabled
      ) {
        return {
          ...baseActionState,
          enabled: false,
          disabledReason: "Fork is already in progress",
        };
      }
      return baseActionState;
    },
    [
      agentType,
      activeConversation?.providerSessionId,
      getBaseActionState,
      hasMessages,
      localForkRuntimeConversationInFlight,
      runtimeConversationTabsAvailable,
      runtimeConversationsQuery.isError,
      runtimeConversationsQuery.isSuccess,
      sandboxResumeNeeded,
      sessionActionBlockReason,
      sessionArchived,
      sessionId,
      sessionSupportsFork,
    ],
  );
  const editActionState = useMemo(
    () => applyRuntimeActionBlockReason(getBaseActionState("edit_message"), sessionActionBlockReason),
    [getBaseActionState, sessionActionBlockReason],
  );
  const showSessionActionControls =
    undoActionState.available || compactActionState.available;

  const handleReplyQuestion = useCallback(async (requestId: string, answers: string[][]) => {
    if (runtimeReplyQuestionAvailabilityEnabled === false) {
      toast.error(runtimeReplyQuestionAvailabilityDisabledReason ?? "Question reply is unavailable right now");
      return false;
    }
    return await sendInteractiveSessionSocketAction({
      actionLabel: "replying to that question",
      failureMessage: "Connection unavailable. Could not send question reply.",
      message: {
        type: "question_reply",
        conversationId: activeConversationId,
        requestID: requestId,
        answers,
      },
    });
  }, [activeConversationId, runtimeReplyQuestionAvailabilityDisabledReason, runtimeReplyQuestionAvailabilityEnabled, sendInteractiveSessionSocketAction]);

  const handleRejectQuestion = useCallback(async (requestId: string) => {
    if (runtimeQuestionRejectAvailabilityEnabled === false) {
      toast.error(runtimeQuestionRejectAvailabilityDisabledReason ?? "Question rejection is unavailable right now");
      return false;
    }
    return await sendInteractiveSessionSocketAction({
      actionLabel: "dismissing that question",
      failureMessage: "Connection unavailable. Could not reject question.",
      message: {
        type: "question_reject",
        conversationId: activeConversationId,
        requestID: requestId,
      },
    });
  }, [activeConversationId, runtimeQuestionRejectAvailabilityDisabledReason, runtimeQuestionRejectAvailabilityEnabled, sendInteractiveSessionSocketAction]);

  const handleReplyPermission = useCallback(async (
    requestId: string,
    reply: "once" | "always" | "reject" | "allow" | "deny",
    updatedInput?: unknown,
  ) => {
    if (runtimeReplyPermissionAvailabilityEnabled === false) {
      toast.error(runtimeReplyPermissionAvailabilityDisabledReason ?? "Permission reply is unavailable right now");
      return false;
    }
    return await sendInteractiveSessionSocketAction({
      actionLabel: "answering that permission request",
      failureMessage: "Connection unavailable. Could not send permission reply.",
      message: {
        type: "permission_reply",
        conversationId: activeConversationId,
        requestID: requestId,
        reply,
        updatedInput,
      },
    });
  }, [activeConversationId, runtimeReplyPermissionAvailabilityDisabledReason, runtimeReplyPermissionAvailabilityEnabled, sendInteractiveSessionSocketAction]);

  const slashCommands = useMemo<SlashCommandItem[]>(() => {
    const claudeOnlyDisabled = "Not available for Claude sessions";
    const reviewUnavailable = "Review mode is unavailable right now";
    const builtins: SlashCommandItem[] = [
      {
        name: "undo",
        description: "Rewind one step",
        source: "builtin",
        autoSubmit: true,
        enabled: undoActionState.enabled,
        ...(undoActionState.enabled ? {} : { disabledReason: undoActionState.disabledReason ?? "Undo is unavailable right now" }),
      },
      {
        name: "redo",
        description: "Restore rewound state",
        source: "builtin",
        autoSubmit: true,
        enabled: redoActionState.enabled,
        ...(redoActionState.enabled ? {} : { disabledReason: redoActionState.disabledReason ?? "Redo is unavailable right now" }),
      },
      {
        name: "compact",
        description: "Summarize and compact context",
        source: "builtin",
        autoSubmit: true,
        enabled: compactActionState.enabled,
        ...(compactActionState.enabled ? {} : { disabledReason: compactActionState.disabledReason ?? "Compact is unavailable right now" }),
      },
      {
        name: "summarize",
        description: "Alias for /compact",
        source: "builtin",
        autoSubmit: true,
        enabled: compactActionState.enabled,
        ...(compactActionState.enabled ? {} : { disabledReason: compactActionState.disabledReason ?? "Compact is unavailable right now" }),
      },
      // Show /fork when either local runtime-tab fork is ready, or when the
      // provider-native session fork fallback is available for this session.
      ...(forkActionState.available ? [{
        name: "fork",
        description: "Fork from the current point",
        source: "builtin" as const,
        autoSubmit: true,
        enabled: forkActionState.enabled,
        ...(forkActionState.enabled ? {} : { disabledReason: forkActionState.disabledReason ?? "Fork is unavailable right now" }),
      }] : []),
      {
        name: "context",
        description: "Reference another thread for context only",
        source: "builtin",
        enabled: true,
      },
      {
        name: "continue",
        description: "Thread-wide: adopt another session's branch/PR, or add it as context if merged",
        source: "builtin",
        enabled: continueSlashState.enabled,
        ...(continueSlashState.enabled ? {} : { disabledReason: continueSlashState.disabledReason ?? "Continue is unavailable right now" }),
      },
      {
        name: "pr",
        description: "Adopt a repository pull request into this fresh session",
        source: "builtin",
        enabled: prSlashState.enabled,
        ...(prSlashState.enabled ? {} : { disabledReason: prSlashState.disabledReason ?? "PR adoption is unavailable right now" }),
      },
      {
        name: "init",
        description: commandListItemsByName.get("init")?.description ?? "Initialize repo context",
        source: "builtin",
        enabled: commandListItemsByName.get("init")?.enabled ?? !isClaudeSession,
        ...(commandListItemsByName.get("init")?.enabled ?? !isClaudeSession
          ? {}
          : { disabledReason: commandListItemsByName.get("init")?.disabledReason ?? claudeOnlyDisabled }),
      },
      {
        name: "review",
        description: "Review the current tab",
        source: "builtin",
        enabled: reviewModeActionState.enabled,
        ...(reviewModeActionState.enabled
          ? {}
          : { disabledReason: reviewModeActionState.disabledReason ?? reviewUnavailable }),
      },
      {
        name: REVIEW_BRANCH_SLASH_COMMAND_NAME,
        description: "Review the full branch",
        source: "builtin",
        enabled: reviewModeActionState.enabled,
        ...(reviewModeActionState.enabled
          ? {}
          : { disabledReason: reviewModeActionState.disabledReason ?? reviewUnavailable }),
      },
    ];

    const builtinNames = new Set(builtins.map((item) => item.name.toLowerCase()));
    const custom = (commandListQuery.data ?? [])
      .filter((item) => !builtinNames.has(item.name.toLowerCase()))
      .filter((item) => item.source !== "builtin")
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...builtins, ...custom];
  }, [
    commandListItemsByName,
    commandListQuery.data,
    compactActionState.disabledReason,
    compactActionState.enabled,
    continueSlashState.disabledReason,
    continueSlashState.enabled,
    forkActionState.available,
    forkActionState.disabledReason,
    forkActionState.enabled,
    isBusy,
    isClaudeSession,
    prSlashState.disabledReason,
    prSlashState.enabled,
    reviewModeActionState.disabledReason,
    reviewModeActionState.enabled,
    redoActionState.disabledReason,
    redoActionState.enabled,
    undoActionState.disabledReason,
    undoActionState.enabled,
  ]);

  const slashCommandByName = useMemo(() => {
    const byName = new Map<string, SlashCommandItem>();
    for (const item of slashCommands) {
      byName.set(item.name.toLowerCase(), item);
    }
    return byName;
  }, [slashCommands]);

  const applyRuntimeUserMessagePrefillToComposer = useCallback((prefill: RuntimeUserMessagePrefill) => {
    applyRuntimeUserMessagePrefillState({
      prefill,
      markLocalDraftInput,
      clearAllAttachments,
      restoreAttachments,
      setStructuredContent: (input) => editorRef.current?.setStructuredContent(input),
      setRequestedSkillRefs: (refs) => {
        const normalized = dedupeRequestedSkillRefs(refs)
        draftRequestedSkillRefsRef.current = normalized
        setDraftRequestedSkillRefs(normalized)
      },
      setPreferredMcpServerRefs: (refs) => {
        const normalized = dedupePreferredMcpServerRefs(refs)
        draftPreferredMcpServerRefsRef.current = normalized
        setDraftPreferredMcpServerRefs(normalized)
      },
      setAttachedPullRequest: (attachedPullRequest) => {
        draftAttachedPullRequestRef.current = attachedPullRequest;
        setDraftAttachedPullRequest(attachedPullRequest);
        if (attachedPullRequest === null) {
          setPendingBootstrapPrToAdopt(null);
        }
        setPrAdoptionStatus((current) => {
          const nextStatus = attachedPullRequest
            ? maybeBuildVisiblePrAdoptionSuccessStatus({
                attachedPullRequest,
                linkedSessionTitle: findPrLinkedSessionTitle(prefill.contextRefs),
              })
            : null;
          return samePrAdoptionStatus(current, nextStatus) ? current : nextStatus;
        });
      },
      focus: () => editorRef.current?.focus(),
      setHasContent,
      setDraftText: (text) => {
        draftTextRef.current = text;
      },
    });
  }, [clearAllAttachments, markLocalDraftInput, restoreAttachments]);

  /**
   * Start local pending-session-action state, optionally with a synthetic
   * user-message bubble in the timeline.
   *
   * Returns a disposable handle with:
   * - `remove()`           – dismiss the bubble immediately (use on failure).
   * - `scheduleRemoval()`  – subscribe to the runtime timeline and dismiss the
   *                          bubble as soon as a confirming marker appears,
   *                          with a generous safety timeout as fallback.
   *
   * All timers and subscriptions are tracked in `actionMessageCleanupRef` and
   * cleaned up automatically when the component unmounts.
   */
  const storeSubscribe: ActionStoreSubscribe = useCallback(
    (listener: () => void) => useAgentRuntime.subscribe(listener),
    [],
  );

  const getTimeline = useCallback(
    (): readonly AgentRuntimeTimelineItem[] =>
      useAgentRuntime.getState().snapshot?.timeline ?? [],
    [],
  );

  const startPendingSessionAction = useCallback(({
    displayText,
    statusText,
    visibleInTimeline = true,
    acknowledgementMode = "confirming_marker",
    acknowledgementMatcher,
  }: {
    displayText?: string;
    statusText?: string;
    visibleInTimeline?: boolean;
    acknowledgementMode?: ActionAcknowledgementMode;
    acknowledgementMatcher?: ActionAcknowledgementMatcher;
  }) => {
    const queuedMessage = visibleInTimeline
      ? {
          ...buildActionQueuedMessage({ displayText: displayText ?? "", statusText }),
          conversationId: activeConversationId,
        }
      : null;
    const actionId = queuedMessage ? String(queuedMessage.id) : `action:${crypto.randomUUID()}`;

    const baseDisposable = createActionMessageDisposable({
      removeFn: () => {
        setPendingSessionActions((prev) => prev.filter((action) => action.id !== actionId));
      },
      releaseComposerBlockFn: () => {
        setPendingSessionActions((prev) => prev.map((action) =>
          action.id === actionId && action.blocksComposer
            ? { ...action, blocksComposer: false }
            : action
        ));
      },
      getTimeline,
      cleanupRegistry: actionMessageCleanupRef.current,
      acknowledgementMode,
      acknowledgementMatcher,
    });
    const pendingAction: PendingSessionAction = {
      id: actionId,
      conversationId: activeConversationId,
      queuedMessage,
      blocksComposer: true,
      cancelScheduledRemoval: baseDisposable.cancelScheduledRemoval,
    };
    setPendingSessionActions((prev) => [...prev, pendingAction]);
    return {
      ...baseDisposable,
      bindClientActionId: (clientActionId: string) => {
        setPendingSessionActions((prev) => prev.map((action) =>
          action.id === actionId
            ? {
                ...action,
                clientActionId,
                queuedMessage: action.queuedMessage
                  ? {
                      ...action.queuedMessage,
                      clientActionId,
                      conversationId: action.conversationId,
                    }
                  : action.queuedMessage,
              }
            : action
        ));
      },
      markQueued: (nextStatusText: string = "Will run after the current response") => {
        baseDisposable.releaseComposerBlock();
        setPendingSessionActions((prev) => prev.map((action) => {
          if (action.id !== actionId || !action.queuedMessage) return action;
          return {
            ...action,
            blocksComposer: false,
            queuedMessage: promoteActionQueuedMessageToServerQueued({
              message: action.queuedMessage,
              statusText: nextStatusText,
            }),
          };
        }));
      },
    };
  }, [activeConversationId, getTimeline]);

  const handleUndo = useCallback(async (): Promise<boolean> => {
    const targetSessionId = sessionId;
    if (sessionActionPending || editMessageOperationPending) return false;
    if (!undoActionState.available) return false;
    if (!undoActionState.enabled) {
      if (undoActionState.disabledReason) {
        toast.error(undoActionState.disabledReason);
      }
      return false;
    }
    if (sandboxResumeNeeded) {
      if (requiresExplicitResume) return false;
      const resumed = await resumeAndWaitForRunning();
      if (!resumed) return false;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    }
    const runtimeSnapshot = useAgentRuntime.getState().snapshot;
    const targetMessageID = runtimeSnapshot
      ? getAgentRuntimeUndoTargetMessageId(runtimeSnapshot)
      : undoTargetMessageID;
    if (!targetMessageID) {
      toast.error("No user message available to undo");
      return false;
    }
    const prefill = buildRuntimeUserMessagePrefill(
      runtimeSnapshot ? getAgentRuntimeUserMessage(runtimeSnapshot, targetMessageID) : null,
    );

    invalidatePersistedReplicaBySessionId(sessionId);
    setOptimisticRevertRestorePending(false);
    setOptimisticRevertMessageId(targetMessageID);
    setOptimisticRevertHiddenMessageIds(collectOptimisticRevertHiddenMessageIds({
      snapshot: runtimeSnapshot,
      messageId: targetMessageID,
    }));
    applyRuntimeUserMessagePrefillToComposer(prefill);
    historyIndexRef.current = -1;
    savedInputRef.current = "";

    try {
      await postSessionAction("revert", { messageID: targetMessageID });
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
      if (isClaudeSession) invalidateCommandList();
      return true;
    } catch (err) {
      setOptimisticRevertMessageId((current) => current === targetMessageID ? null : current);
      setOptimisticRevertHiddenMessageIds((current) => (
        current.includes(targetMessageID) ? [] : current
      ));
      toast.error(err instanceof Error ? err.message : "Undo failed");
      return false;
    }
  }, [
    applyRuntimeUserMessagePrefillToComposer,
    invalidateCommandList,
    isClaudeSession,
    postSessionAction,
    resumeAndWaitForRunning,
    requiresExplicitResume,
    sessionId,
    sessionActionPending,
    sandboxResumeNeeded,
    setOptimisticRevertMessageId,
    undoTargetMessageID,
    undoActionState.available,
    undoActionState.disabledReason,
    undoActionState.enabled,
    editMessageOperationPending,
  ]);

  const handleRedo = useCallback(async (): Promise<boolean> => {
    const targetSessionId = sessionId;
    if (sessionActionPending || editMessageOperationPending) return false;
    if (!redoActionState.available) return false;
    if (!redoActionState.enabled) {
      if (redoActionState.disabledReason) {
        toast.error(redoActionState.disabledReason);
      }
      return false;
    }
    if (sandboxResumeNeeded) {
      if (requiresExplicitResume) return false;
      const resumed = await resumeAndWaitForRunning();
      if (!resumed) return false;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    }
    const previousOptimisticRevertMessageId = optimisticRevertMessageId;
    const previousOptimisticRevertHiddenMessageIds = optimisticRevertHiddenMessageIds;
    setOptimisticRevertRestorePending(true);
    setOptimisticRevertMessageId(null);
    setOptimisticRevertHiddenMessageIds([]);
    setSuppressRevertBanner(false);
    applyRuntimeUserMessagePrefillToComposer(EMPTY_RUNTIME_USER_MESSAGE_PREFILL);
    historyIndexRef.current = -1;
    savedInputRef.current = "";
    try {
      await postSessionAction("unrevert");
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
      setOptimisticRevertMessageId(null);
      setOptimisticRevertHiddenMessageIds([]);
      if (isClaudeSession) invalidateCommandList();
      return true;
    } catch (err) {
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
      setOptimisticRevertRestorePending(false);
      setOptimisticRevertMessageId(previousOptimisticRevertMessageId);
      setOptimisticRevertHiddenMessageIds(previousOptimisticRevertHiddenMessageIds);
      toast.error(err instanceof Error ? err.message : "Restore failed");
      return false;
    }
  }, [
    applyRuntimeUserMessagePrefillToComposer,
    invalidateCommandList,
    isClaudeSession,
    optimisticRevertHiddenMessageIds,
    optimisticRevertMessageId,
    postSessionAction,
    resumeAndWaitForRunning,
    requiresExplicitResume,
    redoActionState.available,
    redoActionState.disabledReason,
    redoActionState.enabled,
    sessionId,
    sessionActionPending,
    setOptimisticRevertMessageId,
    sandboxResumeNeeded,
    editMessageOperationPending,
  ]);

  const handleCompact = useCallback(async (): Promise<boolean> => {
    const targetSessionId = sessionId;
    if (!compactActionState.available) return false;
    if (!compactActionState.enabled) {
      if (compactActionState.disabledReason) {
        toast.error(compactActionState.disabledReason);
      }
      return false;
    }
    if (sandboxResumeNeeded) {
      if (requiresExplicitResume) return false;
      const resumed = await resumeAndWaitForRunning();
      if (!resumed) return false;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    }

    const handle = startPendingSessionAction({
      // No synthetic bubble — the real compaction marker from the timeline
      // (SharedMarkerItem) provides all the visual feedback the user needs.
      visibleInTimeline: false,
      acknowledgementMode: "confirming_marker",
    });

    try {
      if (isClaudeSession) {
        const settings = useInputSettings.getState();
        const compactAgent = settings.getAgent({ sessionId, conversationId: activeConversationId }).mode;
        await postSessionAction("summarize", {
          mode: resolveClaudeOutgoingPermissionMode({
            composeMode: compactAgent,
            runtimePermissionMode: runtimeClaudePermissionMode,
            runtimeBuildPermissionMode: runtimeClaudeBuildPermissionMode,
          }),
        }, {
          localBlockingTimeoutMs: COMMAND_DRIVEN_SESSION_ACTION_BLOCK_TIMEOUT_MS,
          onLocalBlockingTimeout: () => {
            handle.releaseComposerBlock();
            handle.scheduleRemoval(storeSubscribe);
          },
          onLateError: (error) => {
            handle.remove();
            toast.error(getSessionActionErrorMessage(error, "Compact failed"));
          },
        });
      } else {
        await postSessionAction("summarize", compactModelBody ?? undefined, {
          localBlockingTimeoutMs: COMMAND_DRIVEN_SESSION_ACTION_BLOCK_TIMEOUT_MS,
          onLocalBlockingTimeout: () => {
            handle.releaseComposerBlock();
            handle.scheduleRemoval(storeSubscribe);
          },
          onLateError: (error) => {
            handle.remove();
            toast.error(getSessionActionErrorMessage(error, "Compact failed"));
          },
        });
      }
      if (!isCurrentMountedSessionPage(targetSessionId)) return true;
      markSidebarWorkRequested({ sessionId: targetSessionId });
      // Watch the timeline for the real "compaction" / "context_boundary"
      // marker and remove the synthetic bubble as soon as it appears.
      handle.scheduleRemoval(storeSubscribe);
      return true;
    } catch (err) {
      if (isCurrentMountedSessionPage(targetSessionId)) {
        handle.remove();
        toast.error(err instanceof Error ? err.message : "Compact failed");
      }
      return false;
    }
  }, [
    compactActionState.available,
    compactActionState.disabledReason,
    compactActionState.enabled,
    compactModelBody,
    runtimeClaudeBuildPermissionMode,
    isClaudeSession,
    isCurrentMountedSessionPage,
    markSidebarWorkRequested,
    postSessionAction,
    resumeAndWaitForRunning,
    requiresExplicitResume,
    runtimeClaudePermissionMode,
    sessionId,
    sandboxResumeNeeded,
    startPendingSessionAction,
    storeSubscribe,
  ]);

  const handleReviewMode = useCallback(async (scope: "current-tab" | "full-branch"): Promise<boolean> => {
    const targetSessionId = sessionId;
    if (!reviewModeActionState.available) return false;
    if (!reviewModeActionState.enabled) {
      if (reviewModeActionState.disabledReason) {
        toast.error(reviewModeActionState.disabledReason);
      }
      return false;
    }

    if (sandboxResumeNeeded) {
      if (requiresExplicitResume) return false;
      const resumed = await resumeAndWaitForRunning();
      if (!resumed) return false;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    }

    try {
      let reviewPrompt = effectiveReviewPrompt;
      if (!isRepolessRoute && !reviewPromptQuery.data) {
        const refreshedReviewPrompt = await reviewPromptQuery.refetch();
        if (refreshedReviewPrompt.error || !refreshedReviewPrompt.data) {
          toast.error("Failed to load review prompt");
          return false;
        }
        reviewPrompt = refreshedReviewPrompt.data.reviewPrompt
          ?? refreshedReviewPrompt.data.defaultReviewPrompt
          ?? DEFAULT_REVIEW_PROMPT;
      }

      const clientActionId = crypto.randomUUID();
      const sourceConversationId = activeConversationId;
      const sourceConversationTitle = activeConversation?.isDefault
        ? (fetchedSessionTitle || optimisticTitle || activeConversation?.title || "Main")
        : (activeConversation?.title || "New chat");
      const reviewConversation = await createRuntimeConversation.mutateAsync({
        title: scope === "current-tab"
          ? buildReviewRuntimeConversationTitle(sourceConversationTitle)
          : "Review branch",
      });

      const reviewMessage: { type: string; [key: string]: unknown } = {
        type: "message",
        text: scope === "current-tab"
          ? buildCurrentTabReviewPrompt({ reviewPrompt })
          : reviewPrompt,
        clientMessageId: clientActionId,
        conversationId: reviewConversation.conversationId,
      };
      if (scope === "current-tab") {
        reviewMessage.allowEmptyPrompt = true;
        reviewMessage.contextRefs = [{
          sessionId: targetSessionId,
          conversationId: sourceConversationId,
          repositoryId: repoId,
          title: sourceConversationTitle,
          origin: "context",
          summaryIntent: "code_review",
        }];
      }
      const result = sendChatMessage(reviewMessage);
      if (!result.accepted) {
        toast.error(result.reason ?? "Review mode failed");
        return false;
      }
      markSidebarWorkRequested({ sessionId: targetSessionId });
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Review mode failed");
      return false;
    }
  }, [
    activeConversation?.title,
    activeConversation?.isDefault,
    activeConversationId,
    createRuntimeConversation,
    effectiveReviewPrompt,
    fetchedSessionTitle,
    isRepolessRoute,
    markSidebarWorkRequested,
    optimisticTitle,
    repoId,
    reviewPromptQuery.data,
    reviewPromptQuery.refetch,
    resumeAndWaitForRunning,
    requiresExplicitResume,
    reviewModeActionState.available,
    reviewModeActionState.disabledReason,
    reviewModeActionState.enabled,
    sendChatMessage,
    sessionId,
    sandboxResumeNeeded,
  ]);

  const runComposerSecondaryActionWithFeedback = useCallback(async ({
    actionKey,
    run,
    settleWithSessionAction = false,
  }: {
    actionKey: "undo" | "redo" | "compact" | "review-tab" | "review-branch";
    run: () => Promise<boolean>;
    settleWithSessionAction?: boolean;
  }): Promise<boolean> => {
    if (pendingComposerSecondaryActionKeyRef.current) {
      return false;
    }

    pendingComposerSecondaryActionKeyRef.current = actionKey;
    setPendingComposerSecondaryActionKey(actionKey);
    setPendingComposerSecondaryActionSettlesWithSessionAction(false);

    try {
      const handled = await run();
      if (!sessionPageMountedRef.current) {
        return handled;
      }
      if (!handled) {
        pendingComposerSecondaryActionKeyRef.current = null;
        setPendingComposerSecondaryActionKey(null);
        return false;
      }

      if (settleWithSessionAction) {
        setPendingComposerSecondaryActionSettlesWithSessionAction(true);
        return true;
      }

      pendingComposerSecondaryActionKeyRef.current = null;
      setPendingComposerSecondaryActionKey(null);
      return true;
    } catch (error) {
      if (sessionPageMountedRef.current) {
        pendingComposerSecondaryActionKeyRef.current = null;
        setPendingComposerSecondaryActionKey(null);
        setPendingComposerSecondaryActionSettlesWithSessionAction(false);
      }
      throw error;
    }
  }, []);

  const runComposerUndoWithFeedback = useCallback(() => {
    return runComposerSecondaryActionWithFeedback({
      actionKey: "undo",
      run: handleUndo,
    });
  }, [handleUndo, runComposerSecondaryActionWithFeedback]);

  const runComposerRedoWithFeedback = useCallback(() => {
    return runComposerSecondaryActionWithFeedback({
      actionKey: "redo",
      run: handleRedo,
    });
  }, [handleRedo, runComposerSecondaryActionWithFeedback]);

  const runComposerCompactWithFeedback = useCallback(() => {
    return runComposerSecondaryActionWithFeedback({
      actionKey: "compact",
      run: handleCompact,
      settleWithSessionAction: true,
    });
  }, [handleCompact, runComposerSecondaryActionWithFeedback]);

  const runComposerReviewCurrentTabWithFeedback = useCallback(() => {
    return runComposerSecondaryActionWithFeedback({
      actionKey: "review-tab",
      run: () => handleReviewMode("current-tab"),
    });
  }, [handleReviewMode, runComposerSecondaryActionWithFeedback]);

  const runComposerReviewFullBranchWithFeedback = useCallback(() => {
    return runComposerSecondaryActionWithFeedback({
      actionKey: "review-branch",
      run: () => handleReviewMode("full-branch"),
    });
  }, [handleReviewMode, runComposerSecondaryActionWithFeedback]);

  const handleComposerUndo = useCallback(() => {
    void runComposerUndoWithFeedback();
  }, [runComposerUndoWithFeedback]);

  const handleComposerRedo = useCallback(() => {
    void runComposerRedoWithFeedback();
  }, [runComposerRedoWithFeedback]);

  const handleComposerCompact = useCallback(() => {
    void runComposerCompactWithFeedback();
  }, [runComposerCompactWithFeedback]);

  const handleComposerReviewCurrentTab = useCallback(() => {
    void runComposerReviewCurrentTabWithFeedback();
  }, [runComposerReviewCurrentTabWithFeedback]);

  const handleComposerReviewFullBranch = useCallback(() => {
    void runComposerReviewFullBranchWithFeedback();
  }, [runComposerReviewFullBranchWithFeedback]);

  const forkBootstrapMutation = useMutation({
    mutationFn: async (input: {
      parentSessionId: string
      childSessionId: string
      repositoryId: string
      agentType: AgentType
      title: string
      cacheSnapshot: SessionCacheMutationSnapshot
      messageID?: string
      conversationId?: string
    }) => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const body = {
        childSessionId: input.childSessionId,
        ...(input.messageID ? { messageID: input.messageID } : {}),
        ...(input.conversationId ? { conversationId: input.conversationId } : {}),
      }

      const res = await fetch(`${baseUrl}/api/sessions/${input.parentSessionId}/fork-thread`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).catch((error) => {
        throw new ForkBootstrapRequestError({
          message: error instanceof Error ? error.message : "Fork request failed",
          kind: "transport",
        })
      })

      const payload = await res.json().catch(() => null) as
        | { childSession?: ForkThreadChildSession }
        | { error?: unknown }
        | null

      if (!res.ok) {
        throw new ForkBootstrapRequestError({
          message: getForkThreadErrorMessage(payload, "Fork failed"),
          kind: "server",
        })
      }

      const childSession = payload && "childSession" in payload
        ? payload.childSession
        : null

      if (!childSession || typeof childSession.id !== "string" || childSession.id.trim().length === 0) {
        throw new ForkBootstrapRequestError({
          message: "Fork succeeded but child session metadata was missing",
          kind: "server",
        })
      }

      return {
        childSession,
        repositoryId: input.repositoryId,
        agentType: input.agentType,
        title: input.title,
      }
    },
    onSuccess: ({ childSession, repositoryId, agentType, title }, vars) => {
      clearPendingForkBootstrapRequestInFlight(vars.childSessionId)
      clearPendingForkSnapshot(vars.childSessionId)
      patchPendingForkBootstrapEntry(vars.childSessionId, (current) => {
        if (!current) return current
        return {
          ...current,
          status: "ready",
          errorMessage: null,
        }
      })

      const createdAt =
        typeof childSession.createdAt === "string" && childSession.createdAt.trim().length > 0
          ? childSession.createdAt
          : new Date().toISOString()

      const cacheItem: CachedSessionListItem = {
        id: childSession.id,
        repositoryId,
        parentSessionId: childSession.parentSessionId,
        title: childSession.title ?? title,
        createdAt,
        lifecycleStatus: childSession.lifecycleStatus ?? "active",
      }

      applyOptimisticSessionMembershipToSessionListCaches({
        queryClient,
        snapshot: vars.cacheSnapshot,
        sessionId: childSession.id,
        item: cacheItem,
      })
      queryClient.setQueryData(["session", childSession.id], {
        ...childSession,
        agentType,
      })
    },
    onError: (err, vars) => {
      clearPendingForkBootstrapRequestInFlight(vars.childSessionId)

      const message = err instanceof Error ? err.message : "Fork failed"
      let shouldReconcile = false

      patchPendingForkBootstrapEntry(vars.childSessionId, (current) => {
        if (!current) return current
        shouldReconcile =
          isForkBootstrapTransportError(err) &&
          isForkBootstrapWithinReconciliationWindow(current)

        return {
          ...current,
          status: shouldReconcile ? "reconciling" : "failed",
          errorMessage: shouldReconcile ? null : message,
        }
      })

      if (message === "NO_PROVIDER_CONFIGURED") {
        restoreSessionCacheMutationSnapshot({
          queryClient,
          sessionId: vars.childSessionId,
          snapshot: vars.cacheSnapshot,
        })
        toast.error("No AI provider configured", {
          description: "Add an API key in Settings to start a thread.",
          action: {
            label: "Settings",
            onClick: () => navigate({ to: "/settings", search: { tab: "api-keys" as any } }),
          },
        })
        return
      }

      if (shouldReconcile) {
        return
      }

      restoreSessionCacheMutationSnapshot({
        queryClient,
        sessionId: vars.childSessionId,
        snapshot: vars.cacheSnapshot,
      })

      toast.error(message)
    },
  })

  const retryForkBootstrap = useCallback(async (): Promise<boolean> => {
    if (!forkBootstrapEntry || forkBootstrapEntry.status !== "failed") return false
    if (forkBootstrapMutation.isPending) return false

    setPendingForkBootstrapEntry({
      ...forkBootstrapEntry,
      status: "pending",
      reconcileUntilMs: Date.now() + FORK_BOOTSTRAP_RECONCILIATION_WINDOW_MS,
      errorMessage: null,
    })
    markPendingForkBootstrapRequestInFlight(forkBootstrapEntry.childSessionId)
    const cacheSnapshot = captureSessionCacheMutationSnapshot({
      queryClient,
      sessionId: forkBootstrapEntry.childSessionId,
      repoId: forkBootstrapEntry.repositoryId,
    })
    applyOptimisticSessionMembershipToSessionListCaches({
      queryClient,
      snapshot: cacheSnapshot,
      sessionId: forkBootstrapEntry.childSessionId,
      item: {
        id: forkBootstrapEntry.childSessionId,
        repositoryId: forkBootstrapEntry.repositoryId,
        parentSessionId: forkBootstrapEntry.parentSessionId,
        title: forkBootstrapEntry.title,
        createdAt: new Date().toISOString(),
        lifecycleStatus: "active",
      },
    })
    forkBootstrapMutation.mutate({
      parentSessionId: forkBootstrapEntry.parentSessionId,
      childSessionId: forkBootstrapEntry.childSessionId,
      repositoryId: forkBootstrapEntry.repositoryId,
      agentType: forkBootstrapEntry.agentType,
      title: forkBootstrapEntry.title,
      cacheSnapshot,
      ...(forkBootstrapEntry.forkMessageID ? { messageID: forkBootstrapEntry.forkMessageID } : {}),
      ...(forkBootstrapEntry.parentConversationId ? { conversationId: forkBootstrapEntry.parentConversationId } : {}),
    })
    return true
  }, [forkBootstrapEntry, forkBootstrapMutation, queryClient])

  const forkThread = useCallback(async (messageID?: string): Promise<boolean> => {
    const targetSessionId = sessionId;
    if (!forkActionState.available || !forkActionState.enabled) return false;
    if (sandboxResumeNeeded) {
      if (requiresExplicitResume) return false;
      const resumed = await resumeAndWaitForRunning();
      if (!resumed) return false;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    }

    const nextAgentType = resolvedForkAgentType
    if (!nextAgentType) return false

    const trimmedMessageID = typeof messageID === "string" && messageID.trim().length > 0
      ? messageID.trim()
      : undefined

    if (nextAgentType === "opencode" && !runtimeConversationTabsAvailable) {
      return false
    }

    if ((nextAgentType === "opencode" || nextAgentType === "claude_code") && runtimeConversationTabsAvailable) {
      if (localForkRuntimeConversationInFlightRef.current?.sessionId === targetSessionId) return false
      if (!runtimeConversationsQuery.isSuccess || !activeConversation?.providerSessionId) return false
      const sourceTitle = activeConversation?.isDefault
        ? (fetchedSessionTitle || optimisticTitle || activeConversation?.title || "Main")
        : (activeConversation?.title || "New chat")
      const forkTitle = formatLocalForkConversationTitle(sourceTitle)
      const conversationId = createClientRuntimeConversationId()
      const parentSnapshot = useAgentRuntime.getState().snapshot
      const optimisticCreatedAt = Date.now()
      const optimisticSnapshot = buildOptimisticRuntimeConversationSnapshot({
        conversationId,
        agentType: nextAgentType,
        title: forkTitle,
        timestamp: optimisticCreatedAt,
      })
      setOptimisticRuntimeConversation({
        sessionId: targetSessionId,
        snapshot: optimisticSnapshot,
        creationKind: "fork",
        previousConversationId: activeConversationId,
        seed: parentSnapshot
          ? {
              agentType: nextAgentType,
              snapshot: buildForkOptimisticSnapshot({
                parentSnapshot,
                ...(trimmedMessageID ? { forkMessageID: trimmedMessageID } : {}),
              }),
            }
          : null,
      })
      void navigate({
        to: "/$repoId/$sessionId",
        params: { repoId, sessionId },
        search: { conversationId },
        replace: true,
      })
      const localForkRequestId = crypto.randomUUID()
      localForkRuntimeConversationInFlightRef.current = { sessionId: targetSessionId, requestId: localForkRequestId }
      if (sessionPageMountedRef.current && activeRouteSessionIdRef.current === targetSessionId) {
        setLocalForkRuntimeConversationInFlight({ sessionId: targetSessionId, requestId: localForkRequestId })
      }
      try {
        const conversation = await createLocalForkRuntimeConversation.mutateAsync({
          conversationId,
          sourceConversationId: activeConversationId,
          title: forkTitle,
          ...(trimmedMessageID ? { messageID: trimmedMessageID } : {}),
        })
        setOptimisticRuntimeConversation((current) => {
          if (!current) return null
          return current.snapshot.conversationId === conversationId ? null : current
        })
        queryClient.setQueryData<RuntimeConversationSnapshot[]>(
          ["runtime-conversations", targetSessionId],
          (existing) => {
            const current = existing ?? runtimeConversations;
            const withoutDuplicate = current.filter(
              (item) => item.conversationId !== conversationId && item.conversationId !== conversation.conversationId,
            );
            return [...withoutDuplicate, conversation];
          },
        )
        queryClient.setQueryData<RuntimeConversationSnapshot[]>(
          ["runtime-conversations", targetSessionId, "include-archived"],
          (existing) => {
            const current = existing ?? allRuntimeConversations;
            const withoutDuplicate = current.filter(
              (item) => item.conversationId !== conversationId && item.conversationId !== conversation.conversationId,
            );
            return [...withoutDuplicate, conversation];
          },
        )
        if (activeRouteSessionIdRef.current !== targetSessionId) return false
        if (conversation.conversationId !== conversationId) {
          void navigate({
            to: "/$repoId/$sessionId",
            params: { repoId, sessionId },
            search: { conversationId: conversation.isDefault ? undefined : conversation.conversationId },
            replace: true,
          })
        }
        return true
      } catch (error) {
        setOptimisticRuntimeConversation((current) => {
          if (!current) return null
          return current.snapshot.conversationId === conversationId ? null : current
        })
        if (activeConversationIdRef.current === conversationId) {
          void navigate({
            to: "/$repoId/$sessionId",
            params: { repoId, sessionId },
            search: { conversationId: activeConversationId === "default" ? undefined : activeConversationId },
            replace: true,
          })
        }
        if (activeRouteSessionIdRef.current === targetSessionId) {
          toast.error(error instanceof Error ? error.message : "Fork failed")
        }
        return false
      } finally {
        if (localForkRuntimeConversationInFlightRef.current?.requestId === localForkRequestId) {
          localForkRuntimeConversationInFlightRef.current = null
        }
        if (sessionPageMountedRef.current) {
          setLocalForkRuntimeConversationInFlight((current) => current?.requestId === localForkRequestId ? null : current)
        }
      }
    }

    const childSessionId = crypto.randomUUID()
    const forkTitle = formatForkSessionTitle(fetchedSessionTitle || optimisticTitle)
    const parentSnapshot = useAgentRuntime.getState().snapshot
    const optimisticSession = buildForkOptimisticSessionRecord({
      childSessionId,
      repositoryId: repoId,
      parentSessionId: sessionId,
      title: forkTitle,
      agentType: nextAgentType,
    })
    const cacheItem: CachedSessionListItem = {
      id: optimisticSession.id,
      repositoryId: optimisticSession.repositoryId,
      parentSessionId: optimisticSession.parentSessionId,
      title: optimisticSession.title,
      createdAt: optimisticSession.createdAt,
      lifecycleStatus: optimisticSession.lifecycleStatus,
    }

    setPendingForkBootstrapEntry({
      childSessionId,
      parentSessionId: sessionId,
      repositoryId: repoId,
      agentType: nextAgentType,
      title: forkTitle,
      ...(trimmedMessageID ? { forkMessageID: trimmedMessageID } : {}),
      parentConversationId: activeConversationId,
      reconcileUntilMs: Date.now() + FORK_BOOTSTRAP_RECONCILIATION_WINDOW_MS,
      status: "pending",
      errorMessage: null,
    })
    markPendingForkBootstrapRequestInFlight(childSessionId)

    if (parentSnapshot) {
      setPendingForkSnapshot(childSessionId, {
        agentType: nextAgentType,
        snapshot: buildForkOptimisticSnapshot({
          parentSnapshot,
          ...(trimmedMessageID ? { forkMessageID: trimmedMessageID } : {}),
        }),
      })
    }

    sessionStorage.setItem(optimisticSessionTitleStorageKey(childSessionId), forkTitle)
    sessionStorage.setItem(optimisticAgentTypeStorageKey(childSessionId), nextAgentType)

    const cacheSnapshot = captureSessionCacheMutationSnapshot({
      queryClient,
      sessionId: childSessionId,
      repoId,
    })
    applyOptimisticSessionMembershipToSessionListCaches({
      queryClient,
      snapshot: cacheSnapshot,
      sessionId: childSessionId,
      item: cacheItem,
    })
    queryClient.setQueryData(["session", childSessionId], optimisticSession)

    navigate({
      to: "/$repoId/$sessionId",
      params: { repoId, sessionId: childSessionId },
    })

    forkBootstrapMutation.mutate({
      parentSessionId: sessionId,
      childSessionId,
      repositoryId: repoId,
      agentType: nextAgentType,
      title: forkTitle,
      cacheSnapshot,
      ...(trimmedMessageID ? { messageID: trimmedMessageID } : {}),
      conversationId: activeConversationId,
    })

    return true
  }, [
    fetchedSessionTitle,
    activeConversation,
    forkActionState.available,
    forkActionState.enabled,
    forkBootstrapMutation,
    createLocalForkRuntimeConversation,
    navigate,
    optimisticTitle,
    queryClient,
    repoId,
    resumeAndWaitForRunning,
    resolvedForkAgentType,
    requiresExplicitResume,
    sessionId,
    activeConversationId,
    allRuntimeConversations,
    runtimeConversations,
    runtimeConversationTabsAvailable,
    runtimeConversationsQuery.isSuccess,
    sandboxResumeNeeded,
  ]);

  const handleForkFromMessage = useCallback(async (messageID: string): Promise<boolean> => {
    if (!forkActionState.available) return false;
    if (!forkActionState.enabled) {
      if (forkActionState.disabledReason) {
        toast.error(forkActionState.disabledReason);
      }
      return false;
    }

    const runtimeSnapshot = useAgentRuntime.getState().snapshot;
    const resolvedTarget = runtimeSnapshot
      ? resolveAgentRuntimeForkTargetId(runtimeSnapshot, messageID)
      : messageID;
    return forkThread(resolvedTarget);
  }, [forkActionState.available, forkActionState.disabledReason, forkActionState.enabled, forkThread]);

  const handleSessionPickerIntent = useCallback(async (
    payload: {
      intent: "context" | "continue";
      session: SessionPickerItem;
    },
  ): Promise<boolean> => {
    if (payload.intent === "context") {
      return true;
    }

    if (!continueSlashState.enabled) {
      if (continueSlashState.disabledReason) {
        if (continueSlashState.disabledReason === BRANCH_SWITCH_BLOCKED_REASON) {
          showBranchSwitchInProgressToast({ actionLabel: "continuing from that session" });
        } else {
          toast.error(continueSlashState.disabledReason);
        }
      }
      return false;
    }

    const baseUrl = import.meta.env.VITE_API_URL || "";
    setContinuationAdoptionPending(true);
    try {
      const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/adopt-continuation`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceSessionId: payload.session.sourceSessionId ?? payload.session.id }),
      });

      const responseBody = await res.json().catch(() => null) as
        | AdoptContinuationResponse
        | SessionApiErrorBody
        | null;

      if (!res.ok || !responseBody || !("ok" in responseBody) || responseBody.ok !== true) {
        const error = buildSessionRequestError({
          fallbackMessage: "Failed to continue from that session",
          responseBody,
          status: res.status,
        });
        if (isBranchSwitchInProgressError(error)) {
          showBranchSwitchInProgressToast({ actionLabel: "continuing from that session" });
        } else if (isContextOnlyContinuationFallbackError(error)) {
          const sourceSessionId = payload.session.sourceSessionId ?? payload.session.id;
          const sourceTitle = payload.session.title?.trim() || "that thread";
          const continueRef: AgentRuntimeSessionContextRef = {
            localId: crypto.randomUUID(),
            sessionId: sourceSessionId,
            conversationId: payload.session.conversationId ?? undefined,
            repositoryId: payload.session.repositoryId ?? repoId,
            title: sourceTitle,
            origin: "context",
          };
          pendingImplicitContextRefsRef.current = [continueRef];
          const nextContinueRefs = dedupeSessionContextRefs([
            ...draftSessionContextRefsRef.current.filter((ref) =>
              ref.sessionId !== sourceSessionId
            ),
            continueRef,
          ]);
          markLocalDraftInput()
          draftSessionContextRefsRef.current = nextContinueRefs;
          setDraftSessionContextRefs(nextContinueRefs);
          toast.success(`Added context from ${sourceTitle}`, {
            description: "The source is still busy, so it was attached as read-only context.",
          });
          return true;
        } else {
          toast.error(error.message);
        }
        return false;
      }

      const isContextOnly = responseBody.contextOnly === true;

      // When contextOnly, the current session's branch/PR was NOT changed
      // (the source PR was merged/closed).  We still update session caches
      // for the parent link but skip git/file invalidations.
      queryClient.setQueryData(["session", sessionId], responseBody.session);
      if (!isContextOnly) {
        syncCanonicalPullRequestCache(sessionId, responseBody.session as Record<string, unknown>);
      }
      queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
        if (!Array.isArray(old)) return old;
        return old.map((item) => {
          if (!item || typeof item !== "object") return item;
          const row = item as Record<string, unknown>;
          if (row.id !== sessionId) return item;
          return {
            ...row,
            ...responseBody.session,
          };
        });
      });

      if (!isContextOnly) {
        // Only invalidate git/file caches when the branch actually changed.
        queryClient.invalidateQueries({ queryKey: ["git-status", sessionId] });
        queryClient.invalidateQueries({ queryKey: ["git-log", sessionId] });
        void invalidateSessionRepositoryFileQueries(queryClient, sessionId);
      }
      const sourceSessionId = payload.session.sourceSessionId ?? payload.session.id;
      queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["session", sourceSessionId] });
      invalidateContinuityPickerCaches();

      const sourceTitle = responseBody.sourceSession.title?.trim() || payload.session.title;
      // For context-only, use origin "context" so the agent runtime treats
      // it the same as a /context reference rather than a full continuation.
      const continueRef: AgentRuntimeSessionContextRef = {
        localId: crypto.randomUUID(),
        sessionId: sourceSessionId,
        repositoryId: repoId,
        title: sourceTitle,
        origin: isContextOnly ? "context" : "continue",
      };
      pendingImplicitContextRefsRef.current = [continueRef];
      // Also persist in the draft context refs so the continuation source
      // survives session switches (pendingImplicitContextRefsRef is
      // in-memory only and cleared on navigation).
      // Only remove a prior ref for the same source session to avoid
      // accidentally dropping unrelated /context refs the user attached.
      const nextContinueRefs = dedupeSessionContextRefs([
        ...draftSessionContextRefsRef.current.filter((ref) =>
          ref.sessionId !== sourceSessionId
        ),
        continueRef,
      ]);
      markLocalDraftInput()
      draftSessionContextRefsRef.current = nextContinueRefs;
      setDraftSessionContextRefs(nextContinueRefs);

      if (isContextOnly) {
        const prLabel = responseBody.sourceSession.archivedAt
          ? "Source thread is archived"
          : responseBody.sourceSession.prNumber
            ? `PR #${responseBody.sourceSession.prNumber} was already ${responseBody.sourceSession.prState}`
            : `PR was already ${responseBody.sourceSession.prState}`;
        toast.success(`Added context from ${sourceTitle}`, {
          description: `${prLabel} — added as context only`,
        });
      } else {
        const sourcePrLabel = responseBody.sourceSession.prNumber
          ? ` and PR #${responseBody.sourceSession.prNumber}`
          : "";
        toast.success(`Now continuing ${sourceTitle}`, {
          description: responseBody.sourceSession.branch
            ? `This fresh session now tracks ${responseBody.sourceSession.branch}${sourcePrLabel}`
            : undefined,
        });
      }
      return true;
    } catch (err) {
      if (isBranchSwitchInProgressError(err)) {
        showBranchSwitchInProgressToast({ actionLabel: "continuing from that session" });
      } else {
        toast.error(err instanceof Error ? err.message : "Failed to continue from that session");
      }
      return false;
    } finally {
      setContinuationAdoptionPending(false);
    }
  }, [
    continueSlashState.disabledReason,
    continueSlashState.enabled,
    invalidateContinuityPickerCaches,
    isContextOnlyContinuationFallbackError,
    markLocalDraftInput,
    queryClient,
    repoId,
    sessionId,
    setContinuationAdoptionPending,
    syncCanonicalPullRequestCache,
  ]);

  const handlePrPickerIntent = useCallback((
    payload: {
      intent: "pr";
      pullRequest: PrPickerItem;
    },
    options?: {
      allowBootstrapNotReadyRetry?: boolean;
    },
  ): boolean => {
    if (!prAdoptionActionState.enabled) {
      if (prAdoptionActionState.disabledReason) {
        if (prAdoptionActionState.disabledReason === BRANCH_SWITCH_BLOCKED_REASON) {
          showBranchSwitchInProgressToast({ actionLabel: "adopting that pull request" });
        } else {
          toast.error(prAdoptionActionState.disabledReason);
        }
      }
      return false;
    }

    if (prAdoptionInProgress) {
      toast.info("PR adoption is already in progress");
      return false;
    }

    const { pullRequest } = payload;
    const targetSessionId = sessionId;

    // --- Immediately attach the PR metadata so the user can keep typing ---
    const attachedPullRequest: AgentRuntimeAttachedPullRequest = {
      number: pullRequest.number,
      url: pullRequest.url,
      title: pullRequest.title,
      branchName: pullRequest.branchName,
    };
    draftAttachedPullRequestRef.current = attachedPullRequest;
    setDraftAttachedPullRequest(attachedPullRequest);

    // Attach linked session context immediately
    const linkedSessionTitle = pullRequest.linkedSession?.title?.trim() || null;
    const linkedSessionId = pullRequest.linkedSession?.id;
    if (typeof linkedSessionId === "string" && linkedSessionId.trim().length > 0) {
      const linkedRef: AgentRuntimeSessionContextRef = {
        localId: crypto.randomUUID(),
        sessionId: linkedSessionId,
        repositoryId: repoId,
        title: linkedSessionTitle,
        origin: "pr_linked_session",
      };
      pendingImplicitContextRefsRef.current = [linkedRef];
      const nextRefs = dedupeSessionContextRefs([
        ...draftSessionContextRefsRef.current.filter((ref) => ref.origin !== "pr_linked_session"),
        linkedRef,
      ]);
      markLocalDraftInput();
      draftSessionContextRefsRef.current = nextRefs;
      setDraftSessionContextRefs(nextRefs);
    } else {
      pendingImplicitContextRefsRef.current = [];
      const nextRefs = draftSessionContextRefsRef.current.filter((ref) => ref.origin !== "pr_linked_session");
      if (!sameSessionContextRefs(draftSessionContextRefsRef.current, nextRefs)) {
        markLocalDraftInput();
        draftSessionContextRefsRef.current = nextRefs;
        setDraftSessionContextRefs(nextRefs);
      }
    }

    // Show the "preparing" status immediately
    setPrAdoptionStatus((current) => {
      const nextStatus: PrAdoptionStatus = {
        status: "preparing",
        prNumber: pullRequest.number,
        title: pullRequest.title,
      };
      return samePrAdoptionStatus(current, nextStatus) ? current : nextStatus;
    });

    const baseUrl = import.meta.env.VITE_API_URL || "";
    const prepareToken = crypto.randomUUID();
    const allowBootstrapNotReadyRetry = options?.allowBootstrapNotReadyRetry === true;

    // Create a shared promise that handleSend() can await if needed.
    // This promise resolves to true once the DO has durably registered
    // the adoption operation, or false/throws on failure.
    const preparePromise = (async (): Promise<boolean> => {
      let sessionNotReadyRetryCount = 0;
      try {
        while (true) {
          if (activeRouteSessionIdRef.current !== targetSessionId) {
            return false;
          }

          const abortController = new AbortController();
          const timeoutHandle = window.setTimeout(() => {
            abortController.abort();
          }, PR_ADOPTION_PREPARE_TIMEOUT_MS);

          try {
            const res = await fetch(`${baseUrl}/api/sessions/${targetSessionId}/adopt-pr`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              signal: abortController.signal,
              body: JSON.stringify({
                prNumber: pullRequest.number,
              }),
            });

            const responseBody = await res.json().catch(() => null) as
              | { ok: true; accepted: true; operationId: string; pullRequest: Record<string, unknown> }
              | SessionApiErrorBody
              | null;
            const responseMessage = readSessionApiErrorMessage(responseBody).toLowerCase();
            const shouldRetryBootstrapNotReady =
              allowBootstrapNotReadyRetry
              && sessionNotReadyRetryCount < BOOTSTRAP_PR_ADOPTION_NOT_READY_MAX_RETRIES
              && (
                res.status === 404
                || (res.status === 409 && responseMessage === "session not initialized")
              );

            if (shouldRetryBootstrapNotReady) {
              sessionNotReadyRetryCount += 1;
              window.clearTimeout(timeoutHandle);
              await new Promise((resolve) => {
                window.setTimeout(resolve, BOOTSTRAP_PR_ADOPTION_NOT_READY_RETRY_DELAY_MS);
              });
              continue;
            }

            if (!res.ok || !responseBody || !("ok" in responseBody) || responseBody.ok !== true) {
              const retryTimedOutBecauseSessionWasNotReady =
                allowBootstrapNotReadyRetry
                && (
                  res.status === 404
                  || (res.status === 409 && responseMessage === "session not initialized")
                );
              const error = retryTimedOutBecauseSessionWasNotReady
                ? new Error("The new thread was not ready for PR adoption in time. Please retry.")
                : buildSessionRequestError({
                    fallbackMessage: "Failed to adopt pull request",
                    responseBody,
                    status: res.status,
                  });
              const message = error.message;

              if (activeRouteSessionIdRef.current === targetSessionId) {
                setPrAdoptionStatus((current) => {
                  const nextStatus: PrAdoptionStatus = {
                    status: "error",
                    prNumber: pullRequest.number,
                    title: pullRequest.title,
                    error: message,
                  };
                  return samePrAdoptionStatus(current, nextStatus) ? current : nextStatus;
                });
                if (isBranchSwitchInProgressError(error)) {
                  showBranchSwitchInProgressToast({ actionLabel: "adopting that pull request" });
                } else {
                  toast.error(message);
                }
              }
              return false;
            }

            if (activeRouteSessionIdRef.current !== targetSessionId) {
              return false;
            }

            // Prepare succeeded — the DO now has a durable adoption operation.
            // Transition to "adopting" state. The actual success/failure will
            // come from the server via the prAdoption websocket status.
            const operationId = typeof (responseBody as Record<string, unknown>).operationId === "string"
              ? (responseBody as Record<string, unknown>).operationId as string
              : undefined;
            setPrAdoptionStatus((current) => {
              const nextStatus: PrAdoptionStatus = {
                status: "adopting",
                prNumber: pullRequest.number,
                title: pullRequest.title,
                operationId,
              };
              return samePrAdoptionStatus(current, nextStatus) ? current : nextStatus;
            });

            return true;
          } catch (err) {
            const message =
              err instanceof DOMException && err.name === "AbortError"
                ? "Preparing PR adoption took too long. Please retry."
                : err instanceof Error
                  ? err.message
                  : "Failed to adopt pull request";
            if (activeRouteSessionIdRef.current === targetSessionId) {
              setPrAdoptionStatus((current) => {
                const nextStatus: PrAdoptionStatus = {
                  status: "error",
                  prNumber: pullRequest.number,
                  title: pullRequest.title,
                  error: message,
                };
                return samePrAdoptionStatus(current, nextStatus) ? current : nextStatus;
              });
              if (isBranchSwitchInProgressError(err)) {
                showBranchSwitchInProgressToast({ actionLabel: "adopting that pull request" });
              } else {
                toast.error(message);
              }
            }
            return false;
          } finally {
            window.clearTimeout(timeoutHandle);
          }
        }
      } finally {
        if (prAdoptionPrepareRef.current?.token === prepareToken) {
          prAdoptionPrepareRef.current = null;
        }
      }
    })();

    prAdoptionPrepareRef.current = {
      token: prepareToken,
      promise: preparePromise,
    };
    return true;
  }, [
    markLocalDraftInput,
    prAdoptionActionState.disabledReason,
    prAdoptionActionState.enabled,
    prAdoptionInProgress,
    queryClient,
    repoId,
    sessionId,
  ]);

  // Stable callback shared by both error-dismiss and success-dismiss for PR adoption.
  const dismissPrAdoptionStatus = useCallback(() => {
    setPrAdoptionStatus((current) => {
      if (current?.status === "error" || current?.status === "success") {
        const outcomeKey = buildPrAdoptionOutcomeKey({
          operationId: current.operationId,
          status: current.status === "success" ? "succeeded" : "failed",
        });
        if (outcomeKey) {
          writeAcknowledgedPrAdoptionOutcome({ sessionId, outcomeKey });
        }
        if (current.status === "success") {
          writeAcknowledgedPrAdoptionSuccessAttachment({
            sessionId,
            successAttachmentKey: buildPrAdoptionSuccessAttachmentKey({
              attachedPullRequest: {
                number: current.prNumber,
                title: current.title,
                url: current.url,
                branchName: current.branchName,
              },
            }),
          });
        }
      }
      return null;
    });
  }, [sessionId]);

  const handleEditFromMessage = useCallback(async (messageID: string): Promise<boolean> => {
    const targetSessionId = sessionId;
    if (!editActionState.available) return false;
    if (!editActionState.enabled) {
      if (editActionState.disabledReason) {
        toast.error(editActionState.disabledReason);
      }
      return false;
    }
    if (sandboxResumeNeeded) {
      if (requiresExplicitResume) return false;
      const resumed = await resumeAndWaitForRunning();
      if (!resumed) return false;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    }

    const runtimeSnapshot = useAgentRuntime.getState().snapshot;
    const prefill = buildRuntimeUserMessagePrefill(
      runtimeSnapshot ? getAgentRuntimeUserMessage(runtimeSnapshot, messageID) : null,
    );

    invalidatePersistedReplicaBySessionId(sessionId);
    setOptimisticRevertRestorePending(false);
    setOptimisticRevertMessageId(messageID);
    setOptimisticRevertHiddenMessageIds(collectOptimisticRevertHiddenMessageIds({
      snapshot: runtimeSnapshot,
      messageId: messageID,
    }));
    applyRuntimeUserMessagePrefillToComposer(prefill);

    try {
      await postSessionAction("revert", { messageID });
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
      return true;
    } catch (err) {
      setOptimisticRevertMessageId((current) => current === messageID ? null : current);
      setOptimisticRevertHiddenMessageIds((current) => (
        current.includes(messageID) ? [] : current
      ));
      toast.error(err instanceof Error ? err.message : "Failed to edit from message");
      return false;
    }
  }, [applyRuntimeUserMessagePrefillToComposer, editActionState.available, editActionState.disabledReason, editActionState.enabled, postSessionAction, requiresExplicitResume, resumeAndWaitForRunning, sandboxResumeNeeded, sessionId, setOptimisticRevertMessageId]);

  const executeSlashCommand = useCallback(async (
    rawText: string,
  ): Promise<"handled" | "handled_preserve_input" | "not_handled" | "failed"> => {
    if (!capabilities.supportsSlashCommands) return "not_handled";
    const parsed = parseSlashCommandInput(rawText);
    if (!parsed) return "not_handled";

    const command = parsed.name.toLowerCase();
    const commandMeta = slashCommandByName.get(command);
    if (commandMeta?.enabled === false) {
      toast.error(commandMeta.disabledReason ?? `/${parsed.name} is unavailable here`);
      return "failed";
    }

    try {
      if (command === "undo") {
        return await runComposerUndoWithFeedback() ? "handled_preserve_input" : "failed";
      }
      if (command === "redo") {
        return await runComposerRedoWithFeedback() ? "handled" : "failed";
      }
      if (command === "compact" || command === "summarize") {
        // Fire-and-forget: handleCompact manages its own error UI (toast)
        // and pending-action lifecycle, so we return "handled" immediately
        // to let the editor clear without waiting for the HTTP round-trip.
        // The .catch is a safety net for the unlikely requestResume rejection
        // path which would otherwise surface as an unhandled promise rejection.
        void runComposerCompactWithFeedback().catch(() => {});
        return "handled";
      }
      if (command === REVIEW_MODE_SLASH_COMMAND_NAME) {
        return await runComposerReviewCurrentTabWithFeedback() ? "handled" : "failed";
      }
      if (command === REVIEW_BRANCH_SLASH_COMMAND_NAME) {
        return await runComposerReviewFullBranchWithFeedback() ? "handled" : "failed";
      }
      if (command === "fork") {
        return await forkThread(revertMessageID ?? undefined) ? "handled" : "failed";
      }
      if (command === "context" || command === "continue" || command === "pr") {
        toast.error(`/${command} requires selecting an item from the picker`);
        return "failed";
      }

      // General fallthrough: /init and any custom agent commands that should
      // pass straight through to the underlying agent runtime.
      // Keep a local pending guard so command acknowledgement still blocks
      // briefly, but long-running unresolved fetches do not freeze the
      // composer all the way to a Cloudflare edge timeout.
      const slashCommandDisplayText = parsed.arguments.trim().length > 0
        ? `/${parsed.name} ${parsed.arguments}`
        : `/${parsed.name}`;
      const handle = startPendingSessionAction({
        displayText: slashCommandDisplayText,
        acknowledgementMatcher: () => false,
      });
      const clientActionId = crypto.randomUUID();
      handle.bindClientActionId(clientActionId);
      try {
        const response = await postSessionAction("command", {
          command: parsed.name,
          arguments: parsed.arguments,
          clientActionId,
        }, {
          localBlockingTimeoutMs: COMMAND_DRIVEN_SESSION_ACTION_BLOCK_TIMEOUT_MS,
          onLocalBlockingTimeout: () => {
            handle.releaseComposerBlock();
            handle.scheduleRemoval(storeSubscribe);
          },
          onLateError: (error) => {
            handle.remove();
            toast.error(getSessionActionErrorMessage(error, "Command failed"));
          },
        });
        if (response && typeof response === "object" && !Array.isArray(response)) {
          if ((response as Record<string, unknown>).accepted === true) {
            handle.markQueued("Will run after the current response");
            markSidebarWorkRequested({ sessionId });
          }
        }
        handle.scheduleRemoval(storeSubscribe);
        return "handled";
      } catch (innerErr) {
        handle.remove();
        throw innerErr;
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Command failed");
      return "failed";
    }
  }, [capabilities.supportsSlashCommands, forkThread, markSidebarWorkRequested, postSessionAction, revertMessageID, runComposerCompactWithFeedback, runComposerRedoWithFeedback, runComposerReviewCurrentTabWithFeedback, runComposerReviewFullBranchWithFeedback, runComposerUndoWithFeedback, startPendingSessionAction, slashCommandByName, storeSubscribe]);

  const handleRestoreRewind = useCallback(async () => {
    if (!revertMessageID) return false;
    return runComposerRedoWithFeedback();
  }, [revertMessageID, runComposerRedoWithFeedback]);

  const sendMessage = useCallback((
    text: string,
    options?: {
      parts?: unknown[];
      previewUrls?: string[];
      previewAttachmentsMeta?: Array<{ filename: string; mime: string }>;
      clientMessageId?: string;
      contextRefs?: AgentRuntimeSessionContextRef[];
      requestedSkillRefs?: AgentRuntimeRequestedSkillRef[];
      preferredMcpServerRefs?: AgentRuntimePreferredMcpServerRef[];
      scratchPadRefs?: AgentRuntimeScratchPadRef[];
      attachedPullRequest?: AgentRuntimeAttachedPullRequest | null;
      conversationId?: string;
    },
  ) => {
    const {
      settings,
      sessionSelection,
      sessionSelectionConversationId,
      hasStaleExplicitSessionIntent,
      resolvedModel,
      resolvedVariant,
    } = getResolvedComposerSelection();
    const clientMessageId =
      typeof options?.clientMessageId === "string" && options.clientMessageId.trim().length > 0
        ? options.clientMessageId.trim()
        : typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `client-${Date.now()}`;
    const msg: { type: string; [key: string]: unknown } = { type: "message", text };
    msg.clientMessageId = clientMessageId;
    msg.conversationId = typeof options?.conversationId === "string" && options.conversationId.trim().length > 0
      ? options.conversationId.trim()
      : activeConversationId;
    if (
      activeOptimisticRuntimeConversation?.creationKind === "blank" &&
      optimisticCreatingRuntimeConversation?.conversationId === msg.conversationId
    ) {
      msg.createRuntimeConversationIfMissing = true;
    }
    if (capabilities.supportsPromptParts && options?.parts) {
      msg.parts = options.parts;
    }
    if (options?.previewUrls?.length) {
      msg.previewUrls = options.previewUrls;
      // Attach filename/mime metadata so the local outbox can build proper
      // AgentRuntimeMessageAttachment objects for queued-message previews.
      if (options.previewAttachmentsMeta?.length) {
        msg.previewAttachmentsMeta = options.previewAttachmentsMeta;
      }
    }
    const composerAgent = settings.getAgent({ sessionId, conversationId: activeConversationId }).mode;
    if (!isClaudeSession && agentType !== "cursor" && composerAgent) {
      msg.agent = composerAgent;
    }
    if (isClaudeSession && composerAgent) {
      msg.mode = resolveClaudeOutgoingPermissionMode({
        composeMode: composerAgent,
        runtimePermissionMode: runtimeClaudePermissionMode,
        runtimeBuildPermissionMode: runtimeClaudeBuildPermissionMode,
      });
    }
    if (resolvedModel) {
      if (isClaudeSession) {
        const sanitized = sanitizeClaudeModelSelection(resolvedModel);
        if (sanitized) {
          msg.model = sanitized;
          if (
            sessionSelection.model
            && !hasStaleExplicitSessionIntent
            && (
              sanitized.providerID !== sessionSelection.model.providerID ||
              sanitized.modelID !== sessionSelection.model.modelID
            )
          ) {
            settings.setSessionModelSelection({
              sessionId,
              conversationId: activeConversationId,
              model: sanitized,
              variant: sessionSelection.variant,
            });
          }
        } else {
          settings.clearSessionModelSelection({ sessionId, conversationId: activeConversationId });
        }
      } else if (agentType !== "cursor") {
        msg.model = resolvedModel;
      } else {
        settings.clearSessionModelSelection({ sessionId, conversationId: activeConversationId });
      }
    }
    const variant = resolveOutgoingThinkingVariant({
      model: resolvedModel,
      variant: resolvedVariant,
      runtimeVariants: resolvedModel
        ? findCatalogModelVariants(sessionModelController.catalog, resolvedModel)
        : undefined,
    });
    if (variant) {
      msg.variant = variant;
    }

    const mergedContextRefs = dedupeSessionContextRefs([
      ...pendingImplicitContextRefsRef.current,
      ...(options?.contextRefs ?? []),
    ]);
    const requestedSkillRefs = dedupeRequestedSkillRefs(options?.requestedSkillRefs ?? []);
    const preferredMcpServerRefs = dedupePreferredMcpServerRefs(
      options?.preferredMcpServerRefs ?? [],
    );
    const attachedPullRequest = options?.attachedPullRequest === undefined
      ? draftAttachedPullRequestRef.current ?? undefined
      : options.attachedPullRequest ?? undefined;
    if (
      mergedContextRefs.length > 0
      || requestedSkillRefs.length > 0
      || preferredMcpServerRefs.length > 0
      || attachedPullRequest
    ) {
      msg.allowEmptyPrompt = true;
    }
    if (mergedContextRefs.length > 0) {
      msg.contextRefs = mergedContextRefs;
    }
    if (requestedSkillRefs.length > 0) {
      msg.requestedSkillRefs = requestedSkillRefs;
    }
    if (preferredMcpServerRefs.length > 0) {
      msg.preferredMcpServerRefs = preferredMcpServerRefs;
    }
    const scratchPadRefs = options?.scratchPadRefs ?? [];
    if (scratchPadRefs.length > 0) {
      msg.scratchPadRefs = scratchPadRefs;
    }
    if (attachedPullRequest) {
      msg.attachedPullRequest = attachedPullRequest;
    }

    const outgoingSessionModelIntent = buildExplicitSessionModelIntentFromOutgoingMessage({
      model: msg.model,
      variant: msg.variant,
    });
    const shouldPersistOutgoingSessionModelIntent =
      activeConversationId === "default" || (
        sessionSelectionConversationId === activeConversationId && sessionSelection.model !== null
      );
    const outgoingSessionModelIntentMutation = outgoingSessionModelIntent && shouldPersistOutgoingSessionModelIntent
      ? createSessionModelIntentMutationMetadata()
      : null;
    if (outgoingSessionModelIntentMutation) {
      msg.sessionModelIntentMutation = outgoingSessionModelIntentMutation;
    }

    const result = sendChatMessage(msg);
    if (result.accepted) {
      markSessionAsHavingAcceptedUserMessage();
    }
    if (result.accepted && outgoingSessionModelIntent && shouldPersistOutgoingSessionModelIntent) {
      void persistSessionModelIntent({
        sessionId,
        sessionModelIntent: outgoingSessionModelIntent,
        mutation: outgoingSessionModelIntentMutation ?? undefined,
        queryClient,
        conversationId: activeConversationId,
      }).catch(() => undefined);
    }
    if (result.accepted && mergedContextRefs.length > 0) {
      pendingImplicitContextRefsRef.current = [];
    }
    if (result.accepted && attachedPullRequest) {
      setPendingAttachedPullRequestObservation({
        clientMessageId,
        attachedPullRequest,
      });
      draftAttachedPullRequestRef.current = null;
      setDraftAttachedPullRequest(null);
      // Only clear the adoption tray if adoption already succeeded.
      // During active adoption ("preparing"/"adopting"), keep the status visible
      // so the user sees the adoption progress while their message is queued.
      setPrAdoptionStatus((current) => {
        if (current?.status === "success") {
          return null;
        }
        return current;
      });
    }
    return {
      ...result,
      clientMessageId,
    };
  }, [
    agentType,
    capabilities.supportsPromptParts,
    draftAttachedPullRequest,
    activeConversationId,
    getResolvedComposerSelection,
    isClaudeSession,
    markSessionAsHavingAcceptedUserMessage,
    activeOptimisticRuntimeConversation?.creationKind,
    optimisticCreatingRuntimeConversation?.conversationId,
    setPendingAttachedPullRequestObservation,
    resolveOutgoingThinkingVariant,
    runtimeClaudeBuildPermissionMode,
    runtimeClaudePermissionMode,
    sendChatMessage,
    sessionModelController.catalog,
    queryClient,
    sessionId,
  ]);

  const [, bumpBootstrapEnqueueStateVersion] = useReducer((version: number) => version + 1, 0);
  const clearBootstrapServerEnqueueStateMarker = useCallback(() => {
    clearInitialBootstrapServerEnqueueState(sessionId);
    bumpBootstrapEnqueueStateVersion();
  }, [sessionId]);

  const finalizeSuccessfulComposerSend = useCallback((args: {
    hasText: boolean;
    attachmentCount: number;
    contextRefCount: number;
    autoStartedNewWork: boolean;
  }) => {
    captureThreadMessageSent({
      source: "composer",
      hasText: args.hasText,
      attachmentCount: args.attachmentCount,
      contextRefCount: args.contextRefCount,
      autoStartedNewWork: args.autoStartedNewWork,
    });

    sendQueuedForUploadsRef.current = false;
    editorRef.current?.clear();
    setHasContent(false);
    setDraftText("");
    draftTextRef.current = "";
    draftSessionContextRefsRef.current = [];
    setDraftSessionContextRefs([]);
    draftRequestedSkillRefsRef.current = [];
    setDraftRequestedSkillRefs([]);
    draftPreferredMcpServerRefsRef.current = [];
    setDraftPreferredMcpServerRefs([]);
    draftScratchPadRefsRef.current = [];
    setDraftScratchPadRefs([]);
    draftAttachedPullRequestRef.current = null;
    setDraftAttachedPullRequest(null);
    pendingImplicitContextRefsRef.current = [];
    clearReadyAttachments();
    attachmentsForDraftRef.current = [];
    setPrAdoptionStatus((current) => current?.status === "success" ? null : current);
    persistLatestDraftSnapshotNow();
  }, [captureThreadMessageSent, clearReadyAttachments, persistLatestDraftSnapshotNow]);

  const clearComposerAfterHandledSlashCommand = useCallback(() => {
    sendQueuedForUploadsRef.current = false;
    historyIndexRef.current = -1;
    savedInputRef.current = "";

    editorRef.current?.clear();
    setHasContent(false);
    setDraftText("");
    draftTextRef.current = "";
    draftSessionContextRefsRef.current = [];
    setDraftSessionContextRefs([]);
    draftRequestedSkillRefsRef.current = [];
    setDraftRequestedSkillRefs([]);
    draftPreferredMcpServerRefsRef.current = [];
    setDraftPreferredMcpServerRefs([]);
    draftScratchPadRefsRef.current = [];
    setDraftScratchPadRefs([]);
    draftAttachedPullRequestRef.current = null;
    setDraftAttachedPullRequest(null);
    pendingImplicitContextRefsRef.current = [];
    clearReadyAttachments();
    attachmentsForDraftRef.current = [];
    setPrAdoptionStatus((current) => current?.status === "success" ? null : current);
    persistLatestDraftSnapshotNow();
  }, [clearReadyAttachments, persistLatestDraftSnapshotNow]);

  const finalizeSuccessfulBootstrapSend = useCallback((args: {
    hasText: boolean;
    attachmentCount: number;
    contextRefCount: number;
    attachedPullRequest: AgentRuntimeAttachedPullRequest | null;
    attachedPullRequestAlreadyHandled?: boolean;
    preserveComposerDraft?: boolean;
  }) => {
    captureThreadMessageSent({
      source: "new_thread_bootstrap",
      hasText: args.hasText,
      attachmentCount: args.attachmentCount,
      contextRefCount: args.contextRefCount,
      autoStartedNewWork: false,
    });

    if (args.attachedPullRequest && args.attachedPullRequestAlreadyHandled !== true) {
      writeConsumedPrAdoptionAttachment({
        sessionId,
        successAttachmentKey: buildPrAdoptionSuccessAttachmentKey({
          attachedPullRequest: args.attachedPullRequest,
        }),
      });
    }

    draftAttachedPullRequestRef.current = null;
    setDraftAttachedPullRequest(null);
    draftSessionContextRefsRef.current = [];
    setDraftSessionContextRefs([]);
    draftRequestedSkillRefsRef.current = [];
    setDraftRequestedSkillRefs([]);
    draftPreferredMcpServerRefsRef.current = [];
    setDraftPreferredMcpServerRefs([]);
    draftScratchPadRefsRef.current = [];
    setDraftScratchPadRefs([]);
    pendingImplicitContextRefsRef.current = [];

    setPrAdoptionStatus((current) => {
      if (current?.status !== "success") {
        return current;
      }
      if (args.attachedPullRequest && args.attachedPullRequestAlreadyHandled !== true) {
        writeAcknowledgedPrAdoptionSuccessAttachment({
          sessionId,
          successAttachmentKey: buildPrAdoptionSuccessAttachmentKey({
            attachedPullRequest: args.attachedPullRequest,
          }),
        });
        const outcomeKey = buildPrAdoptionOutcomeKey({
          operationId: current.operationId,
          status: "succeeded",
        });
        if (outcomeKey) {
          writeAcknowledgedPrAdoptionOutcome({ sessionId, outcomeKey });
        }
      }
      return null;
    });

    initialMessageSent.current = true;
    setPendingBootstrapActive(false);
    setPendingBootstrapSnapshot(EMPTY_PENDING_BOOTSTRAP_DRAFT);
    setPendingBootstrapClientMessageId(null);
    setPendingBootstrapPrToAdopt(null);
    pendingImageFilesRef.current = null;
    bootstrapPrAdoptionStartedRef.current = false;
    sendQueuedForUploadsRef.current = false;
    sessionStorage.removeItem(initialBootstrapDraftStorageKey(sessionId));
    sessionStorage.removeItem(initialMessageStorageKey(sessionId));
    sessionStorage.removeItem(initialContextRefsStorageKey(sessionId));
    clearBootstrapServerEnqueueStateMarker();

    if (args.preserveComposerDraft === true) {
      persistLatestDraftSnapshotNow();
      return;
    }

    editorRef.current?.clear();
    setHasContent(false);
    clearReadyAttachments();
    attachmentsForDraftRef.current = [];
    setDraftText("");
    draftTextRef.current = "";
    draftAttachedPullRequestRef.current = null;
    setDraftAttachedPullRequest(null);
    draftSessionContextRefsRef.current = [];
    setDraftSessionContextRefs([]);
    draftRequestedSkillRefsRef.current = [];
    setDraftRequestedSkillRefs([]);
    draftPreferredMcpServerRefsRef.current = [];
    setDraftPreferredMcpServerRefs([]);
    draftScratchPadRefsRef.current = [];
    setDraftScratchPadRefs([]);
    pendingImplicitContextRefsRef.current = [];
    persistLatestDraftSnapshotNow();
  }, [
    captureThreadMessageSent,
    clearBootstrapServerEnqueueStateMarker,
    clearReadyAttachments,
    persistLatestDraftSnapshotNow,
    sessionId,
  ]);

  const finalizeHydratedBootstrapWithoutSend = useCallback((args?: {
    preservePendingPrAdoption?: boolean;
  }) => {
    initialMessageSent.current = true;
    setPendingBootstrapActive(false);
    setPendingBootstrapSnapshot(EMPTY_PENDING_BOOTSTRAP_DRAFT);
    setPendingBootstrapClientMessageId(null);
    if (args?.preservePendingPrAdoption !== true) {
      setPendingBootstrapPrToAdopt(null);
    }
    pendingImageFilesRef.current = null;
    pendingImplicitContextRefsRef.current = [];
    if (args?.preservePendingPrAdoption !== true) {
      bootstrapPrAdoptionStartedRef.current = false;
    }
    sendQueuedForUploadsRef.current = false;
    sessionStorage.removeItem(initialBootstrapDraftStorageKey(sessionId));
    sessionStorage.removeItem(initialMessageStorageKey(sessionId));
    sessionStorage.removeItem(initialContextRefsStorageKey(sessionId));
    clearBootstrapServerEnqueueStateMarker();
    persistLatestDraftSnapshotNow();
  }, [
    clearBootstrapServerEnqueueStateMarker,
    persistLatestDraftSnapshotNow,
    sessionId,
  ]);

  const handleEditFromMessageUi = useCallback((messageID: string) => {
    void handleEditFromMessage(messageID);
  }, [handleEditFromMessage]);

  const handleForkFromMessageUi = useCallback((messageID: string) => {
    void handleForkFromMessage(messageID);
  }, [handleForkFromMessage]);

  /**
   * Inline edit while running: enqueue a server-orchestrated operation
   * (abort -> await idle -> revert -> resend).
   */
  const handleSubmitEdit = useCallback(async (messageID: string, newText: string): Promise<boolean> => {
    const targetSessionId = sessionId;
    if (!editActionState.available || !editActionState.enabled) return false;
    if (sessionArchived) {
      toast.error(ARCHIVED_THREAD_READ_ONLY_REASON);
      return false;
    }
    if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    if (sandboxResumeNeeded) {
      if (requiresExplicitResume) return false;
      const resumed = await resumeAndWaitForRunning();
      if (!resumed) return false;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    }

    const {
      settings,
      sessionSelectionConversationId,
      resolvedModel,
      resolvedVariant,
    } = getResolvedComposerSelection();
    const variant = resolveOutgoingThinkingVariant({
      model: resolvedModel,
      variant: resolvedVariant,
    });
    const replacement: Record<string, unknown> = {
      text: newText,
      clientMessageId: crypto.randomUUID(),
      expectedConversationEpoch: conversationEpoch,
    };
    const scopedAgent = settings.getAgent({ sessionId, conversationId: activeConversationId }).mode;
    if (scopedAgent) {
      if (isClaudeSession) {
        replacement.mode = resolveClaudeOutgoingPermissionMode({
          composeMode: scopedAgent,
          runtimePermissionMode: runtimeClaudePermissionMode,
          runtimeBuildPermissionMode: runtimeClaudeBuildPermissionMode,
        });
      } else {
        replacement.agent = scopedAgent;
      }
    }
    if (resolvedModel) {
      if (isClaudeSession) {
        const sanitized = sanitizeClaudeModelSelection(resolvedModel);
        if (sanitized) {
          replacement.model = sanitized;
        }
      } else {
        replacement.model = resolvedModel;
      }
    }
    if (variant) {
      replacement.variant = variant;
    }
    const replacementSessionModelIntent = buildExplicitSessionModelIntentFromOutgoingMessage({
      model: replacement.model,
      variant: replacement.variant,
    });
    const shouldPersistReplacementSessionModelIntent =
      activeConversationId === "default" || (
        sessionSelectionConversationId === activeConversationId && settings.getSessionModelSelection({
          sessionId,
          conversationId: activeConversationId,
        }).model !== null
      );
    if (replacementSessionModelIntent && shouldPersistReplacementSessionModelIntent) {
      replacement.sessionModelIntentMutation = createSessionModelIntentMutationMetadata();
    }

    setSuppressRevertBanner(true);
    invalidatePersistedReplicaBySessionId(sessionId);
    setOptimisticRevertRestorePending(false);
    setOptimisticRevertMessageId(messageID);
    setOptimisticRevertHiddenMessageIds(collectOptimisticRevertHiddenMessageIds({
      snapshot: useAgentRuntime.getState().snapshot,
      messageId: messageID,
    }));
    try {
      await postSessionAction("edit-message", {
        operationId: crypto.randomUUID(),
        messageID,
        replacement,
      });
      return true;
    } catch (err) {
      setOptimisticRevertMessageId((current) => current === messageID ? null : current);
      setOptimisticRevertHiddenMessageIds((current) => (
        current.includes(messageID) ? [] : current
      ));
      setSuppressRevertBanner(false);
      toast.error(err instanceof Error ? err.message : "Failed to edit message");
      return false;
    }
  }, [
    conversationEpoch,
    editActionState.available,
    editActionState.enabled,
    isClaudeSession,
    postSessionAction,
    getResolvedComposerSelection,
    resolveOutgoingThinkingVariant,
    resumeAndWaitForRunning,
    requiresExplicitResume,
    runtimeClaudeBuildPermissionMode,
    runtimeClaudePermissionMode,
    sessionArchived,
    sessionId,
    sandboxResumeNeeded,
  ]);

  const handleSubmitEditFromMessageUi = useCallback((messageID: string, newText: string) => {
    return handleSubmitEdit(messageID, newText);
  }, [handleSubmitEdit]);

  const handleSend = useCallback(async (): Promise<boolean> => {
    const targetSessionId = sessionId;
    const editor = editorRef.current;
    if (!editor || sessionActionPending || editMessageOperationPending) return false;
    if (composerReadOnly) {
      toast.error(ARCHIVED_THREAD_READ_ONLY_REASON);
      return false;
    }
    if (activeOptimisticForkPreparing) {
      toast.info(OPTIMISTIC_FORK_PREPARING_SEND_REASON);
      return false;
    }

    const writableForSend = await ensureSessionWritableForSend();
    if (!writableForSend) return false;
    if (activeRouteSessionIdRef.current !== targetSessionId) return false;

    // If a PR adoption prepare is still in flight (fast phase), wait for it.
    // This is typically sub-second; the user will not notice the wait.
    // We do NOT block on the full adoption (which can take 30s+) — the
    // message will queue durably in the DO behind the adoption operation.
    if (prAdoptionPrepareRef.current) {
      try {
        const prepared = await prAdoptionPrepareRef.current.promise;
        if (!prepared) return false;
      } catch {
        return false;
      }
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    }

    const trackedBootstrapServerEnqueueState = readInitialBootstrapServerEnqueueState(sessionId);
    const preserveComposerAfterTrackedBootstrapFinalize =
      trackedBootstrapServerEnqueueState !== null
      && hasPendingBootstrapDraftIntent({ draft: pendingBootstrapSnapshot })
      && shouldPreserveComposerStateAfterBootstrapFinalize({
        bootstrapDraft: pendingBootstrapSnapshot,
        currentText: draftText,
        currentContextRefs: draftSessionContextRefs,
        currentRequestedSkillRefs: draftRequestedSkillRefs,
        currentPreferredMcpServerRefs: draftPreferredMcpServerRefs,
        currentAttachedPullRequest: draftAttachedPullRequest,
        currentPrToAdopt: pendingBootstrapPrToAdopt,
        currentAttachmentCount: attachments.length,
      });

    const shouldSendTrackedBootstrapSnapshot =
      pendingBootstrapActive
      && trackedBootstrapServerEnqueueState !== null
      && hasPendingBootstrapDraftIntent({ draft: pendingBootstrapSnapshot });

    if (shouldSendTrackedBootstrapSnapshot) {
      if (sandboxResumeNeeded) {
        if (requiresExplicitResume) return false;
      }

      const bootstrapText = pendingBootstrapSnapshot.text?.trim() ?? "";
      const bootstrapContextRefs = pendingBootstrapSnapshot.contextRefs;
      const bootstrapRequestedSkillRefs = pendingBootstrapSnapshot.requestedSkillRefs;
      const bootstrapPreferredMcpServerRefs = pendingBootstrapSnapshot.preferredMcpServerRefs;
      const bootstrapScratchPadRefs = pendingBootstrapSnapshot.scratchPadRefs ?? [];
      const bootstrapAttachedPullRequest = pendingBootstrapSnapshot.attachedPullRequest;

      if (
        bootstrapText.length > 0
        || bootstrapContextRefs.length > 0
        || bootstrapRequestedSkillRefs.length > 0
        || bootstrapPreferredMcpServerRefs.length > 0
      ) {
        historyRef.current.push({
          text: bootstrapText,
          sessionContextRefs: bootstrapContextRefs,
          requestedSkillRefs: bootstrapRequestedSkillRefs,
          preferredMcpServerRefs: bootstrapPreferredMcpServerRefs,
          scratchPadRefs: bootstrapScratchPadRefs,
        });
      }
      historyIndexRef.current = -1;
      savedInputRef.current = "";

      const result = sendMessage(bootstrapText, {
        clientMessageId: pendingBootstrapSnapshot.clientMessageId ?? undefined,
        contextRefs: bootstrapContextRefs,
        requestedSkillRefs: bootstrapRequestedSkillRefs,
        preferredMcpServerRefs: bootstrapPreferredMcpServerRefs,
        scratchPadRefs: bootstrapScratchPadRefs,
        attachedPullRequest: bootstrapAttachedPullRequest,
      });
      if (!result.accepted) return false;

      if (sandboxResumeNeeded) {
        // The message is now durably queued (or locally staged for transport
        // drain), so sandbox startup must be a follow-up side effect rather than
        // a pre-send await. Otherwise a quick thread switch can cancel the send
        // before the SessionDO ever sees it.
        void requestResume({ force: false }).catch(() => undefined);
      }
      markSidebarWorkRequested({ sessionId: targetSessionId });

      finalizeSuccessfulBootstrapSend({
        hasText: bootstrapText.length > 0,
        attachmentCount: 0,
        contextRefCount: bootstrapContextRefs.length,
        attachedPullRequest: bootstrapAttachedPullRequest,
        attachedPullRequestAlreadyHandled: true,
        preserveComposerDraft: preserveComposerAfterTrackedBootstrapFinalize,
      });
      return true;
    }

    const promptParts = editor.getParts();
    const rawText = promptParts
      .filter((part) => part.type !== "session_ref")
      .map((part) => part.content)
      .join("");
    const sessionContextRefs = draftSessionContextRefsRef.current;
    const requestedSkillRefs = draftRequestedSkillRefsRef.current;
    const preferredMcpServerRefs = draftPreferredMcpServerRefsRef.current;
    // Resolve scratch pad content from the query cache at send time (snapshot semantics)
    const resolvedScratchPadRefs: AgentRuntimeScratchPadRef[] = draftScratchPadRefsRef.current.map((ref) => ({
      ...ref,
      content: ref.sessionId === SCRATCH_PAD_GENERAL_SESSION_ID
        ? generalScratchPadQuery.data?.content ?? ""
        : sessionScratchPadQuery.data?.content ?? "",
    }));
    const text = rawText.trim();
    const hasText = text.length > 0;
    const hasContextRefs = sessionContextRefs.length > 0;
    const hasScratchPadRefs = resolvedScratchPadRefs.length > 0;
    const hasToolRefs = requestedSkillRefs.length > 0 || preferredMcpServerRefs.length > 0 || hasScratchPadRefs;
    const uploadsInFlight = isAttachmentUploadInFlight();
    const fileParts = buildOutgoingAttachmentFileParts({
      attachments: readyAttachments,
      supportsImageAttachments: capabilities.supportsImageAttachments,
    });

    if (isClaudeSession) {
      if (uploadsInFlight) {
        sendQueuedForUploadsRef.current = true;
        return false;
      }

      if (attachmentsFailed) return false;
      if (!hasText && fileParts.length === 0 && !hasContextRefs && !hasToolRefs) return false;

      const parts = hasText
        ? [{ type: "text", text }, ...fileParts]
        : fileParts;

      if (hasText && fileParts.length === 0) {
        const slashResult = await executeSlashCommand(rawText);
        if (activeRouteSessionIdRef.current !== targetSessionId) return false;
        if (slashResult === "failed") {
          return false;
        }
        if (slashResult === "handled_preserve_input") {
          sendQueuedForUploadsRef.current = false;
          historyIndexRef.current = -1;
          savedInputRef.current = "";
          return true;
        }
        if (slashResult === "handled") {
          historyRef.current.push({ text, sessionContextRefs, requestedSkillRefs, preferredMcpServerRefs, scratchPadRefs: resolvedScratchPadRefs });
          clearComposerAfterHandledSlashCommand();
          return true;
        }
      }

      let autoStartedNewWork = false;

      // Auto-trigger "start new work" when the user sends a message on a merged PR session.
      // This creates a fresh branch from the base branch so the user can keep working
      // without manually clicking the "Keep Working" button.
      if (startNewWorkPreparationActive) {
        const ready = await ensureStartNewWorkReady();
        if (!ready) return false;
        autoStartedNewWork = true;
        if (activeRouteSessionIdRef.current !== targetSessionId) return false;
      }

      // `start-new-work` already resumes the sandbox before switching branches,
      // so once that path succeeds we should not issue a second resume request
      // from stale pre-await render state.
      // NOTE: plain `requestResume` (not `resumeAndWaitForRunning`) is intentional
      // here — chat messages are queued in the DO's pending_messages table and
      // auto-flushed once the sandbox reaches running. Blocking the send on
      // sandbox startup would delay the message appearing by 20-40 s for no gain.
      if (sandboxResumeNeeded && !autoStartedNewWork) {
        if (requiresExplicitResume && !autoStartedNewWork) return false;
      }

      if (hasText || hasContextRefs || hasToolRefs) {
        historyRef.current.push({ text, sessionContextRefs, requestedSkillRefs, preferredMcpServerRefs, scratchPadRefs: resolvedScratchPadRefs });
      }
      historyIndexRef.current = -1;
      savedInputRef.current = "";

      const { previewUrls, previewAttachmentsMeta } = buildQueuedMessagePreviewArtifacts({
        attachments: readyAttachments,
        createObjectUrl: (blob) => URL.createObjectURL(blob),
      });

      const result = fileParts.length > 0
        ? sendMessage(text, {
            parts,
            previewUrls,
            previewAttachmentsMeta,
            contextRefs: sessionContextRefs,
            requestedSkillRefs,
            preferredMcpServerRefs,
            scratchPadRefs: resolvedScratchPadRefs,
          })
        : sendMessage(text, {
            contextRefs: sessionContextRefs,
            requestedSkillRefs,
            preferredMcpServerRefs,
            scratchPadRefs: resolvedScratchPadRefs,
          });
      if (!result.accepted) {
        // Revoke the fresh blob URLs we created since they won't be used.
        revokeTemporaryPreviewUrls(previewUrls);
        return false;
      }
      if (revertMessageID) {
        setSuppressRevertBanner(true);
      }
      if (sandboxResumeNeeded && !autoStartedNewWork) {
        // Queue first, resume second: the send itself is the user action that
        // must survive route changes. The resume request can complete after the
        // user switches away, and the DO will flush the queued prompt once the
        // sandbox is running.
        void requestResume({ force: autoStartedNewWork }).catch(() => undefined);
      }
      markSidebarWorkRequested({ sessionId: targetSessionId });

      finalizeSuccessfulComposerSend({
        hasText,
        attachmentCount: fileParts.length,
        contextRefCount: sessionContextRefs.length,
        autoStartedNewWork,
      });
      return true;
    }

    if (uploadsInFlight) {
      sendQueuedForUploadsRef.current = true;
      return false;
    }

    if (attachmentsFailed) return false;

    const parts = hasText
      ? [{ type: "text", text }, ...fileParts]
      : fileParts;
    if (parts.length === 0 && !hasContextRefs && !hasToolRefs) return false;

    if (hasText && fileParts.length === 0) {
      const slashResult = await executeSlashCommand(rawText);
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
      if (slashResult === "failed") {
        return false;
      }
      if (slashResult === "handled_preserve_input") {
        sendQueuedForUploadsRef.current = false;
        historyIndexRef.current = -1;
        savedInputRef.current = "";
        return true;
      }
      if (slashResult === "handled") {
        historyRef.current.push({ text, sessionContextRefs, requestedSkillRefs, preferredMcpServerRefs, scratchPadRefs: resolvedScratchPadRefs });
        clearComposerAfterHandledSlashCommand();
        return true;
      }
    }

    let autoStartedNewWork = false;

    // Auto-trigger "start new work" when the user sends a message on a merged PR session.
    if (startNewWorkPreparationActive) {
      const ready = await ensureStartNewWorkReady();
      if (!ready) return false;
      autoStartedNewWork = true;
      if (activeRouteSessionIdRef.current !== targetSessionId) return false;
    }

    // `start-new-work` already resumes the sandbox before switching branches,
    // so once that path succeeds we should not issue a second resume request
    // from stale pre-await render state.
    // NOTE: plain `requestResume` (not `resumeAndWaitForRunning`) is intentional
    // here too — non-Claude chat sends still flow through the DO's durable
    // pending_messages queue, so blocking on sandbox startup would only slow
    // down message delivery without improving correctness.
    if (sandboxResumeNeeded && !autoStartedNewWork) {
      if (requiresExplicitResume && !autoStartedNewWork) return false;
    }

    if (hasText || hasContextRefs || hasToolRefs) {
      historyRef.current.push({ text, sessionContextRefs, requestedSkillRefs, preferredMcpServerRefs, scratchPadRefs: resolvedScratchPadRefs });
    }
    historyIndexRef.current = -1;
    savedInputRef.current = "";

    const { previewUrls, previewAttachmentsMeta } = buildQueuedMessagePreviewArtifacts({
      attachments: readyAttachments,
      createObjectUrl: (blob) => URL.createObjectURL(blob),
    });

    // Keep @mentions as plain text while attachments are structured file refs.
    const result = sendMessage(text, {
      parts: parts.length > 0 ? parts : undefined,
      previewUrls,
      previewAttachmentsMeta,
      contextRefs: sessionContextRefs,
      requestedSkillRefs,
      preferredMcpServerRefs,
      scratchPadRefs: resolvedScratchPadRefs,
    });
    if (!result.accepted) {
      revokeTemporaryPreviewUrls(previewUrls);
      return false;
    }
    if (revertMessageID) {
      setSuppressRevertBanner(true);
    }
    if (sandboxResumeNeeded && !autoStartedNewWork) {
      // Queue first, resume second for the same reason as the Claude path: a
      // quick route switch must not cancel the durable send acceptance path.
      void requestResume({ force: autoStartedNewWork }).catch(() => undefined);
    }
    markSidebarWorkRequested({ sessionId: targetSessionId });

    finalizeSuccessfulComposerSend({
      hasText,
      attachmentCount: fileParts.length,
      contextRefCount: sessionContextRefs.length,
      autoStartedNewWork,
    });
    return true;
  }, [
    attachments.length,
    attachmentsFailed,
    activeOptimisticForkPreparing,
    capabilities.supportsImageAttachments,
    captureThreadMessageSent,
    clearComposerAfterHandledSlashCommand,
    clearReadyAttachments,
    draftAttachedPullRequest,
    draftPreferredMcpServerRefs,
    draftRequestedSkillRefs,
    draftSessionContextRefs,
    draftText,
    ensureSessionWritableForSend,
    executeSlashCommand,
    ensureStartNewWorkReady,
    isAttachmentUploadInFlight,
    isClaudeSession,
    markSidebarWorkRequested,
    pendingBootstrapActive,
    pendingBootstrapPrToAdopt,
    pendingBootstrapSnapshot,
    requestResume,
    readyAttachments,
    editMessageOperationPending,
    finalizeSuccessfulComposerSend,
    finalizeSuccessfulBootstrapSend,
    composerReadOnly,
    requiresExplicitResume,
    revertMessageID,
    sessionId,
    sessionActionPending,
    sendMessage,
    sandboxResumeNeeded,
    startNewWorkPreparationActive,
  ]);

  useEffect(() => {
    if (!capabilities.supportsImageAttachments) return;
    if (!sendQueuedForUploadsRef.current) return;
    if (attachmentsUploading) return;
    if (attachmentsFailed) {
      sendQueuedForUploadsRef.current = false;
      return;
    }
    if (sessionActionPending) return;
    if (editMessageOperationPending) return;

    void handleSend();
  }, [
    attachmentsFailed,
    attachmentsUploading,
    handleSend,
    capabilities.supportsImageAttachments,
    editMessageOperationPending,
    sessionActionPending,
  ]);

  const initialMessageSent = useRef(false);
  // Reset refs that should not persist across session switches
  const pendingImageFilesRef = useRef<File[] | null>(null);
  const pendingImagesAddedRef = useRef(false);
  const [pendingHomepageAttachmentsStatus, setPendingHomepageAttachmentsStatus] = useState<"idle" | "processing" | "ready">("idle");
  useEffect(() => {
    initialMessageSent.current = false;
    // The earlier session-hydration effect rehydrates bootstrap control state
    // from sessionStorage for the new session. If we clear that state here, the
    // later bootstrap adoption/send effects observe an empty draft and never
    // start create-from PR adoption or other client-driven bootstrap flows.
    setAbortRequestsPendingByConversationId({});
    bootstrapPrAdoptionStartedRef.current = false;
    sendQueuedForUploadsRef.current = false;
    pendingImplicitContextRefsRef.current = [];
    historyRef.current = [];
    historyIndexRef.current = -1;
    savedInputRef.current = "";
    pendingImageFilesRef.current = null;
    pendingImagesAddedRef.current = false;
    setPendingHomepageAttachmentsStatus("idle");
    setHasContent(false);
    setSuppressRevertBanner(false);
    lastMessageRejectionTimestampRef.current = 0;
    prAdoptionPrepareRef.current = null;
    lastProcessedPrAdoptionIdRef.current = null;
  }, [sessionId]);

  const setupOnlyPendingBootstrapActive =
    pendingBootstrapActive && isSetupOnlyPendingBootstrapDraft({ draft: pendingBootstrapSnapshot });
  const currentPendingBootstrapDraft = useMemo<PendingBootstrapDraft>(() => {
    if (!pendingBootstrapActive) {
      return EMPTY_PENDING_BOOTSTRAP_DRAFT;
    }

    if (setupOnlyPendingBootstrapActive) {
      return {
        text: null,
        clientMessageId: null,
        contextRefs: draftSessionContextRefs,
        requestedSkillRefs: draftRequestedSkillRefs,
        preferredMcpServerRefs: draftPreferredMcpServerRefs,
        attachedPullRequest: draftAttachedPullRequest,
        prToAdopt: pendingBootstrapPrToAdopt,
      };
    }

    return {
      text: draftText,
      clientMessageId: pendingBootstrapClientMessageId,
      contextRefs: draftSessionContextRefs,
      requestedSkillRefs: draftRequestedSkillRefs,
      preferredMcpServerRefs: draftPreferredMcpServerRefs,
      attachedPullRequest: draftAttachedPullRequest,
      prToAdopt: pendingBootstrapPrToAdopt,
    };
  }, [
    draftAttachedPullRequest,
    pendingBootstrapClientMessageId,
    draftPreferredMcpServerRefs,
    draftRequestedSkillRefs,
    draftSessionContextRefs,
    draftText,
    pendingBootstrapActive,
    pendingBootstrapPrToAdopt,
    pendingBootstrapSnapshot,
    setupOnlyPendingBootstrapActive,
  ]);
  const initialBootstrapServerEnqueueState = readInitialBootstrapServerEnqueueState(sessionId);
  const effectivePendingBootstrapDraft = useMemo(() => {
    if (!pendingBootstrapActive) {
      return EMPTY_PENDING_BOOTSTRAP_DRAFT;
    }
    if (
      initialBootstrapServerEnqueueState !== null
      && hasPendingBootstrapDraftIntent({ draft: pendingBootstrapSnapshot })
    ) {
      return pendingBootstrapSnapshot;
    }
    return currentPendingBootstrapDraft;
  }, [
    currentPendingBootstrapDraft,
    initialBootstrapServerEnqueueState,
    pendingBootstrapActive,
    pendingBootstrapSnapshot,
  ]);
  const trackedBootstrapClientMessageId =
    effectivePendingBootstrapDraft.clientMessageId ?? pendingBootstrapClientMessageId;
  const bootstrapClientMessageObservedInTimeline = useAgentRuntime((state) => {
    if (!trackedBootstrapClientMessageId) return false;
    return state.snapshot?.timeline.some(
      (item) => item.kind === "user_message" && item.clientMessageId === trackedBootstrapClientMessageId,
    ) ?? false;
  });
  const bootstrapClientMessageObserved = hasObservedBootstrapClientMessage({
    clientMessageId: trackedBootstrapClientMessageId,
    queuedMessages: effectiveQueuedMessages,
    renderedClientMessageIds:
      bootstrapClientMessageObservedInTimeline && trackedBootstrapClientMessageId
        ? [trackedBootstrapClientMessageId]
        : [],
  });
  const pendingAttachedPullRequestObservedInServerQueue = useMemo(() => {
    const clientMessageId = pendingAttachedPullRequestObservation?.clientMessageId?.trim() ?? "";
    if (clientMessageId.length === 0) return false;
    return effectiveQueuedMessages.some((message) => {
      return message.queueKind !== "session_action"
        && message.serverObserved === true
        && message.clientMessageId === clientMessageId;
    });
  }, [effectiveQueuedMessages, pendingAttachedPullRequestObservation]);
  const pendingAttachedPullRequestObservedInTimeline = useAgentRuntime((state) => {
    const clientMessageId = pendingAttachedPullRequestObservation?.clientMessageId?.trim() ?? "";
    if (clientMessageId.length === 0) return false;
    return state.snapshot?.timeline.some(
      (item) => item.kind === "user_message" && item.clientMessageId === clientMessageId,
    ) ?? false;
  });
  const pendingAttachedPullRequestObserved =
    pendingAttachedPullRequestObservedInServerQueue || pendingAttachedPullRequestObservedInTimeline;
  const shouldPreserveComposerAfterTrackedBootstrapFinalize = useMemo(() => {
    if (
      !pendingBootstrapActive
      || initialBootstrapServerEnqueueState === null
      || !hasPendingBootstrapDraftIntent({ draft: pendingBootstrapSnapshot })
    ) {
      return false;
    }
    return shouldPreserveComposerStateAfterBootstrapFinalize({
      bootstrapDraft: pendingBootstrapSnapshot,
      currentText: draftText,
      currentContextRefs: draftSessionContextRefs,
      currentRequestedSkillRefs: draftRequestedSkillRefs,
      currentPreferredMcpServerRefs: draftPreferredMcpServerRefs,
      currentAttachedPullRequest: draftAttachedPullRequest,
      currentPrToAdopt: pendingBootstrapPrToAdopt,
      currentAttachmentCount: attachments.length,
    });
  }, [
    attachments.length,
    draftAttachedPullRequest,
    draftPreferredMcpServerRefs,
    draftRequestedSkillRefs,
    draftSessionContextRefs,
    draftText,
    initialBootstrapServerEnqueueState,
    pendingBootstrapActive,
    pendingBootstrapPrToAdopt,
    pendingBootstrapSnapshot,
  ]);
  const bootstrapServerEnqueueStateIsStale =
    initialBootstrapServerEnqueueState !== null &&
    isInitialBootstrapServerEnqueueStateStale({
      state: initialBootstrapServerEnqueueState,
      nowMs: Date.now(),
    });

  useEffect(() => {
    if (initialBootstrapServerEnqueueState === null) return;
    if (bootstrapClientMessageObserved) return;
    if (bootstrapServerEnqueueStateIsStale) {
      clearBootstrapServerEnqueueStateMarker();
      return;
    }

    const remainingMs = Math.max(
      0,
      initialBootstrapServerEnqueueState.updatedAt
        + getInitialBootstrapServerEnqueueStaleMs({ state: initialBootstrapServerEnqueueState })
        - Date.now(),
    );
    const timeoutId = window.setTimeout(() => {
      bumpBootstrapEnqueueStateVersion();
    }, remainingMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    bootstrapClientMessageObserved,
    bootstrapServerEnqueueStateIsStale,
    clearBootstrapServerEnqueueStateMarker,
    initialBootstrapServerEnqueueState,
  ]);

  // Pick up any pending attachments stashed by the homepage composer
  useEffect(() => {
    if (pendingImagesAddedRef.current) return;
    const files = takePendingAttachments(sessionId);
    if (files.length > 0) {
      pendingImageFilesRef.current = files;
      pendingImagesAddedRef.current = true;
      setPendingHomepageAttachmentsStatus("processing");
      let cancelled = false;
      void Promise.resolve(addImageAttachments(files)).finally(() => {
        if (!cancelled) {
          setPendingHomepageAttachmentsStatus("ready");
        }
      });
      return () => {
        cancelled = true;
      };
    } else {
      pendingImagesAddedRef.current = true;
      setPendingHomepageAttachmentsStatus("ready");
    }
  }, [sessionId, addImageAttachments]);

  useEffect(() => {
    if (bootstrapPrAdoptionStartedRef.current) return;
    if (!authoritativeSessionAvailable) return;

    const { prToAdopt } = currentPendingBootstrapDraft;
    if (!prToAdopt) return;

    bootstrapPrAdoptionStartedRef.current = true;
    const accepted = handlePrPickerIntent({
      intent: "pr",
      pullRequest: {
        number: prToAdopt.number,
        title: prToAdopt.title,
        branchName: prToAdopt.branchName,
        baseBranch: sessionBaseBranch ?? repo?.defaultBranch ?? "main",
        url: prToAdopt.url,
        state: "open",
        updatedAt: new Date().toISOString(),
        linkedSession: prToAdopt.linkedSession ?? undefined,
        linkedSessionCount: prToAdopt.linkedSession ? 1 : 0,
      },
    }, {
      allowBootstrapNotReadyRetry: true,
    });

    if (accepted === false) {
      bootstrapPrAdoptionStartedRef.current = false;
    }
  }, [authoritativeSessionAvailable, currentPendingBootstrapDraft, handlePrPickerIntent, repo?.defaultBranch, sessionBaseBranch]);

  useEffect(() => {
    if (initialBootstrapServerEnqueueState === null) return;
    if (!bootstrapClientMessageObserved) return;
    if (!pendingBootstrapActive) {
      clearBootstrapServerEnqueueStateMarker();
      return;
    }

    finalizeSuccessfulBootstrapSend({
      hasText: (effectivePendingBootstrapDraft.text?.trim().length ?? 0) > 0,
      attachmentCount: 0,
      contextRefCount: effectivePendingBootstrapDraft.contextRefs.length,
      attachedPullRequest: effectivePendingBootstrapDraft.attachedPullRequest,
      preserveComposerDraft: shouldPreserveComposerAfterTrackedBootstrapFinalize,
    });
  }, [
    bootstrapClientMessageObserved,
    clearBootstrapServerEnqueueStateMarker,
    effectivePendingBootstrapDraft,
    finalizeSuccessfulBootstrapSend,
    initialBootstrapServerEnqueueState,
    pendingBootstrapActive,
    shouldPreserveComposerAfterTrackedBootstrapFinalize,
  ]);

  useEffect(() => {
    if (!pendingAttachedPullRequestObservation) return;
    if (!pendingAttachedPullRequestObserved) return;
    writeConsumedPrAdoptionAttachment({
      sessionId,
      successAttachmentKey: buildPrAdoptionSuccessAttachmentKey({
        attachedPullRequest: pendingAttachedPullRequestObservation.attachedPullRequest,
      }),
    });
    writeAcknowledgedPrAdoptionSuccessAttachment({
      sessionId,
      successAttachmentKey: buildPrAdoptionSuccessAttachmentKey({
        attachedPullRequest: pendingAttachedPullRequestObservation.attachedPullRequest,
      }),
    });
    setPendingAttachedPullRequestObservation(null);
  }, [pendingAttachedPullRequestObservation, pendingAttachedPullRequestObserved, sessionId]);

  useEffect(() => {
    if (!draftAttachedPullRequest) return;
    if (!hasObservedAttachedPullRequestInConversation(draftAttachedPullRequest)) return;

    const successAttachmentKey = buildPrAdoptionSuccessAttachmentKey({
      attachedPullRequest: draftAttachedPullRequest,
    });
    writeConsumedPrAdoptionAttachment({
      sessionId,
      successAttachmentKey,
    });
    writeAcknowledgedPrAdoptionSuccessAttachment({
      sessionId,
      successAttachmentKey,
    });
    draftAttachedPullRequestRef.current = null;
    setDraftAttachedPullRequest((current) => {
      if (current === null) {
        return current;
      }
      return buildPrAdoptionSuccessAttachmentKey({ attachedPullRequest: current }) === successAttachmentKey
        ? null
        : current;
    });
    setPrAdoptionStatus((current) => {
      if (current?.status !== "success") {
        return current;
      }

      const currentSuccessAttachmentKey = buildPrAdoptionSuccessAttachmentKey({
        attachedPullRequest: {
          number: current.prNumber,
          title: current.title,
          url: current.url,
          branchName: current.branchName,
        },
      });
      if (currentSuccessAttachmentKey !== successAttachmentKey) {
        return current;
      }

      const outcomeKey = buildPrAdoptionOutcomeKey({
        operationId: current.operationId,
        status: "succeeded",
      });
      if (outcomeKey) {
        writeAcknowledgedPrAdoptionOutcome({ sessionId, outcomeKey });
      }
      return null;
    });
  }, [draftAttachedPullRequest, hasObservedAttachedPullRequestInConversation, sessionId]);

  // Send the initial message (from homepage / new-thread flow)
  useEffect(() => {
    if (initialMessageSent.current) return;
    if (initialBootstrapServerEnqueueState !== null && !bootstrapServerEnqueueStateIsStale) return;
    if (!agentIdentityResolved) return;
    if (status !== "connected") return;
    const {
      text: pending,
      clientMessageId: initialClientMessageId,
      contextRefs: initialContextRefs,
      requestedSkillRefs: initialRequestedSkillRefs,
      preferredMcpServerRefs: initialPreferredMcpServerRefs,
      attachedPullRequest: initialAttachedPullRequest,
      prToAdopt: initialPrToAdopt,
    } = effectivePendingBootstrapDraft;
    if (!hasPendingBootstrapDraftIntent({ draft: effectivePendingBootstrapDraft })) return;

    if (initialPrToAdopt) {
      if (!bootstrapPrAdoptionStartedRef.current) {
        return;
      }
      if (prAdoptionPrepareRef.current) {
        return;
      }
      if (prAdoptionStatus?.status === "error") {
        return;
      }
    }

    if (isSetupOnlyPendingBootstrapDraft({ draft: effectivePendingBootstrapDraft })) {
      finalizeHydratedBootstrapWithoutSend({
        preservePendingPrAdoption: initialPrToAdopt !== null,
      });
      return;
    }

    // If we have pending attachments from the homepage, wait for uploads to settle.
    const hasPendingAttachments = pendingImageFilesRef.current !== null && pendingImageFilesRef.current.length > 0;
    if (hasPendingAttachments) {
      if (pendingHomepageAttachmentsStatus !== "ready") return;
      // Still uploading — wait for next re-render when readyAttachments updates
      if (attachmentsUploading) return;
      // Do not silently downgrade the first message if any attachment failed.
      // Keep the pending state intact so a retry/remove can resume auto-send.
      if (attachmentsFailed) return;
      // Build file parts from whatever uploaded successfully
      const fileParts = buildOutgoingAttachmentFileParts({
        attachments: readyAttachments,
        supportsImageAttachments: true,
      });
      // If we have no text and no attachments succeeded, bail
      if (
        !pending
        && fileParts.length === 0
        && initialContextRefs.length === 0
        && initialRequestedSkillRefs.length === 0
        && initialPreferredMcpServerRefs.length === 0
        && initialAttachedPullRequest === null
      ) {
        initialMessageSent.current = true;
        setPendingBootstrapActive(false);
        setPendingBootstrapSnapshot(EMPTY_PENDING_BOOTSTRAP_DRAFT);
        setPendingBootstrapClientMessageId(null);
        setPendingBootstrapPrToAdopt(null);
        pendingImageFilesRef.current = null;
        bootstrapPrAdoptionStartedRef.current = false;
        sessionStorage.removeItem(initialBootstrapDraftStorageKey(sessionId));
        sessionStorage.removeItem(initialMessageStorageKey(sessionId));
        sessionStorage.removeItem(initialContextRefsStorageKey(sessionId));
        clearBootstrapServerEnqueueStateMarker();
        return;
      }
      const textForMessage = pending || "Here are the files I'd like you to look at:";
      const parts = fileParts.length > 0
        ? [{ type: "text" as const, text: textForMessage }, ...fileParts]
        : undefined;
      const { previewUrls: initPreviewUrls, previewAttachmentsMeta: initPreviewMeta } = buildQueuedMessagePreviewArtifacts({
        attachments: readyAttachments,
        createObjectUrl: (blob) => URL.createObjectURL(blob),
      });
      const result = parts
        ? sendMessage(textForMessage, {
            clientMessageId: initialClientMessageId ?? undefined,
            parts,
            previewUrls: initPreviewUrls,
            previewAttachmentsMeta: initPreviewMeta,
            contextRefs: initialContextRefs,
            requestedSkillRefs: initialRequestedSkillRefs,
            preferredMcpServerRefs: initialPreferredMcpServerRefs,
            attachedPullRequest: initialAttachedPullRequest,
          })
        : sendMessage(textForMessage, {
            clientMessageId: initialClientMessageId ?? undefined,
            contextRefs: initialContextRefs,
            requestedSkillRefs: initialRequestedSkillRefs,
            preferredMcpServerRefs: initialPreferredMcpServerRefs,
            attachedPullRequest: initialAttachedPullRequest,
          });
      if (!result.accepted) {
        revokeTemporaryPreviewUrls(initPreviewUrls);
        return;
      }
      markSidebarWorkRequested({ sessionId });
      finalizeSuccessfulBootstrapSend({
        hasText: textForMessage.trim().length > 0,
        attachmentCount: fileParts.length,
        contextRefCount: initialContextRefs.length,
        attachedPullRequest: initialAttachedPullRequest,
        attachedPullRequestAlreadyHandled: true,
      });
      return;
    }

    // No pending attachments — send text-only if we have text
    if (
      !pending
      && initialContextRefs.length === 0
      && initialRequestedSkillRefs.length === 0
      && initialPreferredMcpServerRefs.length === 0
      && initialAttachedPullRequest === null
    ) {
      initialMessageSent.current = true;
      setPendingBootstrapActive(false);
      setPendingBootstrapSnapshot(EMPTY_PENDING_BOOTSTRAP_DRAFT);
      setPendingBootstrapClientMessageId(null);
      setPendingBootstrapPrToAdopt(null);
      bootstrapPrAdoptionStartedRef.current = false;
      sessionStorage.removeItem(initialBootstrapDraftStorageKey(sessionId));
      sessionStorage.removeItem(initialMessageStorageKey(sessionId));
      sessionStorage.removeItem(initialContextRefsStorageKey(sessionId));
      clearBootstrapServerEnqueueStateMarker();
      return;
    }
    const initialText = pending && pending.trim().length > 0
      ? pending
      : "";
    const result = sendMessage(initialText, {
      clientMessageId: initialClientMessageId ?? undefined,
      contextRefs: initialContextRefs,
      requestedSkillRefs: initialRequestedSkillRefs,
      preferredMcpServerRefs: initialPreferredMcpServerRefs,
      attachedPullRequest: initialAttachedPullRequest,
    });
    if (!result.accepted) return;
    markSidebarWorkRequested({ sessionId });
    finalizeSuccessfulBootstrapSend({
      hasText: initialText.trim().length > 0,
      attachmentCount: 0,
      contextRefCount: initialContextRefs.length,
      attachedPullRequest: initialAttachedPullRequest,
      attachedPullRequestAlreadyHandled: true,
    });
  }, [
    agentIdentityResolved,
    attachmentsFailed,
    attachmentsUploading,
    effectivePendingBootstrapDraft,
    finalizeHydratedBootstrapWithoutSend,
    finalizeSuccessfulBootstrapSend,
    markSidebarWorkRequested,
    pendingHomepageAttachmentsStatus,
    prAdoptionStatus,
    readyAttachments,
    sendMessage,
    status,
    bootstrapServerEnqueueStateIsStale,
    initialBootstrapServerEnqueueState,
  ]);

  const bootstrapQueuedMessageTimestamp = useMemo(() => {
    if (!pendingBootstrapActive) {
      return null;
    }

    return Date.now();
  }, [pendingBootstrapActive, sessionId]);

  const pendingBootstrapQueuedMessages = useMemo(() => {
    if (initialMessageSent.current) return [];
    if (effectiveQueuedMessages.length > 0) return [];

    const {
      text: pendingText,
      contextRefs: initialContextRefs,
      requestedSkillRefs: initialRequestedSkillRefs,
      preferredMcpServerRefs: initialPreferredMcpServerRefs,
      attachedPullRequest: initialAttachedPullRequest,
    } = effectivePendingBootstrapDraft;
    const pendingBootstrapAttachmentCount = pendingImageFilesRef.current?.length ?? 0;
    const hasPendingBootstrapAttachments = pendingBootstrapAttachmentCount > 0;
    const hasStoredBootstrapIntent = hasPendingBootstrapDraftIntent({
      draft: effectivePendingBootstrapDraft,
    });
    const hasBootstrapIntent =
      pendingBootstrapActive
      && !isSetupOnlyPendingBootstrapDraft({ draft: effectivePendingBootstrapDraft })
      && (hasStoredBootstrapIntent || hasPendingBootstrapAttachments);

    if (!hasBootstrapIntent) {
      return [];
    }

    const bootstrapAttachmentCount = hasPendingBootstrapAttachments
      ? pendingHomepageAttachmentsStatus === "processing"
        ? Math.max(attachments.length, pendingBootstrapAttachmentCount)
        : attachments.length
      : 0;
    const bootstrapPreviewAttachments = buildQueuedMessagePreviewAttachments({
      attachments,
    });

    return buildNewThreadBootstrapQueuedMessages({
      enabled: hasBootstrapIntent,
      sessionId,
      pendingText,
      contextRefs: initialContextRefs,
      requestedSkillRefs: initialRequestedSkillRefs,
      preferredMcpServerRefs: initialPreferredMcpServerRefs,
      attachedPullRequest: initialAttachedPullRequest,
      attachmentCount: bootstrapAttachmentCount,
      attachments: bootstrapPreviewAttachments,
      timestamp: bootstrapQueuedMessageTimestamp ?? undefined,
      sandboxStatus,
      transportConnected: status === "connected",
      transportAuthoritativeReady: sessionDoInitReady && !isCatchingUp,
      activeEditOperationState: wsActiveEditMessageOperation?.state ?? null,
      prAdoptionActive:
        wsPrAdoption !== null &&
        (wsPrAdoption.status === "accepted" || wsPrAdoption.status === "running"),
      quickPrExecuting,
    });
  }, [
    attachments,
    effectivePendingBootstrapDraft,
    effectiveQueuedMessages.length,
    isCatchingUp,
    pendingHomepageAttachmentsStatus,
    pendingBootstrapActive,
    bootstrapQueuedMessageTimestamp,
    quickPrExecuting,
    sandboxStatus,
    sessionDoInitReady,
    sessionId,
    status,
    wsActiveEditMessageOperation,
    wsPrAdoption,
  ]);

  const visibleQueuedMessages = effectiveQueuedMessages.length > 0
    ? effectiveQueuedMessages
    : pendingBootstrapQueuedMessages;

  const foregroundSessionController = useForegroundSessionController({
    agentIdentityResolved,
    ready: shouldConnectSessionWs,
    isCatchingUp,
    transportLoading: chatTransportLoading,
    hideTransportLoadingWhenRenderable,
    showEmptyState: !forkBootstrapEntry,
    hasQueuedMessages: visibleQueuedMessages.length > 0,
    historyPresence: activeChatHistoryPresence,
    runtimeMaterialization,
    syncReadyReceived,
    transcriptFreshness,
  });

  const previewAvailable = effectivePreviewEndpoints.length > 0 || Boolean(effectiveBasePreviewUrl);
  const shouldShowReviewModeButton = runtimeReadPathActive && hasQuickPrSourceChanges;
  const handleReviewCurrentTabInputAction = shouldShowReviewModeButton && reviewModeActionState.available
    ? () => {
        handleComposerReviewCurrentTab();
      }
    : undefined;
  const handleReviewFullBranchInputAction = shouldShowReviewModeButton && reviewModeActionState.available
    ? () => {
        handleComposerReviewFullBranch();
      }
    : undefined;
  const reviewModeInputDisabled = shouldShowReviewModeButton && !reviewModeActionState.enabled;
  const reviewModeInputDisabledReason = shouldShowReviewModeButton
    ? reviewModeActionState.disabledReason ?? undefined
    : undefined;
  const runtimeChatQueuePresentationContext = useMemo(() => ({
    sandboxStatus,
    bootSource,
    sandboxStartupKind,
    transportConnected: status === "connected",
    transportAuthoritativeReady: sessionDoInitReady && !isCatchingUp,
    activeEditOperationState: wsActiveEditMessageOperation?.state ?? null,
    prAdoptionActive:
      wsPrAdoption !== null &&
      (wsPrAdoption.status === "accepted" || wsPrAdoption.status === "running"),
    quickPrExecuting,
    activeWorkState: activeThreadControlState.workState,
    canMakeProgress: activeThreadControlState.canMakeProgress,
    threadActivityState,
  }), [
    activeThreadControlState.canMakeProgress,
    activeThreadControlState.workState,
    bootSource,
    isCatchingUp,
    quickPrExecuting,
    sandboxStatus,
    sandboxStartupKind,
    sessionDoInitReady,
    status,
    threadActivityState,
    wsActiveEditMessageOperation?.state,
    wsPrAdoption,
  ]);
  const runtimeChatEmptyState = useMemo(() => <EmptyState />, []);

  // Whether the active chat surface has no messages yet — used to center the input box.
  // This intentionally covers both brand-new sessions and new same-session conversation tabs.
  // Only brand-new sessions should trigger the global chrome-hiding mode below.
  const shouldCenterComposer =
    foregroundSessionController.chatState.phase === "empty" &&
    visibleQueuedMessages.length === 0;

  // Whether the overall session has no messages yet — used to hide session chrome for the
  // first-message clean-start experience. New same-session conversation tabs keep the
  // already-loaded top bar and right sidebar visible.
  const isEmptySession =
    shouldCenterComposer && sessionHistoryPresence === "no_history";

  // Sync the empty-session state to the global UI store before paint so the
  // session layout does not flash the header/sidebar while React is settling
  // the clean-start state after a thread switch.
  const setNewSessionCentered = useSessionUI((s) => s.setNewSessionCentered);
  useLayoutEffect(() => {
    setNewSessionCentered(isEmptySession);
    return () => {
      // When this session page unmounts, ensure we leave centered mode off
      // so navigating away doesn't leave the right sidebar hidden.
      setNewSessionCentered(false);
    };
  }, [isEmptySession, setNewSessionCentered]);

  const conversationContextPickerItems = useMemo((): SessionPickerItem[] => {
    if (!runtimeConversationTabsAvailable) return [];
    return runtimeConversations.map((conversation) => {
      const isDefault = conversation.isDefault;
      const label = isDefault
        ? `${fetchedSessionTitle || optimisticTitle || "Main"} (main chat)`
        : `${conversation.title?.trim() || "New chat"}`;
      return {
        id: `${sessionId}:${conversation.conversationId}`,
        sourceSessionId: sessionId,
        conversationId: conversation.conversationId,
        repositoryId: repoId,
        title: label,
        subtitle: isDefault ? "This thread · main chat" : "This thread · side chat",
        createdAt: conversation.createdAt > MIN_RUNTIME_CONVERSATION_CREATED_AT_MS
          ? new Date(conversation.createdAt).toISOString()
          : sessionCreatedAt ?? new Date(MIN_RUNTIME_CONVERSATION_CREATED_AT_MS).toISOString(),
        archivedAt: null,
        lifecycleStatus: conversation.statusKind ?? "active",
        branch: displaySessionBranch,
        prNumber: sessionPrNumber,
        prState: sessionPrState,
        ownerLabel: "Current thread",
        disabledReason: conversation.conversationId === activeConversationId
          ? "Already using this chat tab"
          : null,
      } satisfies SessionPickerItem;
    });
  }, [activeConversationId, displaySessionBranch, fetchedSessionTitle, optimisticTitle, repoId, runtimeConversationTabsAvailable, runtimeConversations, sessionCreatedAt, sessionId, sessionPrNumber, sessionPrState]);

  useEffect(() => {
    const nextSelectedPreviewEndpointId = resolveSelectedPreviewEndpointId({
      previewEndpoints: effectivePreviewEndpoints,
      selectedPreviewEndpointId,
    });

    if (nextSelectedPreviewEndpointId !== selectedPreviewEndpointId) {
      setSelectedPreviewEndpointIdForSession(sessionId, nextSelectedPreviewEndpointId);
    }
  }, [effectivePreviewEndpoints, selectedPreviewEndpointId, sessionId, setSelectedPreviewEndpointIdForSession]);

  const handleEditorEscape = useCallback(() => {
    editorRef.current?.blur();
  }, []);

  const handleEditorRef = useCallback((instance: PromptEditorHandle | null) => {
    editorRef.current = instance;
    setEditorMountVersion((version) => version + 1);
  }, []);

  const handleEditorAbort = useCallback(() => {
    if (activeThreadControlState.stopMode !== "enabled") {
      return;
    }

    const requestedAt = Date.now();
    const requestedRunActivityEventId =
      typeof currentActiveThreadActivityEventId === "number" &&
      Number.isFinite(currentActiveThreadActivityEventId) &&
      (
        typeof currentActiveThreadFinishedEventId !== "number" ||
        !Number.isFinite(currentActiveThreadFinishedEventId) ||
        currentActiveThreadActivityEventId > currentActiveThreadFinishedEventId
      )
        ? currentActiveThreadActivityEventId
        : null;
    const pendingAbortRequest: AbortRequestPendingState = {
      conversationId: activeConversationId,
      requestedAt,
      requestedRunStartedAt: runtimeStatusStartedAt,
      requestedRunActivityEventId,
    };
    setAbortRequestsPendingByConversationId((current) => ({
      ...current,
      [activeConversationId]: pendingAbortRequest,
    }));
    const abortActiveRunMessage = buildAbortActiveRunSocketMessage({
      activeConversationId,
    });
    const accepted = sendSessionSocketAction(
      abortActiveRunMessage,
      "Connection unavailable. Could not send abort request.",
    );
    if (!accepted) {
      setAbortRequestsPendingByConversationId((current) => removeAbortRequestPendingState({
        current,
        conversationId: pendingAbortRequest.conversationId,
        expected: pendingAbortRequest,
      }));
    }
  }, [
    activeThreadControlState.stopMode,
    activeConversationId,
    currentActiveThreadActivityEventId,
    currentActiveThreadFinishedEventId,
    runtimeStatusStartedAt,
    sendSessionSocketAction,
  ]);

  const handleCancelQueuedMessage = useCallback((clientMessageId: string) => {
    cancelQueuedMessage(clientMessageId);
  }, [cancelQueuedMessage]);

  const handleEditorTextChange = useCallback((text: string) => {
    const localDraftInputSuppressed = suppressLocalDraftInputTrackingRef.current
    draftTextRef.current = text;
    draftCursorPositionRef.current = editorRef.current?.getCursorPosition() ?? 0;
    if (pendingBootstrapActive) {
      setPendingBootstrapTextVersion((version) => version + 1)
    }
    markLocalDraftInput()
    if (!localDraftInputSuppressed) {
      requestDraftPersist({
        localDebounceMs: LOCAL_DRAFT_PERSIST_DEBOUNCE_MS,
        remoteDebounceMs: REMOTE_DRAFT_PERSIST_DEBOUNCE_MS,
      })
    }
    maybeStartNewWorkFromDraftInput({ text });
  }, [
    markLocalDraftInput,
    maybeStartNewWorkFromDraftInput,
    pendingBootstrapActive,
    scheduleLocalDraftPersist,
    scheduleRemoteDraftPersist,
  ]);

  useEffect(() => {
    const handler = () => {
      if (editorRef.current?.hasFocus()) {
        draftCursorPositionRef.current = editorRef.current.getCursorPosition();
      }
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, []);

  const handleEditorSessionContextRefsChange = useCallback(
    (contextRefs: AgentRuntimeSessionContextRef[]) => {
      markLocalDraftInput()
      draftSessionContextRefsRef.current = contextRefs
      setDraftSessionContextRefs((current) => (
        sameSessionContextRefs(current, contextRefs) ? current : contextRefs
      ));
      maybeStartNewWorkFromDraftInput({ hasContextRefs: contextRefs.length > 0 });
    },
    [markLocalDraftInput, maybeStartNewWorkFromDraftInput],
  )

  const handleEditorRequestedSkillRefsChange = useCallback(
    (refs: AgentRuntimeRequestedSkillRef[]) => {
      markLocalDraftInput()
      const normalized = dedupeRequestedSkillRefs(refs)
      draftRequestedSkillRefsRef.current = normalized
      setDraftRequestedSkillRefs((current) => (
        sameRequestedSkillRefs(current, normalized) ? current : normalized
      ))
      maybeStartNewWorkFromDraftInput({ hasContextRefs: normalized.length > 0 })
    },
    [markLocalDraftInput, maybeStartNewWorkFromDraftInput],
  )

  const handleEditorPreferredMcpServerRefsChange = useCallback(
    (refs: AgentRuntimePreferredMcpServerRef[]) => {
      markLocalDraftInput()
      const normalized = dedupePreferredMcpServerRefs(refs)
      draftPreferredMcpServerRefsRef.current = normalized
      setDraftPreferredMcpServerRefs((current) => (
        samePreferredMcpServerRefs(current, normalized) ? current : normalized
      ))
      maybeStartNewWorkFromDraftInput({ hasContextRefs: normalized.length > 0 })
    },
    [markLocalDraftInput, maybeStartNewWorkFromDraftInput],
  )

  const handleEditorScratchPadRefsChange = useCallback(
    (refs: AgentRuntimeScratchPadRef[]) => {
      markLocalDraftInput()
      draftScratchPadRefsRef.current = refs
      setDraftScratchPadRefs(refs)
    },
    [markLocalDraftInput],
  )

  const handleRemoveDraftSessionContextRef = useCallback(
    (ref: AgentRuntimeSessionContextRef, index: number) => {
      markLocalDraftInput()
      setDraftSessionContextRefs((current) => {
        const next = current.filter((_, currentIndex) => currentIndex !== index)
        draftSessionContextRefsRef.current = next
        return next
      })
      if (ref.origin === "pr_linked_session") {
        setPendingBootstrapPrToAdopt((current) => {
          if (!current) return null;
          return {
            ...current,
            linkedSession: null,
          };
        });
      }
    },
    [markLocalDraftInput],
  )

  const handleRemoveDraftRequestedSkillRef = useCallback(
    (_ref: AgentRuntimeRequestedSkillRef, index: number) => {
      markLocalDraftInput()
      setDraftRequestedSkillRefs((current) => {
        const next = current.filter((_, currentIndex) => currentIndex !== index)
        draftRequestedSkillRefsRef.current = next
        return next
      })
    },
    [markLocalDraftInput],
  )

  const handleRemoveDraftPreferredMcpServerRef = useCallback(
    (_ref: AgentRuntimePreferredMcpServerRef, index: number) => {
      markLocalDraftInput()
      setDraftPreferredMcpServerRefs((current) => {
        const next = current.filter((_, currentIndex) => currentIndex !== index)
        draftPreferredMcpServerRefsRef.current = next
        return next
      })
    },
    [markLocalDraftInput],
  )

  // Also persist when attachments change (e.g. upload completes)
  const readyAttachmentCount = readyAttachments.length;
  useEffect(() => {
    if (!draftIdbLoadedRef.current) return;
    if (!hasLocalDraftInputRef.current) return;
    requestDraftPersist({
      localDebounceMs: ATTACHMENT_DRAFT_PERSIST_DEBOUNCE_MS,
      remoteDebounceMs: draftPersistenceReady ? REMOTE_DRAFT_PERSIST_DEBOUNCE_MS : undefined,
    })
  }, [draftPersistenceReady, readyAttachmentCount, requestDraftPersist]);

  useEffect(() => {
    if (!draftIdbLoadedRef.current) return;
    if (!hasLocalDraftInputRef.current) return;
    requestDraftPersist({
      localDebounceMs: LOCAL_DRAFT_PERSIST_DEBOUNCE_MS,
      remoteDebounceMs: draftPersistenceReadyRef.current ? REMOTE_DRAFT_PERSIST_DEBOUNCE_MS : undefined,
    })
  }, [
    draftAttachedPullRequest,
    draftPreferredMcpServerRefs,
    draftRequestedSkillRefs,
    draftSessionContextRefs,
    requestDraftPersist,
  ]);

  useEffect(() => {
    if (!draftPersistenceReady) return;
    if (!hasLocalDraftInputRef.current) return;
    requestDraftPersist({ remoteDebounceMs: REMOTE_DRAFT_PERSIST_DEBOUNCE_MS })
  }, [draftPersistenceReady, requestDraftPersist]);

  useEffect(() => {
    if (!draftIdbLoadedRef.current) return;
    if (draftHydratedSessionRef.current === sessionId) return;
    const editor = editorRef.current;
    if (!editor) return;
    if (hasLocalDraftInputRef.current) {
      draftHydratedSessionRef.current = sessionId;
      return;
    }
    if (
      draftTextRef.current.length === 0
      && draftSessionContextRefs.length === 0
      && draftRequestedSkillRefs.length === 0
      && draftPreferredMcpServerRefs.length === 0
      && !draftAttachedPullRequest
    ) {
      draftHydratedSessionRef.current = sessionId;
      return;
    }
    runWithoutLocalDraftInputTracking(() => {
      editor.setStructuredContent({
        text: draftTextRef.current,
        sessionContextRefs: draftSessionContextRefs,
        requestedSkillRefs: draftRequestedSkillRefs,
        preferredMcpServerRefs: draftPreferredMcpServerRefs,
      });
      editor.setCursorPosition(draftCursorPositionRef.current);
    });
    setHasContent(
      draftTextRef.current.trim().length > 0
        || draftSessionContextRefs.length > 0
        || draftRequestedSkillRefs.length > 0
        || draftPreferredMcpServerRefs.length > 0
        || draftAttachedPullRequest !== null,
    );
    draftHydratedSessionRef.current = sessionId;
  }, [
    draftAttachedPullRequest,
    draftPreferredMcpServerRefs,
    draftRequestedSkillRefs,
    draftSessionContextRefs,
    sessionId,
    draftHydrationVersion,
    editorMountVersion,
    runWithoutLocalDraftInputTracking,
  ]);

  const effectivePreviewUrl =
    selectedPreviewEndpointId && effectivePreviewEndpoints.length > 0
      ? effectivePreviewEndpoints.find((endpoint) => endpoint.endpointId === selectedPreviewEndpointId)?.url ?? effectiveBasePreviewUrl
      : effectiveBasePreviewUrl;

  const selectedPreviewProcessId =
    selectedPreviewEndpointId && effectivePreviewEndpoints.length > 0
      ? effectivePreviewEndpoints.find((endpoint) => endpoint.endpointId === selectedPreviewEndpointId)?.processId ?? null
      : effectivePreviewEndpoints[0]?.processId ?? null;
  const selectedPreviewProcess = selectedPreviewProcessId
    ? effectiveRuntimeProcesses.find((process) => process.id === selectedPreviewProcessId) ?? null
    : null;
  const runnableRuntimeProcesses = effectiveRuntimeProcesses.filter((process) => Boolean(process.command));
  const hasPreviewableRuntimeProcess = effectiveRuntimeProcesses.some((process) => process.endpoints.some((endpoint) => endpoint.exposure !== "internal"));
  const selectedPreviewStartTarget = selectedPreviewProcess?.command
    ? selectedPreviewProcess
    : runnableRuntimeProcesses.length === 1
      ? runnableRuntimeProcesses[0] ?? null
      : null;
  const previewRuntimeStartSendable = transportConnectEnabled
    && !transportStandbyInAnotherTab
    && status === "connected"
    && wsDegradedSinceAt === null;
  const getPreviewRuntimeStartRequestKey = useCallback((processId: string) => {
    return `${sessionId}:${sessionSandboxId ?? "no-sandbox"}:${sessionRuntimeGeneration}:${processId}`;
  }, [sessionId, sessionSandboxId, sessionRuntimeGeneration]);

  const requestRuntimeProcessStartOncePerLifecycle = useCallback((args: {
    processId: string;
    restart?: boolean;
    ignoreAcceptedStartRequest?: boolean;
  }) => {
    if (sessionArchived) return false;
    if (sandboxStatus !== "running" || !effectiveRuntimeProcessesConfigured) return false;
    if (!previewRuntimeStartSendable) return false;

    const requestKey = getPreviewRuntimeStartRequestKey(args.processId);
    if (!args.ignoreAcceptedStartRequest && previewRuntimeAcceptedStartRequestKeysRef.current.has(requestKey)) {
      return false;
    }

    const accepted = send({
      type: "start_runtime_process",
      processId: args.processId,
      restart: args.restart === true,
    });
    if (!accepted) {
      return false;
    }

    previewRuntimeAcceptedStartRequestKeysRef.current.add(requestKey);
    return true;
  }, [
    effectiveRuntimeProcessesConfigured,
    getPreviewRuntimeStartRequestKey,
    previewRuntimeStartSendable,
    sandboxStatus,
    send,
    sessionArchived,
  ]);

  useEffect(() => {
    if (!selectedPreviewStartTarget?.id) return;

    const requestKey = getPreviewRuntimeStartRequestKey(selectedPreviewStartTarget.id);
    if (selectedPreviewStartTarget.status === "running") {
      previewRuntimeObservedRunningKeysRef.current.add(requestKey);
      return;
    }

    if (
      (selectedPreviewStartTarget.status === "idle" || selectedPreviewStartTarget.status === "error")
      && previewRuntimeObservedRunningKeysRef.current.has(requestKey)
    ) {
      previewRuntimeObservedRunningKeysRef.current.delete(requestKey);
      previewRuntimeAcceptedStartRequestKeysRef.current.delete(requestKey);
    }
  }, [
    getPreviewRuntimeStartRequestKey,
    selectedPreviewStartTarget?.id,
    selectedPreviewStartTarget?.status,
  ]);

  useEffect(() => {
    // Start the selected preview runtime as soon as the sandbox reaches a live
    // running state. Preview opening should only mount/show the iframe; it
    // should not be responsible for re-triggering the dev-server lifecycle.
    if (sessionArchived) return;
    if (bootSource === "bare") return;
    if (sandboxStatus !== "running" || !effectiveRuntimeProcessesConfigured) return;
    if (!previewRuntimeStartSendable) return;
    if (!selectedPreviewStartTarget?.id) return;
    if (selectedPreviewStartTarget.status === "running" || selectedPreviewStartTarget.status === "starting") {
      return;
    }

    requestRuntimeProcessStartOncePerLifecycle({ processId: selectedPreviewStartTarget.id });
  }, [
    bootSource,
    effectiveRuntimeProcessesConfigured,
    previewRuntimeStartSendable,
    requestRuntimeProcessStartOncePerLifecycle,
    sandboxStatus,
    selectedPreviewStartTarget?.id,
    selectedPreviewStartTarget?.status,
    sessionArchived,
  ]);

  const handleSelectPreviewEndpoint = useCallback(
    (endpointId: string) => {
      if (sessionArchived) return;
      setSelectedPreviewEndpointIdForSession(sessionId, endpointId);
      send({ type: "set_default_preview_endpoint", endpointId });

      if (sandboxStatus !== "running") return;

      const nextEndpoint = effectivePreviewEndpoints.find((endpoint) => endpoint.endpointId === endpointId) ?? null;
      if (!nextEndpoint) return;

      // `process.command` being non-null is the convention for a Cord-managed
      // process. Only those can be started via start_runtime_process.
      const nextProcess = effectiveRuntimeProcesses.find((process) => process.id === nextEndpoint.processId && process.command) ?? null;
      // Guard against rapid endpoint switching: if the server is already
      // running or on its way up, don't send a redundant start. The server
      // treats start_runtime_process as idempotent but avoids unnecessary noise.
      if (!nextProcess || nextProcess.status === "running" || nextProcess.status === "starting") return;

      requestRuntimeProcessStartOncePerLifecycle({ processId: nextProcess.id });
    },
    [
      effectivePreviewEndpoints,
      effectiveRuntimeProcesses,
      requestRuntimeProcessStartOncePerLifecycle,
      sandboxStatus,
      send,
      sessionId,
      sessionArchived,
      setSelectedPreviewEndpointIdForSession,
    ],
  );

  // Disabled-reason predicates are duplicated further down alongside the
  // related `canTogglePreview` / `canToggleDevServerLogs` flags so render
  // code can consume them directly. We re-derive the "no runtime processes"
  // signal here so the click handlers can defensively reject stale events
  // even if a future render change loosens the button gates.
  const handlePreviewClick = () => {
    if (sessionArchived) return;
    // Defensive: if the sandbox is running but nothing previewable exists,
    // the button should be disabled — ignore stray clicks rather than open
    // an empty "Waiting for preview URL…" panel.
    if (
      sandboxStatus === "running" &&
      !previewAvailable &&
      !hasPreviewableRuntimeProcess
    ) {
      return;
    }
    if (sandboxResumeNeeded) {
      if (requiresExplicitResume) return;
      void requestResume();
    }

    if (
      sandboxStatus === "running" &&
      selectedPreviewStartTarget?.id &&
      selectedPreviewStartTarget.status !== "running" &&
      selectedPreviewStartTarget.status !== "starting"
    ) {
      // Opening Preview is explicit user intent to see the app. This is
      // especially important for fresh bare-image sessions where build settings
      // may have just been registered but the dev process is still idle; without
      // this, the iframe can sit on "Waiting for preview URL" until the user
      // discovers the logs restart button.
      requestRuntimeProcessStartOncePerLifecycle({
        processId: selectedPreviewStartTarget.id,
        restart: selectedPreviewStartTarget.status === "error",
        ignoreAcceptedStartRequest: true,
      });
    }

    if (contentPanelMode !== "preview") {
      markPreviewOpenedForSession(sessionId);
    }

    openPreview();
  };

  const handleDevServerLogsClick = () => {
    if (sessionArchived) return;
    // Defensive: if no runtime processes are configured, there is nothing
    // to stream output from — the button should be disabled, so ignore the
    // click rather than opening an empty logs panel.
    if (sandboxStatus === "running" && !runtimeProcessesConfigured) {
      return;
    }
    if (sandboxResumeNeeded) {
      if (requiresExplicitResume) return;
      void requestResume();
    }

    openDevServerLogs();
  };

  const handleTerminalActionRequest = useCallback((options?: { createTerminal?: boolean }) => {
    const createTerminal = options?.createTerminal === true;

    if (terminalVisible && !createTerminal) {
      toggleTerminal();
      return true;
    }

    if (sessionArchived) {
      toast.error(ARCHIVED_THREAD_RESUME_REASON);
      return false;
    }

    if (requiresExplicitResume) {
      toast.error(MANUAL_RESUME_REQUIRED_REASON);
      return false;
    }

    // When terminal is already visible and user requests a new tab, trigger
    // resume if the sandbox needs it — otherwise the pty won't be available.
    // Fire-and-forget is intentional here: the TerminalPanel shows its own
    // suspended/resuming UI state, and `requestResume` sets `resumeActionPending`
    // which drives loading indicators elsewhere.
    if (terminalVisible) {
      if (sandboxResumeNeeded) {
        void requestResume();
      }
      return true;
    }

    if (sandboxResumeNeeded) {
      if (branchSwitchPending) {
        void requestResume();
        return false;
      }

      void requestResume();
    }

    toggleTerminal();
    return true;
  }, [
    branchSwitchPending,
    requestResume,
    requiresExplicitResume,
    sandboxResumeNeeded,
    sessionArchived,
    terminalVisible,
    toggleTerminal,
  ]);

  useEffect(() => {
    const handleTerminalToggleEvent = () => {
      handleTerminalActionRequest();
    };

    window.addEventListener(SESSION_TERMINAL_TOGGLE_REQUEST_EVENT, handleTerminalToggleEvent);
    return () => {
      window.removeEventListener(SESSION_TERMINAL_TOGGLE_REQUEST_EVENT, handleTerminalToggleEvent);
    };
  }, [handleTerminalActionRequest]);

  useEffect(() => {
    const handleFocusComposer = () => {
      editorRef.current?.focusAtEnd();
    };

    window.addEventListener(SESSION_FOCUS_COMPOSER_EVENT, handleFocusComposer);
    return () => {
      window.removeEventListener(SESSION_FOCUS_COMPOSER_EVENT, handleFocusComposer);
    };
  }, []);

  const handleRestartDevServer = useCallback((processId: string | null) => {
    if (sessionArchived) return;
    const targetProcess = processId
      ? effectiveRuntimeProcesses.find((process) => process.id === processId && process.command) ?? null
      : selectedPreviewStartTarget;
    if ((targetProcess?.status ?? devServerStatus) === "starting") return;
    if (targetProcess?.id) {
      send({ type: "start_runtime_process", processId: targetProcess.id, restart: true });
    }
  }, [devServerStatus, effectiveRuntimeProcesses, selectedPreviewStartTarget, send, sessionArchived]);
  const sessionTitle =
    ((session as Record<string, unknown>)?.title as string) ?? optimisticTitle ?? "Session";
  const { data: parentSession } = useQuery({
    queryKey: ["session", sessionParentSessionId],
    queryFn: async () => {
      if (!sessionParentSessionId) return null;
      const res = await api.api.sessions[":id"].$get({
        param: { id: sessionParentSessionId },
      });
      if (!res.ok) {
        return null;
      }
      return res.json();
    },
    enabled: parentRelationType === "continue" && Boolean(sessionParentSessionId),
    staleTime: 30_000,
  });
  const continuedFromTitle =
    typeof (parentSession as Record<string, unknown> | null)?.title === "string"
      ? ((parentSession as Record<string, unknown>).title as string)
      : null;

  /**
   * The preview and process-logs buttons render whenever the sandbox layer is
   * otherwise available (running or needs resume), but are disabled with a
   * tooltip when the underlying feature is unavailable. This keeps the header
   * stable across bare and fully-configured sessions and makes the "why is
   * this off?" answer discoverable on hover.
   *
   * The two predicates are intentionally different:
   *
   * - **Preview** is disabled when the sandbox is running but no previewable
   *   endpoint exists (`!previewAvailable && !hasPreviewableRuntimeProcess`).
   *   This covers bare sessions AND configured repos that only run internal
   *   (non-HTTP) runtime processes like queue workers, where clicking Preview
   *   would just open an empty "Waiting for preview URL…" panel.
   *
   * - **Process Logs** is disabled only when no runtime processes are
   *   configured at all (`!runtimeProcessesConfigured`). Logs are useful even
   *   for configured-but-not-web-exposed repos, so the logs button stays
   *   enabled whenever there is any process to stream output from.
   */
  const previewAndLogsBaseAvailable =
    !sessionArchived &&
    !requiresExplicitResume &&
    (sandboxStatus === "running" || sandboxResumeNeeded);

  const previewDisabledReason: string | null = !previewAndLogsBaseAvailable
    ? null // hidden entirely when sandbox isn't up yet
    : (sandboxStatus === "running" && !previewAvailable && !hasPreviewableRuntimeProcess)
      ? NO_RUNTIME_PROCESSES_DISABLED_REASON
      : null;

  const devServerLogsDisabledReason: string | null = !previewAndLogsBaseAvailable
    ? null // hidden entirely when sandbox isn't up yet
    : (sandboxStatus === "running" && !runtimeProcessesConfigured)
      ? NO_RUNTIME_PROCESSES_DISABLED_REASON
      : null;

  // Show the button whenever the sandbox layer is available. Disabled state
  // is handled via the *DisabledReason vars above.
  const showPreviewButton = previewAndLogsBaseAvailable;
  const showDevServerLogsButton = previewAndLogsBaseAvailable;

  // "Can actually be clicked" — used by the click handlers as a defensive
  // guard in case a future caller loosens the render gates without noticing.
  const canTogglePreview = showPreviewButton && previewDisabledReason === null;
  const canToggleTerminal =
    !sessionArchived && !requiresExplicitResume && (sandboxStatus === "running" || sandboxResumeNeeded);
  const canToggleCodeServer =
    !sessionArchived && !requiresExplicitResume && (sandboxStatus === "running" || sandboxResumeNeeded);
  const canToggleDevServerLogs = showDevServerLogsButton && devServerLogsDisabledReason === null;

  /* Whether the sandbox can be explicitly suspended from the UI. The desktop
     "More actions" menu exposes this; we mirror it in mobile tools. */
  const canSuspendSandbox =
    !sessionArchived &&
    lifecycleStatus === "active" &&
    !sandboxResumeNeeded &&
    sandboxStatus === "running";
  const canResumeSandbox =
    !sessionArchived && sandboxResumeNeeded && lifecycleStatus !== "suspending";
  const showMergedBannerResumeAction = false;
  const mergedBannerResumeDisabledReason = null;
  const terminalStartupPending = terminalVisible && !effectivePtyUrl && sandboxTransientSpinUp;
  const terminalStartupLabel = `${sandboxStartupLabel} sandbox…`;
  // Preview startup pending: mirrors terminal logic. Show spinner overlay when
  // the preview panel is open (or being opened) while sandbox is spinning up.
  const previewStartupPending = contentPanelMode === "preview" && sandboxTransientSpinUp;
  const previewStartupLabel = `${sandboxStartupLabel} sandbox…`;
  const showContinueInNewThreadAction = canContinueNewSession && !startNewWorkActionPending;
  const showMobileContinueInNewThreadAction = showContinueInNewThreadAction;
  const showMobileArchiveThreadAction =
    !mergedPrNeedsContinuation || (!sessionArchived && !startNewWorkActionPending);

  const mobileToolsContent = (
    <MobileToolsPanel>
      {/* --- Branch / Git info (desktop shows this inline in the header) --- */}
      {displaySessionBranch && (
        <MobileToolsSection title="Branch">
          {displayBranchTreeUrl ? (
            <MobileToolsLink
              icon={GitBranch}
              label={displaySessionBranch}
              description={displaySessionBaseBranch && displaySessionBaseBranch !== displaySessionBranch
                ? `Branched from ${displaySessionBaseBranch}`
                : undefined}
              href={displayBranchTreeUrl}
            />
          ) : (
            <MobileToolsInfoRow
              icon={GitBranch}
              label="Branch"
              value={displaySessionBranch}
            />
          )}
          {displaySessionBaseBranch && displaySessionBaseBranch !== displaySessionBranch && !displayBranchTreeUrl && (
            <MobileToolsInfoRow
              icon={GitBranch}
              label="Base branch"
              value={displaySessionBaseBranch}
            />
          )}
          <MobileToolsButton
            icon={Copy}
            label={branchCopied ? "Copied!" : "Copy Branch Name"}
            description="Copy the branch name to clipboard"
            onClick={() => {
              if (!sessionBranch) return;
              void navigator.clipboard.writeText(sessionBranch).then(() => {
                setBranchCopied(true);
                if (branchCopiedTimeoutRef.current) {
                  clearTimeout(branchCopiedTimeoutRef.current);
                }
                branchCopiedTimeoutRef.current = setTimeout(() => {
                  setBranchCopied(false);
                  branchCopiedTimeoutRef.current = null;
                }, 1500);
              });
            }}
          />
        </MobileToolsSection>
      )}

      {/* --- Workspace tools (preview, terminal, VS Code, etc.) --- */}
      <MobileToolsSection title="Workspace">
        {showPreviewButton && (
          <MobileToolsButton
            icon={Globe}
            label={contentPanelMode === "preview" ? "Hide Preview" : "Open Preview"}
            description={
              previewDisabledReason ?? "Open app preview in a full-screen viewer"
            }
            disabled={Boolean(previewDisabledReason)}
            onClick={() => {
              handlePreviewClick();
            }}
          />
        )}
        {showDevServerLogsButton && (
          <MobileToolsButton
            icon={FileText}
            label={contentPanelMode === "devlogs" ? "Hide Process Logs" : "Open Process Logs"}
            description={
              devServerLogsDisabledReason ?? "Watch live runtime process output and restart"
            }
            disabled={Boolean(devServerLogsDisabledReason)}
            onClick={() => {
              handleDevServerLogsClick();
            }}
          />
        )}
        {canToggleTerminal && (
          <MobileToolsButton
            icon={Terminal}
            label={terminalVisible ? "Hide Terminal" : "Open Terminal"}
            description="Run commands in the sandbox terminal"
            onClick={() => {
              handleTerminalActionRequest();
            }}
          />
        )}
        {canToggleCodeServer && (
          <MobileToolsButton
            icon={VSCodeLogoIcon}
            label={
              codeServerStatus === "running"
                ? "Open VS Code"
                : codeServerStatus === "starting"
                  ? "Starting VS Code..."
                  : "Start VS Code"
            }
            description="Launch a browser-based VS Code workspace"
            disabled={codeServerStatus === "starting"}
              onClick={async () => {
                if (sandboxResumeNeeded) {
                  resumeThenStartCodeServerRef.current = sessionId;
                  const resumed = await requestResume();
                  if (!resumed) {
                    resumeThenStartCodeServerRef.current = null;
                  }
                  return;
                }
              startCodeServerFromUi();
            }}
          />
        )}
      </MobileToolsSection>

      {/* --- Sandbox controls (resume / suspend) --- */}
      {(canResumeSandbox || canSuspendSandbox) && (
        <MobileToolsSection title="Sandbox">
          {canResumeSandbox && (
            <MobileToolsButton
              icon={Play}
              label={resumeActionPending ? "Resuming..." : "Resume Sandbox"}
              description={branchSwitchPending
                ? BRANCH_SWITCH_BLOCKED_REASON
                : "Start the sandbox back up"}
              disabled={resumeActionPending || branchSwitchPending}
              loading={resumeActionPending}
              onClick={() => {
                void requestResume({ force: true });
              }}
            />
          )}
          {canSuspendSandbox && (
            <MobileToolsButton
              icon={Pause}
              label={suspendSession.isPending ? "Suspending..." : "Suspend Sandbox"}
              description={branchSwitchPending
                ? BRANCH_SWITCH_BLOCKED_REASON
                : "Pause the sandbox to save resources"}
              disabled={suspendSession.isPending || branchSwitchPending}
              onClick={() => {
                if (
                  sandboxStatus === "running"
                  && !window.confirm(RUNNING_SANDBOX_SUSPEND_CONFIRMATION_MESSAGE)
                ) {
                  return;
                }
                suspendSession.mutate();
              }}
            />
          )}
        </MobileToolsSection>
      )}

      {showSessionActionControls && (
        <MobileToolsSection title="Session Actions">
          <MobileToolsButton
            icon={ArrowLeft}
            label={pendingComposerSecondaryActionKey === "undo" ? "Undoing..." : "Undo"}
            description={undoActionState.disabledReason ?? "Rewind one step in the thread"}
            disabled={!undoActionState.enabled || pendingComposerSecondaryActionKey !== null}
            loading={pendingComposerSecondaryActionKey === "undo"}
            onClick={handleComposerUndo}
          />
          <MobileToolsButton
            icon={ArrowsClockwise}
            label={pendingComposerSecondaryActionKey === "compact" ? "Compacting..." : "Compact Context"}
            description={compactActionState.disabledReason ?? "Summarize and compact the current context"}
            disabled={!compactActionState.enabled || pendingComposerSecondaryActionKey !== null}
            loading={pendingComposerSecondaryActionKey === "compact"}
            onClick={handleComposerCompact}
          />
        </MobileToolsSection>
      )}

      <MobileToolsSection title="Thread">
        {mergedPrNeedsContinuation && (
          <MobileToolsButton
            icon={Play}
            label={startNewWorkActionIsKeepWorking ? "Keep Working in this Thread" : startNewWorkActionLabel}
            description={startNewWorkDisabledReason ?? startNewWorkActionDescription}
            disabled={startNewWorkActionPending || Boolean(startNewWorkDisabledReason)}
            loading={startNewWorkActionPending}
            onClick={handleStartNewWork}
          />
        )}
        {showMobileContinueInNewThreadAction && (
          <MobileToolsButton
            icon={LinkSimple}
            label={continueSession.isPending ? "Starting Thread..." : "Start New Thread"}
            description={continueInNewSessionDisabledReason ?? (mergedPrNeedsContinuation
              ? "Opens a new thread with context from this one"
              : "Opens a new thread on this branch after changes are pushed")}
            disabled={continueSession.isPending || Boolean(continueInNewSessionDisabledReason)}
            loading={continueSession.isPending}
            onClick={handleContinueInNewSession}
          />
        )}
        {showMobileArchiveThreadAction && (
          <MobileToolsButton
            icon={Archive}
            label={archiveTogglePending
              ? sessionArchived ? "Unarchiving Thread..." : "Archiving Thread..."
              : sessionArchived ? "Unarchive Thread" : "Archive Thread"}
            description="Hide this thread from the active list"
            disabled={archiveToggleDisabled}
            loading={archiveTogglePending}
            onClick={() => {
              void handleArchiveToggle();
            }}
          />
        )}
        {canManageSession && recoveryPatchAvailable && (
          <>
            <MobileToolsButton
              icon={DownloadSimple}
              label={recoveryPatchDownloadPending ? "Downloading..." : "Download Recovery Bundle"}
              description="Download the latest recovery bundle"
              disabled={recoveryPatchDownloadPending}
              onClick={() => {
                void downloadRecoveryPatch();
              }}
            />
            <MobileToolsButton
              icon={ClockCounterClockwise}
              label={recoveryVersionsLoading ? "Loading..." : "Older Recovery Bundles"}
              description="Download older recovery bundle versions"
              disabled={recoveryVersionsLoading || recoveryPatchDownloadPending}
              onClick={() => {
                toggleRecoveryVersions();
              }}
            />
            {showRecoveryVersions && (
              <div className="ml-4 space-y-1 border-l-2 border-zinc-700/60 pl-3">
                {recoveryVersionsLoading && (
                  <div className="flex items-center py-2 text-xs text-zinc-500">
                    <SpinnerGap className="mr-2 size-3.5 animate-spin" />
                    Loading versions…
                  </div>
                )}
                {!recoveryVersionsLoading && recoveryVersions.length === 0 && (
                  <div className="py-2 text-xs text-zinc-500">No recovery bundles found</div>
                )}
                {!recoveryVersionsLoading && recoveryVersions.map((v) => (
                  <button
                    key={v.capturedAtMs}
                    onClick={() => {
                      setShowRecoveryVersions(false);
                      void downloadRecoveryPatch({ versionMs: v.capturedAtMs });
                    }}
                    disabled={recoveryPatchDownloadPending}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-zinc-300 transition-colors hover:bg-zinc-800 disabled:opacity-40"
                  >
                    <DownloadSimple className="size-3.5 shrink-0" />
                    <span className="truncate">{new Date(v.capturedAtMs).toLocaleString()}</span>
                    {v.isCurrent && (
                      <span className="ml-auto shrink-0 rounded bg-emerald-900/60 px-1.5 py-0.5 text-xs font-medium text-emerald-300">
                        latest
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        <MobileToolsButton
          icon={Trash}
          label="Delete Thread"
          description="Permanently remove this thread"
          disabled={!canManageSession || deleteSession.isPending}
          tone="danger"
          onClick={() => {
            requestDeleteThread("mobile_tools");
          }}
        />
        <MobileToolsButton
          icon={FileText}
          label="Debug Logs"
          description="View WebSocket, sandbox, and connection debug logs"
          onClick={() => {
            useSessionUI.getState().toggleDebugLogs();
          }}
        />
        <MobileToolsButton
          icon={ArrowsClockwise}
          label="Reset Thread View"
          description="Reset local thread state and reload"
          onClick={() => {
            void handleClearLocalThreadState();
          }}
        />
      </MobileToolsSection>
    </MobileToolsPanel>
  );

  // Compute compact PR chip for the mobile header's inline actions slot.
  // Shows contextually: create button when changes exist, status chip when PR exists.
  const mobilePrInlineAction: ReactNode = (() => {
    if (!isMobileLayout) return null;

    // Determine effective PR number and state from real-time status or session data.
    const prNum =
      sessionPrNumber === null
        ? null
        : currentPrStatus?.prNumber ?? sessionPrNumber;
    const prState =
      sessionPrNumber === null
        ? null
        : currentPrStatus?.prState ?? sessionPrState;
    const rawPrUrl =
      sessionPrNumber === null
        ? null
        : currentPrStatus?.prUrl ?? sessionPrUrl;
    const prVisitHref = buildSessionPullRequestVisitHref({
      sessionId,
      repoProvider,
      pullRequestNumber: prNum,
      rawPullRequestUrl: rawPrUrl,
    });

    // Quick PR "done" state — compact success chip with just #number
    if (quickPrStep === "done" && quickPrResult) {
      const quickResultHref = buildSessionPullRequestVisitHref({
        sessionId,
        repoProvider,
        pullRequestNumber: quickPrResult.prNumber,
        rawPullRequestUrl: quickPrResult.prUrl,
      });
      if (quickResultHref) {
        return (
          <a
            href={quickResultHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-7 items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 text-xs font-medium text-emerald-300 transition-colors active:bg-emerald-500/20"
          >
            <Check className="size-3" />
            #{quickPrResult.prNumber}
          </a>
        );
      }
    }

    // Active PR (open or draft) — compact linked chip
    if (prNum && (prState === "open" || prState === "draft") && prVisitHref) {
      const toneClass =
        prState === "draft"
          ? "border-zinc-700/70 text-zinc-400 active:bg-zinc-800"
          : "border-emerald-500/30 text-emerald-300 active:bg-emerald-500/10";
      return (
        <a
          href={prVisitHref}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex h-7 items-center gap-1 rounded-md border px-1.5 text-xs font-medium transition-colors ${toneClass}`}
        >
          <GitPullRequest className="size-3" />
          #{prNum}
        </a>
      );
    }

    // Merged or closed PR — compact status chip
    if (prNum && (prState === "merged" || prState === "closed") && prVisitHref) {
      const toneClass =
        prState === "merged"
          ? "border-purple-500/30 text-purple-300 active:bg-purple-500/10"
          : "border-red-500/30 text-red-300 active:bg-red-500/10";
      return (
        <a
          href={prVisitHref}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex h-7 items-center gap-1 rounded-md border px-1.5 text-xs font-medium transition-colors ${toneClass}`}
        >
          <GitPullRequest className="size-3" />
          #{prNum}
        </a>
      );
    }

    // Quick PR in progress — compact spinner chip
    const isQuickPrActive = quickPrStep !== "idle" && quickPrStep !== "done" && quickPrStep !== "error";
    if (isQuickPrActive) {
      return (
        <button
          type="button"
          disabled
          className="flex h-7 items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 text-xs font-medium text-amber-300"
        >
          <SpinnerGap className="size-3 animate-spin" />
          PR
        </button>
      );
    }

    // Quick PR error with sync needed — compact sync chip
    if (quickPrStep === "error" && quickPrErrorCode === "BRANCH_BEHIND_REMOTE") {
      return (
        <button
          type="button"
          onClick={() => {
            void triggerSyncFromQuickPrError();
          }}
          className="flex h-7 items-center gap-1 rounded-md border border-amber-500/40 bg-amber-600 px-1.5 text-xs font-medium text-white transition-colors active:bg-amber-500"
        >
          <ArrowsClockwise className="size-3" />
          Sync
        </button>
      );
    }

    // Quick PR error (other) — compact retry chip
    if (quickPrStep === "error") {
      return (
        <button
          type="button"
          onClick={() => { void triggerQuickPr(); }}
          className="flex h-7 items-center gap-1 rounded-md border border-red-500/30 bg-red-500/10 px-1.5 text-xs font-medium text-red-300 transition-colors active:bg-red-500/20"
        >
          <GitPullRequest className="size-3" />
          Retry
        </button>
      );
    }

    // No PR yet, but changes exist — show inline "create PR" button.
    // This is the key discoverability improvement: the button is always visible
    // in the header when there are changes, no menu-diving required.
    if (showQuickPrButton && !prNum && sandboxAvailableForPr && canManageSession) {
      const isDisabled = quickPrSyncPending || manualCreatePrActive || !hasQuickPrSourceChanges || sessionArchived;
      if (!isDisabled) {
        return (
          <MobileQuickPrCreateChip
            onTrigger={() => { void triggerQuickPr(); }}
          />
        );
      }
    }

    return null;
  })();

  const mobileHeaderContent = isMobileLayout ? (
    <MobileSessionHeader
      title={sessionTitle}
      sessionCreatedAt={sessionCreatedAt}
      repoName={repo?.displayName ?? repo?.githubFullName ?? null}
      inlineActions={mobilePrInlineAction}
      showTerminalButton
      onOpenSurface={(surface) => setMobileSurface(surface)}
      menuItems={
        <>
          {/* --- Primary Actions: Quick PR & YOLO (execute directly) --- */}
          {showQuickPrButton && (() => {
            const isActive = quickPrStep !== "idle" && quickPrStep !== "done" && quickPrStep !== "error";
            const isDisabled = quickPrSyncPending || manualCreatePrActive || !hasQuickPrSourceChanges || sessionArchived || !canManageSession || !sandboxAvailableForPr || isActive;
            const label = isActive
              ? "Creating PR..."
              : quickPrStep === "error"
                ? "Retry Quick PR"
                : sessionPrNumber
                  ? "Update PR"
                  : "Quick PR";
            return (
              <MobileMenuItem
                icon={GitPullRequest}
                disabled={isDisabled}
                loading={isActive || quickPrSyncPending}
                onSelect={() => {
                  void triggerQuickPr();
                }}
              >
                {label}
              </MobileMenuItem>
            );
          })()}
          {showYoloButton && (() => {
            const canRunYolo = canManageSession && (!sessionPrNumber || sessionPrIsActiveOwner);
            const yoloNeedsSandbox = !yoloFlowBusy && !sandboxAvailableForPr;
            const yoloNeedsChanges = !yoloFlowBusy && !hasQuickPrSourceChanges;
            const isDisabled =
              yoloFlowBusy ||
              (quickPrStep !== "idle" && quickPrStep !== "done" && quickPrStep !== "error") ||
              manualCreatePrActive ||
              archiveSession.isPending ||
              unarchiveSession.isPending ||
              !canRunYolo ||
              yoloNeedsSandbox ||
              yoloNeedsChanges ||
              sessionArchived;
            const label = yoloFlowBusy ? "YOLO running..." : "YOLO";
            return (
              <MobileMenuItem
                icon={Rocket}
                disabled={isDisabled}
                loading={yoloFlowBusy}
                description="Quick PR → Merge → Archive"
                onSelect={() => {
                  void triggerYoloFlow();
                }}
              >
                {label}
              </MobileMenuItem>
            );
          })()}
          <MobileMenuDivider />
          {/* --- Thread management --- */}
          {showMobileArchiveThreadAction && (
            <MobileMenuItem
              icon={Archive}
              disabled={archiveToggleDisabled}
              loading={archiveTogglePending}
              onSelect={() => {
                void handleArchiveToggle();
              }}
            >
              {archiveTogglePending
                ? sessionArchived ? "Unarchiving..." : "Archiving..."
                : sessionArchived ? "Unarchive Thread" : "Archive Thread"}
            </MobileMenuItem>
          )}
          <MobileMenuItem
            icon={PencilSimple}
            onSelect={() => startEditingTitle()}
          >
            Rename thread
          </MobileMenuItem>
          <MobileMenuDivider />
          {/* --- Danger zone --- */}
          <MobileMenuItem
            icon={Trash}
            destructive
            disabled={!canManageSession || deleteSession.isPending}
            onSelect={() => {
              requestDeleteThread("mobile_overflow_menu");
            }}
          >
            Delete Thread
          </MobileMenuItem>
        </>
      }
    />
  ) : null;

  const headerContent = (
    <>
      {mobileHeaderContent || (
      <header
        className={cn(
          "flex items-center border-b border-zinc-700/50",
          runtimeConversationTabsEnabled && visibleRuntimeConversations.length > 1
              ? "justify-between px-3 py-2"
              : "justify-between px-5 py-3",
        )}
      >
        <div className={cn("flex min-w-0 flex-1 items-center gap-2") }>
          {/* Browser-style conversation tabs */}
          {runtimeConversationTabsEnabled && visibleRuntimeConversations.length > 1 && !isMobileLayout ? (
            <>
              {/* Tab strip */}
              <div className="flex min-w-0 max-w-full shrink items-center gap-0.5 overflow-x-auto">
                {visibleRuntimeConversations.map((conversation) => {
                  const isActive = conversation.conversationId === activeConversationId;
                  const isDefault = conversation.isDefault;
                  const isOptimisticCreatingTab = optimisticCreatingRuntimeConversation?.conversationId === conversation.conversationId;
                  const optimisticCreationKind = isOptimisticCreatingTab
                    ? activeOptimisticRuntimeConversation?.creationKind ?? null
                    : null;
                  const isOptimisticBlankCreatingTab = optimisticCreationKind === "blank";
                  const isOptimisticForkCreatingTab = optimisticCreationKind === "fork";
                  const conversationStatusSnapshot = selectRuntimeConversationSnapshotForControlState({
                    querySnapshot: conversation,
                    activitySnapshot: activityStoreConversationsById[conversation.conversationId] ?? null,
                  }) ?? conversation;
                  const conversationStatus = deriveRuntimeConversationStatusState({
                    conversationActivity: {
                      sessionId,
                      conversationId: conversationStatusSnapshot.conversationId,
                      agentType: conversationStatusSnapshot.agentType,
                      providerSessionId: conversationStatusSnapshot.providerSessionId,
                      title: conversationStatusSnapshot.title,
                      isDefault: conversationStatusSnapshot.isDefault,
                      statusKind: conversationStatusSnapshot.statusKind,
                      activityState: conversationStatusSnapshot.activityState ?? null,
                      activityEventSequence: conversationStatusSnapshot.activityEventSequence ?? null,
                      lastActivityEventId: conversationStatusSnapshot.lastActivityEventId ?? null,
                      lastFinishedEventId: conversationStatusSnapshot.lastFinishedEventId ?? null,
                      lastFinishedAt: conversationStatusSnapshot.lastFinishedAt ?? null,
                      lastFinishReason: conversationStatusSnapshot.lastFinishReason ?? (conversationStatusSnapshot.statusKind === "waiting_input" ? "waiting_input" : null),
                      activityObservedAt: conversationStatusSnapshot.activityObservedAt ?? null,
                      createdAt: conversationStatusSnapshot.createdAt,
                      updatedAt: conversationStatusSnapshot.updatedAt,
                      archivedAt: conversationStatusSnapshot.archivedAt,
                    },
                    lastViewedEventId: runtimeConversationViewedEventIds[`${sessionId}:${conversation.conversationId}`] ?? 0,
                  });
                  const showTabIndicator = conversationStatus.renderState !== "hidden";
                  const tabLabel = isDefault
                    ? (sessionTitle || "Main")
                    : (conversation.title?.trim() || "New chat");
                  const isEditingThisTab = isDefault
                    ? isEditingTitle
                    : editingRuntimeConversationId === conversation.conversationId;

                  if (isOptimisticBlankCreatingTab) {
                    return (
                      <div
                        key={conversation.conversationId}
                        aria-live="polite"
                        aria-label={`Creating ${tabLabel} tab`}
                        className="relative flex min-w-0 max-w-[220px] shrink-0 items-center gap-2 overflow-hidden rounded-md border border-sky-400/15 bg-zinc-800/80 px-3 py-1.5 text-sm font-medium text-zinc-300 shadow-sm shadow-sky-950/20"
                        title={`Creating ${tabLabel} tab…`}
                      >
                        <SpinnerGap className="size-4 shrink-0 animate-spin text-sky-300" />
                        <span className="truncate">{tabLabel}</span>
                        <span className="pointer-events-none absolute inset-0 animate-pulse bg-sky-300/5" />
                      </div>
                    );
                  }

                  if (isEditingThisTab) {
                    return (
                      <div
                        key={conversation.conversationId}
                        className={cn(
                          "group relative flex min-w-0 max-w-[220px] shrink-0 items-center gap-2 rounded-md px-2 py-1 text-sm font-medium transition-all duration-150",
                          isActive
                            ? "bg-zinc-700/70 text-zinc-50 shadow-sm"
                            : "bg-zinc-800/70 text-zinc-200",
                        )}
                        title={tabLabel}
                      >
                        {showTabIndicator && (
                          <ThreadStatusBadge status={conversationStatus} className="size-4" />
                        )}
                        <input
                          ref={titleInputRef}
                          value={editTitle}
                          onChange={(event) => setEditTitle(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") commitRuntimeConversationTitle(conversation);
                            if (event.key === "Escape") {
                              suppressNextTitleBlurCommitRef.current = true;
                              if (isDefault) setIsEditingTitle(false);
                              else setEditingRuntimeConversationId(null);
                            }
                          }}
                          onBlur={() => commitRuntimeConversationTitle(conversation)}
                          onClick={(event) => event.stopPropagation()}
                          onMouseDown={(event) => event.stopPropagation()}
                          placeholder={tabLabel}
                          className="min-w-0 flex-1 rounded border border-zinc-600 bg-zinc-900 px-1.5 py-0.5 text-sm font-medium text-zinc-100 outline-none focus:border-zinc-500"
                          autoFocus
                        />
                      </div>
                    );
                  }

                  return (
                    <button
                      key={conversation.conversationId}
                      type="button"
                      className={cn(
                        "group relative flex min-w-0 max-w-[220px] shrink-0 items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-150",
                        isActive
                          ? "bg-zinc-700/70 text-zinc-50 shadow-sm"
                          : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/70",
                      )}
                      onClick={() => {
                        useRuntimeConversationActivity.getState().markViewed({
                          sessionId,
                          conversationId: conversation.conversationId,
                        });
                        void navigate({
                          to: "/$repoId/$sessionId",
                          params: { repoId, sessionId },
                          search: { conversationId: isDefault ? undefined : conversation.conversationId },
                          replace: true,
                        });
                      }}
                      onDoubleClick={isOptimisticCreatingTab ? undefined : () => startEditingRuntimeConversationTitle(conversation)}
                      title={tabLabel}
                    >
                      {isOptimisticForkCreatingTab ? (
                        <SpinnerGap className="size-4 shrink-0 animate-spin text-sky-300" />
                      ) : showTabIndicator && (
                        <ThreadStatusBadge status={conversationStatus} className="size-4" />
                      )}
                      <AnimatedTitle className="truncate" text={tabLabel} />
                      {!isDefault && !isOptimisticCreatingTab && (
                        <span
                          className={cn(
                            "ml-auto flex size-4 shrink-0 items-center justify-center rounded transition-opacity hover:bg-zinc-600",
                            isActive ? "opacity-60 hover:opacity-100" : "opacity-0 group-hover:opacity-60 group-hover:hover:opacity-100",
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            requestRuntimeConversationClose(conversation);
                          }}
                          role="button"
                          aria-label="Close tab"
                        >
                          <X className="size-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
                {/* New tab button */}
                <button
                  type="button"
                  className={cn(
                    "ml-1 flex size-7 shrink-0 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-700/60 hover:text-zinc-300 disabled:opacity-50",
                    createRuntimeConversation.isPending ? "disabled:cursor-default" : "disabled:cursor-not-allowed",
                  )}
                  disabled={sessionArchived || createRuntimeConversation.isPending}
                  title={sessionArchived ? "Unarchive this thread before creating a new chat tab" : "New chat tab (shares sandbox)"}
                  onClick={createBlankRuntimeConversationOptimistically}
                >
                  {createRuntimeConversation.isPending ? (
                    <SpinnerGap className="size-4 animate-spin text-sky-300" />
                  ) : (
                    <Plus className="size-4" />
                  )}
                </button>
              </div>

              {/* Compact status badges after tabs */}
              {sandboxStartupUiActive && (
                <SandboxStartupHeaderIndicator
                  progress={sandboxProgress}
                  bootSource={bootSource}
                  startupKind={sandboxStartupKind}
                />
              )}
              {sessionArchived && (
                <span className="inline-flex items-center gap-1 rounded-md border border-zinc-700/60 bg-zinc-900/70 px-2 py-0.5 text-xs font-medium text-zinc-400">
                  <Archive className="size-3" />
                  Archived
                </span>
              )}
              {topBarAvailabilityStatus && (
                <TopBarAvailabilityBadge status={topBarAvailabilityStatus} />
              )}
            </>
          ) : (
            <>
              {/* Single-tab / non-OpenCode: classic title with optional + button */}
              {isEditingTitle ? (
                <div className="flex min-w-0 items-center gap-1.5">
                  <input
                    ref={titleInputRef}
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitTitle();
                      if (e.key === "Escape") {
                        suppressNextTitleBlurCommitRef.current = true;
                        setIsEditingTitle(false);
                      }
                    }}
                    onBlur={commitTitle}
                    className={cn(
                      "min-w-0 flex-1 rounded-md border border-zinc-600 bg-zinc-900 px-2 py-0.5 text-sm font-medium text-zinc-200 outline-none focus:border-zinc-500",
                      isMobileLayout ? "max-w-[10rem]" : "w-48",
                    )}
                    autoFocus
                  />
                  <button
                    aria-label="Confirm rename"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={commitTitle}
                    className="flex size-6 shrink-0 items-center justify-center rounded text-zinc-400 hover:text-zinc-200"
                  >
                    <Check className="size-3.5" />
                  </button>
                  <button
                    aria-label="Cancel rename"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      suppressNextTitleBlurCommitRef.current = true;
                      setIsEditingTitle(false);
                    }}
                    className="flex size-6 shrink-0 items-center justify-center rounded text-zinc-400 hover:text-zinc-200"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex min-w-0 items-center gap-2">
                  <h1
                    onClick={startEditingTitle}
                    className={cn(
                      "min-w-0 cursor-pointer truncate rounded px-1 text-sm font-medium text-zinc-200 transition-colors hover:bg-zinc-800/60",
                      isMobileLayout
                        ? showYoloButton && showQuickPrButton
                          ? "max-w-[9rem] text-xs"
                          : "max-w-[12rem]"
                        : "-mx-1",
                    )}
                  >
                    <AnimatedTitle text={sessionTitle} className="truncate" />
                  </h1>
                  {/* Show + button for OpenCode single-tab sessions */}
                  {runtimeConversationTabsEnabled && !isMobileLayout && (
                    <button
                      type="button"
                      className={cn(
                        "flex size-6 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-700/60 hover:text-zinc-200 disabled:opacity-50",
                        createRuntimeConversation.isPending ? "disabled:cursor-default" : "disabled:cursor-not-allowed",
                      )}
                      disabled={sessionArchived || createRuntimeConversation.isPending}
                      title={sessionArchived ? "Unarchive this thread before creating a new chat tab" : "New chat tab (shares sandbox)"}
                      onClick={createBlankRuntimeConversationOptimistically}
                    >
                      {createRuntimeConversation.isPending ? (
                        <SpinnerGap className="size-3.5 animate-spin text-sky-300" />
                      ) : (
                        <Plus className="size-3.5" />
                      )}
                    </button>
                  )}
                </div>
              )}

              {sandboxStartupUiActive && (
                <SandboxStartupHeaderIndicator
                  progress={sandboxProgress}
                  bootSource={bootSource}
                  startupKind={sandboxStartupKind}
                />
              )}

              {parentRelationType === "continue" && sessionParentSessionId && !isEditingTitle && (
                <Link
                  to="/$repoId/$sessionId"
                  params={{ repoId, sessionId: sessionParentSessionId }}
                  className="inline-flex items-center gap-1 rounded-md bg-white/5 px-2 py-0.5 text-xs text-zinc-400 ring-1 ring-white/8 transition-colors hover:bg-white/10 hover:text-zinc-200"
                  title={continuedFromTitle ? `Continued from ${continuedFromTitle}` : "Continued from parent thread"}
                >
                  <LinkSimple className="size-3" />
                  <span className="max-w-[12rem] truncate">
                    Continued from {continuedFromTitle?.trim() || "parent thread"}
                  </span>
                </Link>
              )}

              {isMobileLayout && topBarAvailabilityStatus && (
                <TopBarAvailabilityBadge status={topBarAvailabilityStatus} compact />
              )}

              {!isMobileLayout && sessionArchived && (
                <span className="inline-flex items-center gap-1 rounded-md border border-zinc-700/60 bg-zinc-900/70 px-2 py-0.5 text-xs font-medium text-zinc-400">
                  <Archive className="size-3" />
                  Archived
                </span>
              )}

              {!isMobileLayout && topBarAvailabilityStatus && (
                <div className="flex shrink-0 items-center gap-2">
                  <TopBarAvailabilityBadge status={topBarAvailabilityStatus} />
                </div>
              )}
            </>
          )}
        </div>

        <div className={cn("flex shrink-0 items-center", isMobileLayout ? "gap-1.5" : "gap-2") }>
          {canShowRepositoryDeployUi({ repository: repo }) && (
            <DeploymentTopBarControl
              repoId={repoId}
              sessionId={sessionId}
              deployEnabled
              compact={isMobileLayout}
            />
          )}
          {runtimeConversationTabsAvailable && (
            <DropdownMenu>
              <Tooltip content={backgroundRunningRuntimeConversationCount > 0 ? `${backgroundRunningRuntimeConversationCount} background chat ${backgroundRunningRuntimeConversationCount === 1 ? "session" : "sessions"} running` : "Chat sessions"}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "relative flex shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200",
                      isMobileLayout ? "size-9" : "size-8",
                    )}
                    aria-label="Open chat sessions"
                  >
                    <List className="size-4" />
                    {backgroundRunningRuntimeConversationCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-xs font-semibold leading-4 text-zinc-950 ring-2 ring-zinc-950">
                        {backgroundRunningRuntimeConversationCount}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
              </Tooltip>
              <DropdownMenuContent align="end" className="w-80 border-zinc-800 bg-zinc-950 text-zinc-200">
                <DropdownMenuLabel className="flex items-center justify-between px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  <span>Chat sessions</span>
                  {allRuntimeConversationsQuery.isFetching && <SpinnerGap className="size-3 animate-spin" />}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                {visibleAllRuntimeConversations.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-zinc-500">No chat sessions yet.</div>
                ) : (
                  visibleAllRuntimeConversations.map((conversation) => {
                    const isActive = conversation.conversationId === activeConversationId && conversation.archivedAt === null;
                    const statusSnapshot = selectRuntimeConversationSnapshotForControlState({
                      querySnapshot: conversation,
                      activitySnapshot: activityStoreConversationsById[conversation.conversationId] ?? null,
                    }) ?? conversation;
                    const conversationStatus = deriveRuntimeConversationStatusState({
                      conversationActivity: {
                        sessionId,
                        conversationId: statusSnapshot.conversationId,
                        agentType: statusSnapshot.agentType,
                        providerSessionId: statusSnapshot.providerSessionId,
                        title: statusSnapshot.title,
                        isDefault: statusSnapshot.isDefault,
                        statusKind: statusSnapshot.statusKind,
                        activityState: statusSnapshot.activityState ?? null,
                        activityEventSequence: statusSnapshot.activityEventSequence ?? null,
                        lastActivityEventId: statusSnapshot.lastActivityEventId ?? null,
                        lastFinishedEventId: statusSnapshot.lastFinishedEventId ?? null,
                        lastFinishedAt: statusSnapshot.lastFinishedAt ?? null,
                        lastFinishReason: statusSnapshot.lastFinishReason ?? (statusSnapshot.statusKind === "waiting_input" ? "waiting_input" : null),
                        activityObservedAt: statusSnapshot.activityObservedAt ?? null,
                        createdAt: statusSnapshot.createdAt,
                        updatedAt: statusSnapshot.updatedAt,
                        archivedAt: statusSnapshot.archivedAt,
                      },
                      lastViewedEventId: runtimeConversationViewedEventIds[`${sessionId}:${conversation.conversationId}`] ?? 0,
                    });
                    const label = conversation.isDefault
                      ? (sessionTitle || "Main")
                      : (conversation.title?.trim() || "New chat");
                    const isArchived = conversation.archivedAt !== null;
                    const isBackgroundRunning = isArchived && runtimeConversationIsRunning(statusSnapshot);

                    return (
                      <DropdownMenuItem
                        key={conversation.conversationId}
                        className={cn(
                          "flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm focus:bg-zinc-800 focus:text-zinc-100",
                          isActive && "bg-zinc-800 text-zinc-50",
                        )}
                        disabled={restoreRuntimeConversation.isPending && isArchived}
                        onSelect={() => openRuntimeConversationFromList(conversation)}
                      >
                        {conversationStatus.renderState !== "hidden" ? (
                          <ThreadStatusBadge status={conversationStatus} className="size-4" />
                        ) : (
                          <span className="size-4 rounded-full border border-zinc-700" />
                        )}
                        <span className="min-w-0 flex-1 truncate">{label}</span>
                        {isActive && (
                          <span className="rounded bg-zinc-700 px-1.5 py-0.5 text-xs font-medium text-zinc-300">
                            open
                          </span>
                        )}
                        {isBackgroundRunning && (
                          <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-xs font-medium text-emerald-300">
                            running
                          </span>
                        )}
                        {isArchived && !isBackgroundRunning && (
                          <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs font-medium text-zinc-500">
                            closed
                          </span>
                        )}
                      </DropdownMenuItem>
                    );
                  })
                )}
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-zinc-300 focus:bg-zinc-800 focus:text-zinc-100"
                  disabled={sessionArchived || createRuntimeConversation.isPending}
                  onSelect={() => createRuntimeConversation.mutate({ title: null })}
                >
                  {createRuntimeConversation.isPending ? (
                    <SpinnerGap className="size-4 animate-spin text-sky-300" />
                  ) : (
                    <Plus className="size-4" />
                  )}
                  <span>{createRuntimeConversation.isPending ? OPTIMISTIC_RUNTIME_CONVERSATION_TITLE : "New chat session"}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* Pull Request button — with Quick PR support */}
          {(() => {
            const prNum =
              sessionPrNumber === null
                ? null
                : currentPrStatus?.prNumber ?? sessionPrNumber
            const prState =
              sessionPrNumber === null
                ? null
                : currentPrStatus?.prState ?? sessionPrState
            const rawPrUrl =
              sessionPrNumber === null
                ? null
                : currentPrStatus?.prUrl ?? sessionPrUrl
            const prUrl = buildSessionPullRequestVisitHref({
              sessionId,
              repoProvider,
              pullRequestNumber: prNum,
              rawPullRequestUrl: rawPrUrl,
            })
            const quickPrResultHref = quickPrResult
              ? buildSessionPullRequestVisitHref({
                sessionId,
                repoProvider,
                pullRequestNumber: quickPrResult.prNumber,
                rawPullRequestUrl: quickPrResult.prUrl,
              })
              : null

            // Quick PR can create a PR from branch commits even when working tree is clean.
            const hasUncommittedChanges = gitStatus
              ? gitStatus.staged.length > 0 ||
                gitStatus.unstaged.length > 0 ||
                gitStatus.untracked.length > 0
              : prFlowRepoReadiness?.hasUncommittedChanges === true
            const commitsAheadOfBaseRaw = gitStatus?.commitsAheadOfBase ?? prFlowRepoReadiness?.commitsAheadOfBase
            const commitsAheadOfBase =
              typeof commitsAheadOfBaseRaw === "number" &&
                Number.isFinite(commitsAheadOfBaseRaw)
                ? Math.max(0, commitsAheadOfBaseRaw)
                : 0
            const hasBranchCommits = commitsAheadOfBase > 0
            const hasQuickPrSourceChanges = hasUncommittedChanges || hasBranchCommits
            const hasUnpushedCommits = gitStatus
              ? gitStatus.ahead > 0
              : prFlowRepoReadiness?.hasUnpushedCommits === true
            const canRunYoloFromSession = canManageSession && (!prNum || sessionPrIsActiveOwner)
            const prActionsReadOnly = Boolean(prNum) && !sessionPrIsActiveOwner
            const canQuickPrCreate = !prNum && sandboxAvailableForPr && canManageSession
            const canShowQuickPrCreateButton = showQuickPrButton && canQuickPrCreate
            const quickPrCreateDisabled = quickPrSyncPending || manualCreatePrActive || !hasQuickPrSourceChanges || sessionArchived || !canManageSession
            const prCreateActionsEnabled = canQuickPrCreate && !quickPrCreateDisabled
            const prCreateDisabledReason =
              quickPrSyncPending
                ? "Wait for branch sync to finish before retrying Quick PR"
                : manualCreatePrActive
                ? "A pull request is already being created"
                : !canManageSession
                ? "You do not have permission to manage this session"
                : sessionArchived
                ? "Archived threads are read-only. Unarchive this thread before creating a PR."
                : !sandboxAvailableForPr
                  ? "Create PR needs a running or resumable sandbox"
                  : !hasQuickPrSourceChanges
                    ? "Create PR needs file changes or branch commits"
                    : null
            const isQuickPrActive = quickPrStep !== "idle" && quickPrStep !== "done" && quickPrStep !== "error"

            // Quick PR progress labels
            const quickPrLabels: Record<string, string> = {
              queued: "Queued...",
              resuming: "Resuming...",
              checking: "Checking...",
              staging: "Staging...",
              generating_commit: "Generating...",
              committing: "Committing...",
              pushing: "Pushing...",
              generating_pr: "Generating PR...",
              creating_pr: "Creating PR...",
            }
            const quickPrNeedsSync = quickPrStep === "error" && quickPrErrorCode === "BRANCH_BEHIND_REMOTE"
            const quickPrBehindLabel = quickPrBehind && quickPrBehind > 0 ? `${quickPrBehind} behind` : null
            const showYoloButtonInTopBar = showYoloButton && (!prNum || yoloFlowBusy)
            const showDesktopYolo = showYoloButtonInTopBar && !isMobileLayout
            const showMobileYolo = showYoloButtonInTopBar && isMobileLayout
            const yoloNeedsSandbox = yoloFlowStep === "idle" && !yoloStartOptimisticPending && !sandboxAvailableForPr
            const yoloNeedsChangesOrCommits = yoloFlowStep === "idle" && !yoloStartOptimisticPending && !hasQuickPrSourceChanges
            const yoloDisabled =
              yoloFlowBusy ||
              isQuickPrActive ||
              mergeRequestInFlight ||
              archiveSession.isPending ||
              unarchiveSession.isPending ||
              manualCreatePrActive ||
              !canRunYoloFromSession ||
              yoloNeedsSandbox ||
              yoloNeedsChangesOrCommits ||
              sessionArchived
            const yoloLabel =
              yoloStartOptimisticPending
                ? "Starting..."
                : yoloFlowStep === "quick_pr"
                ? (quickPrLabels[quickPrStep] || "Quick PR...")
                : yoloFlowStep === "merge_pr"
                  ? "Merging..."
                  : yoloFlowStep === "archiving"
                    ? "Archiving..."
                    : yoloFlowStep === "done"
                      ? "Done"
                      : yoloFlowStep === "error"
                        ? "Retry YOLO"
                        : "YOLO"
            const yoloTooltip = yoloFlowError
              ?? (yoloFlowStep === "done"
                ? "YOLO completed"
                    : !canManageSession
                      ? "You do not have permission to manage this session"
                    : prNum && !sessionPrIsActiveOwner
                      ? "Only the active PR owner thread can run YOLO for this pull request"
                    : sessionArchived
                    ? "Archived threads are read-only. Unarchive this thread before running YOLO."
                    : manualCreatePrActive
                      ? "Wait for the current pull request to finish creating"
                    : yoloNeedsSandbox
                      ? "YOLO needs a running or resumable sandbox"
                      : yoloNeedsChangesOrCommits
                        ? "YOLO needs file changes or branch commits"
                      : YOLO_TOOLTIP_TEXT)
            const yoloTooltipWithStarting = yoloStartOptimisticPending ? "Starting YOLO..." : yoloTooltip

            const yoloButton = (showDesktopYolo || showMobileYolo) ? (
              <Tooltip content={yoloTooltipWithStarting}>
                <button
                  type="button"
                  onClick={() => {
                    void triggerYoloFlow();
                  }}
                  disabled={yoloDisabled}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md border border-white/30 bg-[linear-gradient(110deg,#fb7185_0%,#f59e0b_22%,#22c55e_44%,#38bdf8_66%,#6366f1_82%,#ec4899_100%)] font-semibold text-white shadow-[0_0_0_1px_rgba(255,255,255,0.08)] transition-all",
                    isMobileLayout ? "h-8 px-2 text-xs" : "h-7 px-2.5 text-[12px]",
                    yoloDisabled
                      ? "cursor-not-allowed opacity-55"
                      : "hover:scale-[1.02] hover:brightness-110",
                  )}
                >
                  {yoloFlowBusy && (
                    <SpinnerGap className="size-3.5 animate-spin" />
                  )}
                  {yoloLabel}
                </button>
              </Tooltip>
            ) : null

            const withMobileYolo = (content: ReactNode) =>
              showMobileYolo && yoloButton
                ? (
                  <div className="flex items-center gap-1">
                    {yoloButton}
                    {content}
                  </div>
                )
                : content

            const attachedPrAction =
              prNum &&
              sessionPrIsActiveOwner &&
              (prState === "open" || prState === "draft") &&
              attachedPullRequestActionState
                ? attachedPullRequestActionState
                : null

            const renderAttachedPrHeader = (mobile: boolean) => {
              if (!attachedPrAction || !prNum || (prState !== "open" && prState !== "draft")) {
                return null
              }

              const prLabel = prState === "draft" ? `Draft #${prNum}` : `PR #${prNum}`
              const controlHeightClass = mobile ? "h-8 px-2 text-xs" : "h-7 px-2.5 text-[12px]"
              const readOnlyHeightClass = mobile ? "h-8 px-2 text-xs" : "h-7 px-2.5 text-xs"
              const primaryButtonClass = `flex items-center gap-1.5 rounded-md border ${controlHeightClass} font-medium transition-colors`
              const statusChipClass = `inline-flex items-center rounded-md border ${readOnlyHeightClass} font-medium`
              const linkToneClass =
                prState === "draft"
                  ? mobile
                    ? "border-zinc-700/70 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
                    : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 border-zinc-600/60 hover:border-zinc-500"
                  : attachedPrAction.prTone === "amber"
                    ? mobile
                      ? "border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                      : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 border-amber-500/30 hover:border-amber-500/50"
                    : attachedPrAction.prTone === "emerald"
                      ? mobile
                        ? "border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                        : "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50"
                      : mobile
                        ? "border-zinc-700/70 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
                        : "text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 border-zinc-700/70"
              const prDotClass =
                prState === "draft"
                  ? "bg-zinc-500"
                  : attachedPrAction.prTone === "amber"
                    ? "bg-amber-400"
                    : attachedPrAction.prTone === "emerald"
                      ? "bg-emerald-400"
                      : "bg-zinc-500"

              const linkNode = prUrl ? (
                <a
                  href={prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    `flex items-center gap-1.5 rounded-md border ${controlHeightClass} font-medium transition-colors`,
                    linkToneClass,
                  )}
                >
                  {!mobile && <span className={cn("size-1.5 shrink-0 rounded-full", prDotClass)} />}
                  <GitPullRequest className="size-3.5" />
                  {prLabel}
                  {!mobile && <ArrowSquareOut className="size-3 opacity-60" />}
                </a>
              ) : (
                <span className={cn(`flex items-center gap-1.5 rounded-md border ${controlHeightClass} font-medium`, mobile ? "border-zinc-700/70 text-zinc-500" : "border-zinc-600/60 text-zinc-500")}>
                  {!mobile && <span className={cn("size-1.5 shrink-0 rounded-full", prDotClass)} />}
                  <GitPullRequest className="size-3.5" />
                  {prLabel}
                </span>
              )

              const action = attachedPrAction.action

              let actionNode: ReactNode
              if (action.kind === "read_only") {
                actionNode = (
                  <span className={cn(statusChipClass, "border-zinc-700/70 bg-zinc-900/70 text-zinc-400")}>
                    {action.label}
                  </span>
                )
              } else if (quickPrSyncPending) {
                actionNode = (
                  <button
                    type="button"
                    disabled
                    className={cn(primaryButtonClass, "border-amber-500/30 bg-amber-500/10 text-amber-300")}
                  >
                    <SpinnerGap className="size-3.5 animate-spin" />
                    Syncing...
                  </button>
                )
              } else if (action.kind === "update_pr") {
                if (action.phase === "sync_required") {
                  actionNode = (
                    <div className={cn("flex items-center", mobile ? "gap-1" : "gap-1.5")}>
                      <span className={cn(mobile ? "text-xs" : "text-xs", "text-amber-300")}>Sync required</span>
                      <button
                        type="button"
                        onClick={() => {
                          void triggerSyncFromQuickPrError()
                        }}
                        disabled={quickPrSyncPending}
                        className={cn(primaryButtonClass, "border-amber-500/40 bg-amber-600 text-white hover:bg-amber-500")}
                      >
                        {quickPrSyncPending ? (
                          <SpinnerGap className="size-3.5 animate-spin" />
                        ) : (
                          <ArrowsClockwise className="size-3.5" />
                        )}
                        {quickPrSyncPending
                          ? "Syncing..."
                          : action.behindLabel ? `Sync ${action.behindLabel}` : "Sync now"}
                      </button>
                    </div>
                  )
                } else if (action.phase === "failed") {
                  actionNode = (
                    <Tooltip content={action.error || "Update PR failed. Click to retry."}>
                      <button
                        type="button"
                        onClick={() => {
                          void triggerQuickPr()
                        }}
                        className={cn(primaryButtonClass, "border-red-500/30 bg-red-500/10 text-red-400 hover:border-red-500/50 hover:bg-red-500/20")}
                      >
                        <GitPullRequest className="size-3.5" />
                        Retry Update
                      </button>
                    </Tooltip>
                  )
                } else if (action.phase === "running") {
                  actionNode = (
                    <button
                      type="button"
                      disabled
                      className={cn(primaryButtonClass, "border-amber-500/30 bg-amber-500/10 text-amber-300")}
                    >
                      <SpinnerGap className="size-3.5 animate-spin" />
                      {quickPrLabels[quickPrStep] || "Updating..."}
                    </button>
                  )
                } else {
                  actionNode = (
                    <button
                      type="button"
                      onClick={() => {
                        void triggerQuickPr()
                      }}
                      className={cn(primaryButtonClass, "border-emerald-500/40 bg-emerald-600 text-white hover:bg-emerald-500")}
                    >
                      <GitPullRequest className="size-3.5" />
                      Update PR
                    </button>
                  )
                }
              } else if (action.kind === "resolve_conflicts") {
                const resolveDisabled =
                  !sandboxAvailableForPr ||
                  action.phase === "starting" ||
                  action.phase === "queued" ||
                  action.phase === "resolving" ||
                  action.phase === "indeterminate"
                const resolveTooltip =
                  action.phase === "failed"
                    ? action.error || "Failed to start conflict resolution"
                    : action.phase === "indeterminate"
                    ? action.error || "Checking whether conflict resolution started on the server."
                    : transportUnavailableForInteractiveActions
                      ? transportInteractiveActionUnavailableReason
                      : "Send conflict resolution instructions to the AI agent"
                const resolveLabel =
                  action.phase === "starting"
                    ? (mobile ? "Starting..." : "Starting…")
                    : action.phase === "queued"
                      ? "Queued…"
                      : action.phase === "resolving"
                        ? "Resolving…"
                        : action.phase === "indeterminate"
                          ? "Checking…"
                          : action.phase === "failed"
                            ? (mobile ? "Retry" : "Retry Resolve")
                            : mobile
                              ? "Resolve"
                              : "Resolve Conflicts"
                actionNode = (
                  <Tooltip content={resolveTooltip}>
                    <button
                      type="button"
                      onClick={() => {
                        if (action.phase === "starting" || action.phase === "queued" || action.phase === "resolving" || action.phase === "indeterminate") {
                          return
                        }
                        void triggerResolveMergeConflict()
                      }}
                      disabled={resolveDisabled}
                      className={cn(
                        primaryButtonClass,
                        resolveDisabled
                          ? "border-amber-500/20 bg-amber-500/5 text-amber-500/50 cursor-not-allowed"
                          : action.phase === "failed"
                            ? "border-red-500/30 bg-red-500/10 text-red-400 hover:border-red-500/50 hover:bg-red-500/20"
                            : "border-amber-500/40 bg-amber-600 text-white hover:bg-amber-500",
                      )}
                    >
                      {resolveDisabled ? (
                        <SpinnerGap className="size-3.5 animate-spin" />
                      ) : (
                        <GitBranch className="size-3.5" />
                      )}
                      {resolveLabel}
                    </button>
                  </Tooltip>
                )
              } else if (action.kind === "status") {
                actionNode = (
                  <Tooltip content={action.description}>
                    <span className={cn(
                      statusChipClass,
                      action.tone === "draft"
                        ? "border-zinc-700/70 bg-zinc-900/70 text-zinc-400"
                        : "border-amber-500/30 bg-amber-500/10 text-amber-300",
                    )}>
                      {action.label}
                    </span>
                  </Tooltip>
                )
              } else {
                const mergeTooltip =
                  action.phase === "failed"
                    ? action.error || "Failed to merge PR"
                    : action.phase === "indeterminate"
                      ? action.error || "Merge state is still being confirmed"
                      : action.phase === "checking"
                        ? "Checking PR state..."
                        : action.warningKind === "behind"
                          ? "This branch is behind base, but your git provider may still allow merging. Click to try."
                          : action.warningKind === "unstable"
                            ? "Your git provider reports this PR as mergeable, but some non-required status checks are failing."
                            : action.warningKind === "has_hooks"
                              ? "Your git provider reports this PR as mergeable, but repository hooks may still run during merge."
                              : transportUnavailableForInteractiveActions
                                ? transportInteractiveActionUnavailableReason
                                : "Merge this PR (squash)"
                const mergeDisabled =
                  transportUnavailableForInteractiveActions ||
                  action.phase === "running" ||
                  action.phase === "checking" ||
                  action.phase === "indeterminate"
                const mergeLabel =
                  action.phase === "running"
                    ? (mobile ? "Merging..." : "Merging…")
                    : action.phase === "checking"
                      ? "Checking…"
                      : action.phase === "indeterminate"
                        ? "Checking…"
                        : action.phase === "failed"
                          ? "Retry Merge"
                          : "Merge"
                actionNode = (
                  <Tooltip content={mergeTooltip}>
                    <button
                      type="button"
                      onClick={() => {
                        if (mergeDisabled && action.phase !== "failed") return
                        void triggerMergePr()
                      }}
                      disabled={mergeDisabled && action.phase !== "failed"}
                      className={cn(
                        primaryButtonClass,
                        action.phase === "failed"
                          ? "border-red-500/30 bg-red-500/10 text-red-400 hover:border-red-500/50 hover:bg-red-500/20"
                          : mergeDisabled
                            ? "border-purple-500/30 bg-purple-600/60 text-purple-200 cursor-not-allowed"
                            : "border-purple-500/40 bg-purple-600 text-white hover:bg-purple-500",
                      )}
                    >
                      {mergeDisabled && action.phase !== "failed" ? (
                        <SpinnerGap className="size-3.5 animate-spin" />
                      ) : (
                        <GitPullRequest className="size-3.5" />
                      )}
                      {mergeLabel}
                    </button>
                  </Tooltip>
                )
              }

              return (
                <div className={cn("flex items-center", mobile ? "gap-1" : "gap-1.5")}>
                  {linkNode}
                  {actionNode}
                </div>
              )
            }

            if (isMobileLayout) {
              const attachedMobilePrHeader = renderAttachedPrHeader(true)
              if (attachedMobilePrHeader) {
                return withMobileYolo(attachedMobilePrHeader)
              }

              if (quickPrStep === "done" && quickPrResult && quickPrResultHref) {
                return withMobileYolo(
                  <a
                    href={quickPrResultHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-8 items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 text-xs font-medium text-emerald-300 transition-colors hover:bg-emerald-500/20"
                  >
                    <Check className="size-3.5" />
                    {quickPrResult.updatedExisting ? `Updated #${quickPrResult.prNumber}` : `PR #${quickPrResult.prNumber}`}
                  </a>
                )
              }

              if (quickPrStep === "error") {
                if (quickPrNeedsSync) {
                  return withMobileYolo(
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-amber-300">Sync required</span>
                      <button
                        type="button"
                        onClick={() => {
                          void triggerSyncFromQuickPrError();
                        }}
                        disabled={quickPrSyncPending}
                        className="flex h-8 items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-600 px-2 text-xs font-medium text-white transition-colors hover:bg-amber-500"
                      >
                        {quickPrSyncPending ? (
                          <SpinnerGap className="size-3.5 animate-spin" />
                        ) : (
                          <ArrowsClockwise className="size-3.5" />
                        )}
                        {quickPrSyncPending
                          ? "Syncing..."
                          : quickPrBehindLabel ? `Sync ${quickPrBehindLabel}` : "Sync now"}
                      </button>
                    </div>
                  )
                }

                return withMobileYolo(
                  <button
                    type="button"
                    onClick={() => {
                      void triggerQuickPr();
                    }}
                    className="flex h-8 items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-2 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/20"
                  >
                    <GitPullRequest className="size-3.5" />
                    Retry PR
                  </button>
                )
              }

              if (quickPrSyncPending) {
                return withMobileYolo(
                  <button
                    type="button"
                    disabled
                    className="flex h-8 items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 text-xs font-medium text-amber-300"
                  >
                    <SpinnerGap className="size-3.5 animate-spin" />
                    Syncing...
                  </button>
                )
              }

              if (isQuickPrActive) {
                return withMobileYolo(
                  <button
                    type="button"
                    disabled
                    className="flex h-8 items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 text-xs font-medium text-amber-300"
                  >
                    <SpinnerGap className="size-3.5 animate-spin" />
                    {quickPrLabels[quickPrStep] || "Working..."}
                  </button>
                )
              }

              if (canShowQuickPrCreateButton) {
                return withMobileYolo(
                  <Tooltip content={prCreateDisabledReason || QUICK_PR_TOOLTIP_TEXT}>
                  <button
                    type="button"
                    onClick={() => {
                      void triggerQuickPr();
                    }}
                    disabled={quickPrCreateDisabled}
                    className={cn(
                      "flex h-8 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors",
                      quickPrCreateDisabled
                        ? "cursor-not-allowed border-zinc-700/70 bg-zinc-900 text-zinc-500"
                        : "border-emerald-500/40 bg-emerald-600 text-white hover:bg-emerald-500",
                    )}
                  >
                    <GitPullRequest className="size-3.5" />
                    Quick PR
                  </button>
                  </Tooltip>
                )
              }

              return withMobileYolo(
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowPrPopover((prev) => !prev)}
                    disabled={!prCreateActionsEnabled}
                    className={cn(
                      "flex h-8 items-center gap-1.5 rounded-md border border-zinc-700/70 px-2 text-xs font-medium transition-colors",
                      !prCreateActionsEnabled
                        ? "cursor-not-allowed bg-zinc-900 text-zinc-500"
                        : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200",
                      showPrPopover && "bg-zinc-800",
                    )}
                  >
                    <GitPullRequest className="size-3.5 shrink-0" />
                    PR
                  </button>
                  <CreatePrPopover
                    sessionId={sessionId}
                    open={prCreateActionsEnabled && showPrPopover}
                    onClose={() => setShowPrPopover(false)}
                    send={send}
                  />
                </div>
              )
            }

            const attachedDesktopPrHeader = renderAttachedPrHeader(false)
            if (attachedDesktopPrHeader) {
              return attachedDesktopPrHeader
            }

            if (prNum && (prState === "merged" || prState === "closed")) {
              const prLabel = prState === "merged" ? `Merged #${prNum}` : `Closed #${prNum}`
              const prColorClass = prState === "merged"
                ? "text-purple-300 hover:text-purple-200 hover:bg-purple-500/10 border-purple-500/30 hover:border-purple-500/50"
                : "text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30 hover:border-red-500/50"
              const prDotClass = prState === "merged" ? "bg-purple-400" : "bg-red-400"

              return (
                <div className="flex items-center gap-1.5">
                  {prUrl ? (
                    <a
                      href={prUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-[12px] font-medium transition-colors",
                        prColorClass,
                      )}
                    >
                      <span className={cn("size-1.5 shrink-0 rounded-full", prDotClass)} />
                      {prLabel}
                      <ArrowSquareOut className="size-3 opacity-60" />
                    </a>
                  ) : (
                    <span
                      className={cn(
                        "flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-[12px] font-medium",
                        prState === "merged" ? "border-purple-500/30 text-purple-300" : "border-red-500/30 text-red-400",
                      )}
                    >
                      <span className={cn("size-1.5 shrink-0 rounded-full", prDotClass)} />
                      {prLabel}
                    </span>
                  )}
                </div>
              )
            }

            // Quick PR done state (brief success display)
            if (quickPrStep === "done" && quickPrResult && quickPrResultHref) {
              return (
                <a
                  href={quickPrResultHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-7 items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 text-[12px] font-medium text-emerald-400 transition-colors hover:border-emerald-500/50 hover:bg-emerald-500/20"
                >
                  <Check className="size-3.5" />
                  {quickPrResult.updatedExisting ? `Updated #${quickPrResult.prNumber}` : `PR #${quickPrResult.prNumber}`}
                  <ArrowSquareOut className="size-3 opacity-60" />
                </a>
              )
            }

            // Quick PR error state
            if (quickPrStep === "error") {
              if (quickPrNeedsSync) {
                return (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-amber-300">Sync required</span>
                    <button
                      onClick={() => {
                        void triggerSyncFromQuickPrError();
                      }}
                      disabled={quickPrSyncPending}
                      className="flex h-7 items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-600 px-2.5 text-[12px] font-medium text-white transition-colors hover:bg-amber-500"
                    >
                      {quickPrSyncPending ? (
                        <SpinnerGap className="size-3.5 animate-spin" />
                      ) : (
                        <ArrowsClockwise className="size-3.5" />
                      )}
                      {quickPrSyncPending
                        ? "Syncing..."
                        : quickPrBehindLabel ? `Sync ${quickPrBehindLabel}` : "Sync now"}
                    </button>
                  </div>
                )
              }

              return (
                <Tooltip content={quickPrError || "Quick PR failed. Click to retry."}>
                  <button
                    onClick={() => {
                      void triggerQuickPr();
                    }}
                    className="flex h-7 items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-2.5 text-[12px] font-medium text-red-400 transition-colors hover:border-red-500/50 hover:bg-red-500/20"
                  >
                    <GitPullRequest className="size-3.5" />
                    Retry Quick PR
                  </button>
                </Tooltip>
              )
            }

            if (quickPrSyncPending) {
              return (
                <button
                  disabled
                  className="flex h-7 items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 text-[12px] font-medium text-amber-300"
                >
                  <SpinnerGap className="size-3.5 animate-spin" />
                  Syncing...
                </button>
              )
            }

            // Quick PR in progress
            if (isQuickPrActive) {
              return (
                <button
                  disabled
                  className="flex h-7 items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 text-[12px] font-medium text-amber-300"
                >
                  <SpinnerGap className="size-3.5 animate-spin" />
                  {quickPrLabels[quickPrStep] || "Working..."}
                </button>
              )
            }

            // Show Quick PR button when we can create from the current branch
            if (canShowQuickPrCreateButton) {
              return (
                <div className="flex items-center gap-1.5">
                  {yoloButton}
                  <div className="relative flex items-center">
                    <Tooltip content={prCreateDisabledReason || QUICK_PR_TOOLTIP_TEXT}>
                    <button
                      type="button"
                      onClick={() => {
                        void triggerQuickPr();
                      }}
                      disabled={!prCreateActionsEnabled}
                      className={cn(
                        "flex h-7 items-center gap-1.5 rounded-l-md border border-r-0 px-2.5 text-[12px] font-medium transition-colors",
                        !prCreateActionsEnabled
                          ? "cursor-not-allowed border-zinc-700/70 bg-zinc-900 text-zinc-500"
                          : "border-emerald-500/40 bg-emerald-600 text-white hover:bg-emerald-500",
                      )}
                    >
                      <GitPullRequest className="size-3.5 shrink-0" />
                      Quick PR
                    </button>
                    </Tooltip>
                    <Tooltip content={prCreateDisabledReason || "Manual PR options"}>
                      <button
                        type="button"
                        aria-label="Manual PR options"
                        onClick={() => setShowPrPopover((prev) => !prev)}
                        disabled={!prCreateActionsEnabled}
                        className={cn(
                          "flex h-7 items-center rounded-r-md border px-1.5 transition-colors",
                          !prCreateActionsEnabled
                            ? "cursor-not-allowed border-zinc-700/70 bg-zinc-900 text-zinc-500"
                            : "border-emerald-500/40 bg-emerald-600 text-white hover:bg-emerald-500",
                          showPrPopover && "bg-emerald-500",
                        )}
                      >
                        <CaretDown className="size-3" />
                      </button>
                    </Tooltip>
                    <CreatePrPopover
                      sessionId={sessionId}
                      open={prCreateActionsEnabled && showPrPopover}
                      onClose={() => setShowPrPopover(false)}
                      send={send}
                    />
                  </div>
                </div>
              )
            }

            // Default: Create PR button (opens popover)
            return (
              <div className="flex items-center gap-1.5">
                {yoloButton}
                <Tooltip content={prCreateDisabledReason || "Manual PR options"}>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowPrPopover((prev) => !prev)}
                      disabled={!prCreateActionsEnabled}
                      className={cn(
                        "flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-[12px] font-medium transition-colors",
                        !prCreateActionsEnabled
                          ? "cursor-not-allowed border-zinc-700/70 bg-zinc-900 text-zinc-500"
                          : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 border-zinc-700/60 hover:border-zinc-600",
                        showPrPopover && "bg-zinc-800",
                      )}
                    >
                      <GitPullRequest className="size-3.5 shrink-0" />
                      Create PR
                    </button>
                    <CreatePrPopover
                      sessionId={sessionId}
                      open={prCreateActionsEnabled && showPrPopover}
                      onClose={() => setShowPrPopover(false)}
                      send={send}
                    />
                  </div>
                </Tooltip>
              </div>
            )
          })()}

          {/* PR history dropdown — shown when there are archived PRs but no current active (open/draft) PR */}
          {!isMobileLayout && prHistoryEntries.length > 0 && effectivePrState !== "open" && effectivePrState !== "draft" && (
            <PrHistoryDropdown entries={prHistoryEntries} />
          )}

          {!isMobileLayout && (
            <>
              {/* Separator */}
              <div className="h-5 w-px bg-zinc-700/60" />

              <div className="flex items-center gap-1">
                {connectionCount > 1 && (
                  <Tooltip content="Active session connections (tabs/users)">
                    <span className="flex items-center gap-1 rounded-md bg-zinc-800/50 px-2 py-1 text-xs text-zinc-400">
                      <Users className="size-3" />
                      {connectionCount}
                    </span>
                  </Tooltip>
                )}
                 {/* Preview button — rendered whenever the sandbox layer is
                     available, disabled with a tooltip when no dev server is
                     configured (e.g. bare sessions). Keeps the header stable
                     and makes "why is this off?" discoverable on hover. The
                     tooltip text flips between the disabled reason and the
                     standard "Toggle preview" label so there is always a
                     useful hint on hover. */}
                 {showPreviewButton && (
                   <Tooltip content={previewDisabledReason ?? "Toggle preview"}>
                     <span className="inline-flex">
                       <button
                         aria-label="Toggle preview"
                         onClick={handlePreviewClick}
                         disabled={Boolean(previewDisabledReason)}
                         className={cn(
                           "flex size-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-zinc-400",
                           contentPanelMode === "preview" && "bg-zinc-800 text-zinc-200",
                         )}
                       >
                           <Monitor className="size-4" />
                       </button>
                     </span>
                   </Tooltip>
                 )}
                 {/* Process logs button — same disabled-with-tooltip pattern
                     as Preview. Shown so users can see it exists even when
                     there's no runtime process to stream from yet. */}
                 {showDevServerLogsButton && (
                   <Tooltip content={devServerLogsDisabledReason ?? "Toggle process logs"}>
                     <span className="inline-flex">
                       <button
                         aria-label="Toggle process logs"
                         onClick={handleDevServerLogsClick}
                         disabled={Boolean(devServerLogsDisabledReason)}
                         className={cn(
                           "flex size-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-zinc-400",
                           contentPanelMode === "devlogs" && "bg-zinc-800 text-zinc-200",
                         )}
                       >
                          <FileText className="size-4" />
                       </button>
                     </span>
                   </Tooltip>
                 )}
                 {/* Terminal toggle */}
                  {canToggleTerminal && (
                    <Tooltip content={<span>Toggle terminal <kbd className="ml-1 rounded bg-zinc-700 px-1 py-0.5 text-xs font-mono text-zinc-300">{shortcuts.terminal}</kbd></span>}>
                      <button
                        aria-label="Toggle terminal"
                        onClick={() => {
                          handleTerminalActionRequest();
                        }}
                        className="flex size-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
                      >
                         <Terminal className="size-4" />
                     </button>
                   </Tooltip>
                 )}
                 {/* File explorer toggle */}
                 <Tooltip content={<span>Toggle file explorer <kbd className="ml-1 rounded bg-zinc-700 px-1 py-0.5 text-xs font-mono text-zinc-300">{shortcuts.rightSidebar}</kbd></span>}>
                   <button
                     aria-label="Toggle file explorer"
                     onClick={toggleRightSidebar}
                     className="flex size-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
                   >
                     <SidebarSimple className="size-4" />
                   </button>
                 </Tooltip>
                 {/* VS Code */}
                  {canToggleCodeServer && (
                     <Tooltip content={
                       codeServerStatus === "starting"
                         ? "Starting VS Code (can take up to 30 seconds)..."
                        : codeServerStatus === "running"
                          ? "Open VS Code"
                          : "Start VS Code"
                   }>
                     <button
                       aria-label={
                         codeServerStatus === "starting"
                           ? "Starting VS Code"
                           : codeServerStatus === "running"
                             ? "Open VS Code"
                             : "Start VS Code"
                       }
                       onClick={async () => {
                         if (sandboxResumeNeeded) {
                           resumeThenStartCodeServerRef.current = sessionId;
                           const resumed = await requestResume();
                           if (!resumed) {
                             resumeThenStartCodeServerRef.current = null;
                           }
                           return;
                         }

                         startCodeServerFromUi();
                       }}
                       disabled={codeServerStatus === "starting"}
                       className="flex size-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed"
                     >
                       {codeServerStatus === "starting"
                         ? <SpinnerGap className="size-4 animate-spin" />
                          : <VSCodeLogoIcon className="size-4" />
                       }
                      </button>
                    </Tooltip>
                  )}
                  {canOpenRecoverySshDialog && (
                    <Tooltip content={requestRecoverySshAccess.isPending ? "Generating SSH access..." : recoveryDialogMode === "healthy" ? "Sandbox tools" : "Sandbox recovery"}>
                      <button
                        aria-label={recoveryDialogMode === "healthy" ? "Sandbox tools" : "Sandbox recovery"}
                        onClick={() => setShowRecoverySshDialog(true)}
                        className={cn(
                          "flex size-8 items-center justify-center rounded-md transition-colors hover:bg-zinc-800",
                          recoverySshAccessActive
                            ? "text-amber-300 hover:text-amber-200"
                            : "text-zinc-400 hover:text-zinc-200",
                        )}
                      >
                        {requestRecoverySshAccess.isPending
                          ? <SpinnerGap className="size-4 animate-spin" />
                          : <ClockCounterClockwise className="size-4" />}
                      </button>
                    </Tooltip>
                  )}

                 <Tooltip content={sessionArchived ? "Unarchive thread" : "Archive thread"}>
                   <button
                     aria-label={sessionArchived ? "Unarchive thread" : "Archive thread"}
                    onClick={() => {
                      void handleArchiveToggle();
                    }}
                    disabled={archiveToggleDisabled}
                    className="flex size-8 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-50"
                  >
                    {archiveTogglePending
                      ? <SpinnerGap className="size-4 animate-spin" />
                      : <Archive className="size-4" />
                    }
                  </button>
                </Tooltip>
                <div className="relative" ref={threadActionsMenuRef}>
                  <Tooltip content="More actions">
                    <button
                      aria-label="More actions"
                      onClick={() => setShowThreadActionsMenu((prev) => !prev)}
                      className="flex size-8 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
                    >
                      <DotsThree className="size-4" weight="bold" />
                    </button>
                  </Tooltip>
                  {showThreadActionsMenu && (
                    <div className={cn(
                      "absolute right-0 top-full z-50 mt-1.5 max-h-[min(80vh,42rem)] max-w-[calc(100vw-1rem)] overflow-x-hidden overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-900 py-1 shadow-xl shadow-black/40",
                      showConnectionFlow && canViewConnectionFlow ? "w-[26rem]" : "w-80",
                    )}>
                      {showConnectionFlow && canViewConnectionFlow ? (
                        <div className="px-3 py-2.5">
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <button
                              onClick={() => setShowConnectionFlow(false)}
                              className="rounded-md px-2 py-1 text-xs text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
                            >
                              Back
                            </button>
                            <button
                              onClick={() => {
                                queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
                                void connectionFlowQuery.refetch();
                              }}
                              disabled={connectionFlowQuery.isFetching}
                              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700/70 px-2 py-1 text-xs text-zinc-300 transition-colors hover:bg-zinc-800 disabled:opacity-50"
                            >
                              <ArrowsClockwise className={cn("size-3.5", connectionFlowQuery.isFetching && "animate-spin")} />
                              Refresh
                            </button>
                          </div>

                          <div className="rounded-lg border border-zinc-800/80 bg-zinc-950/40 p-2.5">
                            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Connection Flow</p>
                            <p className="mt-1 text-xs text-zinc-500">Admin diagnostics for Neon, Durable Object, and OpenCode state.</p>
                          </div>

                          <RuntimeInspector />

                          {connectionFlowQuery.error && (
                            <div className="mt-2 rounded-md border border-red-900/40 bg-red-950/30 px-2 py-1.5 text-xs text-red-300">
                              Failed to load runtime snapshots. You can still use the links and resync actions.
                            </div>
                          )}
                          {connectionFlowQuery.isFetching && !runtimeSnapshot && (
                            <div className="mt-2 rounded-md border border-zinc-800/70 bg-zinc-950/40 px-2 py-1.5 text-xs text-zinc-400">
                              Loading runtime snapshot...
                            </div>
                          )}

                          <div className="mt-2 rounded-lg border border-zinc-800/70 bg-zinc-950/40 p-2.5">
                            <p className="text-xs font-medium text-zinc-400">Neon `agent_session`</p>
                            <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs">
                              <dt className="text-zinc-500">session_id</dt>
                              <dd className="truncate text-zinc-300">{sessionId}</dd>
                              <dt className="text-zinc-500">status</dt>
                              <dd className="text-zinc-300">{typeof (session as Record<string, unknown>)?.status === "string" ? (session as Record<string, unknown>).status as string : "--"}</dd>
                              <dt className="text-zinc-500">lifecycle</dt>
                              <dd className="text-zinc-300">{lifecycleStatus}</dd>
                              <dt className="text-zinc-500">do_id</dt>
                              <dd className="truncate text-zinc-300">{dbDoId ?? "--"}</dd>
                              <dt className="text-zinc-500">sandbox_id</dt>
                              <dd className="truncate text-zinc-300">{dbSandboxId ?? "--"}</dd>
                            </dl>
                          </div>

                          <div className="mt-2 rounded-lg border border-zinc-800/70 bg-zinc-950/40 p-2.5">
                            <p className="text-xs font-medium text-zinc-400">Durable Object runtime</p>
                            <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs">
                              <dt className="text-zinc-500">WebSocket</dt>
                              <dd className="text-zinc-300">{formatWsStatus(status)}</dd>
                              <dt className="text-zinc-500">Probe</dt>
                              <dd className="text-zinc-300">{wsTransportProbeState === "session_reachable" ? "reachable" : wsTransportProbeState === "session_unreachable" ? "unreachable" : "n/a"}</dd>
                              <dt className="text-zinc-500">do.session_id</dt>
                              <dd className="truncate text-zinc-300">{runtimeDoSessionId ?? "--"}</dd>
                              <dt className="text-zinc-500">do.sandbox_id</dt>
                              <dd className="truncate text-zinc-300">{runtimeDoSandboxId ?? "--"}</dd>
                              <dt className="text-zinc-500">sandbox</dt>
                              <dd className="text-zinc-300">{runtimeSnapshot?.sandboxStatus ? formatSandboxStatus(runtimeSnapshot.sandboxStatus) : formatSandboxStatus(sandboxStatus)}</dd>
                              <dt className="text-zinc-500">events</dt>
                              <dd className="text-zinc-300">{typeof runtimeSnapshot?.eventCount === "number" ? runtimeSnapshot.eventCount : "--"}</dd>
                              <dt className="text-zinc-500">sync cursor</dt>
                              <dd className="text-zinc-300">{syncNewDebug.committedCursor}</dd>
                              <dt className="text-zinc-500">last applied</dt>
                              <dd className="text-zinc-300">{syncNewDebug.lastAppliedId}</dd>
                              <dt className="text-zinc-500">last page</dt>
                              <dd className="text-zinc-300">{`${syncNewDebug.lastPageCount} / ${syncNewDebug.lastPageBytes}B`}</dd>
                              <dt className="text-zinc-500">apply error</dt>
                              <dd className="text-zinc-300">{syncNewDebug.lastApplyError?.reason ?? "--"}</dd>
                              <dt className="text-zinc-500">sync.ready</dt>
                              <dd className="text-zinc-300">{formatSyncReadyDecisionSummary({ decision: syncNewDebug.lastSyncReadyDecision })}</dd>
                              <dt className="text-zinc-500">last reset</dt>
                              <dd className="text-zinc-300">{formatSyncReadyDecisionSummary({ decision: syncNewDebug.lastReset })}</dd>
                              <dt className="text-zinc-500">prev token</dt>
                              <dd className="truncate text-zinc-300">{formatSyncHistoryTokenDebug({
                                token: syncNewDebug.lastSyncReadyDecision?.previousParsedHistoryToken ?? null,
                                fallbackRaw: syncNewDebug.lastSyncReadyDecision?.previousHistoryToken ?? null,
                              })}</dd>
                              <dt className="text-zinc-500">next token</dt>
                              <dd className="truncate text-zinc-300">{formatSyncHistoryTokenDebug({
                                token: syncNewDebug.lastSyncReadyDecision?.nextParsedHistoryToken ?? null,
                                fallbackRaw: syncNewDebug.lastSyncReadyDecision?.nextHistoryToken ?? null,
                              })}</dd>
                              <dt className="text-zinc-500">DO token</dt>
                              <dd className="truncate text-zinc-300">{formatServerSyncHistoryDebug({
                                tokenVersion: runtimeHistoryDebug?.tokenVersion ?? null,
                                historyContinuity: runtimeHistoryDebug?.historyContinuity ?? null,
                                headCursor: runtimeHistoryDebug?.headCursor ?? null,
                                lineageField: runtimeHistoryDebug?.lineageField ?? null,
                                lineageValue: runtimeHistoryDebug?.browserHistoryEpoch ?? null,
                              })}</dd>
                              <dt className="text-zinc-500">DO tail</dt>
                              <dd className="text-zinc-300">{runtimeHistoryDebug?.tailCursor ?? "--"}</dd>
                              <dt className="text-zinc-500">DO journal</dt>
                              <dd className="truncate text-zinc-300">{runtimeReplicationDebug?.journalEpoch ?? "--"}</dd>
                              <dt className="text-zinc-500">DO replication</dt>
                              <dd className="text-zinc-300">{runtimeReplicationDebug?.replicationContinuity ?? "--"}</dd>
                              <dt className="text-zinc-500">DO browser epoch</dt>
                              <dd className="text-zinc-300">{runtimeReplicationDebug?.browserHistoryEpoch ?? "--"}</dd>
                            </dl>
                            {(sandboxIdMismatch || doSessionMismatch) && (
                              <div className="mt-2 rounded-md border border-amber-900/40 bg-amber-950/30 px-2 py-1.5 text-xs text-amber-200">
                                {sandboxIdMismatch && <p>Neon sandbox ID does not match Durable Object sandbox ID.</p>}
                                {doSessionMismatch && <p>Durable Object session ID does not match this thread ID.</p>}
                              </div>
                            )}
                          </div>

                          <ConnectionFlowRecoveryLifecyclePanel
                            runtimeLifecycle={connectionFlowRuntimeLifecycle}
                          />

                          <div className="mt-2 rounded-lg border border-zinc-800/70 bg-zinc-950/40 p-2.5">
                            <p className="text-xs font-medium text-zinc-400">OpenCode service</p>
                            <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs">
                              <dt className="text-zinc-500">Agent</dt>
                              <dd className="text-zinc-300">{getAgentTypeLabel(agentType)}</dd>
                              <dt className="text-zinc-500">session_id</dt>
                              <dd className="truncate text-zinc-300">{runtimeDoOpenCodeSessionId ?? openCodeAccessSnapshot?.sessionId ?? "--"}</dd>
                              <dt className="text-zinc-500">Tunnel URL</dt>
                              <dd className="text-zinc-300">{openCodeAccessSnapshot?.url ? "Available" : "Missing"}</dd>
                              <dt className="text-zinc-500">Auth secret</dt>
                              <dd className="text-zinc-300">{openCodeAccessSnapshot?.password ? "Available" : "Missing"}</dd>
                              <dt className="text-zinc-500">Dev server</dt>
                              <dd className="text-zinc-300">{formatServiceStatus(devServerStatus)}</dd>
                              <dt className="text-zinc-500">VS Code</dt>
                              <dd className="text-zinc-300">{formatServiceStatus(codeServerStatus)}</dd>
                            </dl>
                          </div>

                           <div className="mt-2 space-y-1.5 border-t border-zinc-800/70 pt-2">
                              <button
                                onClick={() => {
                                requestSandboxRetry();
                              }}
                              disabled={
                                sessionArchived ||
                                retrySandboxPending ||
                                (sandboxStatus !== "error" && sandboxStatus !== "stopped")
                              }
                              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-amber-300 transition-colors hover:bg-amber-950/30 disabled:pointer-events-none disabled:opacity-40"
                            >
                              {retrySandboxPending
                                ? <SpinnerGap className="size-4 animate-spin" />
                                : <ArrowsClockwise className="size-4" />}
                              {retrySandboxPending ? "Retrying sandbox startup..." : "Retry sandbox startup"}
                            </button>
                          </div>

                          <div className="mt-2 space-y-1.5 border-t border-zinc-800/70 pt-2">
                            <button
                              onClick={() => {
                                if (!cloudflareDurableObjectUrl) return;
                                window.open(cloudflareDurableObjectUrl, "_blank", "noopener,noreferrer");
                              }}
                              disabled={!cloudflareDurableObjectUrl}
                              className="flex w-full items-center justify-between gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-zinc-300 transition-colors hover:bg-zinc-800 disabled:pointer-events-none disabled:opacity-40"
                            >
                              <span>Open Cloudflare DO</span>
                              <ArrowSquareOut className="size-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                if (!sandboxDashboardUrl) return;
                                window.open(sandboxDashboardUrl, "_blank", "noopener,noreferrer");
                              }}
                              disabled={!sandboxDashboardUrl}
                              className="flex w-full items-center justify-between gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-zinc-300 transition-colors hover:bg-zinc-800 disabled:pointer-events-none disabled:opacity-40"
                            >
                              <span>Open sandbox dashboard</span>
                              <ArrowSquareOut className="size-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                if (!connectionFlowNeonTablesUrl) return;
                                window.open(connectionFlowNeonTablesUrl, "_blank", "noopener,noreferrer");
                              }}
                              disabled={!connectionFlowNeonTablesUrl}
                              className="flex w-full items-center justify-between gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-zinc-300 transition-colors hover:bg-zinc-800 disabled:pointer-events-none disabled:opacity-40"
                            >
                              <span>Open Neon tables</span>
                              <ArrowSquareOut className="size-3.5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* ── Compact status indicator ─────────────── */}
                          <div className="flex items-center gap-2 px-3 py-2">
                            <span className={cn(
                              "size-2 shrink-0 rounded-full",
                              sandboxStatus === "running" ? "bg-green-400"
                                : sandboxStatus === "error" || sandboxStatus === "stopped" || sandboxStatus === "none" ? "bg-red-400"
                                : "bg-yellow-400",
                            )} />
                            <span className="text-sm text-zinc-300">{sandboxStatusDisplayLabel}</span>
                          </div>

                          {/* ── Primary actions ──────────────────────── */}
                          <div className="border-t border-zinc-800/60">
                            {(canResumeSandbox || canSuspendSandbox) && (
                              <button
                                onClick={() => {
                                  setShowThreadActionsMenu(false);
                                  if (canResumeSandbox) {
                                    void requestResume({ force: true });
                                  } else if (canSuspendSandbox) {
                                    if (
                                      sandboxStatus === "running"
                                      && !window.confirm(RUNNING_SANDBOX_SUSPEND_CONFIRMATION_MESSAGE)
                                    ) {
                                      return;
                                    }
                                    suspendSession.mutate();
                                  }
                                }}
                                disabled={
                                  resumeActionPending ||
                                  suspendSession.isPending ||
                                  branchSwitchPending
                                }
                                title={branchSwitchPending ? BRANCH_SWITCH_BLOCKED_REASON : undefined}
                                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40 disabled:pointer-events-none"
                              >
                                {canResumeSandbox ? (
                                  <>
                                    <Play className="size-4" />
                                    {resumeActionPending ? "Resuming..." : "Resume sandbox"}
                                  </>
                                ) : (
                                  <>
                                    <Pause className="size-4" />
                                    {suspendSession.isPending
                                      ? "Suspending..."
                                      : "Suspend sandbox"}
                                  </>
                                )}
                              </button>
                            )}
                            {showContinueInNewThreadAction && (
                              <button
                                onClick={() => {
                                  setShowThreadActionsMenu(false);
                                  handleContinueInNewSession();
                                }}
                                disabled={continueSession.isPending || Boolean(continueInNewSessionDisabledReason)}
                                title={continueInNewSessionDisabledReason ?? undefined}
                                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:pointer-events-none disabled:opacity-40"
                              >
                                <LinkSimple className="size-4" />
                                {continueSession.isPending ? "Continuing..." : "Continue in a new thread"}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setShowThreadActionsMenu(false);
                                requestDeleteThread("thread_actions_menu");
                              }}
                              disabled={!canManageSession || deleteSession.isPending}
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-red-400 transition-colors hover:bg-red-950/30 disabled:opacity-50"
                            >
                              <Trash className="size-4" />
                              Delete thread
                            </button>
                          </div>

                          {/* ── Troubleshooting (collapsed by default) ── */}
                          <div className="border-t border-zinc-800/60">
                            <button
                              onClick={() => setShowTroubleshooting((v) => !v)}
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
                            >
                              <Wrench className="size-4" />
                              <span className="flex-1">Troubleshooting</span>
                              {showTroubleshooting
                                ? <CaretDown className="size-3.5" />
                                : <CaretRight className="size-3.5" />}
                            </button>

                            {showTroubleshooting && (
                              <div className="border-t border-zinc-800/40">
                                <button
                                  onClick={() => {
                                    setShowThreadActionsMenu(false);
                                    useSessionUI.getState().toggleDebugLogs();
                                  }}
                                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
                                >
                                  <FileText className="size-4" />
                                  Debug logs
                                </button>
                                <button
                                  onClick={() => {
                                    setShowThreadActionsMenu(false);
                                    void handleClearLocalThreadState();
                                  }}
                                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
                                >
                                  <ArrowsClockwise className="size-4" />
                                  Reset thread view
                                </button>
                                {canViewConnectionFlow && (
                                  <button
                                    onClick={() => {
                                      setShowConnectionFlow(true);
                                    }}
                                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
                                  >
                                    <GitBranch className="size-4" />
                                    Connection Flow
                                  </button>
                                )}
                                {canManageSession && (
                                  <div className="relative" ref={recoveryVersionsRef}>
                                    <div className="flex items-center">
                                      <button
                                        onClick={() => {
                                          setShowThreadActionsMenu(false);
                                          setShowRecoveryVersions(false);
                                          void downloadRecoveryPatch();
                                        }}
                                        disabled={!recoveryPatchAvailable || recoveryPatchDownloadPending}
                                        className="flex flex-1 items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40 disabled:pointer-events-none"
                                      >
                                        <DownloadSimple className="size-4" />
                                        {recoveryPatchDownloadPending ? "Downloading..." : "Download recovery bundle"}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleRecoveryVersions();
                                        }}
                                        className="flex items-center px-2 py-2 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
                                        title="Show older versions"
                                      >
                                        <ClockCounterClockwise className="size-4" />
                                      </button>
                                    </div>
                                    {showRecoveryVersions && (
                                      <div className="border-t border-zinc-700/60 bg-zinc-900/80">
                                        <div className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-zinc-500">
                                          Older versions
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                          {recoveryVersionsLoading && (
                                            <div className="flex items-center justify-center py-3 text-xs text-zinc-500">
                                              <SpinnerGap className="mr-2 size-4 animate-spin" />
                                              Loading versions…
                                            </div>
                                          )}
                                          {!recoveryVersionsLoading && recoveryVersions.length === 0 && (
                                            <div className="px-3 py-3 text-center text-xs text-zinc-500">
                                              No recovery bundles found
                                            </div>
                                          )}
                                          {!recoveryVersionsLoading && recoveryVersions.map((v) => (
                                            <button
                                              key={v.capturedAtMs}
                                              onClick={() => {
                                                setShowRecoveryVersions(false);
                                                setShowThreadActionsMenu(false);
                                                void downloadRecoveryPatch({ versionMs: v.capturedAtMs });
                                              }}
                                              disabled={recoveryPatchDownloadPending}
                                              className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-xs text-zinc-300 transition-colors hover:bg-zinc-800 disabled:opacity-40 disabled:pointer-events-none"
                                            >
                                              <DownloadSimple className="size-3.5 shrink-0" />
                                              <span className="truncate">
                                                {new Date(v.capturedAtMs).toLocaleString()}
                                              </span>
                                              {v.isCurrent && (
                                                <span className="ml-auto shrink-0 rounded bg-zinc-700/60 px-1.5 py-0.5 text-xs font-medium text-zinc-300">
                                                  latest
                                                </span>
                                              )}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <button
                                  onClick={() => {
                                    setShowThreadActionsMenu(false);
                                    void openOpenCodeTunnelUrl();
                                  }}
                                  disabled={sandboxStatus !== "running" || !agentIdentityResolved || isClaudeSession}
                                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40 disabled:pointer-events-none"
                                >
                                  <ArrowSquareOut className="size-4" />
                                  OpenCode tunnel
                                </button>

                                {/* ── Full status grid + diagnostics ─── */}
                                <div className="border-t border-zinc-800/40 px-3 py-2.5">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
                                    Sandbox details
                                  </p>
                                  <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs">
                                    <dt className="text-zinc-500">Connection</dt>
                                    <dd className="text-zinc-300">{formatWsStatus(status)}</dd>
                                    <dt className="text-zinc-500">Sandbox</dt>
                                    <dd className="text-zinc-300">{formatSandboxStatus(sandboxStatus)}</dd>
                                    <dt className="text-zinc-500">Agent</dt>
                                    <dd className="text-zinc-300">
                                      {agentType === "claude_code"
                                        ? "Claude"
                                        : agentType === "opencode"
                                          ? "OpenCode"
                                          : "Resolving..."}
                                    </dd>
                                    <dt className="text-zinc-500">Sessions</dt>
                                    <dd className="text-zinc-300">{connectionCount}</dd>
                                    <dt className="text-zinc-500">Dev server</dt>
                                    <dd className="text-zinc-300">{formatServiceStatus(devServerStatus)}</dd>
                                    <dt className="text-zinc-500">VS Code</dt>
                                    <dd className="text-zinc-300">{formatServiceStatus(codeServerStatus)}</dd>
                                    {activeCredentialSource && (
                                      <>
                                        <dt className="text-zinc-500">Credential</dt>
                                        <dd className="text-zinc-300">{activeCredentialSource.label}</dd>
                                      </>
                                    )}
                                    {nextCredentialSource && (
                                      <>
                                        <dt className="text-zinc-500">Fallback</dt>
                                        <dd className="text-zinc-300">{nextCredentialSource.label}</dd>
                                      </>
                                    )}
                                    {credentialStateRefreshedAt !== null && (
                                      <>
                                        <dt className="text-zinc-500">Creds checked</dt>
                                        <dd className="text-zinc-300">{formatMenuTimestamp(credentialStateRefreshedAt)}</dd>
                                      </>
                                    )}
                                    {lastCredentialFallbackAt !== null && (
                                      <>
                                        <dt className="text-zinc-500">Last fallback</dt>
                                        <dd className="text-zinc-300">{formatMenuTimestamp(lastCredentialFallbackAt)}</dd>
                                      </>
                                    )}
                                    {runtimeErrorType === RUNTIME_ERROR_BASE_IMAGE_STALE && (
                                      <>
                                        <dt className="text-zinc-500">Base image</dt>
                                        <dd className="text-yellow-400">Stale</dd>
                                      </>
                                    )}
                                  </dl>

                                  {(latestSandboxError || devServerError || codeServerError || chatGptOauthDebug?.error || runtimeLastErrorMessage || credentialIssue?.message) && (
                                    <div className="mt-2 rounded-md border border-red-900/40 bg-red-950/30 px-2 py-1.5 text-xs text-red-300">
                                      {latestSandboxError && (
                                        <p className="truncate">Sandbox: {latestSandboxError}</p>
                                      )}
                                      {credentialIssue?.message && (
                                        <p className="truncate">Credential: {credentialIssue.message}</p>
                                      )}
                                      {!credentialIssue?.message && runtimeLastErrorMessage && (
                                        <p className="truncate">Runtime: {runtimeLastErrorMessage}</p>
                                      )}
                                      {devServerError && (
                                        <p className="truncate">Dev server: {devServerError}</p>
                                      )}
                                      {codeServerError && (
                                        <p className="truncate">VS Code: {codeServerError}</p>
                                      )}
                                      {chatGptOauthDebug?.error && (
                                        <p className="truncate">ChatGPT OAuth: {chatGptOauthDebug.error}</p>
                                      )}
                                    </div>
                                  )}

                                  {/* Collapsible diagnostics */}
                                  <button
                                    onClick={() => setShowSandboxDiagnostics((v) => !v)}
                                    className="mt-2 flex w-full items-center gap-1 text-xs text-zinc-500 transition-colors hover:text-zinc-300"
                                  >
                                    {showSandboxDiagnostics ? (
                                      <CaretDown className="size-3" />
                                    ) : (
                                      <CaretRight className="size-3" />
                                    )}
                                    Diagnostics
                                  </button>
                                  {showSandboxDiagnostics && (
                                    <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs">
                                      <dt className="text-zinc-500">Updated</dt>
                                      <dd className="text-zinc-300">{formatMenuTimestamp(wsStatusChangedAt)}</dd>
                                      <dt className="text-zinc-500">Transport probe</dt>
                                      <dd className="text-zinc-300">
                                        {wsTransportProbeState === "unknown"
                                          ? "n/a"
                                          : wsTransportProbeState === "session_reachable"
                                            ? "reachable"
                                            : "unreachable"}
                                      </dd>
                                      <dt className="text-zinc-500">Probe updated</dt>
                                      <dd className="text-zinc-300">{formatMenuTimestamp(wsTransportProbeAt ?? 0)}</dd>
                                      <dt className="text-zinc-500">Client close</dt>
                                      <dd className="text-zinc-300">{formatMenuTimestamp(lastWsCloseDiagnostics?.at ?? 0)}</dd>
                                      <dt className="text-zinc-500">Client close code</dt>
                                      <dd className="text-zinc-300">{lastWsCloseDiagnostics?.code ?? "--"}</dd>
                                      <dt className="text-zinc-500">Client reason</dt>
                                      <dd className="truncate text-zinc-300">{lastWsCloseDiagnostics?.reason ?? lastWsCloseDiagnostics?.localInitiatorReason ?? "--"}</dd>
                                      <dt className="text-zinc-500">Client context</dt>
                                      <dd className="text-zinc-300">
                                        {lastWsCloseDiagnostics
                                          ? `${lastWsCloseDiagnostics.visibilityState ?? "unknown"} · ${lastWsCloseDiagnostics.online === null ? "online?" : lastWsCloseDiagnostics.online ? "online" : "offline"}${lastWsCloseDiagnostics.localInitiatorReason ? ` · local:${lastWsCloseDiagnostics.localInitiatorReason}` : ""}`
                                          : "--"}
                                      </dd>
                                      <dt className="text-zinc-500">Client error</dt>
                                      <dd className="text-zinc-300">{formatMenuTimestamp(lastWsErrorDiagnostics?.at ?? 0)}</dd>
                                      <dt className="text-zinc-500">Client error detail</dt>
                                      <dd className="truncate text-zinc-300">{lastWsErrorDiagnostics?.message ?? "--"}</dd>
                                      <dt className="text-zinc-500">DO close</dt>
                                      <dd className="text-zinc-300">{formatMenuTimestamp(remoteWsDiagnostics?.lastCloseAt ?? 0)}</dd>
                                      <dt className="text-zinc-500">DO close code</dt>
                                      <dd className="text-zinc-300">{remoteWsDiagnostics?.lastCloseCode ?? "--"}</dd>
                                      <dt className="text-zinc-500">DO reason</dt>
                                      <dd className="truncate text-zinc-300">{remoteWsDiagnostics?.lastCloseReason ?? "--"}</dd>
                                      <dt className="text-zinc-500">DO error</dt>
                                      <dd className="truncate text-zinc-300">{remoteWsDiagnostics?.lastErrorMessage ?? "--"}</dd>
                                      <dt className="text-zinc-500">ChatGPT OAuth</dt>
                                      <dd className="text-zinc-300">{formatChatGptOauthDebug(chatGptOauthDebug)}</dd>
                                      <dt className="text-zinc-500">OAuth checked</dt>
                                      <dd className="text-zinc-300">{formatMenuTimestamp(chatGptOauthDebug?.updatedAt ?? 0)}</dd>
                                      <dt className="text-zinc-500">Sync cursor</dt>
                                      <dd className="text-zinc-300">{syncNewDebug.committedCursor}</dd>
                                      <dt className="text-zinc-500">Apply error</dt>
                                      <dd className="text-zinc-300">{syncNewDebug.lastApplyError?.reason ?? "--"}</dd>
                                      <dt className="text-zinc-500">sync.ready</dt>
                                      <dd className="text-zinc-300">{formatSyncReadyDecisionSummary({ decision: syncNewDebug.lastSyncReadyDecision })}</dd>
                                      <dt className="text-zinc-500">last reset</dt>
                                      <dd className="text-zinc-300">{formatSyncReadyDecisionSummary({ decision: syncNewDebug.lastReset })}</dd>
                                      <dt className="text-zinc-500">DO token</dt>
                                      <dd className="truncate text-zinc-300">{formatServerSyncHistoryDebug({
                                        tokenVersion: runtimeHistoryDebug?.tokenVersion ?? null,
                                        historyContinuity: runtimeHistoryDebug?.historyContinuity ?? null,
                                        headCursor: runtimeHistoryDebug?.headCursor ?? null,
                                        lineageField: runtimeHistoryDebug?.lineageField ?? null,
                                        lineageValue: runtimeHistoryDebug?.browserHistoryEpoch ?? null,
                                      })}</dd>
                                    </dl>
                                  )}

                                  {recentSandboxProgress.length > 0 && (
                                    <div className="mt-2 border-t border-zinc-800/70 pt-2">
                                      <p className="text-xs font-medium text-zinc-400">Recent events</p>
                                      <div className="mt-1.5 space-y-1.5">
                                        {recentSandboxProgress.map((entry, index) => (
                                          <div
                                            key={`${entry.timestamp}-${entry.step}-${index}`}
                                            className="rounded-md bg-zinc-800/40 px-2 py-1.5 text-xs"
                                          >
                                            <div className="flex items-center justify-between gap-2">
                                              <span className="truncate text-zinc-300">{entry.message}</span>
                                              <span className="shrink-0 text-zinc-500">
                                                {formatMenuTimestamp(Number(entry.timestamp ?? 0))}
                                              </span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
             </>
          )}
        </div>
      </header>
      )}

    </>
  );

  return (
    <FilePathSetProvider
      sessionId={sessionId}
      sandboxRunning={runtimeInteractionEnabled}
      cachedWarningReason={cachedSidebarWarningReason}
    >
      <Dialog
        open={runtimeConversationPendingClose !== null}
        onOpenChange={(open) => {
          if (!open) setRuntimeConversationPendingClose(null);
        }}
      >
        <DialogContent className="border-white/[0.06] bg-zinc-900 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-zinc-100">Close running chat session?</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-zinc-400">
              This chat session is still running. You can close the tab and let it continue in the background, or stop the run before closing it.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-md px-3 py-2 text-sm font-medium text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
              onClick={() => setRuntimeConversationPendingClose(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm font-medium text-zinc-200 transition-colors hover:bg-zinc-800 disabled:opacity-50"
              disabled={!runtimeConversationPendingClose || closeRuntimeConversation.isPending}
              onClick={() => {
                if (runtimeConversationPendingClose) {
                  closeRuntimeConversationKeepingRun(runtimeConversationPendingClose);
                }
              }}
            >
              Close tab, keep running
            </button>
            <button
              type="button"
              className="rounded-md bg-red-500/15 px-3 py-2 text-sm font-medium text-red-200 ring-1 ring-red-500/30 transition-colors hover:bg-red-500/25 disabled:opacity-50"
              disabled={!runtimeConversationPendingClose || closeRuntimeConversation.isPending}
              onClick={() => {
                if (runtimeConversationPendingClose) {
                  stopAndCloseRuntimeConversation(runtimeConversationPendingClose);
                }
              }}
            >
              Stop & close
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showRecoverySshDialog && canOpenRecoverySshDialog} onOpenChange={setShowRecoverySshDialog}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-white/[0.06] bg-zinc-900 p-0 sm:max-w-lg">
          <div className="px-6 pt-6 pb-4">
            <DialogHeader>
              <DialogTitle className="text-base font-semibold text-zinc-100">{recoveryDialogTitle}</DialogTitle>
              <DialogDescription className="mt-1.5 text-sm text-zinc-400">
                {recoveryDialogDescription}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-2.5 px-6 pb-6">
            {/* Primary card — state-aware. Renders a different CTA depending on
                whether the sandbox is healthy, disconnected, inactive, in error,
                or has blocked Cord recovery. */}
            {recoveryDialogMode === "healthy" ? (
              /* Sandbox is already running and connected — no action needed. */
              <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/[0.06] p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15">
                    <Check className="size-4 text-emerald-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-100">Sandbox is running</p>
                    <p className="mt-0.5 text-[12px] leading-relaxed text-zinc-400">
                      Everything is connected and working normally. Advanced recovery options are available below if you need them.
                    </p>
                  </div>
                </div>
              </div>
            ) : recoveryDialogMode === "sandbox_inactive" ? (
              /* Sandbox is stopped/suspended — offer to start/resume it. */
              <div className="rounded-xl border border-blue-400/20 bg-blue-500/[0.06] p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/15">
                    <Play className="size-4 text-blue-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-100">{sessionSuspended ? "Resume sandbox" : "Start sandbox"}</p>
                    <p className="mt-0.5 text-[12px] leading-relaxed text-zinc-400">
                      {sessionSuspended
                        ? "Wake the sandbox from its suspended state so you can continue working."
                        : "Start the sandbox so the agent can run commands again."}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        void requestResume({ force: true });
                        setShowRecoverySshDialog(false);
                      }}
                      disabled={resumeActionPending}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-blue-500 px-3.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {resumeActionPending ? <SpinnerGap className="size-3.5 animate-spin" /> : <Play className="size-3.5" />}
                      {resumeActionPending ? "Starting…" : sessionSuspended ? "Resume sandbox" : "Start sandbox"}
                    </button>
                  </div>
                </div>
              </div>
            ) : recoveryDialogMode === "cord_disconnected" ? (
              /* Sandbox is running but WS/transport lost — reconnect the session. */
              <div className="rounded-xl border border-blue-400/20 bg-blue-500/[0.06] p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/15">
                    <ArrowsClockwise className="size-4 text-blue-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-100">Reconnect session</p>
                    <p className="mt-0.5 text-[12px] leading-relaxed text-zinc-400">
                      The sandbox is still running — this reconnects the app's session link.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        requestSessionReconnect();
                        setShowRecoverySshDialog(false);
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-blue-500 px-3.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <ArrowsClockwise className="size-3.5" />
                      {status === "connecting" || transportLoading ? "Force reconnect" : "Reconnect session"}
                    </button>
                  </div>
                </div>
              </div>
            ) : recoveryDialogMode === "recovery_blocked" ? (
              /* Cord recovery is blocked — SSH is the recommended path.
                 We do NOT show a primary reconnect CTA here because Cord already
                 tried and gave up. Instead we show the retry as a secondary
                 option below the SSH section. */
              <div className="rounded-xl border border-amber-400/20 bg-amber-500/[0.06] p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15">
                    <WarningCircle className="size-4 text-amber-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-100">Use SSH for direct access</p>
                    <p className="mt-0.5 text-[12px] leading-relaxed text-zinc-400">
                      Automatic reconnect failed. SSH into the sandbox directly — it works even when the in-app connection is blocked. Use the SSH section below to generate access.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* sandbox_error — original reconnect behavior. */
              <div className="rounded-xl border border-blue-400/20 bg-blue-500/[0.06] p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/15">
                    <ArrowsClockwise className="size-4 text-blue-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-100">Reconnect sandbox</p>
                    <p className="mt-0.5 text-[12px] leading-relaxed text-zinc-400">
                      Start here — this fixes most issues in a few seconds.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        requestSandboxRetry();
                        setShowRecoverySshDialog(false);
                      }}
                      disabled={retrySandboxPending}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-blue-500 px-3.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {retrySandboxPending ? <SpinnerGap className="size-3.5 animate-spin" /> : <ArrowsClockwise className="size-3.5" />}
                      {retrySandboxPending ? "Reconnecting…" : "Reconnect sandbox"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Advanced path A (opencode only): attach from the user's local
                OpenCode install. This is the "native" recovery path for opencode
                — they stay in their familiar TUI and just point it at the remote
                server. Requires the sandbox to be reachable; if not, SSH below
                can force a restart. Claude Code has no equivalent so this
                section does not render for claude_code sessions. */}
            {agentType === "opencode" && recoveryDialogMode !== "recovery_blocked" && (
              <div className="overflow-hidden rounded-xl border border-zinc-800/60">
                <button
                  type="button"
                  onClick={() => setRecoveryShowOpenCodeAttach((v) => !v)}
                  aria-expanded={recoveryShowOpenCodeAttach}
                  className="flex w-full items-center gap-3 bg-zinc-900/40 px-4 py-3 text-left transition-colors hover:bg-zinc-900/60"
                >
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-zinc-800">
                    <Terminal className="size-3.5 text-zinc-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-200">Attach with local OpenCode</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">
                      Open this session in your local <span className="font-mono text-zinc-400">opencode</span> TUI. Fastest path if the sandbox is still reachable.
                    </p>
                  </div>
                  {recoveryShowOpenCodeAttach
                    ? <CaretDown className="size-3.5 shrink-0 text-zinc-500" />
                    : <CaretRight className="size-3.5 shrink-0 text-zinc-500" />}
                </button>
                {recoveryShowOpenCodeAttach && (
                  <div className="space-y-2.5 border-t border-zinc-800/60 bg-zinc-950/30 px-4 py-3">
                    {recoveryOpenCodeAttachCommand ? (
                      <>
                        <p className="text-xs leading-relaxed text-zinc-500">
                          Run this in your local terminal. It contains a session credential — treat it like a password.
                        </p>
                        <div className="group relative rounded-lg border border-zinc-800/60 bg-zinc-900/80 px-3 py-2.5">
                          <pre className="overflow-x-auto whitespace-pre-wrap break-all pr-14 font-mono text-xs leading-relaxed text-zinc-300">{recoveryOpenCodeAttachCommand.command}</pre>
                          <button
                            onClick={() => {
                              void copyRecoveryOpenCodeAttachCommand(recoveryOpenCodeAttachCommand.command);
                            }}
                            className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-zinc-800 px-2 py-1 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                          >
                            <Copy className="size-3" />
                            Copy
                          </button>
                        </div>
                        <p className="text-xs leading-relaxed text-zinc-500">
                          Don't have <span className="font-mono text-zinc-400">opencode</span> installed? See the{" "}
                          <a href="https://opencode.ai/docs#install" target="_blank" rel="noopener noreferrer" className="text-zinc-400 underline underline-offset-2 hover:text-zinc-200">install docs</a>, or use SSH below instead.
                        </p>
                      </>
                    ) : recoveryRuntimeAccessQuery.isFetching ? (
                      <div className="flex items-center gap-2 py-1">
                        <SpinnerGap className="size-3.5 animate-spin text-zinc-500" />
                        <p className="text-[12px] text-zinc-500">Loading session details…</p>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-zinc-800/60 bg-zinc-950/40 px-3 py-2">
                        <p className="text-[12px] text-zinc-400">Attach command is unavailable.</p>
                        <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                          The session details needed to attach aren't loaded right now. Use SSH below instead — it works even when attach details are missing.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Advanced path B: SSH into the sandbox. Universal — works for both
                opencode and claude_code, and can start/recover the sandbox even
                when it's stopped. For claude_code this is the ONLY direct
                recovery path (no local attach equivalent exists). The
                "then run inside the sandbox" block shows the right CLI for each
                agent (opencode --session X / claude --resume X). */}
            <div className="overflow-hidden rounded-xl border border-zinc-800/60">
              <button
                type="button"
                onClick={() => setRecoveryShowShellAccess((v) => !v)}
                aria-expanded={recoveryShowShellAccess}
                className="flex w-full items-center gap-3 bg-zinc-900/40 px-4 py-3 text-left transition-colors hover:bg-zinc-900/60"
              >
                <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-zinc-800">
                  <Key className="size-3.5 text-zinc-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-200">
                    {agentType === "claude_code" ? "Resume in a terminal (SSH)" : "SSH into sandbox"}
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">
                    {agentType === "claude_code"
                      ? <>Opens a remote shell where you can run <span className="font-mono text-zinc-400">claude --resume</span> to continue this session.</>
                      : agentType === "opencode"
                        ? "Full shell access. Also starts the sandbox if it's stopped — use this if attach above doesn't connect."
                        : "Full shell access inside the sandbox. Also starts the sandbox if it's stopped."}
                  </p>
                </div>
                {recoveryShowShellAccess
                  ? <CaretDown className="size-3.5 shrink-0 text-zinc-500" />
                  : <CaretRight className="size-3.5 shrink-0 text-zinc-500" />}
              </button>
              {recoveryShowShellAccess && (
                <div className="space-y-3 border-t border-zinc-800/60 bg-zinc-950/30 px-4 py-3">
                  {recoverySshStatus?.error && (
                    <p className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                      SSH isn't available right now: {recoverySshStatus.error}
                    </p>
                  )}

                  {recoverySshAccessActive ? (
                    <div className="space-y-2.5">
                      <p className="text-xs leading-relaxed text-zinc-500">
                        Run this in your local terminal. Treat it like a password — it grants full shell access to the sandbox.
                      </p>
                      <div className="group relative rounded-lg border border-zinc-800/60 bg-zinc-900/80 px-3 py-2.5">
                        <pre className="overflow-x-auto whitespace-pre-wrap break-all pr-14 font-mono text-xs leading-relaxed text-zinc-300">{recoverySshAccessActive.sshCommand}</pre>
                        <button
                          onClick={() => {
                            void copyRecoverySshCommand();
                          }}
                          className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-zinc-800 px-2 py-1 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                        >
                          <Copy className="size-3" />
                          Copy
                        </button>
                      </div>

                      {recoveryRuntimeCliCommands.length > 0 && (
                        <div className="rounded-lg border border-zinc-800/40 bg-zinc-900/50 px-3 py-2">
                          <p className="mb-1.5 text-xs font-medium text-zinc-500">
                            {agentType === "claude_code"
                              ? "Then, inside the sandbox, resume Claude Code:"
                              : "Then, inside the sandbox, resume the session:"}
                          </p>
                          {recoveryRuntimeCliCommands.map((entry) => (
                            <div key={entry.label} className="mt-1">
                              <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-zinc-300">{entry.command}</pre>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <p className="text-xs text-zinc-500">
                          Expires {formatMenuTimestamp(Date.parse(recoverySshAccess!.expiresAt))}
                        </p>
                        <button
                          onClick={() => {
                            requestRecoverySshAccess.mutate({
                              previousToken: recoverySshAccessActive.token,
                            });
                          }}
                          disabled={requestRecoverySshAccess.isPending}
                          className="text-xs text-zinc-400 transition-colors hover:text-zinc-200 disabled:opacity-50"
                        >
                          {requestRecoverySshAccess.isPending ? "Regenerating…" : "Regenerate"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[12px] leading-relaxed text-zinc-400">
                        {recoverySshAccessExpired
                          ? "Your previous SSH token expired."
                          : "Generate a short-lived SSH token to connect directly to the sandbox."}
                      </p>
                      <button
                        onClick={() => {
                          requestRecoverySshAccess.mutate({});
                        }}
                        disabled={recoverySshAccessDisabled}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-800 px-3 py-1.5 text-[12px] font-medium text-zinc-200 transition-colors hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {requestRecoverySshAccess.isPending || recoverySshStatusLoading
                          ? <SpinnerGap className="size-3.5 animate-spin" />
                          : <Key className="size-3.5" />}
                        {recoverySshStatusLoading ? "Checking SSH availability…" : "Generate SSH access"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Secondary: retry in-app recovery. Only shown when Cord recovery
                is blocked so the user can still attempt the in-app path after
                SSH. In other modes the primary card already handles reconnect. */}
            {recoveryDialogMode === "recovery_blocked" && (
              <div className="overflow-hidden rounded-xl border border-zinc-800/60">
                <div className="flex items-start gap-3 bg-zinc-900/40 px-4 py-3">
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-zinc-800">
                    <ArrowsClockwise className="size-3.5 text-zinc-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-200">Retry in-app recovery</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">
                      Attempts to repair the session connection through the normal path. This may work if the issue was transient.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        requestSandboxRetry();
                        setShowRecoverySshDialog(false);
                      }}
                      disabled={retrySandboxPending}
                      className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-zinc-800 px-3 py-1.5 text-[12px] font-medium text-zinc-200 transition-colors hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {retrySandboxPending ? <SpinnerGap className="size-3.5 animate-spin" /> : <ArrowsClockwise className="size-3.5" />}
                      {retrySandboxPending ? "Retrying…" : "Retry recovery"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Advanced: GitHub auth for the shell session. Only useful once the
                user has SSH access — without it there's nowhere to paste this
                snippet. Gate by SSH being active (or already having a token)
                so we don't expose a scary-looking GitHub token option to users
                who still have a working connection. */}
            {repoProvider === "github" && (recoverySshAccessActive || recoveryGithubAccess) && (
              <div className="overflow-hidden rounded-xl border border-zinc-800/60">
                <button
                  type="button"
                  onClick={() => setRecoveryShowGithubAccess((v) => !v)}
                  aria-expanded={recoveryShowGithubAccess}
                  className="flex w-full items-center gap-3 bg-zinc-900/40 px-4 py-3 text-left transition-colors hover:bg-zinc-900/60"
                >
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-zinc-800">
                    <GitBranch className="size-3.5 text-zinc-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-200">GitHub auth inside the shell</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">
                      Only needed if git or gh fails inside your SSH session. Web push/PR works without this.
                    </p>
                  </div>
                  {recoveryShowGithubAccess
                    ? <CaretDown className="size-3.5 shrink-0 text-zinc-500" />
                    : <CaretRight className="size-3.5 shrink-0 text-zinc-500" />}
                </button>
                {recoveryShowGithubAccess && (
                  <div className="space-y-2.5 border-t border-zinc-800/60 bg-zinc-950/30 px-4 py-3">
                    {recoveryGithubAccess && !recoveryGithubAccessExpired ? (
                      <>
                        <div className="group relative rounded-lg border border-zinc-800/60 bg-zinc-900/80 px-3 py-2.5">
                          <pre className="overflow-x-auto whitespace-pre-wrap break-all pr-14 font-mono text-xs leading-relaxed text-zinc-300">{recoveryGithubAccess.shellSnippet}</pre>
                          <button
                            onClick={() => {
                              void copyRecoveryGithubSnippet();
                            }}
                            className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-zinc-800 px-2 py-1 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                          >
                            <Copy className="size-3" />
                            Copy
                          </button>
                        </div>
                        <p className="text-xs leading-relaxed text-zinc-500">
                          Expires {formatMenuTimestamp(Date.parse(recoveryGithubAccess.expiresAt))}. Paste this into your shell before running git or gh.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[12px] leading-relaxed text-zinc-400">
                          {recoveryGithubAccessExpired
                            ? "Your previous GitHub token expired."
                            : "Generate a short-lived GitHub token you can paste into your SSH session."}
                        </p>
                        <button
                          onClick={() => {
                            requestRecoveryGithubAccess.mutate();
                          }}
                          disabled={requestRecoveryGithubAccess.isPending}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-800 px-3 py-1.5 text-[12px] font-medium text-zinc-200 transition-colors hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {requestRecoveryGithubAccess.isPending ? <SpinnerGap className="size-3.5 animate-spin" /> : <Key className="size-3.5" />}
                          Generate GitHub token
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Debug footer. Deliberately small and de-emphasized — this info is
                for the user to quote to support, not to act on. */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-3 text-xs text-zinc-600">
              <span>
                Sandbox state: <span className="text-zinc-500">{recoveryProviderStatusLabel}</span>
              </span>
              {recoveryProviderRuntime?.sandboxId && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span className="truncate font-mono text-zinc-500">{recoveryProviderRuntime.sandboxId}</span>
                </>
              )}
              {recoveryStatusQuery.isFetching && <SpinnerGap className="size-3 animate-spin text-zinc-500" />}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SessionLayout
        key={sessionId}
        header={headerContent}
        sessionId={sessionId}
        sandboxRunning={runtimeInteractionEnabled}
        sandboxUnavailableReason={sandboxUnavailableReason}
        baseBranch={sessionBaseBranch}
        sandboxSuspended={sandboxResumeNeeded}
        previewResumeDisabled={requiresExplicitResume || sessionArchived}
        cachedWarningReason={cachedSidebarWarningReason}
        historyPresenceHint={sessionHistoryPresence}
        sessionCreatedAt={sessionCreatedAt}
        ptyUrl={effectivePtyUrl}
        previewUrl={effectivePreviewUrl}
        previewKey={previewKey}
        previewFrameEnabled={previewFrameEnabled}
        previewEndpoints={effectivePreviewEndpoints}
        selectedPreviewEndpointId={selectedPreviewEndpointId}
        onSelectPreviewEndpoint={handleSelectPreviewEndpoint}
        runtimeProcesses={effectiveRuntimeProcesses}
        preferredLogProcessId={selectedPreviewStartTarget?.id ?? null}
        devServerStatus={devServerStatus}
        terminalStartupPending={terminalStartupPending}
        terminalStartupLabel={terminalStartupLabel}
        previewStartupPending={previewStartupPending}
        previewStartupLabel={previewStartupLabel}
        send={send}
        onPtyStatusChange={setPtyWsStatus}
        onTerminalActionRequest={handleTerminalActionRequest}
        onReloadPreview={handleReloadPreview}
        onRestartDevServer={handleRestartDevServer}
        onResumeAndWaitForRunning={resumeAndWaitForRunning}
        mobileToolsPanel={mobileToolsContent}
      >
        <ImagePreviewProvider key={`${repoId}:${sessionId}`}>
          {/* Chat panel */}
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            {/* Messages */}
            <div ref={handleChatScrollRootRef} className={cn(
              "relative flex flex-col overflow-y-auto overflow-x-hidden",
              shouldCenterComposer ? "hidden" : "flex-1 animate-[fade-in_0.3s_ease-out]"
            )}>
              <div className={cn("mx-auto max-w-3xl px-4 py-3 w-full", isMobileLayout && "pb-36")}>
                {forkBootstrapPending && (
                  <ForkBootstrapBanner
                    status={forkBootstrapEntry?.status === "reconciling" ? "reconciling" : "pending"}
                  />
                )}

                {forkBootstrapFailed && (
                  <ForkBootstrapBanner
                    status="failed"
                    errorMessage={forkBootstrapEntry?.errorMessage ?? null}
                    retryPending={forkBootstrapMutation.isPending}
                    onRetry={() => {
                      void retryForkBootstrap()
                    }}
                  />
                )}

                {!sessionArchived && sandboxStatus === "error" && !sandboxErrorDismissed && (
                  <SandboxErrorBanner
                    activeCredentialSource={activeCredentialSource}
                    credentialFallbackPending={manualCredentialFallback.isPending}
                    credentialFallbackTargetLabel={
                      shouldOfferManualCredentialFallback
                        ? credentialFallbackActionLabel
                        : null
                    }
                    onCredentialFallback={
                      shouldOfferManualCredentialFallback
                        ? () => {
                            void requestManualCredentialFallback("sandbox_error");
                          }
                        : undefined
                    }
                    progress={sandboxProgress}
                    onRecoveryOptions={canOpenRecoverySshDialog ? () => {
                      setShowRecoverySshDialog(true);
                    } : undefined}
                    recoveryOptionsLabel={recoveryCordRetryBlocked ? "Open recovery options" : "Recovery options"}
                    retryLabel={
                      recoveryCordRetryBlocked
                        ? "Retry in-app"
                        : runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_STOPPED"
                          ? "Resume/recover sandbox"
                          : undefined
                    }
                    retryPendingLabel={
                      recoveryCordRetryBlocked
                        ? "Retrying in-app..."
                        : runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_STOPPED"
                          ? "Resuming..."
                          : undefined
                    }
                    retryPending={retrySandboxPending}
                    title={
                      recoveryCordRetryBlocked
                        ? "Sandbox recovery needs attention"
                        : runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_STOPPED"
                          ? "Sandbox stopped unexpectedly"
                          : runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_SANDBOX_UNROUTABLE"
                            ? "Sandbox is unreachable"
                            : runtimeLastErrorCode === "SANDBOX_AUTH_BROKER_HEALTH_TIMEOUT"
                              ? "Sandbox needs recovery"
                              : undefined
                    }
                    onRetry={sessionArchived ? undefined : () => {
                      requestSandboxRetry();
                    }}
                    onDismiss={dismissSandboxError}
                  />
                )}

                {!sessionArchived && (
                  <SessionCredentialUsagePanel
                    activeSource={activeCredentialSource}
                    nextSource={nextCredentialSource}
                    canManage={canManageCredentialUsage}
                    fallbackPending={manualCredentialFallback.isPending}
                    fallbackWarningReason={localCredentialFallbackWarningReason}
                    fallbackRequiresConfirmation={localCredentialFallbackWarningReason !== null}
                    refreshPending={refreshCredentialUsage.isPending}
                    usageLoading={credentialUsageQuery.isLoading}
                    usageResponse={canManageCredentialUsage ? credentialUsageQuery.data : undefined}
                    onFallback={
                      canManageCredentialUsage && nextCredentialSource
                        ? () => {
                            void requestManualCredentialFallback("subscription_usage");
                          }
                        : null
                    }
                    onRefresh={
                      canManageCredentialUsage
                        ? () => {
                            captureAnalyticsEvent({
                              event: ANALYTICS_EVENTS.credentialUsageRefreshRequested,
                              properties: buildThreadAnalyticsProperties({
                                agentType: sessionAgentType,
                                repositoryId: repoId,
                                sessionId,
                                source: "thread_page",
                              }),
                            });
                            refreshCredentialUsage.mutate();
                          }
                        : null
                    }
                  />
                )}

              {sandboxAuthBannerVisible && (
                <SandboxAuthHealthBanner
                  canRepair={sandboxAuthBannerCanRepair}
                  error={sandboxAuthHealth.data?.error ?? sandboxAuthHealthError?.message ?? null}
                  errorCode={sandboxAuthErrorCode}
                  isRepairing={repairSandboxAuth.isPending}
                  isRefreshing={sandboxAuthHealth.isFetching}
                  onRepair={() => {
                    repairSandboxAuth.mutate();
                  }}
                  onRefresh={() => {
                    void sandboxAuthHealth.refetch();
                  }}
                  title={sandboxAuthProviderUnavailable ? "Sandbox unavailable" : undefined}
                />
              )}

              {!isRepolessRoute &&
                bootSource === "bare" &&
                sandboxStatus === "running" &&
                !bareImageBannerDismissed && (
                  buildHealthData ? (
                    <BuildHealthBareImageBanner
                      repoId={repoId}
                      buildHealth={buildHealthData}
                      onRebuild={() => buildHealthRebuildMutation.mutate()}
                      onDismiss={() => setBareImageBannerDismissed(true)}
                    />
                  ) : (
                    <BareImageBanner
                      repoId={repoId}
                      buildSummary={bareImageBuildSummary}
                      hasBuildConfig={bareImageHasBuildConfig}
                      onDismiss={() => setBareImageBannerDismissed(true)}
                    />
                  )
              )}

              {!isRepolessRoute &&
                bootSource === "prebuilt" &&
                sandboxStatus === "running" &&
                !staleBuildBannerDismissed &&
                prebuiltBuildHealthData?.status === "blocked" && (
                <StaleBuildBanner
                  repoId={repoId}
                  buildHealth={prebuiltBuildHealthData}
                  onRebuild={() => buildHealthRebuildMutation.mutate()}
                  onDismiss={() => setStaleBuildBannerDismissed(true)}
                />
              )}

              {agentError &&
                !shouldSuppressDuplicateAgentErrorBanner &&
                !isAbortLikeError(agentError) &&
                agentError.type !== RUNTIME_ERROR_BASE_IMAGE_STALE && (
                <AgentErrorBanner error={agentError} onDismiss={dismissError} />
              )}

              {!agentError && runtimeReplicationIntegrityState === "suspect" && (
                <div className="rounded-xl border border-amber-900/50 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
                  {runtimeReplicationIntegrityReason ?? "Recovering session history."}
                </div>
              )}

              <RuntimeChatPane
                sessionId={sessionId}
                activeConversationId={activeConversationId}
                sandboxStatus={sandboxStatus}
                queuePresentationContext={runtimeChatQueuePresentationContext}
                chatState={foregroundSessionController.chatState}
                queuedMessages={visibleQueuedMessages}
                optimisticRevertMessageId={optimisticRevertMessageId}
                optimisticRevertHiddenMessageIds={optimisticRevertHiddenMessageIds}
                optimisticRevertRestorePending={optimisticRevertRestorePending}
                messageRejectionTimestamp={lastMessageRejection?.timestamp ?? null}
                editFromMessageAvailable={editActionState.available}
                onEditFromMessage={handleEditFromMessageUi}
                onSubmitEditFromMessage={isClaudeSession
                  ? handleSubmitEditFromMessageUi
                  : undefined}
                forkFromMessageAvailable={forkActionState.available}
                onForkFromMessage={handleForkFromMessageUi}
                onCancelQueuedMessage={sessionArchived ? undefined : handleCancelQueuedMessage}
                disableEditFromMessage={!editActionState.enabled}
                editFromMessageDisabledReason={editActionState.disabledReason ?? undefined}
                disableForkActions={!forkActionState.enabled}
                forkFromMessageDisabledReason={forkActionState.disabledReason ?? undefined}
                sessionActionNotice={visibleSessionActionNotice}
                actionOutcome={visibleRuntimeActionOutcome}
                timelineInterstitials={startNewWorkTimelineInterstitials}
                interactionBlockedReason={interactionBlockedReason}
                sandboxResuming={resumeActionPending && sandboxResumeNeeded}
                onReplyQuestion={handleReplyQuestion}
                onRejectQuestion={handleRejectQuestion}
                onReplyPermission={handleReplyPermission}
                emptyState={runtimeChatEmptyState}
                externalScrollRootRef={chatScrollRootRef}
                buildPrVisitHref={buildPrVisitHrefForChildren}
              />

              {historicalSessionPullRequest && sessionPrNumber !== null && (
                <HistoricalPullRequestBanner
                  repoId={repoId}
                  prNumber={sessionPrNumber}
                  prUrl={sessionPrVisitHref}
                  prState={effectivePrState}
                  activeOwnerSessionId={sessionPrActiveOwnerSessionId}
                  activeOwnerSessionTitle={sessionPrActiveOwnerSessionTitle}
                  moveOwnershipPending={reclaimPrOwnership.isPending}
                  onMoveOwnershipHere={canReclaimHistoricalPrOwnership
                    ? () => {
                        void reclaimPrOwnership.mutateAsync();
                      }
                    : undefined}
                />
              )}

              {mergedPrNeedsContinuation && (
                <>
                  <MergedPrBanner
                    archived={sessionArchived}
                    archiveDisabled={archiveToggleDisabled}
                    archivePending={archiveTogglePending}
                    onToggleArchive={() => {
                      void handleArchiveToggle();
                    }}
                    canResume={showMergedBannerResumeAction}
                    resumePending={resumeActionPending}
                    resumeDisabledReason={mergedBannerResumeDisabledReason}
                    onResume={() => {
                      void requestResume({ force: true });
                    }}
                    canContinue={canContinueNewSession}
                    continuePending={continueSession.isPending}
                    continueDisabledReason={continueInNewSessionDisabledReason}
                    onContinue={handleContinueInNewSession}
                    startNewWorkStatusLabel={startNewWorkStatusLabel}
                    startNewWorkStatusDescription={startNewWorkStatusDescription}
                    startNewWorkLabel={startNewWorkActionLabel}
                    startNewWorkDescription={startNewWorkActionDescription}
                    startNewWorkPending={startNewWorkActionPending}
                    startNewWorkDisabledReason={startNewWorkDisabledReason}
                    onStartNewWork={handleStartNewWork}
                  />
                  <div className="h-px" />
                </>
              )}

              {effectivePrState === "closed" && (
                <ClosedPrNotice
                  prNumber={sessionPrNumber}
                  prUrl={sessionPrVisitHref}
                />
              )}

              {effectivePrState !== "merged" && requiresExplicitResume && (
                <ResumeRequiredBanner
                  archived={sessionArchived}
                  isPending={resumeActionPending}
                  resumeDisabledReason={resumeBannerResumeDisabledReason}
                  onResume={() => {
                    void requestResume({ force: true });
                  }}
                  canContinue={canContinueNewSession}
                  continuePending={continueSession.isPending}
                  continueDisabledReason={continueInNewSessionDisabledReason}
                  onContinue={handleContinueInNewSession}
                />
              )}
            </div>
          </div>

          {/* Input section — on mobile: floats over content with glass blur. Desktop: pinned below scroll. */}
           <div
             ref={handleMobileComposerContainerRef}
             className={cn(
               "flex flex-col",
               shouldCenterComposer
                 ? "flex-1 justify-center"
                 : isMobileLayout
                   ? "absolute inset-x-0 bottom-0 z-30"
                   : "flex-none",
             )}
             // On mobile empty-session state, prevent taps on the background from
             // blurring the editor — this keeps the keyboard open (Codex-style).
             onMouseDown={isMobileLayout && shouldCenterComposer ? (e) => {
               // Only prevent on the container itself, not on interactive children
               if (e.target === e.currentTarget) e.preventDefault()
             } : undefined}
             onTouchStart={isMobileLayout && shouldCenterComposer ? (e) => {
               if (e.target === e.currentTarget) e.preventDefault()
             } : undefined}
           >

          {/* Codex-style centered prompt text for mobile empty session */}
          {isMobileLayout && shouldCenterComposer && (
            <div
              className="flex flex-1 flex-col items-center justify-center px-6"
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
            >
              <p className="text-xl font-medium text-zinc-200">What should we work on?</p>
              {repo && (
                <div className="mt-3 flex items-center gap-2 text-sm text-zinc-500">
                  <RepoIcon
                    repositoryId={repo.id}
                    iconUpdatedAt={null}
                    className="size-4 rounded"
                    fallbackClassName="size-4 rounded bg-zinc-700/60 p-0.5 text-zinc-400"
                  />
                  <span>{repo.displayName || repo.githubFullName}</span>
                </div>
              )}
            </div>
          )}

          {/* Todo dock */}
          <TodoDock
            sessionId={sessionId}
            ready={foregroundSessionController.ready}
            isCatchingUp={isCatchingUp}
          />

          {/* Banners above chat input — width-constrained to match input box */}
          <div className={cn(isMobileLayout ? "px-2" : "px-4")}>
            <div className={cn("mx-auto", isMobileLayout ? "max-w-full" : "max-w-3xl")}>
              {editMessageOperationPending && activeEditMessageOperation && (
                <div className="mb-2 flex items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-950/25 px-3 py-2 text-xs text-blue-100">
                  <SpinnerGap className="size-3.5 animate-spin" />
                  <span>
                    {activeEditMessageOperation.state === "queued" && "Preparing edit operation..."}
                    {activeEditMessageOperation.state === "aborting" && "Stopping current run..."}
                    {activeEditMessageOperation.state === "awaiting_idle" && "Waiting for run to stop..."}
                    {activeEditMessageOperation.state === "reverting" && "Rewinding timeline..."}
                    {activeEditMessageOperation.state === "resending" && "Sending edited message..."}
                  </span>
                </div>
              )}

              {branchSwitchNotice && (
                <BranchSwitchInProgressNotice
                  title={branchSwitchNotice.title}
                  description={branchSwitchNotice.description}
                />
              )}

              {redoActionState.available && revertMessageID && !suppressRevertBanner && (
                <div className="mb-2 flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
                  <div className="flex items-center gap-1.5">
                    <span>Message undone — send a new reply or restore the original.</span>
                    <Tooltip
                      content="Undo rewinds the conversation to just before your message. Any responses after that point are hidden but not deleted. Send a new message to continue, or click Restore to bring everything back."
                      side="top"
                      className="max-w-[280px]"
                    >
                      <button type="button" className="inline-flex items-center text-amber-400/60 transition-colors hover:text-amber-300">
                        <Info className="size-3.5" />
                      </button>
                    </Tooltip>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      void handleRestoreRewind();
                    }}
                    disabled={!redoActionState.enabled}
                    title={redoActionState.disabledReason ?? "Restore conversation"}
                    className="shrink-0 rounded border border-amber-500/40 px-2 py-0.5 text-amber-100 transition-colors hover:bg-amber-500/20 disabled:opacity-40"
                  >
                    Restore
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Shared chat input */}
          {!agentIdentityResolved ? (
            <div className={cn(!shouldCenterComposer && "border-t border-zinc-700/50", "px-4 py-3 text-xs text-zinc-500")}>
              Waiting for thread identity before loading chat input...
            </div>
          ) : (
            <ChatInputArea
              ref={handleEditorRef}
              repositoryId={repoId}
              sessionId={sessionId}
              conversationId={activeConversationId}
              baseBranch={displaySessionBaseBranch}
              sandboxRunning={runtimeInteractionEnabled}
              historyPresenceHint={sessionHistoryPresence}
              serverSessionIntent={persistedSessionModelIntent}
              serverSessionIntentConversationId={persistedSessionModelIntentConversationId}
              workState={activeThreadControlState.workState}
              stopMode={activeThreadControlState.stopMode}
              stopPending={activeThreadControlState.stopPending}
              stopDisabledReason={activeThreadControlState.stopDisabledReason}
              busyComposerPlaceholder={activeThreadControlState.busyComposerPlaceholder}
              enableAbortHotkeys={activeThreadControlState.enableAbortHotkeys}
              canSend={canSend}
              onSend={handleSend}
              onAbort={handleEditorAbort}
              onEscape={handleEditorEscape}
              onContentChange={setHasContent}
              onTextChange={handleEditorTextChange}
              onSessionContextRefsChange={handleEditorSessionContextRefsChange}
              sessionContextRefs={draftSessionContextRefs}
              onRemoveSessionContextRef={handleRemoveDraftSessionContextRef}
              onRequestedSkillRefsChange={handleEditorRequestedSkillRefsChange}
              requestedSkillRefs={draftRequestedSkillRefs}
              onRemoveRequestedSkillRef={handleRemoveDraftRequestedSkillRef}
              onPreferredMcpServerRefsChange={handleEditorPreferredMcpServerRefsChange}
              preferredMcpServerRefs={draftPreferredMcpServerRefs}
              onRemovePreferredMcpServerRef={handleRemoveDraftPreferredMcpServerRef}
              scratchPadNoteItems={scratchPadNoteItems}
              onScratchPadRefsChange={handleEditorScratchPadRefsChange}
              scratchPadRefs={draftScratchPadRefs}
              additionalSessionPickerItems={conversationContextPickerItems}
              onInteraction={handleInputInteraction}
              onEditorTransientBusyChange={setEditorTransientBusy}
              onSessionPickerIntent={handleSessionPickerIntent}
              onPrPickerIntent={handlePrPickerIntent}
              prAdoptionStatus={prAdoptionStatus}
              onDismissPrAdoptionError={dismissPrAdoptionStatus}
              onDismissPrAdoptionSuccess={dismissPrAdoptionStatus}
              history={historyRef.current}
              historyIndex={historyIndexRef}
              savedInput={savedInputRef}
              attachments={capabilities.supportsImageAttachments ? attachments : undefined}
              onAddImageFiles={capabilities.supportsImageAttachments ? addImageAttachments : undefined}
              onRemoveAttachment={capabilities.supportsImageAttachments ? removeAttachment : undefined}
              onRetryAttachment={capabilities.supportsImageAttachments ? retryAttachment : undefined}
              onUndo={undoActionState.available ? handleComposerUndo : undefined}
              onCompact={compactActionState.available ? handleComposerCompact : undefined}
              onReviewCurrentTab={handleReviewCurrentTabInputAction}
              onReviewFullBranch={handleReviewFullBranchInputAction}
              slashCommands={capabilities.supportsSlashCommands ? slashCommands : undefined}
              disableUndo={!undoActionState.enabled}
              disableCompact={!compactActionState.enabled}
              showReviewModeButton={shouldShowReviewModeButton}
              disableReviewMode={reviewModeInputDisabled}
              undoDisabledReason={undoActionState.disabledReason ?? undefined}
              compactDisabledReason={compactActionState.disabledReason ?? undefined}
              reviewModeDisabledReason={reviewModeInputDisabledReason}
              interactionBlockedReason={composerInteractionBlockedReason}
              agentType={chatInputAgentType}
              sessionBranch={displaySessionBranch}
              sessionBaseBranch={displaySessionBaseBranch}
              repoDefaultBranch={repo?.defaultBranch ?? null}
              branchTreeUrl={displayBranchTreeUrl}
              branchPending={startNewWork.isPending || localStartNewWorkGitPending !== null || startNewWorkGitPending}
              onCopyBranch={copySessionBranch}
              branchCopied={branchCopied}
              pendingSecondaryActionKey={pendingComposerSecondaryActionKey}
              activeCredentialSource={activeCredentialSource}
              credentialUsageResponse={canManageCredentialUsage ? credentialUsageQuery.data : undefined}
              credentialUsageLoading={credentialUsageQuery.isLoading}
              credentialUsageRefreshPending={refreshCredentialUsage.isPending}
              onRefreshCredentialUsage={
                canManageCredentialUsage
                  ? () => {
                      captureAnalyticsEvent({
                        event: ANALYTICS_EVENTS.credentialUsageRefreshRequested,
                        properties: buildThreadAnalyticsProperties({
                          agentType: sessionAgentType,
                          repositoryId: repoId,
                          sessionId,
                          source: "chat_input_toolbar",
                        }),
                      });
                      refreshCredentialUsage.mutate();
                    }
                  : undefined
              }
              canManageSession={canManageCredentialUsage}
              hideBorder={shouldCenterComposer}
            />
          )}
          </div>
        </div>
        </ImagePreviewProvider>
      </SessionLayout>
    </FilePathSetProvider>
  );
}
